import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  MousePointer2, Square, Minus, Hand, Grid3X3, Magnet,
  ZoomIn, ZoomOut, Maximize2, BookOpen, Printer,
  Plus, Trash2, Save, X, AlertTriangle, ChevronDown, ChevronRight, RotateCcw, RotateCw,
  FileImage, Eye, EyeOff, SplitSquareHorizontal, MapPin,
  Scissors, HelpCircle, Link2, Zap, LayoutGrid, Map as MapIcon, Pencil,
  LayoutDashboard, Package, Flame, Palette, Wrench, ClipboardList,
  Expand, Shrink, Download, Upload, ArrowLeftRight,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import apiClient, { api } from '../api/client';
import useFacilityRooms, { FacilityRoom, NewRoomData } from '../hooks/useFacilityRooms';
import RoomDetailPanel from '../components/RoomDetailPanel';
import DatePicker from '../components/DatePicker';
import '../styles/buttonAnimations.css';
import { spinIconOut } from '../utils/iconAnimations';

// ─── Constants ────────────────────────────────────────────────────────────────

const SNAP_SIZE = 10; // Round 21: halved from 20 -> denser grid. snap() (Math.round to nearest
// line -- no separate tolerance to scale) and DotGridSvg (tiles + hides at SNAP_SIZE*scale<6)
// both read this directly, so they track automatically. The snap-to-rooms THRESH (15) is a
// literal, unrelated (room-edge alignment), left as-is.
const MIN_ROOM_SIZE = 40;
const HANDLE_RADIUS = 5;
// Room-name label target size, in constant screen pixels (matches the mockup's rendered .room-label
// size). Divided by scale wherever used, same 1/scale compensation as the grid-dot fix, so labels
// stay legible at a fixed screen size instead of ballooning as the map is zoomed in.
const ROOM_LABEL_FONT_PX = 13;
// Below this zoom, room labels overlap/garble (many rooms, few screen pixels each) and text-wrap
// measurement is the most expensive part of a render -- skip labels entirely instead.
const LABEL_MIN_ZOOM = 0.25;

const ROOM_COLORS: Record<string, { fill: string; stroke: string; text: string }> = {
  resident:   { fill: '#dbeafe', stroke: '#93c5fd', text: '#1e40af' },
  medical:    { fill: '#dcfce7', stroke: '#86efac', text: '#166534' },
  common:     { fill: '#fef9c3', stroke: '#fde047', text: '#713f12' },
  service:    { fill: '#fce7f3', stroke: '#f9a8d4', text: '#9d174d' },
  mechanical: { fill: '#fee2e2', stroke: '#fca5a5', text: '#991b1b' },
  admin:      { fill: '#ede9fe', stroke: '#c4b5fd', text: '#5b21b6' },
  hallway:    { fill: '#f3f4f6', stroke: '#d1d5db', text: '#374151' },
  other:      { fill: '#fff7ed', stroke: '#fdba74', text: '#92400e' },
};

const ROOM_TYPES = Object.keys(ROOM_COLORS) as (keyof typeof ROOM_COLORS)[];

// Room's own color override (nullable facility_rooms.color) wins for fill/stroke when set;
// text stays on the type-based color so an arbitrary user-picked hue can't produce unreadable
// label text. Falls back to the full type-based triple when color is null, unchanged from before.
function roomColors(r: { type: string; color?: string | null }): { fill: string; stroke: string; text: string } {
  const base = ROOM_COLORS[r.type] ?? ROOM_COLORS.other;
  return r.color ? { fill: r.color, stroke: r.color, text: base.text } : base;
}

// ─── Facility Marker definitions ───────────────────────────────────────────────

interface FacilityMarker {
  id: number;
  facility_id: number;
  floor_index: number;
  x: number;
  y: number;
  marker_type: string;
  label: string | null;
  linked_extinguisher_id: number | null;
}

const MARKER_DEFS = [
  { id: 'extinguisher', color: '#dc2626', symbol: 'E',  shape: 'circle',  label: 'Extinguisher' },
  { id: 'pull_station', color: '#dc2626', symbol: '↓',  shape: 'square',  label: 'Pull Station' },
  { id: 'fire_alarm',   color: '#f97316', symbol: 'FA', shape: 'circle',  label: 'Fire Alarm'   },
  { id: 'o2',           color: '#3b82f6', symbol: 'O₂', shape: 'circle',  label: 'O₂ Storage'  },
  { id: 'hazard',       color: '#f59e0b', symbol: 'H',  shape: 'diamond', label: 'Hazard'       },
  { id: 'nurse_call',   color: '#10b981', symbol: 'N',  shape: 'circle',  label: 'Nurse Call'   },
] as const;

type Tool = 'select' | 'draw' | 'door' | 'window' | 'pan' | 'marker';
type HandleDir = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';

interface Handle { dir: HandleDir; x: number; y: number }
interface DrawingState { startX: number; startY: number; x: number; y: number; w: number; h: number }
// origX/origY/origPoints capture the room's state at drag-start so mousemove can compute a delta
// and translate `points` in lockstep with x/y (item 3 fix) -- previously only x/y moved, leaving
// `points` (what RoomShapeSvg actually renders for any polygon room) frozen in place, so dragging
// silently did nothing visually for the vast majority of rooms.
// `attached` captures the same origX/origY/origPoints for any bathroom whose notes.parent_room_id
// points at this room, snapshotted once at drag-start -- so the dragged room's own doors (already
// wall+pos relative to ITS rect, so they already move for free every render) AND any attached
// bathroom (a fully separate FacilityRoom with its own independent absolute x/y/points, otherwise
// completely oblivious to the parent moving) both actually travel with it (item 4 fix).
interface DragState {
  roomId: number; offsetX: number; offsetY: number; origX: number; origY: number; origPoints: { x: number; y: number }[] | null;
  attached: { id: number; origX: number; origY: number; origPoints: { x: number; y: number }[] | null }[];
}
interface ResizeState { roomId: number; dir: HandleDir; origX: number; origY: number; origW: number; origH: number; startWX: number; startWY: number; proportional?: boolean; centerX?: number; centerY?: number; origDist?: number; }
interface PanState { mx: number; my: number; ox: number; oy: number }

// ─── Geometry helpers ──────────────────────────────────────────────────────────

function snap(v: number, enabled: boolean) {
  return enabled ? Math.round(v / SNAP_SIZE) * SNAP_SIZE : v;
}

function canvasToWorld(cx: number, cy: number, ox: number, oy: number, scale: number) {
  return { wx: (cx - ox) / scale, wy: (cy - oy) / scale };
}

// Inverse of the map-view rotation (applied as `rotate(deg)` to the outer render <g> about the
// canvas centre). Any pointer/screen coordinate must pass through this BEFORE canvasToWorld once
// the view is rotated, otherwise every hit-test, drag delta and placement lands off by the
// rotation angle. deg is clockwise degrees; passing 0 is a cheap identity.
function unrotateCanvasPoint(px: number, py: number, cx: number, cy: number, deg: number) {
  if (!deg) return { x: px, y: py };
  const rad = (-deg * Math.PI) / 180;
  const dx = px - cx;
  const dy = py - cy;
  return {
    x: cx + dx * Math.cos(rad) - dy * Math.sin(rad),
    y: cy + dx * Math.sin(rad) + dy * Math.cos(rad),
  };
}

function worldToCanvas(wx: number, wy: number, ox: number, oy: number, scale: number) {
  return { cx: wx * scale + ox, cy: wy * scale + oy };
}

function getHandles(r: FacilityRoom): Handle[] {
  const x = Number(r.x), y = Number(r.y), w = Number(r.w), h = Number(r.h);
  const mx = x + w / 2, my = y + h / 2;
  return [
    { dir: 'nw', x, y }, { dir: 'n', x: mx, y }, { dir: 'ne', x: x + w, y },
    { dir: 'e', x: x + w, y: my }, { dir: 'se', x: x + w, y: y + h },
    { dir: 's', x: mx, y: y + h }, { dir: 'sw', x, y: y + h }, { dir: 'w', x, y: my },
  ];
}

function pointInPolygon(points: { x: number; y: number }[], px: number, py: number): boolean {
  let inside = false;
  const n = points.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = points[i].x, yi = points[i].y;
    const xj = points[j].x, yj = points[j].y;
    if (((yi > py) !== (yj > py)) && (px < (xj - xi) * (py - yi) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
}

// ── Polygon-room measurement helpers (item 5 -- ports the mockup's per-wall exact-length editor
// and area/vertex stat, both entirely missing today for polygon rooms). World units are already
// real inches in this app (see PropertyPanel's own "1 unit = 1 inch" label on the rectangle
// Dimensions block), so unlike the mockup's own version of these functions there's no separate
// scale_inches_per_unit factor to thread through here -- these operate directly in world units.
function polygonAreaWorld(points: { x: number; y: number }[]): number {
  let area = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const a = points[i], b = points[(i + 1) % n];
    area += a.x * b.y - b.x * a.y;
  }
  return Math.abs(area) / 2;
}
function edgeLengthWorld(points: { x: number; y: number }[], idx: number): number {
  const n = points.length;
  const a = points[idx], b = points[(idx + 1) % n];
  return Math.hypot(b.x - a.x, b.y - a.y);
}
// Moves only the edge's "far end" vertex ((idx+1) % n) along the edge's direction to hit the new
// length, keeping the near end fixed -- mirrors the mockup's applyWallLength exactly.
function applyWallLengthWorld(points: { x: number; y: number }[], idx: number, totalInches: number): { x: number; y: number }[] {
  const n = points.length;
  const a = points[idx], b = points[(idx + 1) % n];
  const dx = b.x - a.x, dy = b.y - a.y;
  const oldLen = Math.hypot(dx, dy) || 1;
  const ux = dx / oldLen, uy = dy / oldLen;
  const newB = { x: a.x + ux * totalInches, y: a.y + uy * totalInches };
  return points.map((p, i) => (i === (idx + 1) % n ? newB : p));
}
// Accepts "8'4"", "8'-4\"", "100\"", "100", or a plain number (inches assumed) -- same formats the
// mockup's own parseLengthInput accepts.
function parseLengthInput(str: string): number | null {
  const s = str.trim();
  const ftIn = s.match(/(\d+(?:\.\d+)?)\s*'\s*-?\s*(\d+(?:\.\d+)?)?/);
  if (ftIn && s.includes("'")) {
    const feet = parseFloat(ftIn[1]) || 0;
    const inches = parseFloat(ftIn[2] ?? '0') || 0;
    return feet * 12 + inches;
  }
  const n = parseFloat(s.replace(/["']/g, ''));
  return isNaN(n) ? null : n;
}
function formatLength(totalInches: number): string {
  if (totalInches < 12) return `${Math.round(totalInches)}"`;
  let feet = Math.floor(totalInches / 12);
  let inches = Math.round(totalInches - feet * 12);
  if (inches === 12) { feet += 1; inches = 0; }
  return inches > 0 ? `${feet}'-${inches}"` : `${feet}'`;
}

// Caps a Cloudinary-delivered image URL to a display-appropriate pixel width via a URL-only
// transformation (item 3 fix) -- no re-upload, no server change, the stored original is untouched.
// Trace photos are uploaded via cloudinary.uploader.upload_stream with no size/quality limit
// (server/src/routes/facility-floor-images.ts), so a phone photo is served and rendered at its full
// original resolution -- often several thousand px per side. That full bitmap sits in an <image>
// inside the same <g> that pan moves via a pure CSS transform (zero React/JS work per frame, see
// handleMouseMove's pan branch) -- so with the skeleton (plain vector shapes, cheap to
// paint/composite regardless of element count) that transform is effectively free, but with a
// multi-megapixel bitmap the browser has real per-frame resampling/compositing work to do, which
// tracks with the reported asymmetry (trace panning slower than skeleton) far better than element
// count does -- the skeleton's ~2,900 elements are already confirmed NOT mounted in raster mode at
// all (see the `backgroundModeRef.current === 'svg' && ...` guard around its <g>), so there's no
// "hidden but still there" cost on trace mode's side to blame instead. 2000px covers this app's
// realistic display size with headroom; c_limit only shrinks an image that's actually larger, never
// upscales; q_auto/f_auto let Cloudinary pick a smaller encoded size too. Falls back to the
// untouched original URL if it doesn't look like a Cloudinary /upload/ URL, so a differently-hosted
// or malformed URL never breaks image display.
function capCloudinaryWidth(url: string, maxWidth: number): string {
  const marker = '/upload/';
  const i = url.indexOf(marker);
  if (i === -1) return url;
  const insertAt = i + marker.length;
  return `${url.slice(0, insertAt)}w_${maxWidth},c_limit,q_auto,f_auto/${url.slice(insertAt)}`;
}

// A room's CURRENT bounding rect -- derived from `points`' own bounding box when the room is a
// polygon (which is what RoomShapeSvg actually renders, per its own points-first branch above the
// legacy x/y/w/h fallback), falling back to the raw x/y/w/h fields only for a genuine legacy
// plain-rect room that has no points at all.
//
// Every door/window wall+pos computation (click-to-place, hover-preview) and DoorsSvg's own
// rendering read x/y/w/h DIRECTLY instead of this, which was fine as long as x/y/w/h and points
// always agreed -- but nothing enforced that. Whole-room drag (dragRef, before this same pass)
// updated ONLY x/y and left points frozen; vertex reshaping updated ONLY points and left x/y/w/h
// frozen. Either one, alone, silently desyncs the two: x/y/w/h keeps meaning "the room's original
// rectangle" while points is "what's actually on screen." Door/window math computed off the
// stale rectangle then places (and DoorsSvg then renders) the door relative to a rectangle that
// isn't the one visible on screen -- for a large enough drift, off it entirely, which is
// indistinguishable from "nothing happened." This was very likely the actual reason door
// placement looked "flaky" and window looked totally broken: it worked fine on a room that had
// never been dragged/reshaped (x/y/w/h and points still agree) and silently misplaced itself on
// any room that had (increasingly common now that every room is polygon-editable from creation).
//
// This pass also makes drag/vertex-edit keep x/y/w/h and points in sync going forward (see
// dragRef's mousemove handler and polyVertexDragRef's), so effectiveRect and the raw fields should
// agree for anything edited after this fix -- effectiveRect stays in place regardless as a
// defensive read for any room whose data already drifted before this fix shipped.
function effectiveRect(r: FacilityRoom): { x: number; y: number; w: number; h: number } {
  if (r.points && r.points.length > 2) {
    const xs = r.points.map(p => p.x), ys = r.points.map(p => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
  }
  return { x: Number(r.x), y: Number(r.y), w: Number(r.w), h: Number(r.h) };
}

// Single-room version of hitRoom's own point test, factored out so the Rooms tab's extinguishers
// list (item 2, this round) can ask "is this marker inside THIS specific room" without needing a
// whole floor's room list the way hitRoom does.
function pointInRoom(r: FacilityRoom, wx: number, wy: number): boolean {
  if (r.points && r.points.length > 2) return pointInPolygon(r.points, wx, wy);
  return wx >= Number(r.x) && wx <= Number(r.x) + Number(r.w) && wy >= Number(r.y) && wy <= Number(r.y) + Number(r.h);
}

function hitRoom(rooms: FacilityRoom[], wx: number, wy: number): FacilityRoom | null {
  for (let i = rooms.length - 1; i >= 0; i--) {
    const r = rooms[i];
    if (pointInRoom(r, wx, wy)) return r;
  }
  return null;
}

// Door/window placement + hover-preview hit test (item 1/2 fix). Plain hitRoom() requires the
// click to land strictly INSIDE the room polygon -- but placing a door/window is a click ON the
// wall line itself, which is a zero-width boundary. A click landing a sub-pixel's worth outside
// that boundary (extremely common -- users aim at the drawn wall, not the room's interior) missed
// entirely and silently no-opped: no hit, no toast, no visual result. That's what made door
// placement "flaky" (pure luck whether a click resolved a hair inside vs. outside) and window
// placement look like it did nothing at all (same miss, but window's placed symbol is a thin,
// easy-to-miss gap-rect vs. door's more visible swing arc, so a successful placement was less
// likely to even be noticed). Fix: first try the strict interior hit (unchanged, cheap, common
// case); if that misses, fall back to nearest-room-boundary-within-tolerance, using the same
// projectPointOnSegment used by Alt+Click vertex-insert. tolerance is in WORLD units -- pass
// (SOME_SCREEN_PX / scale) so the tolerance stays a constant number of screen pixels at any zoom.
function hitRoomForWallTool(rooms: FacilityRoom[], wx: number, wy: number, tolerance: number): FacilityRoom | null {
  const inside = hitRoom(rooms, wx, wy);
  if (inside) return inside;
  let best: FacilityRoom | null = null;
  let bestDist = tolerance;
  for (const r of rooms) {
    const boundary = (r.points && r.points.length > 2) ? r.points : getRectBoundaryPoints(r);
    for (let i = 0; i < boundary.length; i++) {
      const a = boundary[i], b = boundary[(i + 1) % boundary.length];
      const proj = projectPointOnSegment(wx, wy, a, b);
      const dist = Math.hypot(wx - proj.x, wy - proj.y);
      if (dist < bestDist) { bestDist = dist; best = r; }
    }
  }
  return best;
}

// Item 5 helpers — selecting/dragging an individual door or window in place.

// The door's anchor point in world space, given the OWNING room's current effectiveRect -- the
// same anchor math DoorsSvg/DoorWindowPreviewSvg already use for hx/hy, factored out here so the
// new hit-test and drag-update code doesn't re-derive it a third time with a chance to drift out
// of sync with how the door actually renders.
function doorAnchorPoint(rect: { x: number; y: number; w: number; h: number }, door: { wall: string; pos: number }): { hx: number; hy: number } {
  const p = door.pos ?? 0.5;
  if (door.wall === 'n') return { hx: rect.x + rect.w * p, hy: rect.y };
  if (door.wall === 's') return { hx: rect.x + rect.w * p, hy: rect.y + rect.h };
  if (door.wall === 'w') return { hx: rect.x, hy: rect.y + rect.h * p };
  return { hx: rect.x + rect.w, hy: rect.y + rect.h * p };
}

// Nearest wall (of ONE specific room, not the whole floor) and clamped position for a world point
// -- the same formula the door/window placement tool already uses, factored out so dragging an
// existing door/window onto a different wall of its own room reuses it exactly rather than a
// fourth ad-hoc copy.
function computeWallPos(rect: { x: number; y: number; w: number; h: number }, wx: number, wy: number): { wall: string; pos: number } {
  const relX = wx - rect.x, relY = wy - rect.y;
  const dists: Record<string, number> = { n: relY, s: rect.h - relY, w: relX, e: rect.w - relX };
  const wall = Object.entries(dists).sort((a, b) => a[1] - b[1])[0][0];
  const raw = wall === 'n' || wall === 's' ? relX / rect.w : relY / rect.h;
  return { wall, pos: Math.max(0.1, Math.min(0.9, raw)) };
}

// ── Edge-anchored doors on non-rectangular polygon rooms ──────────────────────────────────────
// A door's stored {wall,pos} anchors against effectiveRect (the room's bounding box), which for
// a jogged / L / notched / traced polygon has no way to name the actual local wall segment -- it
// can only address the outer bbox extent, so the door sticks to the wrong line. For those rooms
// a door/window carries an optional `edge` signature (midpoint-as-bbox-fraction + outward normal;
// see the door type in useFacilityRooms.ts) and anchors to the matched polygon edge instead.
// Rectangular rooms and all pre-existing data have no signature and use the bbox path unchanged.

type DoorLike = { wall: string; pos: number; edge?: { fx: number; fy: number; nx: number; ny: number } };
type WallBasis = {
  hx: number; hy: number;      // anchor point, world
  alx: number; aly: number;    // unit vector along the wall in the +pos direction (NOT guaranteed
                                // to point in +world-x/+world-y -- an edge-signature door's matched
                                // polygon segment can run either way; use this signed vector, never
                                // assume a canonical positive direction)
  nnx: number; nny: number;    // unit INWARD normal (into the room)
  wall: string;                // compass hint ('n'|'s'|'e'|'w')
  axisAligned: boolean;        // wall runs exactly horizontal or vertical -> gap can be an axis <rect>
  len: number;                 // wall/edge length pos is normalized against (bbox w/h, or the
                                // matched polygon edge's own length for an edge-signature door)
};

// Outward-facing basis for polygon edge i (from points[i] to points[i+1]), world coords. The
// normal is forced to point away from the polygon centroid so winding order doesn't matter.
function polyEdgeBasis(pts: { x: number; y: number }[], i: number) {
  const a = pts[i], b = pts[(i + 1) % pts.length];
  const dx = b.x - a.x, dy = b.y - a.y;
  const len = Math.hypot(dx, dy) || 1;
  const alx = dx / len, aly = dy / len;
  let nx = aly, ny = -alx; // along-vector rotated -90deg in screen (y-down) space
  const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
  let cx = 0, cy = 0;
  for (const p of pts) { cx += p.x; cy += p.y; }
  cx /= pts.length; cy /= pts.length;
  if ((mx - cx) * nx + (my - cy) * ny < 0) { nx = -nx; ny = -ny; }
  return { a, b, alx, aly, nx, ny, mx, my, len };
}

// Outward normal -> nearest compass wall. Screen space is y-down, so +y is south.
function compassOfNormal(nx: number, ny: number): string {
  return Math.abs(nx) >= Math.abs(ny) ? (nx >= 0 ? 'e' : 'w') : (ny >= 0 ? 's' : 'n');
}

// True only for a 4-corner polygon whose every edge is exactly horizontal or vertical -- i.e. a
// plain rectangle expressed as points (the default room shape). Anything cut / reshaped / traced
// into an L, notch, or diagonal fails this and takes the edge-signature path.
function isAxisAlignedRect(pts: { x: number; y: number }[]): boolean {
  if (pts.length !== 4) return false;
  for (let i = 0; i < 4; i++) {
    const a = pts[i], b = pts[(i + 1) % 4];
    if (Math.abs(a.x - b.x) > 0.01 && Math.abs(a.y - b.y) > 0.01) return false;
  }
  return true;
}

// THE single anchor resolver. Every render / hit-test / highlight path goes through this so there
// is one source of truth, not four. `edge` signature present + a real polygon -> match it to the
// nearest current edge and anchor there (robust to reshape / vertex re-index). Otherwise -> the
// exact bounding-box math doors have always used (doorAnchorPoint + the per-wall basis table).
function resolveDoorAnchor(room: FacilityRoom, door: DoorLike): WallBasis {
  const rect = effectiveRect(room);
  const poly = room.points && room.points.length > 2 ? room.points : null;
  const sig = door.edge;
  if (sig && poly) {
    let bestScore = -Infinity, bi = 0;
    for (let i = 0; i < poly.length; i++) {
      const e = polyEdgeBasis(poly, i);
      const align = e.nx * sig.nx + e.ny * sig.ny; // -1..1, want ~1
      const efx = rect.w ? (e.mx - rect.x) / rect.w : 0;
      const efy = rect.h ? (e.my - rect.y) / rect.h : 0;
      const score = align - 0.5 * Math.hypot(efx - sig.fx, efy - sig.fy);
      if (score > bestScore) { bestScore = score; bi = i; }
    }
    const e = polyEdgeBasis(poly, bi);
    const p = Math.max(0, Math.min(1, door.pos ?? 0.5));
    return {
      hx: e.a.x + (e.b.x - e.a.x) * p,
      hy: e.a.y + (e.b.y - e.a.y) * p,
      alx: e.alx, aly: e.aly,
      nnx: -e.nx, nny: -e.ny,
      wall: compassOfNormal(e.nx, e.ny),
      axisAligned: Math.abs(e.alx) > 0.999 || Math.abs(e.aly) > 0.999,
      len: e.len,
    };
  }
  // Fallback: identical to the pre-signature behaviour.
  const { hx, hy } = doorAnchorPoint(rect, door);
  const w = door.wall;
  return {
    hx, hy,
    alx: (w === 'n' || w === 's') ? 1 : 0,
    aly: (w === 'n' || w === 's') ? 0 : 1,
    nnx: w === 'w' ? 1 : w === 'e' ? -1 : 0,
    nny: w === 'n' ? 1 : w === 's' ? -1 : 0,
    wall: w,
    axisAligned: true,
    len: (w === 'n' || w === 's') ? rect.w : rect.h,
  };
}

// Allowed `pos` range for an edge-signature door/window nudged along its matched polygon edge.
// The old nudge clamp was a flat 0.1..0.9 FRACTION of the edge, which is wrong in both directions
// on a jogged / staggered wall: on a long edge (e.g. a 960-unit hallway wall) it left a ~96-unit
// dead zone before the real corner, and on a short stagger step (60-80 units) 0.9 let the door's
// opening run past the corner. What actually has to stay inside the edge is the door's OPENING,
// in world units. DoorsSvg draws a door's opening from the anchor toward +al for `radius`
// (max(8, width ?? 32)) regardless of hinge side, and a window centred on the anchor for
// `width ?? 36`, so those extents set the bounds. EDGE_INSET keeps the frame off the perpendicular
// corner wall (the gap rect is drawn +-2 around the opening).
const DOOR_EDGE_INSET = 2;
function edgeDoorPosBounds(door: { type?: string; width?: number }, len: number): { lo: number; hi: number } {
  const isWindow = door.type === 'window';
  const span = isWindow ? (door.width ?? 36) : Math.max(8, door.width ?? 32);
  const before = isWindow ? span / 2 : 0;          // opening extent behind the anchor
  const after = isWindow ? span / 2 : span;        // opening extent ahead of the anchor (+al)
  const lo = (before + DOOR_EDGE_INSET) / len;
  const hi = (len - after - DOOR_EDGE_INSET) / len;
  // Edge too short to fit the opening at all -> pin to centre instead of an inverted range.
  return lo <= hi ? { lo, hi } : { lo: 0.5, hi: 0.5 };
}

// Given a world-space click on `room`, produce the door fields to store. Rectangle (or no
// polygon) -> today's bbox {wall,pos}, no signature. Non-rect polygon -> nearest actual edge,
// `pos` as the param along THAT edge, plus a reshape-robust `edge` signature.
function computeDoorPlacement(room: FacilityRoom, wx: number, wy: number): DoorLike {
  const rect = effectiveRect(room);
  const poly = room.points && room.points.length > 2 ? room.points : null;
  if (!poly || isAxisAlignedRect(poly)) {
    return computeWallPos(rect, wx, wy);
  }
  let bestD = Infinity, bi = 0, bt = 0.5;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i], b = poly[(i + 1) % poly.length];
    const abx = b.x - a.x, aby = b.y - a.y;
    const lenSq = abx * abx + aby * aby || 1;
    let t = ((wx - a.x) * abx + (wy - a.y) * aby) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const px = a.x + t * abx, py = a.y + t * aby;
    const d = Math.hypot(wx - px, wy - py);
    if (d < bestD) { bestD = d; bi = i; bt = t; }
  }
  const e = polyEdgeBasis(poly, bi);
  return {
    wall: compassOfNormal(e.nx, e.ny),
    pos: Math.max(0.1, Math.min(0.9, bt)),
    edge: {
      fx: rect.w ? (e.mx - rect.x) / rect.w : 0,
      fy: rect.h ? (e.my - rect.y) / rect.h : 0,
      nx: e.nx, ny: e.ny,
    },
  };
}

// Finds the nearest existing door/window across every room on the floor, within `tolerance` world
// units of its rendered anchor point. Used by Shift+Click to select one directly, since doors
// aren't their own FacilityRoom records to hit-test against the way rooms/vertices are.
function hitDoorAt(rooms: FacilityRoom[], wx: number, wy: number, tolerance: number): { roomId: number; doorIndex: number } | null {
  let best: { roomId: number; doorIndex: number } | null = null;
  let bestDist = tolerance;
  for (const r of rooms) {
    if (!r.doors?.length) continue;
    r.doors.forEach((d, i) => {
      const { hx, hy } = resolveDoorAnchor(r, d);
      const dist = Math.hypot(wx - hx, wy - hy);
      if (dist < bestDist) { bestDist = dist; best = { roomId: r.id, doorIndex: i }; }
    });
  }
  return best;
}

function hitHandle(room: FacilityRoom, wx: number, wy: number, scale: number): Handle | null {
  const hs = (HANDLE_RADIUS + 3) / scale;
  for (const h of getHandles(room)) {
    if (Math.abs(wx - h.x) <= hs && Math.abs(wy - h.y) <= hs) return h;
  }
  return null;
}

function applyResize(orig: ResizeState, wx: number, wy: number, snapEnabled: boolean): Partial<FacilityRoom> {
  const dx = wx - orig.startWX, dy = wy - orig.startWY;
  let { origX: nx, origY: ny, origW: nw, origH: nh } = orig;
  const d = orig.dir;
  if (d.includes('e')) nw = Math.max(MIN_ROOM_SIZE, orig.origW + dx);
  if (d.includes('s')) nh = Math.max(MIN_ROOM_SIZE, orig.origH + dy);
  if (d.includes('w')) { nx = orig.origX + dx; nw = Math.max(MIN_ROOM_SIZE, orig.origW - dx); }
  if (d.includes('n')) { ny = orig.origY + dy; nh = Math.max(MIN_ROOM_SIZE, orig.origH - dy); }
  return {
    x: snap(nx, snapEnabled), y: snap(ny, snapEnabled),
    w: snap(nw, snapEnabled), h: snap(nh, snapEnabled),
  };
}

function floorLabel(idx: number, name?: string) {
  if (name && name.trim()) return name;
  if (idx === -1) return 'Basement';
  if (idx === 0) return 'First Floor';
  return `Floor ${idx + 1}`;
}

// ─── Canvas drawing primitives ──────────────────────────────────────────────────

// Hidden, never-rendered canvas 2D context used only for text-width measurement (wrapLines) --
// SVG has no synchronous text-measure API without mounting+getBBox, and this is far cheaper.
let _measureCtx: CanvasRenderingContext2D | null = null;
function measureCtx(): CanvasRenderingContext2D {
  if (!_measureCtx) _measureCtx = document.createElement('canvas').getContext('2d')!;
  return _measureCtx;
}

function DotGridSvg({ scale }: { scale: number }) {
  // Native SVG pattern tiling instead of a JS loop generating hundreds of <circle> elements --
  // same visual result (a dot every SNAP_SIZE world-units), far cheaper to re-render per frame.
  // The pattern itself lives in world space (so dots line up with the floor plan), but the dot
  // RADIUS is compensated by 1/scale so it stays a constant ~1 screen px regardless of zoom --
  // matching the old canvas version's ctx.arc(x, y, 1, ...), which drew in already-scaled screen
  // coordinates. Also mirrors its `if (step < 6) return` guard so far-zoomed-out views don't try
  // to render a grid finer than the eye (or SVG) can usefully resolve.
  if (SNAP_SIZE * scale < 6) return null;
  return (
    <>
      <defs>
        <pattern id="mapDotGrid" width={SNAP_SIZE} height={SNAP_SIZE} patternUnits="userSpaceOnUse">
          <circle cx={0} cy={0} r={1 / scale} fill="rgba(150,140,120,0.35)" />
        </pattern>
      </defs>
      <rect x={-100000} y={-100000} width={200000} height={200000} fill="url(#mapDotGrid)" />
    </>
  );
}

// Perf item 3: profiled a pan drag on First Floor (89 rooms) and found the dominant per-frame
// cost wasn't render frequency (rAF-coalescing draw() made no measurable difference -- a single
// render already costs far more than one frame budget) but per-frame COMPUTATION cost that scales
// with room count, matching the reported symptom exactly. wrapLines() is called fresh for every
// room's label on every render, and canvas measureText() (called once per word, in a loop) is one
// of the more expensive things happening per room. During a pure pan, ox/oy are the only things
// that change -- text/maxWidth/fontPx (this function's only inputs) are room- and zoom-derived,
// identical frame to frame, so every pan-frame was redoing 89 rooms' worth of measureText calls
// for a result that hadn't changed. Cached here, keyed on the exact inputs. fontPx is bucketed to
// the nearest quarter-pixel so the cache also gets useful hits across a continuous zoom gesture
// (fontPx = ROOM_LABEL_FONT_PX / scale changes by a tiny amount on every zoom tick otherwise,
// never repeating exactly). Size-capped and cleared wholesale rather than LRU-evicted -- simple,
// and re-populating after a clear is cheap relative to the cost this eliminates.
const _wrapLinesCache = new Map<string, string[]>();
function wrapLines(text: string, maxWidth: number, fontPx: number): string[] {
  const key = `${text}|${Math.round(maxWidth)}|${Math.round(fontPx * 4) / 4}`;
  const cached = _wrapLinesCache.get(key);
  if (cached) return cached;

  const ctx = measureCtx();
  ctx.font = `500 ${fontPx}px system-ui, sans-serif`;
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const test = cur ? `${cur} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && cur) {
      lines.push(cur);
      cur = w;
    } else {
      cur = test;
    }
  }
  if (cur) lines.push(cur);
  const result = lines.slice(0, 3);

  if (_wrapLinesCache.size > 2000) _wrapLinesCache.clear();
  _wrapLinesCache.set(key, result);
  return result;
}

function DoorsSvg({ room: r, highlightIndex, scale, white = false }: { room: FacilityRoom; highlightIndex?: number | null; scale?: number; white?: boolean }) {
  if (!r.doors?.length) return null;
  // Round 29: the Room Preview passes white -- same door geometry (thick leaf + wall stub, thin
  // swing arc), rendered white instead of the map's #1a2a4a navy. Default keeps the map palette.
  const doorInk = white ? '#ffffff' : '#1a2a4a';
  // Every anchor below goes through resolveDoorAnchor, which reads effectiveRect (points' bbox, not
  // possibly-stale x/y/w/h) internally and additionally honours an `edge` signature for jogged /
  // traced polygon walls. One resolver for render + hit-test + highlight, so they can't drift.
  return (
    <>
      {/* Item 5: highlight ring for an individually Shift+Click-selected door/window -- a separate
          element positioned via the same resolveDoorAnchor math used for hit-testing/dragging, drawn
          as a sibling rather than threaded into the door/window branches below so the existing,
          already-correct rendering for both types is untouched. */}
      {highlightIndex != null && r.doors[highlightIndex] && (
        <circle
          cx={resolveDoorAnchor(r, r.doors[highlightIndex]).hx}
          cy={resolveDoorAnchor(r, r.doors[highlightIndex]).hy}
          r={14 / (scale ?? 1)}
          fill="none" stroke="#3b82f6" strokeWidth={2 / (scale ?? 1)} strokeDasharray={`${3 / (scale ?? 1)} ${2 / (scale ?? 1)}`}
        />
      )}
      {r.doors.map((door, i) => {
        const isWindow = (door as any).type === 'window';
        if (isWindow) {
          // Item 2(b) fix: this used to be a fixed `len = 16` regardless of the door object's own
          // `width` -- the door branch below has always read width correctly (`radius = ... width
          // ?? 32`), but the window branch never did, so handleUpdateDoorWidth's writes landed in
          // state/DB correctly (confirmed identical for both in handleUpdateDoorWidth itself) and
          // the width-preset buttons even reflected the new value back (PropertyPanel's own `w`
          // read from door.width), but the actual rendered window symbol on the canvas never moved
          // -- looked exactly like "clicking a preset does nothing" when the real gap was here, not
          // in the click handler or the state update. 36 matches PropertyPanel's own default
          // fallback for a window with no width set yet.
          const winLen = (door as any).width ?? 36;
          const half = winLen / 2;
          // Anchor via the shared resolver (edge-signature door on a jogged/traced wall -> its
          // matched local segment; every other door -> the bbox math used before). `vertical` = the
          // gap bar runs along x (n/s walls), matching the old `wall === 'n' || 's'` test for every
          // axis-aligned wall.
          const A = resolveDoorAnchor(r, door);
          const hx = A.hx, hy = A.hy;
          const vertical = Math.abs(A.alx) >= Math.abs(A.aly);
          const gapRect: [number, number, number, number] = vertical
            ? [hx - half, hy - 2, winLen, 4]
            : [hx - 2, hy - half, 4, winLen];
          return (
            <g key={i}>
              <rect x={gapRect[0]} y={gapRect[1]} width={gapRect[2]} height={gapRect[3]} fill="#f0ede8" />
              {vertical ? (
                <>
                  <line x1={hx - half} y1={hy - 4} x2={hx - half} y2={hy + 4} stroke="#6b7280" strokeWidth={1.5} />
                  <line x1={hx + half} y1={hy - 4} x2={hx + half} y2={hy + 4} stroke="#6b7280" strokeWidth={1.5} />
                </>
              ) : (
                <>
                  <line x1={hx - 4} y1={hy - half} x2={hx + 4} y2={hy - half} stroke="#6b7280" strokeWidth={1.5} />
                  <line x1={hx - 4} y1={hy + half} x2={hx + 4} y2={hy + half} stroke="#6b7280" strokeWidth={1.5} />
                </>
              )}
            </g>
          );
        }
        // Architectural door symbol. Round 16: matched to the grey glyphs in the skeleton reference
        // (leaf ~3/4 open, THICK leaf + THICK wall stub on the hinge side, THIN swing arc). Round
        // 20: a pure vector model so BOTH properties render correctly for every wall --
        //   swing = 'inward' | 'outward'  -> which side of the wall the leaf opens toward
        //   hinge = 'left'   | 'right'    -> which END of the opening the leaf pivots from
        // Defaults: swing 'inward', hinge 'left' (what pre-Round-20 doors already render as; the
        // left-hinge path below is geometrically identical to Round 16's for n/s AND e walls; the
        // old per-wall angle table had the WEST wall's swing inverted -- this fixes it).
        const outward = (door as any).swing === 'outward';
        const hingeRight = (door as any).hinge === 'right';
        const radius = Math.max(8, (door as any).width ?? 32);
        const OPEN = 0.75 * (Math.PI / 2); // 3/4 of a right angle

        // Anchor + wall basis from the shared resolver. A door with an `edge` signature (placed on
        // a jogged / L / notched / traced polygon wall) anchors to its matched local segment; every
        // other door -- all rectangular rooms, all pre-existing data -- falls through to the exact
        // bounding-box math this branch used before. `ax,ay` = anchor, `al` = +pos direction along
        // the wall, `n` = inward normal (into the room).
        const A = resolveDoorAnchor(r, door);
        const ax = A.hx, ay = A.hy, alx = A.alx, aly = A.aly, nnx = A.nnx, nny = A.nny;

        // The opening always spans A -> A + radius*al. Hinge sits at one end of it.
        const hjx = hingeRight ? ax + radius * alx : ax;
        const hjy = hingeRight ? ay + radius * aly : ay;
        // Closed leaf lies flat along the wall, pointing toward the free (latch) end.
        const cdx = hingeRight ? -alx : alx;
        const cdy = hingeRight ? -aly : aly;
        // Open direction: into the room (inward) or out of it (outward). al ⟂ n, so the leaf at a
        // 3/4-open angle is cos(OPEN)*closedDir + sin(OPEN)*openDir.
        const odx = outward ? -nnx : nnx;
        const ody = outward ? -nny : nny;
        const ldx = Math.cos(OPEN) * cdx + Math.sin(OPEN) * odx;
        const ldy = Math.cos(OPEN) * cdy + Math.sin(OPEN) * ody;

        // Gap in the wall line. Axis-aligned wall (every rectangular-room door + most jogged-wall
        // doors, whose segments are still H/V) -> the same thin <rect> as before, orientation from
        // `al`. A genuinely diagonal wall (traced import) -> a thick stroke along `al` instead,
        // since an axis-aligned <rect> can't represent it.
        // The opening spans A -> A + radius*al, and `al` is only guaranteed positive for the old
        // bbox-fallback doors (canonical +x/+y by construction there). An edge-signature door's
        // matched polygon segment can run either way, so `al` can be negative here -- Math.min
        // picks whichever end is actually the rect's left/top edge instead of assuming it's always
        // `ax`. This was the "white gap offset from the door" bug: the gap always rendered starting
        // at ax extending in +x/+y regardless of which way the door's opening actually faced.
        const farGapX = ax + radius * alx, farGapY = ay + radius * aly;
        const gapWide = Math.abs(alx) >= Math.abs(aly);
        const gapRect: [number, number, number, number] | null = A.axisAligned
          ? (gapWide
              ? [Math.min(ax, farGapX) - 2, ay - 2, radius + 4, 4]
              : [ax - 2, Math.min(ay, farGapY) - 2, 4, radius + 4])
          : null;
        const gapHalf = radius / 2 + 2;
        const stubX = hjx + radius * cdx, stubY = hjy + radius * cdy;
        const leafX = hjx + radius * ldx, leafY = hjy + radius * ldy;
        // Minor arc, closed (stub) end -> leaf tip. sweep-flag from the screen-space (y-down)
        // cross product closedDir × leafDir: positive => leafDir is clockwise from closedDir => 1.
        const sweep = (cdx * ldy - cdy * ldx) > 0 ? 1 : 0;
        const arcPath = `M ${stubX} ${stubY} A ${radius} ${radius} 0 0 ${sweep} ${leafX} ${leafY}`;
        return (
          <g key={i}>
            {gapRect
              ? <rect x={gapRect[0]} y={gapRect[1]} width={gapRect[2]} height={gapRect[3]} fill="#f0ede8" />
              : <line x1={ax - gapHalf * alx} y1={ay - gapHalf * aly} x2={ax + gapHalf * alx} y2={ay + gapHalf * aly} stroke="#f0ede8" strokeWidth={4} />}
            <line x1={hjx} y1={hjy} x2={stubX} y2={stubY} stroke={doorInk} strokeWidth={3} strokeLinecap="round" />
            <line x1={hjx} y1={hjy} x2={leafX} y2={leafY} stroke={doorInk} strokeWidth={2.8} strokeLinecap="round" />
            <path d={arcPath} fill="none" stroke={doorInk} strokeWidth={0.9} />
          </g>
        );
      })}
    </>
  );
}

function MarkerShapeSvg({ cx, cy, markerType, alpha, selected, hasLink, label, scale, counterRot = 0 }: {
  cx: number; cy: number; markerType: string; alpha: number; selected: boolean; hasLink: boolean; label: string | null; scale: number;
  counterRot?: number; // Round 10 item 2: degrees of map-view rotation to cancel so the glyph stays upright
}) {
  const def = (MARKER_DEFS as readonly { id: string; color: string; symbol: string; shape: string; label: string }[])
    .find(d => d.id === markerType) ?? MARKER_DEFS[0];
  const R = 9 / scale;
  let shape: React.ReactNode;
  if (def.shape === 'circle') {
    shape = <circle cx={cx} cy={cy} r={R} fill={def.color} stroke="#ffffff" strokeWidth={1.5 / scale} />;
  } else if (def.shape === 'square') {
    shape = <rect x={cx - R} y={cy - R} width={R * 2} height={R * 2} fill={def.color} stroke="#ffffff" strokeWidth={1.5 / scale} />;
  } else {
    shape = <polygon points={`${cx},${cy - R} ${cx + R},${cy} ${cx},${cy + R} ${cx - R},${cy}`} fill={def.color} stroke="#ffffff" strokeWidth={1.5 / scale} />;
  }
  const fz = (def.symbol.length > 1 ? 7 : 9) / scale;
  return (
    <g opacity={alpha} transform={counterRot ? `rotate(${-counterRot} ${cx} ${cy})` : undefined}>
      {shape}
      <text x={cx} y={cy} fill="#ffffff" fontSize={fz} fontWeight="bold" textAnchor="middle" dominantBaseline="middle">{def.symbol}</text>
      {label && (
        <text x={cx} y={cy + R + 2 / scale} fill="#1e293b" fontSize={8 / scale} textAnchor="middle" dominantBaseline="hanging">
          {label.length > 12 ? label.slice(0, 11) + '…' : label}
        </text>
      )}
      {selected && <circle cx={cx} cy={cy} r={R + 5 / scale} fill="none" stroke="#3b82f6" strokeWidth={2 / scale} />}
      {hasLink && <circle cx={cx + R - 2 / scale} cy={cy + R - 2 / scale} r={3 / scale} fill="#10b981" />}
    </g>
  );
}

function DoorWindowPreviewSvg({ rooms, scale, preview, activeTool, windowWidth, doorSwing }: {
  rooms: FacilityRoom[]; scale: number;
  preview: { roomId: number; wall: string; pos: number; edge?: { fx: number; fy: number; nx: number; ny: number } } | null;
  activeTool: Tool; windowWidth: number; doorSwing: 'inward' | 'outward';
}) {
  const prev = preview;
  if (!prev || (activeTool !== 'door' && activeTool !== 'window')) return null;
  const pr = rooms.find(r => r.id === prev.roomId);
  if (!pr) return null;
  // Anchor + wall basis from the SAME resolver the real placement and DoorsSvg use -- so on a
  // jogged / L / traced wall the ghost sits on the actual target segment (carried in prev.edge),
  // not the outer bounding-box line. Falls back to bbox math for a plain rectangular room.
  const A = resolveDoorAnchor(pr, prev);
  const hx = A.hx, hy = A.hy;
  const plen = 16;
  if (activeTool === 'window') {
    const ww = windowWidth / 2;
    const vertical = Math.abs(A.alx) >= Math.abs(A.aly); // gap bar runs along x (n/s-type walls)
    return (
      <g opacity={0.55} stroke="#60a5fa" strokeWidth={2 / scale} strokeDasharray={`${4 / scale} ${2 / scale}`}>
        {vertical ? (
          <>
            <rect x={hx - ww} y={hy - 2} width={ww * 2} height={4} fill="#93c5fd" stroke="none" />
            <line x1={hx - ww} y1={hy - 5} x2={hx - ww} y2={hy + 5} />
            <line x1={hx + ww} y1={hy - 5} x2={hx + ww} y2={hy + 5} />
          </>
        ) : (
          <>
            <rect x={hx - 2} y={hy - ww} width={4} height={ww * 2} fill="#93c5fd" stroke="none" />
            <line x1={hx - 5} y1={hy - ww} x2={hx + 5} y2={hy - ww} />
            <line x1={hx - 5} y1={hy + ww} x2={hx + 5} y2={hy + ww} />
          </>
        )}
      </g>
    );
  }
  // Door preview arc: quarter turn from the along-wall direction toward the swing side, pivoting at
  // the anchor -- same basis vectors DoorsSvg builds its leaf from (al = along wall, n = inward).
  const outward = doorSwing === 'outward';
  const odx = outward ? -A.nnx : A.nnx;
  const ody = outward ? -A.nny : A.nny;
  const sx = hx + plen * A.alx, sy = hy + plen * A.aly;
  const ex = hx + plen * odx, ey = hy + plen * ody;
  const sweep = (A.alx * ody - A.aly * odx) > 0 ? 1 : 0;
  return (
    <path d={`M ${sx} ${sy} A ${plen} ${plen} 0 0 ${sweep} ${ex} ${ey}`} fill="none" stroke="#60a5fa" strokeWidth={2 / scale} strokeDasharray={`${4 / scale} ${2 / scale}`} opacity={0.55} />
  );
}

// Fill-alpha values ported from the mockup's .room-poly CSS (fill-opacity: default 0.22, hover
// 0.42, selected 0.5). The mockup's fourth "dimmed" value (0.28, described there as "rooms on a
// floor you're not viewing") doesn't apply here -- this app only ever renders the current floor's
// rooms -- so it's reused instead for item 4's "pending, not yet traced" rectangle treatment.
const ROOM_FILL_ALPHA = { default: 0.22, hover: 0.42, selected: 0.5, pending: 0.28 };
const MIDPOINT_RADIUS = 4;

function edgeMidpoints(points: { x: number; y: number }[]): { x: number; y: number }[] {
  return points.map((p, i) => {
    const n = points[(i + 1) % points.length];
    return { x: (p.x + n.x) / 2, y: (p.y + n.y) / 2 };
  });
}

// The 4 corners of an x/y/w/h rectangle as an ordered boundary, same winding as every traced
// polygon room in this file (clockwise starting top-left). Single source of truth shared by
// getRectBoundaryPoints (Alt+Click vertex-insert on a not-yet-converted rect) and by room
// creation (createRoom call sites), so a brand-new room is a real editable polygon from its
// first interaction rather than a bare rect whose vertex/midpoint dots stay inert until a first
// Alt+Click converts it. x/y/w/h are kept alongside points on every room regardless -- DoorsSvg/
// FireWallsSvg/glow/resize-handle logic all still position from x/y/w/h.
function rectCornerPoints(x: number, y: number, w: number, h: number): { x: number; y: number }[] {
  return [{ x, y }, { x: x + w, y }, { x: x + w, y: y + h }, { x, y: y + h }];
}

// A plain rectangle room (no points array yet) as an ordered 4-corner boundary -- used by
// Alt+Click vertex-insert so it works uniformly whether the room is already a polygon or still
// a plain rect.
function getRectBoundaryPoints(r: FacilityRoom): { x: number; y: number }[] {
  return rectCornerPoints(Number(r.x), Number(r.y), Number(r.w), Number(r.h));
}

// Closest point to (px,py) on the segment a→b, clamped to the segment (not the infinite line).
function projectPointOnSegment(px: number, py: number, a: { x: number; y: number }, b: { x: number; y: number }) {
  const abx = b.x - a.x, aby = b.y - a.y;
  const lenSq = abx * abx + aby * aby;
  if (lenSq === 0) return { x: a.x, y: a.y };
  let t = ((px - a.x) * abx + (py - a.y) * aby) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return { x: a.x + t * abx, y: a.y + t * aby };
}

function RoomLabelSvg({ cx, cy, name, boxW, fontSize, scale, counterRot = 0, previewThemed = false }: {
  cx: number; cy: number; name: string; boxW: number; fontSize: number; scale: number;
  counterRot?: number; // Round 10 item 2: cancel the map-view rotation so the label stays upright
  previewThemed?: boolean; // Round 25: Room Preview only -- follow the app theme instead of the
                           // always-dark map palette (#f8fafc on a dark halo), which is invisible
                           // on a light-theme preview canvas
}) {
  const lines = wrapLines(name, boxW - 10, fontSize);
  const lineH = fontSize * 1.25;
  return (
    <text
      // Uniform label color (#f8fafc, matching the mockup's own single fill for every room label
      // regardless of type) -- was previously ROOM_COLORS[type].text, coloring labels per category
      // (blue/orange/red/purple). ROOM_COLORS itself is untouched -- still used for the room
      // fill/stroke and other, unrelated per-type UI (PropertyPanel's type picker, etc).
      x={cx} fontSize={fontSize} fontWeight={600} fontFamily="'Inter', sans-serif" textAnchor="middle"
      paintOrder="stroke" strokeWidth={2.5 / scale}
      style={previewThemed
        ? { fill: 'var(--text-primary)', stroke: 'var(--bg-surface)' } // halo == the inner grid box bg (Round 29 revert)
        : { fill: '#f8fafc', stroke: 'rgba(13,17,23,0.75)' }}
      transform={counterRot ? `rotate(${-counterRot} ${cx} ${cy})` : undefined}
    >
      {lines.map((line, i) => (
        <tspan key={i} x={cx} y={cy - (lines.length - 1) * lineH / 2 + i * lineH} dominantBaseline="middle">{line}</tspan>
      ))}
    </text>
  );
}

function RoomShapeSvg({ room: r, selected, dirty, multiSelected, hovered, floorHasPolygons, hoveredMidpointIdx, scale, labelCounterRot = 0, hideLabel = false, previewBlue = false }: {
  room: FacilityRoom; selected: boolean; dirty: boolean; multiSelected: boolean;
  hovered: boolean; floorHasPolygons: boolean; hoveredMidpointIdx: number | null; scale: number;
  labelCounterRot?: number; // Round 10 item 2: forwarded to RoomLabelSvg so labels stay upright when the view is rotated
  hideLabel?: boolean; // Round 23 item 3: the Room Preview draws its own collision-avoiding label
  previewBlue?: boolean; // Round 24: blueprint style -- accent-blue outline, faint blue fill, rounded joins
}) {
  // ── Polygon room (result of cut tool, or the traced import) ──────────────
  if (r.points && r.points.length > 2) {
    const colors = roomColors(r);
    const fillAlpha = (selected || multiSelected) ? ROOM_FILL_ALPHA.selected : hovered ? ROOM_FILL_ALPHA.hover : ROOM_FILL_ALPHA.default;
    const stroke = previewBlue ? '#3b82f6' : multiSelected ? '#60a5fa' : selected ? '#3b82f6' : dirty ? '#f59e0b' : colors.stroke;
    const strokeW = previewBlue ? 2.4 : multiSelected || selected ? 2.5 : 2;
    const allX = r.points.map(p => p.x), allY = r.points.map(p => p.y);
    const bboxW = Math.max(...allX) - Math.min(...allX);
    const bx = (Math.min(...allX) + Math.max(...allX)) / 2;
    const by = (Math.min(...allY) + Math.max(...allY)) / 2;
    const fontSize = ROOM_LABEL_FONT_PX / scale;
    return (
      <g>
        <polygon
          points={r.points.map(p => `${p.x},${p.y}`).join(' ')}
          fill={previewBlue ? 'rgba(37,99,235,0.10)' : colors.fill} fillOpacity={previewBlue ? 1 : fillAlpha}
          stroke={stroke} strokeWidth={strokeW}
          strokeLinejoin={previewBlue ? 'round' : undefined}
        />
        {selected && (
          <>
            {r.points.map((pt, i) => (
              <circle key={`v-${i}`} cx={pt.x} cy={pt.y} r={HANDLE_RADIUS} fill="#ffffff" stroke="#3b82f6" strokeWidth={1.5} />
            ))}
            {edgeMidpoints(r.points).map((mid, idx) => {
              const isHovered = hoveredMidpointIdx === idx;
              return (
                <circle
                  key={`m-${idx}`} cx={mid.x} cy={mid.y} r={MIDPOINT_RADIUS}
                  fill={isHovered ? '#3b82f6' : 'rgba(59,130,246,0.35)'}
                  fillOpacity={isHovered ? 1 : 0.85}
                  stroke={isHovered ? '#3b82f6' : '#93c5fd'} strokeWidth={1.1}
                />
              );
            })}
          </>
        )}
        {!hideLabel && scale >= LABEL_MIN_ZOOM && bboxW > 30 && r.name && (
          <RoomLabelSvg cx={bx} cy={by} name={r.name} boxW={bboxW} fontSize={fontSize} scale={scale} counterRot={labelCounterRot} />
        )}
      </g>
    );
  }

  const rx = Number(r.x), ry = Number(r.y), rw = Number(r.w), rh = Number(r.h);

  // Hallway rooms — subtle background strip, no label
  if (r.type === 'hallway') {
    return <rect x={rx} y={ry} width={rw} height={rh} rx={3} fill="rgba(200,200,195,0.4)" stroke="rgba(150,150,145,0.5)" strokeWidth={1} />;
  }

  const colors = roomColors(r);

  // Expand non-hallway/non-common rooms 1 unit each side to close visual gaps
  const expand = (r.type !== 'hallway' && r.type !== 'common') ? 1 : 0;
  const drx = rx - expand, dry = ry - expand;
  const drw = rw + expand * 2, drh = rh + expand * 2;

  // "Pending, not yet traced" treatment (item 4): only kicks in when this floor already has SOME
  // polygon rooms -- i.e. a floor mid-conversion. Scoping it to floorHasPolygons (rather than "any
  // rectangle room, anywhere") means every facility that's never touched the polygon feature
  // renders exactly as before -- exclusively rectangles, no dimmed change.
  // NOTE: this used to also dash the stroke, but that made ordinary, already-committed,
  // intentionally-rectangular rooms (e.g. Room 106) look dashed/unfinished just because OTHER
  // rooms on the same floor had been traced to polygons -- a floor-wide flag, not a per-room
  // commit-status one. Chris asked for dashed outlines gone entirely; removed here. Fill dimming
  // (fillAlpha below) is untouched since that wasn't part of the complaint.
  const pending = floorHasPolygons;
  const fillAlpha = (selected || multiSelected) ? ROOM_FILL_ALPHA.selected : hovered ? ROOM_FILL_ALPHA.hover : pending ? ROOM_FILL_ALPHA.pending : ROOM_FILL_ALPHA.default;
  const stroke = previewBlue ? '#3b82f6' : multiSelected ? '#60a5fa' : selected ? '#3b82f6' : dirty ? '#f59e0b' : colors.stroke;
  const strokeW = previewBlue ? 2.4 : multiSelected || selected ? 2.5 : 2;
  const fontSize = ROOM_LABEL_FONT_PX / scale;

  return (
    <g>
      <rect
        x={drx} y={dry} width={drw} height={drh} rx={previewBlue ? 6 : 3}
        fill={previewBlue ? 'rgba(37,99,235,0.10)' : colors.fill} fillOpacity={previewBlue ? 1 : fillAlpha}
        stroke={stroke} strokeWidth={strokeW}
        strokeLinejoin={previewBlue ? 'round' : undefined}
      />
      <DoorsSvg room={r} white={previewBlue} />
      {!hideLabel && scale >= LABEL_MIN_ZOOM && rw > 30 && rh > 20 && (
        <RoomLabelSvg cx={drx + drw / 2} cy={dry + drh / 2} name={r.name} boxW={drw} fontSize={fontSize} scale={scale} counterRot={labelCounterRot} />
      )}
    </g>
  );
}

function FireWallsSvg({ room: r }: { room: FacilityRoom }) {
  if (!r.fire_wall_edges?.length) return null;
  const rx = Number(r.x), ry = Number(r.y), rw = Number(r.w), rh = Number(r.h);
  return (
    <>
      {r.fire_wall_edges.map(edge => {
        let x1 = rx, y1 = ry, x2 = rx, y2 = ry;
        if (edge === 'n') { x2 = rx + rw; }
        else if (edge === 's') { y1 = ry + rh; x2 = rx + rw; y2 = ry + rh; }
        else if (edge === 'w') { y2 = ry + rh; }
        else if (edge === 'e') { x1 = rx + rw; x2 = rx + rw; y2 = ry + rh; }
        return <line key={edge} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#dc2626" strokeWidth={4} />;
      })}
    </>
  );
}

function HandlesSvg({ room: r }: { room: FacilityRoom }) {
  return (
    <>
      {getHandles(r).map(h => (
        <circle key={h.dir} cx={h.x} cy={h.y} r={HANDLE_RADIUS} fill="#ffffff" stroke="#3b82f6" strokeWidth={1.5} />
      ))}
    </>
  );
}

// ─── Property Panel ───────────────────────────────────────────────────────────

interface PropPanelProps {
  room: FacilityRoom;
  canEdit: boolean;
  canLinkEdit: boolean;
  facilityId: number;
  onClose: () => void;
  onChange: (patch: Partial<FacilityRoom>) => void;
  onDelete: () => void;
  onSave: () => void;
  onLinkArea: (areaId: number | null, locationId: number | null) => Promise<void>;
  isSaving: boolean;
  isAdmin?: boolean;
  onAddBathroom?: () => void;
  onBathroomHoverChange?: (hovering: boolean) => void;
  onDeleteDoor?: (doorIndex: number) => void;
  onUpdateDoorWidth?: (doorIndex: number, widthIn: number) => void;
  onCycleDoorPivot?: (doorIndex: number) => void;
  onUpdateDoorWall?: (doorIndex: number, wall: 'n' | 'e' | 's' | 'w') => void;
  onSelectDoorRow?: (doorIndex: number) => void;
  selectedDoorIndex?: number | null;
  multiSelectCount?: number;
  onCut?: () => void;
  onJoin?: () => void;
  attachedRooms?: FacilityRoom[]; // Round 23 item 1: sub-rooms (bathroom) to list alongside doors/windows
  onSelectRoomById?: (roomId: number) => void;
  onDeleteRoomById?: (roomId: number) => void;
}

function PropertyPanel({ room, canEdit, canLinkEdit, facilityId, onClose, onChange, onDelete, onSave, onLinkArea, isSaving, isAdmin, onAddBathroom, onBathroomHoverChange, onDeleteDoor, onUpdateDoorWidth, onCycleDoorPivot, onUpdateDoorWall, onSelectDoorRow, selectedDoorIndex, multiSelectCount, onCut, onJoin, attachedRooms = [], onSelectRoomById, onDeleteRoomById }: PropPanelProps) {
  const [areaOptions, setAreaOptions] = useState<{ id: number; label: string }[]>([]);
  const [locationOptions, setLocationOptions] = useState<{ id: number; label: string }[]>([]);
  const [showLinkEdit, setShowLinkEdit] = useState(false);
  const [pendingAreaId, setPendingAreaId] = useState<number | null>(null);
  const [pendingLocationId, setPendingLocationId] = useState<number | null>(null);
  const [linkSaving, setLinkSaving] = useState(false);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [linkedLocName, setLinkedLocName] = useState<string | null>(null);

  // Fetch areas once when facilityId is known — response shape is { success, options[] }
  useEffect(() => {
    if (!facilityId) return;
    api.get(`/dropdowns?dropdown_type=area&facility_id=${facilityId}`)
      .then(res => {
        const opts = ((res as any).options ?? []) as { id: number; label: string }[];
        setAreaOptions(opts);
      })
      .catch(() => {});
  }, [facilityId]);

  // Fetch locations when pending area changes in edit mode
  useEffect(() => {
    if (!showLinkEdit || pendingAreaId == null || !facilityId) {
      setLocationOptions([]);
      return;
    }
    setLoadingLocations(true);
    api.get(`/dropdowns?dropdown_type=location&facility_id=${facilityId}&parent_id=${pendingAreaId}`)
      .then(res => {
        const opts = ((res as any).options ?? []) as { id: number; label: string }[];
        setLocationOptions(opts);
      })
      .catch(() => {})
      .finally(() => setLoadingLocations(false));
  }, [pendingAreaId, showLinkEdit, facilityId]);

  // Fetch current linked location name for read-only display
  useEffect(() => {
    if (room.linked_location_id == null || room.linked_area_id == null || !facilityId) {
      setLinkedLocName(null);
      return;
    }
    api.get(`/dropdowns?dropdown_type=location&facility_id=${facilityId}&parent_id=${room.linked_area_id}`)
      .then(res => {
        const opts = ((res as any).options ?? []) as { id: number; label: string }[];
        setLinkedLocName(opts.find(l => l.id === room.linked_location_id)?.label ?? null);
      })
      .catch(() => setLinkedLocName(null));
  }, [room.linked_area_id, room.linked_location_id, facilityId]);

  // Reset edit state when selected room changes
  useEffect(() => {
    setShowLinkEdit(false);
    setPendingAreaId(null);
    setPendingLocationId(null);
    setLocationOptions([]);
  }, [room.id]);

  const areaName = areaOptions.find(a => a.id === room.linked_area_id)?.label ?? null;

  const panel = (
    <div
      style={{
        // Was position:fixed/zIndex:999 (a slide-in overlay via createPortal to document.body) --
        // now a normal grid child (item 4): full height/width of its 360px grid column, same visual
        // treatment (solid dark panel, blur) minus the parts that only made sense as an overlay.
        width: '100%', height: '100%',
        background: 'rgba(20,30,50,0.97)', backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}
      onMouseDown={e => e.stopPropagation()}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 14 }}>Room Properties</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', padding: 4, display: 'flex' }}>
          <X size={16} />
        </button>
      </div>

      {/* Multi-select message */}
      {(multiSelectCount ?? 0) >= 2 && (
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 13, color: '#94a3b8' }}>
            <strong style={{ color: '#f1f5f9' }}>{multiSelectCount} rooms selected</strong>
          </span>
          <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.45)' }}>
            Use Cut to subtract the smaller room from the larger.
          </span>
          {onCut && (
            <button
              onClick={onCut}
              style={{
                padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(250,204,21,0.35)',
                background: 'rgba(250,204,21,0.1)', color: '#fde68a', fontSize: 13,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              <Scissors size={13} /> Cut Shapes
            </button>
          )}
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', padding: '16px', display: (multiSelectCount ?? 0) >= 2 ? 'none' : 'block' }}>
        <Field label="Name">
          <input
            type="text" value={room.name} disabled={!canEdit}
            onChange={e => onChange({ name: e.target.value })}
            style={inputStyle}
          />
        </Field>
        <Field label="Room Number">
          <input
            type="text" value={room.room_number ?? ''} disabled={!canEdit}
            onChange={e => onChange({ room_number: e.target.value || null })}
            style={inputStyle}
          />
        </Field>
        <Field label="Type">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
            {ROOM_TYPES.map(t => {
              const active = room.type === t;
              const c = ROOM_COLORS[t];
              return (
                <button
                  key={t}
                  disabled={!canEdit}
                  onClick={() => canEdit && onChange({ type: t })}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '5px 8px', borderRadius: 5, textAlign: 'left',
                    border: `1px solid ${active ? '#3b82f6' : 'rgba(255,255,255,0.10)'}`,
                    background: active ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.04)',
                    color: active ? '#93c5fd' : 'rgba(255,255,255,0.55)',
                    cursor: canEdit ? 'pointer' : 'default', fontSize: 12,
                  }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: 2, background: c.fill, border: `1px solid ${c.stroke}`, flexShrink: 0 }} />
                  {t.charAt(0).toUpperCase() + t.slice(1)}
                </button>
              );
            })}
          </div>
        </Field>

        <Field label="Color">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="color"
              value={room.color || ROOM_COLORS[room.type]?.fill || ROOM_COLORS.other.fill}
              disabled={!canEdit}
              onChange={e => canEdit && onChange({ color: e.target.value })}
              style={{ width: 40, height: 32, padding: 2, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 6, cursor: canEdit ? 'pointer' : 'default' }}
            />
            {room.color && canEdit && (
              <button
                onClick={() => onChange({ color: null })}
                style={{ fontSize: 11, padding: '4px 8px', borderRadius: 5, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'var(--text-muted)', cursor: 'pointer' }}
              >
                Reset to type default
              </button>
            )}
          </div>
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {(['x', 'y', 'w', 'h'] as const).map(k => (
            <Field key={k} label={k.toUpperCase()}>
              <input
                type="number" value={Math.round(room[k])} disabled={!canEdit}
                onChange={e => onChange({ [k]: Number(e.target.value) })}
                style={inputStyle}
              />
            </Field>
          ))}
        </div>

        <Field label="Notes">
          <textarea
            value={room.notes ?? ''} disabled={!canEdit} rows={3}
            onChange={e => onChange({ notes: e.target.value || null })}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 64 }}
          />
        </Field>

        {/* Dimensions — 1:1 scale (1 canvas unit = 1 inch) */}
        {!(room.points && room.points.length > 2) && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={labelStyle}>Dimensions</span>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>1 unit = 1 inch</span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                Canvas area: <strong style={{ color: '#f1f5f9' }}>{((Number(room.w) * Number(room.h)) / 144).toFixed(1)} sq ft</strong>
              </div>
              {canEdit && (
                <button
                  onClick={() => onChange({ dimensions_ft_w: Number(room.w) / 12, dimensions_ft_h: Number(room.h) / 12 })}
                  style={{
                    padding: '4px 10px', borderRadius: 5, fontSize: 11, cursor: 'pointer', width: 'fit-content',
                    border: '1px solid rgba(59,130,246,0.3)', background: 'rgba(59,130,246,0.1)', color: '#93c5fd',
                  }}
                >
                  Auto-Calculate Dimensions
                </button>
              )}
              {room.dimensions_ft_w != null && room.dimensions_ft_h != null && (
                <>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                    Measured: <strong style={{ color: '#f1f5f9' }}>{Number(room.dimensions_ft_w).toFixed(1)} × {Number(room.dimensions_ft_h).toFixed(1)} ft</strong>
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                    Measured area: <strong style={{ color: '#f1f5f9' }}>{(Number(room.dimensions_ft_w) * Number(room.dimensions_ft_h)).toFixed(1)} sq ft</strong>
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                    Paint: ~<strong style={{ color: '#f1f5f9' }}>{(2 * (Number(room.dimensions_ft_w) + Number(room.dimensions_ft_h)) * 8 / 350).toFixed(1)} gal</strong>
                  </div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                    Flooring: <strong style={{ color: '#f1f5f9' }}>{(Number(room.dimensions_ft_w) * Number(room.dimensions_ft_h) * 1.1).toFixed(1)} sq ft</strong> + 10% waste
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Polygon rooms had NO area/vertex stat and NO per-wall length editor at all before this
            (item 5) -- the rectangle Dimensions block above is explicitly gated to exclude
            polygon rooms, so this was a real "nothing shown" gap, not a duplicate of it. Ports
            the mockup's vertex/area stat + per-wall exact-length typing, adapted to this app's
            existing 1-unit=1-inch world space (no separate scale factor needed here). */}
        {room.points && room.points.length > 2 && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                Vertices: <strong style={{ color: '#f1f5f9' }}>{room.points.length}</strong>
              </div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)' }}>
                Area: <strong style={{ color: '#f1f5f9' }}>{(polygonAreaWorld(room.points) / 144).toFixed(1)} sq ft</strong>
              </div>
            </div>
            <span style={labelStyle}>Walls ({room.points.length})</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4, maxHeight: 160, overflowY: 'auto' }}>
              {room.points.map((_, idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', width: 24, flexShrink: 0 }}>W{idx + 1}</span>
                  <input
                    type="text"
                    disabled={!canEdit}
                    defaultValue={formatLength(edgeLengthWorld(room.points!, idx))}
                    onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                    onBlur={e => {
                      const totalInches = parseLengthInput(e.target.value);
                      if (totalInches != null && totalInches > 0 && room.points) {
                        onChange({ points: applyWallLengthWorld(room.points, idx, totalInches) });
                      } else {
                        e.target.value = formatLength(edgeLengthWorld(room.points!, idx));
                      }
                    }}
                    style={{ ...inputStyle, padding: '4px 8px', fontSize: 12 }}
                  />
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginTop: 6, lineHeight: 1.4 }}>
              Type a new length (e.g. 8&apos;4&quot; or 100&quot;) and press Enter — the far end of that wall moves to match.
            </div>
          </div>
        )}

        <div style={{ marginTop: 8 }}>
          <span style={labelStyle}>Fire Wall Edges</span>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
            {(['n', 's', 'e', 'w'] as const).map(edge => {
              const active = room.fire_wall_edges?.includes(edge);
              return (
                <button
                  key={edge}
                  disabled={!canEdit}
                  onClick={() => {
                    const cur = room.fire_wall_edges ?? [];
                    onChange({ fire_wall_edges: active ? cur.filter(x => x !== edge) : [...cur, edge] });
                  }}
                  style={{
                    padding: '3px 10px', borderRadius: 4, border: `1px solid ${active ? '#ef4444' : 'rgba(255,255,255,0.15)'}`,
                    background: active ? 'rgba(239,68,68,0.2)' : 'transparent',
                    color: active ? '#fca5a5' : 'rgba(255,255,255,0.5)',
                    cursor: canEdit ? 'pointer' : 'default', fontSize: 12,
                  }}
                >
                  {edge.toUpperCase()}
                </button>
              );
            })}
          </div>
        </div>

        {((room.doors ?? []).length > 0 || attachedRooms.length > 0) && (
          <div style={{ marginTop: 10 }}>
            <span style={labelStyle}>Doors, Windows &amp; Rooms</span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
              {(room.doors ?? []).map((door, i) => {
                const isDoor = !(door as any).type;
                const w = (door as any).width as number ?? (isDoor ? 32 : 36);
                const ftPart = Math.floor(w / 12);
                const inPart = w % 12;
                const sizeLabel = ftPart > 0 ? `${ftPart} ft ${inPart} in` : `${inPart} in`;
                const quickSizes = isDoor ? [24, 28, 30, 32, 36] : [24, 30, 36, 48];
                const rowSelected = selectedDoorIndex === i;
                return (
                  <div
                    key={i}
                    onClick={() => onSelectDoorRow?.(i)}
                    title="Select on canvas (then drag to move)"
                    style={{
                      padding: '5px 6px', borderRadius: 5, cursor: onSelectDoorRow ? 'pointer' : 'default',
                      background: rowSelected ? 'rgba(59,130,246,0.16)' : 'rgba(255,255,255,0.04)',
                      border: `1px solid ${rowSelected ? 'rgba(59,130,246,0.55)' : 'rgba(255,255,255,0.08)'}`,
                    }}
                  >
                    {/* Item 3: type ("Door"/"Window") is the primary label. Item 4: the whole row is
                        a select target (mirrors canvas Shift+Click). Item 6: crisp lucide icons. */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11 }}>{isDoor ? '🚪' : '🪟'}</span>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>
                        {isDoor ? 'Door' : 'Window'}
                      </span>
                      <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.45)', flex: 1 }}>
                        {{ n: 'North', e: 'East', s: 'South', w: 'West' }[door.wall as 'n' | 'e' | 's' | 'w'] ?? door.wall.toUpperCase()} wall · {Math.round((door.pos ?? 0) * 100)}%
                        {isDoor && ` · ${(door as any).swing === 'outward' ? 'out' : 'in'}/${(door as any).hinge === 'right' ? 'R' : 'L'}`}
                      </span>
                      {canEdit && isDoor && onCycleDoorPivot && (
                        <button
                          title="Cycle door hinge + swing (in/out × left/right) — 4 steps"
                          onClick={e => { e.stopPropagation(); onCycleDoorPivot(i); }}
                          onMouseLeave={spinIconOut}
                          className="combo-jello-spin" data-tint="neutral"
                          style={{ padding: '2px 4px', borderRadius: 4, border: '1px solid rgba(59,130,246,0.3)', color: '#93c5fd', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                          <ArrowLeftRight size={13} className="btn-icon" />
                        </button>
                      )}
                      {canEdit && onDeleteDoor && (
                        <button
                          title={`Delete this ${isDoor ? 'door' : 'window'}`}
                          onClick={e => { e.stopPropagation(); onDeleteDoor(i); }}
                          onMouseLeave={spinIconOut}
                          className="combo-jello-spin" data-tint="neutral"
                          style={{ padding: '2px 4px', borderRadius: 4, border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                        >
                          <Trash2 size={13} className="btn-icon" />
                        </button>
                      )}
                    </div>
                    {canEdit && onUpdateDoorWall && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 5 }}>
                        <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Wall:</span>
                        {(['n', 'e', 's', 'w'] as const).map(wl => {
                          const active = door.wall === wl;
                          return (
                            <button
                              key={wl}
                              title={`Move to ${{ n: 'North', e: 'East', s: 'South', w: 'West' }[wl]} wall`}
                              onClick={e => { e.stopPropagation(); if (!active) onUpdateDoorWall(i, wl); }}
                              style={{
                                padding: '1px 6px', borderRadius: 3, fontSize: 10, fontWeight: 600, cursor: active ? 'default' : 'pointer',
                                border: `1px solid ${active ? 'rgba(59,130,246,0.6)' : 'rgba(255,255,255,0.15)'}`,
                                background: active ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.05)',
                                color: active ? '#bfdbfe' : 'rgba(255,255,255,0.55)',
                              }}
                            >
                              {wl.toUpperCase()}
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {canEdit && onUpdateDoorWidth && (
                      <div style={{ marginTop: 5 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>Width:</span>
                          <input
                            type="number"
                            min={18}
                            max={72}
                            step={2}
                            value={w}
                            onClick={e => e.stopPropagation()}
                            onChange={e => onUpdateDoorWidth(i, Number(e.target.value))}
                            style={{ width: 48, padding: '2px 4px', borderRadius: 3, border: '1px solid rgba(255,255,255,0.15)', background: 'rgba(255,255,255,0.07)', color: '#f1f5f9', fontSize: 11, outline: 'none' }}
                          />
                          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>in</span>
                          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)' }}>({sizeLabel})</span>
                        </div>
                        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                          {quickSizes.map(s => (
                            <button
                              key={s}
                              onClick={e => { e.stopPropagation(); onUpdateDoorWidth(i, s); }}
                              style={{
                                padding: '2px 6px', borderRadius: 3, fontSize: 10, cursor: 'pointer',
                                border: `1px solid ${w === s ? 'rgba(59,130,246,0.5)' : 'rgba(255,255,255,0.1)'}`,
                                background: w === s ? 'rgba(59,130,246,0.15)' : 'rgba(255,255,255,0.04)',
                                color: w === s ? '#93c5fd' : 'rgba(255,255,255,0.4)',
                              }}
                            >
                              {s}&quot;
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Round 23 item 1: attached sub-rooms (bathroom) as list entries -- same card
                  treatment as the door/window rows, so they're discoverable + manageable here
                  instead of only by clicking them on the canvas. */}
              {attachedRooms.map(br => (
                <div
                  key={`sub-${br.id}`}
                  onClick={() => onSelectRoomById?.(br.id)}
                  title="Select this sub-room on the canvas"
                  style={{
                    padding: '5px 6px', borderRadius: 5, cursor: onSelectRoomById ? 'pointer' : 'default',
                    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ fontSize: 11 }}>🚽</span>
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,0.85)' }}>{br.name || 'Bathroom'}</span>
                    <span style={{ fontSize: 10.5, color: 'rgba(255,255,255,0.45)', flex: 1 }}>sub-room</span>
                    {canEdit && onDeleteRoomById && (
                      <button
                        title="Delete this sub-room"
                        onClick={e => { e.stopPropagation(); onDeleteRoomById(br.id); }}
                        onMouseLeave={spinIconOut}
                        className="combo-jello-spin" data-tint="neutral"
                        style={{ padding: '2px 4px', borderRadius: 4, border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      >
                        <Trash2 size={13} className="btn-icon" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {isAdmin && onAddBathroom && (
          <div style={{ marginTop: 10 }}>
            <button
              onClick={onAddBathroom}
              onMouseEnter={() => onBathroomHoverChange?.(true)}
              onMouseLeave={() => onBathroomHoverChange?.(false)}
              style={{
                width: '100%', padding: '6px 0', borderRadius: 6, cursor: 'pointer',
                border: '1px solid rgba(139,92,246,0.3)',
                background: 'rgba(139,92,246,0.1)',
                color: '#c4b5fd', fontSize: 12,
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
              }}
            >
              <Plus size={12} /> Add Bathroom
            </button>
          </div>
        )}

        {/* Area Link */}
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <span style={labelStyle}>Area Link</span>
            {canLinkEdit && !showLinkEdit && (
              <button
                onClick={() => {
                  setPendingAreaId(room.linked_area_id ?? null);
                  setPendingLocationId((room as any).linked_location_id ?? null);
                  setShowLinkEdit(true);
                }}
                style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.45)', cursor: 'pointer' }}
              >
                Edit Link
              </button>
            )}
          </div>

          {/* Read-only display */}
          {!showLinkEdit && (
            room.linked_area_id == null
              ? <span style={{ fontSize: 12, color: '#fbbf24' }}>Not linked to an area</span>
              : <span style={{ fontSize: 12, color: '#e2e8f0' }}>
                  {areaName ?? `Area #${room.linked_area_id}`}
                  {linkedLocName && (
                    <span style={{ color: 'rgba(255,255,255,0.45)', marginLeft: 4 }}>› {linkedLocName}</span>
                  )}
                </span>
          )}

          {/* Edit mode — two-step area → location selector */}
          {showLinkEdit && canLinkEdit && (
            <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 10 }}>

              {/* Step 1: Area */}
              <div>
                <div style={{ ...labelStyle, marginBottom: 4 }}>Select Area</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 180, overflowY: 'auto' }}>
                  {/* None option */}
                  <button
                    disabled={linkSaving}
                    onClick={() => { setPendingAreaId(null); setPendingLocationId(null); }}
                    style={{
                      padding: '5px 8px', borderRadius: 5, textAlign: 'left',
                      border: `1px solid ${pendingAreaId == null ? '#3b82f6' : 'rgba(255,255,255,0.10)'}`,
                      background: pendingAreaId == null ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.04)',
                      color: pendingAreaId == null ? '#93c5fd' : 'rgba(255,255,255,0.55)',
                      cursor: linkSaving ? 'default' : 'pointer', fontSize: 12,
                    }}
                  >
                    — None —
                  </button>
                  {areaOptions.map(a => {
                    const active = pendingAreaId === a.id;
                    return (
                      <button
                        key={a.id}
                        disabled={linkSaving}
                        onClick={() => { setPendingAreaId(a.id); setPendingLocationId(null); }}
                        style={{
                          padding: '5px 8px', borderRadius: 5, textAlign: 'left',
                          border: `1px solid ${active ? '#3b82f6' : 'rgba(255,255,255,0.10)'}`,
                          background: active ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.04)',
                          color: active ? '#93c5fd' : 'rgba(255,255,255,0.55)',
                          cursor: linkSaving ? 'default' : 'pointer', fontSize: 12,
                        }}
                      >
                        {a.label}
                      </button>
                    );
                  })}
                  {areaOptions.length === 0 && (
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', padding: '4px 0' }}>No areas configured</span>
                  )}
                </div>
              </div>

              {/* Step 2: Location (shown when area is selected) */}
              {pendingAreaId != null && (
                <div>
                  <div style={{ ...labelStyle, marginBottom: 4 }}>
                    {loadingLocations ? 'Loading…' : 'Select Location'}
                  </div>
                  {!loadingLocations && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 140, overflowY: 'auto' }}>
                      <button
                        disabled={linkSaving}
                        onClick={() => setPendingLocationId(null)}
                        style={{
                          padding: '5px 8px', borderRadius: 5, textAlign: 'left',
                          border: `1px solid ${pendingLocationId == null ? '#3b82f6' : 'rgba(255,255,255,0.10)'}`,
                          background: pendingLocationId == null ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.04)',
                          color: pendingLocationId == null ? '#93c5fd' : 'rgba(255,255,255,0.55)',
                          cursor: linkSaving ? 'default' : 'pointer', fontSize: 12,
                        }}
                      >
                        — None —
                      </button>
                      {locationOptions.map(l => {
                        const active = pendingLocationId === l.id;
                        return (
                          <button
                            key={l.id}
                            disabled={linkSaving}
                            onClick={() => setPendingLocationId(l.id)}
                            style={{
                              padding: '5px 8px', borderRadius: 5, textAlign: 'left',
                              border: `1px solid ${active ? '#3b82f6' : 'rgba(255,255,255,0.10)'}`,
                              background: active ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.04)',
                              color: active ? '#93c5fd' : 'rgba(255,255,255,0.55)',
                              cursor: linkSaving ? 'default' : 'pointer', fontSize: 12,
                            }}
                          >
                            {l.label}
                          </button>
                        );
                      })}
                      {locationOptions.length === 0 && (
                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', padding: '4px 0' }}>No sub-locations</span>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Save / Cancel */}
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button
                  disabled={linkSaving}
                  onClick={async () => {
                    setLinkSaving(true);
                    try {
                      await onLinkArea(pendingAreaId, pendingLocationId);
                      setShowLinkEdit(false);
                    } finally {
                      setLinkSaving(false);
                    }
                  }}
                  style={{
                    padding: '5px 14px', borderRadius: 6, border: 'none',
                    background: linkSaving ? 'rgba(59,130,246,0.4)' : '#3b82f6',
                    color: '#fff', fontSize: 12, fontWeight: 600,
                    cursor: linkSaving ? 'default' : 'pointer',
                  }}
                >
                  {linkSaving ? 'Saving…' : 'Save Link'}
                </button>
                <button
                  disabled={linkSaving}
                  onClick={() => {
                    setShowLinkEdit(false);
                    setPendingAreaId(null);
                    setPendingLocationId(null);
                    setLocationOptions([]);
                  }}
                  style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.15)', background: 'transparent', color: 'rgba(255,255,255,0.5)', cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>

            </div>
          )}
        </div>
      </div>

      {canEdit && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.08)', display: 'flex', gap: 8 }}>
          <button
            onClick={onSave} disabled={isSaving}
            style={{ flex: 1, padding: '7px 0', borderRadius: 6, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={onDelete}
            style={{ padding: '7px 12px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.4)', background: 'rgba(239,68,68,0.1)', color: '#fca5a5', fontSize: 13, cursor: 'pointer' }}
          >
            <Trash2 size={14} />
          </button>
        </div>
      )}
    </div>
  );
  return panel;
}

const labelStyle: React.CSSProperties = { fontSize: 11, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: '6px 8px', borderRadius: 5,
  border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.06)',
  color: '#f1f5f9', fontSize: 13, outline: 'none',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ ...labelStyle, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

// ─── Location sort helper ─────────────────────────────────────────────────────

function sortLocations(locs: { id: number; label: string }[]) {
  return [...locs].sort((a, b) => {
    const na = parseInt(a.label.trim(), 10);
    const nb = parseInt(b.label.trim(), 10);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
    if (!isNaN(na)) return -1;
    if (!isNaN(nb)) return 1;
    return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
  });
}

// ─── Legend Corner ────────────────────────────────────────────────────────────

function LegendCorner({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  return (
    <div
      style={{
        position: 'absolute', bottom: 16, left: 16, zIndex: 20,
        background: 'rgba(255,255,255,0.95)', borderRadius: 10,
        boxShadow: '0 2px 14px rgba(0,0,0,0.18)', minWidth: 158, overflow: 'hidden',
      }}
      onMouseDown={e => e.stopPropagation()}
    >
      <button
        onClick={onToggle}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          width: '100%', padding: '8px 12px', border: 'none', cursor: 'pointer',
          background: collapsed ? 'none' : 'rgba(59,130,246,0.1)',
          borderBottom: collapsed ? 'none' : '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <span style={{ fontSize: 11, fontWeight: 700, color: collapsed ? '#1e293b' : '#2563eb', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Map Legend</span>
        <ChevronDown size={12} style={{ color: collapsed ? '#64748b' : '#3b82f6', transform: collapsed ? 'rotate(-90deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }} />
      </button>
      {!collapsed && (
        <div style={{ padding: '8px 12px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
          {ROOM_TYPES.map(t => {
            const c = ROOM_COLORS[t];
            return (
              <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 12, height: 12, borderRadius: 2, background: c.fill, border: `1.5px solid ${c.stroke}`, flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: '#374151' }}>{t.charAt(0).toUpperCase() + t.slice(1)}</span>
              </div>
            );
          })}
          <div style={{ borderTop: '1px solid rgba(0,0,0,0.08)', marginTop: 2, paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 12, height: 3, background: '#dc2626', borderRadius: 1, flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: '#374151' }}>Rated Fire Wall</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 12, height: 12, borderRadius: 2, background: '#f0fdf4', border: '1.5px solid #22c55e', flexShrink: 0 }} />
              <span style={{ fontSize: 11, color: '#374151' }}>Exit / Egress</span>
            </div>
          </div>
          <div style={{ borderTop: '1px solid rgba(0,0,0,0.08)', marginTop: 2, paddingTop: 6, display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Facility Markers</span>
            {(MARKER_DEFS as readonly { id: string; color: string; symbol: string; shape: string; label: string }[]).map(m => (
              <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  width: 12, height: 12,
                  borderRadius: m.shape === 'circle' ? '50%' : m.shape === 'square' ? 2 : 0,
                  background: m.color,
                  flexShrink: 0,
                  transform: m.shape === 'diamond' ? 'rotate(45deg)' : 'none',
                }} />
                <span style={{ fontSize: 11, color: '#374151' }}>{m.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Print Modal ──────────────────────────────────────────────────────────────

function PrintModal({ floorName, facilityName, onClose, onPrint }: { floorName: string; facilityName: string; onClose: () => void; onPrint: () => void }) {
  return ReactDOM.createPortal(
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onMouseDown={onClose}
    >
      <div
        style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 360, boxShadow: '0 8px 24px rgba(0,0,0,0.6)' }}
        onMouseDown={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Printer size={18} style={{ color: 'var(--text-secondary)' }} />
          <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 15 }}>Print Floor Plan</span>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>
          Print <strong style={{ color: 'var(--text-primary)' }}>{floorName}</strong> floor plan for{' '}
          <strong style={{ color: 'var(--text-primary)' }}>{facilityName}</strong>.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={onPrint} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Print</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Cancel Confirm Modal ─────────────────────────────────────────────────────

function CancelConfirmModal({ onClose, onConfirm }: { onClose: () => void; onConfirm: () => void }) {
  return ReactDOM.createPortal(
    // z-index 2100, not the usual 1000: same fullscreen-portal stacking collision fixed on the
    // delete-room confirm modal (both portal to document.body as a sibling of the fullscreen map
    // wrapper's zIndex:2000; a plain 1000 renders this fully behind/unreachable in fullscreen).
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
      onMouseDown={onClose}
    >
      <div
        style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 20, padding: 28, width: '100%', maxWidth: 360, boxShadow: '0 8px 24px rgba(0,0,0,0.6)' }}
        onMouseDown={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <AlertTriangle size={18} style={{ color: 'var(--text-secondary)' }} />
          <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 15 }}>Discard Changes?</span>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>
          Discard all unsaved changes and restore the last saved layout?
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>
            Keep Editing
          </button>
          <button onClick={onConfirm} style={{ padding: '7px 16px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'rgba(255,255,255,0.08)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Discard
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Shortcuts Modal ─────────────────────────────────────────────────────────

// Audited against the actual key/mouse bindings in this file (item 5) -- every row below is
// verified accurate as of this pass, not carried forward unchecked. Changes made:
//   - Removed 'Ctrl + −/+ → Zoom in/out (buttons)': no such Ctrl+key handler exists anywhere in
//     this file (checked handleWheel and the keydown handler) -- the zoom buttons need no modifier
//     at all, so this row just described a shortcut that was never real.
//   - Added 'Escape → Exit fullscreen': a real, separate keydown listener (isMapFullscreen effect)
//     that was never documented here.
//   - Added 'Alt + Click (selected room edge) → Insert a new vertex': confirmed missing, the one
//     Chris flagged explicitly.
//   - Added 'Ctrl + Click (on a vertex) → Delete that vertex': a second, more specific meaning of
//     Ctrl+Click that only applies to an already-selected polygon room's own vertex handles --
//     previously only the multi-select meaning of Ctrl+Click was documented.
//   - Reworded 'Click wall (door tool)' → 'Click near a wall (door/window tool)': window placement
//     uses the exact same wall-click gesture as door and was never mentioned; also now genuinely
//     accurate post-fix -- placement snaps to the NEAREST wall within a small tolerance, not only a
//     literal wall click (see hitRoomForWallTool).
//   - Reworded 'Ctrl + Click 2 rooms' description to mention Join alongside Cut, and added the
//     'Join button' row -- Join exists (handleJoin, admin-only, needs multiSelectCount===2) and was
//     completely undocumented even though it sits right next to the Cut button in the toolbar.
const SHORTCUT_ROWS: [string, string, boolean?][] = [
  ['Navigation', '', true],
  ['Scroll wheel', 'Zoom in / out'],
  ['Middle-click + drag', 'Pan canvas'],
  ['H', 'Fit all rooms to view'],
  ['Escape', 'Exit fullscreen mode (when active)'],
  ['', '', true],
  ['Selection & Editing', '', true],
  ['Click', 'Select room'],
  ['Ctrl + Click', 'Add / remove from multi-select'],
  ['Alt + Click (selected room edge)', 'Insert a new vertex/corner there'],
  ['Ctrl + Click (on a vertex)', 'Delete that vertex (min. 3 corners)'],
  ['Shift + Click (on a door/window)', 'Select it individually to move or flip it'],
  ['Escape', 'Clear selection / multi-select'],
  ['Ctrl + Z', 'Undo'],
  ['Ctrl + Shift + Z (or Ctrl + Y)', 'Redo'],
  ['Ctrl + S', 'Save all dirty rooms'],
  ['Arrow keys', 'Nudge selected room (or selected door/window along its wall) 1 unit'],
  ['Shift + Arrows', 'Nudge 10 units'],
  ['', '', true],
  ['Tool Shortcuts', '', true],
  ['S', 'Select tool'],
  ['D', 'Draw room tool'],
  ['P', 'Pan tool'],
  ['W', 'Window tool'],
  ['', '', true],
  ['Resize & Scale', '', true],
  ['Drag handle', 'Resize room'],
  ['Shift + drag handle', 'Proportional scale from center'],
  ['', '', true],
  ['Door / Window', '', true],
  ['Click near a wall (door/window tool)', 'Place door or window on the nearest wall'],
  ['R', 'Flip door swing direction (door tool, next placement)'],
  ['Scroll wheel (window tool)', 'Adjust window width'],
  ['Right-click near door', 'Delete door / window'],
  ['Drag a Shift+Click-selected door/window', 'Move it along or onto a different wall of the same room'],
  ['⇄ button (property panel)', 'Cycle a door’s hinge + swing — 4 steps: in/L → in/R → out/R → out/L'],
  ['', '', true],
  ['Multi-select & Cut/Join', '', true],
  ['Ctrl + Click 2 rooms', 'Select two rooms for Cut or Join'],
  ['Cut button', 'Subtract smaller from larger (corner/edge only)'],
  ['Join button', 'Merge two adjacent/overlapping rooms into one'],
  ['Escape', 'Clear multi-select'],
];

function ShortcutsModal({ onClose }: { onClose: () => void }) {
  return ReactDOM.createPortal(
    // zIndex 2100, not 2000: at exactly 2000 this would tie the fullscreen map wrapper's own
    // zIndex:2000 (same stacking-context collision class as the delete-confirm/cancel-confirm
    // modals) -- a tie is fragile (falls back to DOM/mount order, not guaranteed on top), so this
    // gets the same explicit >2000 bump those got rather than relying on a tie resolving correctly.
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onMouseDown={onClose}
    >
      <div
        style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 12, width: 560, maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxShadow: '0 8px 32px rgba(0,0,0,0.6)' }}
        onMouseDown={e => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <span style={{ color: 'var(--text-primary)', fontWeight: 600, fontSize: 15 }}>Keyboard &amp; Mouse Shortcuts</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', padding: 4 }}>
            <X size={16} />
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '14px 20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              {SHORTCUT_ROWS.map(([key, action, isHeader], i) => (
                isHeader ? (
                  <tr key={i}>
                    <td colSpan={2} style={{ paddingTop: i === 0 ? 2 : 14, paddingBottom: 4, color: '#60a5fa', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      {key}
                    </td>
                  </tr>
                ) : key === '' ? null : (
                  <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '5px 8px 5px 0', width: '45%', color: 'var(--text-primary)', fontFamily: 'monospace', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {key}
                    </td>
                    <td style={{ padding: '5px 0', color: 'var(--text-secondary)', fontSize: 13 }}>
                      {action}
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Room Inline View ─────────────────────────────────────────────────────────
// Renders room detail tabs inline in the Rooms sub-tab.
// Data-fetch logic mirrors RoomDetailPanel.tsx (see that file for the original).

type RIVTab = 'overview' | 'equipment' | 'extinguishers' | 'paint' | 'fixtures' | 'breakers' | 'workorders';

const RIV_TABS: { id: RIVTab; label: string; icon: React.ReactNode }[] = [
  { id: 'overview',      label: 'Overview',      icon: <LayoutDashboard size={13} /> },
  { id: 'equipment',     label: 'Equipment',      icon: <Package size={13} /> },
  { id: 'extinguishers', label: 'Extinguishers',  icon: <Flame size={13} /> },
  { id: 'paint',         label: 'Paint',          icon: <Palette size={13} /> },
  { id: 'fixtures',      label: 'Fixtures',       icon: <Wrench size={13} /> },
  { id: 'breakers',      label: 'Breakers',       icon: <Zap size={13} /> },
  { id: 'workorders',    label: 'Work Orders',    icon: <ClipboardList size={13} /> },
];

const RIV_FIXTURE_KW = ['light', 'fixture', 'lamp', 'electrical', 'bulb'];
const RIV_SURFACES = ['Room', 'Accent', 'Ceiling', 'Trim', 'Door'] as const;

const ROOM_TYPE_LABELS_MAP: Record<string, string> = {
  resident: 'Resident', medical: 'Medical', common: 'Common Area',
  service: 'Service', mechanical: 'Mechanical', admin: 'Admin',
  hallway: 'Hallway', other: 'Other',
};

function RIVSpinner() {
  return <div style={{ color: 'var(--text-secondary)', fontSize: 13, textAlign: 'center', padding: '28px 0' }}>Loading…</div>;
}

function RIVEmpty({ icon, title, hint }: { icon: string; title: string; hint: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '28px 0' }}>
      <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.6 }}>{icon}</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>{title}</div>
      <div style={{ color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.5 }}>{hint}</div>
    </div>
  );
}

// Round 16 / 21 / 23 / 24: read-only, non-interactive BLUEPRINT-style render of a room's geometry
// for the Rooms > Overview split view -- the room + any attached sub-rooms (bathroom), reusing the
// exact module-level shape components the canvas draws with (RoomShapeSvg / DoorsSvg / FireWallsSvg,
// no duplicated room-drawing code), on a fine grid, with dimension callouts (feet-inches via the
// app's own formatLength), a compass badge, and the live square-footage pill (value passed in from
// the Overview form -- not recomputed). All geometry is in world units (== inches).
function RoomShapePreview({ room, extraRooms = [], sqFt }: { room: FacilityRoom; extraRooms?: FacilityRoom[]; sqFt?: number }) {
  const all = [room, ...extraRooms];
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  let doorPad = 0;
  for (const r of all) {
    const er = effectiveRect(r);
    if (!(er.w > 0 && er.h > 0)) continue;
    if (er.x < minX) minX = er.x;
    if (er.y < minY) minY = er.y;
    if (er.x + er.w > maxX) maxX = er.x + er.w;
    if (er.y + er.h > maxY) maxY = er.y + er.h;
    for (const d of (r.doors ?? [])) doorPad = Math.max(doorPad, Math.max(8, Number((d as any).width) || 32));
  }
  if (!(maxX > minX && maxY > minY)) {
    return <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>No shape to preview</div>;
  }
  const contentW = maxX - minX, contentH = maxY - minY;
  const S = Math.max(contentW, contentH); // reference dimension for laying out callouts/badges

  // Asymmetric viewBox padding: room on TOP + LEFT for the dimension callouts and their text; a
  // little on the RIGHT/BOTTOM for the sqft pill. doorPad keeps swing arcs unclipped.
  const padTL = Math.max(doorPad + 8, S * 0.22, 26);
  const padRB = Math.max(doorPad + 8, S * 0.12, 18);
  const vbX = minX - padTL, vbY = minY - padTL;
  const vbW = contentW + padTL + padRB;
  const vbH = contentH + padTL + padRB;
  const scale = 260 / vbW; // px-per-world-unit at a nominal 260px render width; drives text/stroke sizes
  const px = (n: number) => n / scale; // n screen px expressed in world units

  // ── Dimension callouts (world units are inches -> formatLength, the same feet-inches format the
  //    per-wall length editor uses). Line + text colours are CSS vars so the whole preview follows
  //    the app theme (Round 25 -- the earlier "larger" build hardcoded a light palette here). ──
  const dimOff = S * 0.09;   // gap from the room's outer edge to the extension/measure line
  const extGap = S * 0.02;   // small break between the room edge and where the extension line starts
  const arrow = px(5);       // arrowhead half-size, constant screen px
  const dimStroke = px(1);
  const dimFs = px(11);
  const wLineY = minY - dimOff;
  const hLineX = minX - dimOff;

  // ── Square-footage pill, bottom-left, inside the canvas. Sized to sit with the callout text /
  //    room label, not dominate (Round 25 -- was an oversized badge). ──
  const areaVal = sqFt != null
    ? sqFt
    : (room.points && room.points.length > 2 ? polygonAreaWorld(room.points) / 144 : (Number(room.w) * Number(room.h)) / 144);
  const pillTxt = `${areaVal.toFixed(1)} sq ft`;
  const pillFs = px(9.5);
  const pillPadX = px(5), pillH = px(14);
  const pillW = pillPadX * 2 + pillTxt.length * pillFs * 0.58;
  const pillX = vbX + px(6);
  const pillY = vbY + vbH - pillH - px(6);

  // ── Parent room label -- one label, placed in the most open spot of its shape (Round 23) ──
  const pRect = effectiveRect(room);
  const obstacles: { x: number; y: number }[] = [
    ...extraRooms.map(r => { const er = effectiveRect(r); return { x: er.x + er.w / 2, y: er.y + er.h / 2 }; }),
    ...(room.doors ?? []).map(d => { const a = resolveDoorAnchor(room, d); return { x: a.hx, y: a.hy }; }),
  ];
  let labelPt = { x: pRect.x + pRect.w / 2, y: pRect.y + pRect.h / 2 };
  let bestScore = -1;
  for (const fx of [0.25, 0.5, 0.75]) {
    for (const fy of [0.3, 0.5, 0.7]) {
      const c = { x: pRect.x + pRect.w * fx, y: pRect.y + pRect.h * fy };
      if (!pointInRoom(room, c.x, c.y)) continue;
      const s = obstacles.length ? Math.min(...obstacles.map(o => Math.hypot(o.x - c.x, o.y - c.y))) : 1e9;
      if (s > bestScore) { bestScore = s; labelPt = c; }
    }
  }
  const showLabel = !!room.name && room.type !== 'hallway';

  // Round 27/28: match the mockup's grid -- finer cell (13px vs 22) and a faint BLUE line.
  // A flat low-alpha blue (not a theme token) is deliberate: the same accent-blue at ~13% reads
  // as a blueprint-blue grid on the dark AND the light canvas, which a single white-or-black
  // alpha token can't. Sub-pixel (0.75px) stop keeps the line hairline-subtle.
  const GRID_PX = 13; // blueprint grid cell, in SCREEN px -> always square regardless of room aspect
  const GRID_LINE = 'rgba(59,130,246,0.13)';

  return (
    // Wrapper owns the blueprint background + grid so (a) cells stay square whatever the room's
    // aspect ratio -- CSS backgroundSize is px-square, not tied to the viewBox -- and it fills the
    // whole card even where the SVG below letterboxes ('meet'); (b) card colours are theme CSS
    // vars, so the preview matches the app's current theme in BOTH directions (Round 25 fix).
    <div style={{ position: 'relative', width: '100%', height: '100%', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage:
          `linear-gradient(${GRID_LINE} 0.75px, transparent 0.75px), linear-gradient(90deg, ${GRID_LINE} 0.75px, transparent 0.75px)`,
        backgroundSize: `${GRID_PX}px ${GRID_PX}px`,
        backgroundPosition: 'center center',
      }} />
      <svg
        viewBox={`${vbX} ${vbY} ${vbW} ${vbH}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}
      >
        {all.map(r => {
          const isPoly = !!(r.points && r.points.length > 2);
          return (
            <g key={r.id}>
              <RoomShapeSvg
                room={r}
                selected={false} dirty={false} multiSelected={false} hovered={false}
                floorHasPolygons={isPoly}
                hoveredMidpointIdx={null}
                scale={scale}
                hideLabel
                previewBlue
              />
              {isPoly && <DoorsSvg room={r} scale={scale} white />}
              <FireWallsSvg room={r} />
            </g>
          );
        })}

        {showLabel && (
          <RoomLabelSvg cx={labelPt.x} cy={labelPt.y} name={room.name} boxW={pRect.w} fontSize={px(13)} scale={scale} previewThemed />
        )}

        {/* ── Width callout (across the top) ── */}
        <g style={{ stroke: 'var(--text-accent)' }} strokeWidth={dimStroke} fill="none">
          <line x1={minX} y1={minY - extGap} x2={minX} y2={wLineY - arrow} />
          <line x1={maxX} y1={minY - extGap} x2={maxX} y2={wLineY - arrow} />
          <line x1={minX} y1={wLineY} x2={maxX} y2={wLineY} />
          <path d={`M ${minX} ${wLineY} l ${arrow} ${-arrow} M ${minX} ${wLineY} l ${arrow} ${arrow}`} />
          <path d={`M ${maxX} ${wLineY} l ${-arrow} ${-arrow} M ${maxX} ${wLineY} l ${-arrow} ${arrow}`} />
        </g>
        <text x={(minX + maxX) / 2} y={wLineY - px(4)} fontSize={dimFs} fontWeight={600} fontFamily="'Inter', sans-serif" textAnchor="middle"
          paintOrder="stroke" strokeWidth={px(2.5)} style={{ fill: 'var(--text-primary)', stroke: 'var(--bg-surface)' }}>{formatLength(contentW)}</text>

        {/* ── Height callout (down the left) ── */}
        <g style={{ stroke: 'var(--text-accent)' }} strokeWidth={dimStroke} fill="none">
          <line x1={minX - extGap} y1={minY} x2={hLineX - arrow} y2={minY} />
          <line x1={minX - extGap} y1={maxY} x2={hLineX - arrow} y2={maxY} />
          <line x1={hLineX} y1={minY} x2={hLineX} y2={maxY} />
          <path d={`M ${hLineX} ${minY} l ${-arrow} ${arrow} M ${hLineX} ${minY} l ${arrow} ${arrow}`} />
          <path d={`M ${hLineX} ${maxY} l ${-arrow} ${-arrow} M ${hLineX} ${maxY} l ${arrow} ${-arrow}`} />
        </g>
        <text x={hLineX - px(4)} y={(minY + maxY) / 2} fontSize={dimFs} fontWeight={600} fontFamily="'Inter', sans-serif" textAnchor="middle"
          transform={`rotate(-90 ${hLineX - px(4)} ${(minY + maxY) / 2})`}
          paintOrder="stroke" strokeWidth={px(2.5)} style={{ fill: 'var(--text-primary)', stroke: 'var(--bg-surface)' }}>{formatLength(contentH)}</text>

        {/* ── Square-footage pill ── */}
        <g>
          <rect x={pillX} y={pillY} width={pillW} height={pillH} rx={pillH / 2} strokeWidth={px(0.75)} style={{ fill: 'var(--bg-elevated)', stroke: 'var(--text-accent)' }} />
          <text x={pillX + pillW / 2} y={pillY + pillH / 2} fontSize={pillFs} fontWeight={600} fontFamily="'Inter', sans-serif" textAnchor="middle" dominantBaseline="central" style={{ fill: 'var(--text-accent)' }}>{pillTxt}</text>
        </g>
      </svg>
    </div>
  );
}

function RoomInlineView({
  room, facilityId, userRole = '', initialTab, onRoomUpdate, attachedRooms = [],
}: {
  room: FacilityRoom;
  facilityId: number;
  userRole?: string;
  initialTab?: RIVTab;
  onRoomUpdate?: (updated: Partial<FacilityRoom>) => void;
  attachedRooms?: FacilityRoom[]; // Round 21: sub-rooms (bathroom) to include in the Overview preview
}) {
  const { user: _u } = useAuth();
  const [activeTab, setActiveTab]             = useState<RIVTab>('overview');
  const initialTabRef                         = useRef<RIVTab | null>(initialTab ?? null);
  const [areaLabel, setAreaLabel]             = useState<string | null>(null);
  const [locationLabel, setLocationLabel]     = useState<string | null>(null);
  const [supplies, setSupplies]               = useState<any[]>([]);
  const [suppliesLoading, setSuppliesLoading] = useState(false);
  const [suppliesLoaded, setSuppliesLoaded]   = useState(false);
  const [extinguishers, setExtinguishers]     = useState<any[]>([]);
  const [extLoading, setExtLoading]           = useState(false);
  const [extLoaded, setExtLoaded]             = useState(false);
  const [paintEntries, setPaintEntries]       = useState<any[]>([]);
  const [paintLoading, setPaintLoading]       = useState(false);
  const [paintLoaded, setPaintLoaded]         = useState(false);
  const [fixtures, setFixtures]               = useState<any[]>([]);
  const [fixturesLoading, setFixturesLoading] = useState(false);
  const [fixturesLoaded, setFixturesLoaded]   = useState(false);
  const [breakers, setBreakers]               = useState<any[]>([]);
  const [breakersLoading, setBreakersLoading] = useState(false);
  const [breakersLoaded, setBreakersLoaded]   = useState(false);

  // Overview: edit form state
  const [editType, setEditType]             = useState<string>(room.type);
  const [editName, setEditName]             = useState<string>(room.name);
  const [editRoomNumber, setEditRoomNumber] = useState<string>(room.room_number ?? '');
  // Manual Width/Height removed from this panel -- square footage now comes live from the Facility
  // Map polygon area (the "Facility Map area" line in the Overview render). dimensions_ft_w/h are
  // still a real column, still set from RoomDetailPanel and (for legacy rect rooms) the Facility
  // Map property panel's Auto-Calculate button; this panel just no longer reads or writes them.
  const [editNotes, setEditNotes]           = useState<string>(room.notes ?? '');
  const [overviewSaveStatus, setOverviewSaveStatus] = useState<'' | 'saved' | 'error'>('');

  // Paint tab — add / edit form state
  const [showAddForm, setShowAddForm]               = useState(false);
  const [addWalls, setAddWalls]                     = useState<string[]>([]);
  const [addWallLabel, setAddWallLabel]             = useState('');
  const [addLastPainted, setAddLastPainted]         = useState('');
  const [addNotes, setAddNotes]                     = useState('');
  const [addSelectedSupply, setAddSelectedSupply]   = useState<any | null>(null);
  const [addSaving, setAddSaving]                   = useState(false);
  const [editingEntryId, setEditingEntryId]         = useState<number | null>(null);
  const [allPaintSupplies, setAllPaintSupplies]     = useState<any[]>([]);
  const [paintSuppliesLoading, setPaintSuppliesLoading] = useState(false);
  const [paintSuppliesLoaded, setPaintSuppliesLoaded]   = useState(false);
  const [searchInput, setSearchInput]               = useState('');
  const [searchDisplay, setSearchDisplay]           = useState('');
  const [collapsedWalls, setCollapsedWalls]         = useState<Set<string>>(new Set());
  const rivSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setActiveTab(initialTabRef.current ?? 'overview');
    initialTabRef.current = null;
    setAreaLabel(null); setLocationLabel(null);
    setSupplies([]); setSuppliesLoaded(false);
    setExtinguishers([]); setExtLoaded(false);
    setPaintEntries([]); setPaintLoaded(false);
    setFixtures([]); setFixturesLoaded(false);
    setBreakers([]); setBreakersLoaded(false);
    setShowAddForm(false); setAddWalls([]); setAddWallLabel('');
    setAddLastPainted(''); setAddNotes(''); setAddSelectedSupply(null);
    setAddSaving(false); setEditingEntryId(null);
    setAllPaintSupplies([]); setPaintSuppliesLoaded(false);
    setSearchInput(''); setSearchDisplay('');
    setCollapsedWalls(new Set());
    setEditType(room.type);
    setEditName(room.name);
    setEditRoomNumber(room.room_number ?? '');
    setEditNotes(room.notes ?? '');
    setOverviewSaveStatus('');
  }, [room.id]);

  useEffect(() => {
    if (!facilityId || room.linked_area_id == null) return;
    api.get(`/dropdowns?dropdown_type=area&facility_id=${facilityId}`)
      .then(res => {
        const opts = ((res as any).options ?? []) as { id: number; label: string }[];
        setAreaLabel(opts.find(a => a.id === room.linked_area_id)?.label ?? null);
      }).catch(() => {});
  }, [facilityId, room.linked_area_id]);

  useEffect(() => {
    if (!facilityId || room.linked_area_id == null || room.linked_location_id == null) { setLocationLabel(null); return; }
    api.get(`/dropdowns?dropdown_type=location&facility_id=${facilityId}&parent_id=${room.linked_area_id}`)
      .then(res => {
        const opts = ((res as any).options ?? []) as { id: number; label: string }[];
        setLocationLabel(opts.find(l => l.id === room.linked_location_id)?.label ?? null);
      }).catch(() => {});
  }, [facilityId, room.linked_area_id, room.linked_location_id]);

  useEffect(() => {
    if (activeTab !== 'equipment' || suppliesLoaded) return;
    setSuppliesLoading(true);
    api.get<any[]>('/inventory/supplies').then(res => {
      const all = res.data ?? [];
      const nL = room.name.toLowerCase(), rL = (room.room_number ?? '').toLowerCase(), lL = (locationLabel ?? '').toLowerCase();
      setSupplies(all.filter((s: any) => {
        const sL = (s.location ?? '').toLowerCase(), sA = (s.room_area ?? '').toLowerCase();
        return (sA && (sA.includes(nL) || (rL && sA.includes(rL)) || (lL && sA.includes(lL)))) ||
               (sL && (sL.includes(nL) || (rL && sL.includes(rL)) || (lL && sL.includes(lL))));
      }));
      setSuppliesLoaded(true);
    }).catch(() => setSuppliesLoaded(true)).finally(() => setSuppliesLoading(false));
  }, [activeTab, suppliesLoaded, room.id, room.name, room.room_number, locationLabel]);

  // Item 2 (this round): "in this room" is now purely spatial, computed at read time, rather than
  // a stored facility_extinguishers.room_id that had to be separately written and kept in sync on
  // every marker move -- there's no real equipment-linkage system yet, per Chris, so a marker
  // counts as being in a room purely by its position falling inside that room's own boundary.
  // Fetches every 'extinguisher' marker on the room's floor, keeps the ones whose (x,y) land
  // inside THIS room's polygon (pointInRoom -- the same test hitRoom uses on the canvas), then
  // enriches any that happen to be linked to a real facility_extinguishers record (optional --
  // linking still exists for recording which real inventory item a pin refers to, just no longer
  // gates whether it shows up here at all) with that record's real details.
  useEffect(() => {
    if (activeTab !== 'extinguishers' || extLoaded) return;
    setExtLoading(true);
    (async () => {
      try {
        const markersRes = await api.get<any[]>('/facility-markers', { floor_index: room.floor_index });
        const inRoom = (markersRes.data ?? []).filter((m: any) =>
          m.marker_type === 'extinguisher' && pointInRoom(room, Number(m.x), Number(m.y))
        );
        const linkedIds = Array.from(new Set(inRoom.map((m: any) => m.linked_extinguisher_id).filter((id: any) => id != null)));
        let byId: Record<number, any> = {};
        if (linkedIds.length > 0) {
          const exRes = await api.get<any[]>('/extinguishers');
          byId = Object.fromEntries((exRes.data ?? []).filter((e: any) => linkedIds.includes(e.id)).map((e: any) => [e.id, e]));
        }
        setExtinguishers(inRoom.map((m: any) => {
          const linked = m.linked_extinguisher_id != null ? byId[m.linked_extinguisher_id] : null;
          return linked ? { ...linked, marker_id: m.id } : { id: `marker-${m.id}`, marker_id: m.id, label: m.label };
        }));
        setExtLoaded(true);
      } catch {
        setExtLoaded(true);
      } finally {
        setExtLoading(false);
      }
    })();
  }, [activeTab, extLoaded, room.id, room.floor_index]);

  useEffect(() => {
    if (activeTab !== 'paint' || paintLoaded) return;
    setPaintLoading(true);
    api.get<any[]>(`/facility-rooms/${room.id}/wall-colors`)
      .then(res => { setPaintEntries(res.data ?? []); setPaintLoaded(true); })
      .catch(() => setPaintLoaded(true)).finally(() => setPaintLoading(false));
  }, [activeTab, paintLoaded, room.id]);

  useEffect(() => {
    if (!showAddForm || paintSuppliesLoaded) return;
    setPaintSuppliesLoading(true);
    api.get<any[]>('/inventory/supplies', { type: 'paint' } as Record<string, unknown>)
      .then(res => { setAllPaintSupplies(res.data ?? []); setPaintSuppliesLoaded(true); })
      .catch(() => { setAllPaintSupplies([]); setPaintSuppliesLoaded(true); })
      .finally(() => setPaintSuppliesLoading(false));
  }, [showAddForm, paintSuppliesLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== 'fixtures' || fixturesLoaded) return;
    setFixturesLoading(true);
    api.get<any[]>('/inventory/supplies').then(res => {
      const all = res.data ?? [];
      const nL = room.name.toLowerCase(), rL = (room.room_number ?? '').toLowerCase(), lL = (locationLabel ?? '').toLowerCase();
      setFixtures(all.filter((s: any) => {
        const sL = (s.location ?? '').toLowerCase(), sA = (s.room_area ?? '').toLowerCase();
        const sT = (s.supply_type ?? '').toLowerCase(), sN = s.name.toLowerCase();
        const locM = (sA && (sA.includes(nL) || (rL && sA.includes(rL)) || (lL && sA.includes(lL)))) ||
                     (sL && (sL.includes(nL) || (rL && sL.includes(rL)) || (lL && sL.includes(lL))));
        return locM && RIV_FIXTURE_KW.some(k => sT.includes(k) || sN.includes(k));
      }));
      setFixturesLoaded(true);
    }).catch(() => setFixturesLoaded(true)).finally(() => setFixturesLoading(false));
  }, [activeTab, fixturesLoaded, room.id, room.name, room.room_number, locationLabel]);

  useEffect(() => {
    if (activeTab !== 'breakers' || breakersLoaded) return;
    setBreakersLoading(true);
    api.get<any[]>(`/breaker-panels/by-room/${room.id}`)
      .then(res => { setBreakers(res.data ?? []); setBreakersLoaded(true); })
      .catch(() => setBreakersLoaded(true)).finally(() => setBreakersLoading(false));
  }, [activeTab, breakersLoaded, room.id]);

  const filteredPaintSupplies = useMemo(() => {
    const term = searchDisplay.trim().toLowerCase();
    const list = term
      ? allPaintSupplies.filter((s: any) =>
          s.name.toLowerCase().includes(term) ||
          (s.brand ?? '').toLowerCase().includes(term) ||
          (s.color ?? '').toLowerCase().includes(term)
        )
      : allPaintSupplies;
    return list.slice(0, 12);
  }, [allPaintSupplies, searchDisplay]);

  const rivEntriesByWall = useMemo(() => {
    const map: Record<string, any[]> = {};
    for (const w of RIV_SURFACES) map[w] = [];
    for (const e of paintEntries) {
      if (!map[e.wall_name]) map[e.wall_name] = [];
      map[e.wall_name].push(e);
    }
    return map;
  }, [paintEntries]);

  const canManage = userRole === 'manager' || userRole === 'org_admin' || userRole === 'admin';

  function resetAddForm() {
    setAddWalls([]);
    setEditingEntryId(null);
    setAddWallLabel('');
    setAddLastPainted('');
    setAddNotes('');
    setAddSelectedSupply(null);
    setSearchInput('');
    setSearchDisplay('');
  }

  async function deleteEntry(entryId: number) {
    if (!window.confirm('Remove this paint entry?')) return;
    try {
      await apiClient.delete(`/facility-rooms/${room.id}/wall-colors/${entryId}`);
      setPaintLoaded(false);
    } catch { /* silent */ }
  }

  async function saveEntry() {
    if (!addWalls.length || !addSelectedSupply) return;
    setAddSaving(true);
    try {
      if (editingEntryId != null) {
        await apiClient.put(`/facility-rooms/${room.id}/wall-colors/${editingEntryId}`, {
          wall_name:          addWalls[0],
          facility_supply_id: addSelectedSupply.id,
          wall_label:         addWallLabel || null,
          last_painted_date:  addLastPainted || null,
          notes:              addNotes || null,
        });
      } else {
        for (const wall of addWalls) {
          await apiClient.post(`/facility-rooms/${room.id}/wall-colors`, {
            wall_name:          wall,
            facility_supply_id: addSelectedSupply.id,
            wall_label:         addWallLabel || null,
            last_painted_date:  addLastPainted || null,
            notes:              addNotes || null,
          });
        }
      }
      setShowAddForm(false);
      resetAddForm();
      setPaintLoaded(false);
    } catch { /* silent */ }
    finally { setAddSaving(false); }
  }

  async function saveOverview() {
    setOverviewSaveStatus('');
    // dimensions_ft_w/h intentionally NOT in this payload -- the server's PUT is a partial update
    // (only touches fields present in the body), so leaving them out preserves whatever's already
    // stored rather than nulling it. This panel no longer owns those values.
    const payload: Partial<FacilityRoom> = {
      type:            editType,
      name:            editName,
      room_number:     editRoomNumber || null,
      notes:           editNotes || null,
    };
    try {
      await apiClient.put(`/facility-rooms/${room.id}`, payload);
      setOverviewSaveStatus('saved');
      onRoomUpdate?.(payload);
      setTimeout(() => setOverviewSaveStatus(''), 2000);
    } catch {
      setOverviewSaveStatus('error');
      setTimeout(() => setOverviewSaveStatus(''), 2000);
    }
  }

  const rivLabelStyle: React.CSSProperties = {
    fontSize: 11, fontWeight: 500, textTransform: 'uppercase',
    letterSpacing: '0.07em', color: 'var(--text-muted)', marginBottom: 6,
  };
  const rivInputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 10px', borderRadius: 8,
    border: '1px solid var(--border-default)',
    background: 'rgba(255,255,255,0.045)',
    color: 'var(--text-primary)', fontSize: 13, outline: 'none',
    boxSizing: 'border-box' as const,
  };
  const rivPaintLabelStyle: React.CSSProperties = {
    fontSize: 11, color: 'var(--text-muted)',
    textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4,
  };
  const rivPaintInputStyle: React.CSSProperties = {
    width: '100%', background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--border-default)', borderRadius: 6,
    color: 'var(--text-primary)', fontSize: 12, padding: '6px 8px',
    outline: 'none', boxSizing: 'border-box' as const,
  };
  const rivPaintBtnPrimary: React.CSSProperties = {
    background: '#3b82f6', border: 'none', borderRadius: 6,
    color: '#fff', fontSize: 12, fontWeight: 500, padding: '6px 16px', cursor: 'pointer',
  };
  const rivPaintBtnGhost: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-default)',
    borderRadius: 6, color: 'var(--text-muted)', fontSize: 12, padding: '6px 14px', cursor: 'pointer',
  };

  // Round 24: one live square-footage value -- shown in the form line below AND passed to the
  // Room Preview pill so it's never computed twice. polygon rooms use the shoelace area (same as
  // the Facility Map's own Room Properties "Area"); legacy rect rooms fall back to w*h.
  const overviewSqFt = (room.points && room.points.length > 2)
    ? polygonAreaWorld(room.points) / 144
    : (Number(room.w) * Number(room.h)) / 144;

  const renderContent = () => {
    if (activeTab === 'overview') return (
      // Round 16 / 24: Overview only -- form on the left, a large read-only blueprint preview of
      // the room (+ any attached bathroom) filling the right column. Other sub-tabs are unchanged.
      <div style={{ display: 'flex', gap: 20, alignItems: 'stretch', flexWrap: 'wrap' }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flex: '1 1 320px', minWidth: 260 }}>

        {/* Room Type Picker */}
        <div>
          <div style={rivLabelStyle}>Room Type</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
            {ROOM_TYPES.map(t => {
              const c = ROOM_COLORS[t];
              const active = editType === t;
              return (
                <button
                  key={t}
                  onClick={() => setEditType(t)}
                  style={{
                    fontSize: 11, padding: '4px 10px', borderRadius: 20,
                    cursor: 'pointer', fontWeight: 500, transition: 'all 0.15s',
                    background: active ? c.fill : 'transparent',
                    border: `1px solid ${active ? c.stroke : 'var(--border-default)'}`,
                    color: active ? c.text : 'var(--text-muted)',
                  }}
                >
                  {ROOM_TYPE_LABELS_MAP[t]}
                </button>
              );
            })}
          </div>
        </div>

        {/* Room Name */}
        <div>
          <div style={rivLabelStyle}>Room Name</div>
          <input
            style={rivInputStyle}
            value={editName}
            onChange={e => setEditName(e.target.value)}
            onFocus={e => (e.currentTarget.style.borderColor = '#3b82f6')}
            onBlur={e => (e.currentTarget.style.borderColor = 'var(--border-default)')}
          />
        </div>

        {/* Room Number */}
        <div>
          <div style={rivLabelStyle}>Room Number</div>
          <input
            style={rivInputStyle}
            value={editRoomNumber}
            onChange={e => setEditRoomNumber(e.target.value)}
            onFocus={e => (e.currentTarget.style.borderColor = '#3b82f6')}
            onBlur={e => (e.currentTarget.style.borderColor = 'var(--border-default)')}
          />
        </div>

        {/* Square footage -- live from the Facility Map's own polygon geometry (polygonAreaWorld,
            the exact same shoelace-formula function backing the "Area" figure in the Facility Map's
            Room Properties panel). The old manual "Dimensions (ft)" Width/Height inputs were
            removed from this panel this round -- square footage is derived, not typed, and
            dimensions_ft_w/h are still editable from RoomDetailPanel / the Facility Map panel's
            Auto-Calculate button for the legacy-rect rooms whose Paint/Flooring estimates use
            them. */}
        {room.points && room.points.length > 2 && (
          <div>
            <div style={rivLabelStyle}>Square Footage</div>
            <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 4 }}>
              <strong>{overviewSqFt.toFixed(1)} sq ft</strong>
              <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>live, from the Facility Map floor plan</span>
            </div>
          </div>
        )}

        {/* Notes */}
        <div>
          <div style={rivLabelStyle}>Notes</div>
          <textarea
            style={{ ...rivInputStyle, resize: 'vertical' }}
            rows={3}
            value={editNotes}
            onChange={e => setEditNotes(e.target.value)}
            onFocus={e => (e.currentTarget.style.borderColor = '#3b82f6')}
            onBlur={e => (e.currentTarget.style.borderColor = 'var(--border-default)')}
          />
        </div>

        {/* Area / Location (read-only) */}
        {(areaLabel || locationLabel || room.linked_area_id != null) && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {(areaLabel || room.linked_area_id != null) && (
              <div>
                <div style={rivLabelStyle}>Area</div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 4 }}>
                  {areaLabel ?? `Area #${room.linked_area_id}`}
                </div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                  Managed in Settings › Areas &amp; Locations
                </div>
              </div>
            )}
            {locationLabel && (
              <div>
                <div style={rivLabelStyle}>Location</div>
                <div style={{ fontSize: 13, color: 'var(--text-primary)', marginTop: 4 }}>{locationLabel}</div>
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                  Managed in Settings › Areas &amp; Locations
                </div>
              </div>
            )}
          </div>
        )}

        {/* Save button */}
        {canManage && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 4 }}>
            <button
              onClick={saveOverview}
              style={{
                background: '#3b82f6', color: 'white', border: 'none',
                padding: '8px 20px', borderRadius: 8, fontSize: 13,
                cursor: 'pointer',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#2563eb')}
              onMouseLeave={e => (e.currentTarget.style.background = '#3b82f6')}
            >
              Save Changes
            </button>
            {overviewSaveStatus === 'saved' && (
              <span style={{ fontSize: 12, color: '#10b981', fontWeight: 500 }}>Saved ✓</span>
            )}
            {overviewSaveStatus === 'error' && (
              <span style={{ fontSize: 12, color: '#ef4444', fontWeight: 500 }}>Save failed</span>
            )}
          </div>
        )}

      </div>

      {/* Round 26: genuine nested card -- an outer card with "Room Preview" as an in-card title,
          then a visually distinct inner card holding the grid + blueprint + dimension callouts +
          sqft chip. alignSelf:flex-start opts this column OUT of the row's stretch, so the card
          sizes to its content instead of filling the form column's full height -- roughly half
          its Round-24/25 size, snug around the blueprint. Both card backgrounds are theme vars
          (Round 25 dark-theme fix stays).
          Round 27: swapped the two shades to match the mockup -- outer card is now the lighter
          --bg-elevated, inner grid box the darker --bg-surface.
          Round 28:
          - marginTop drops the card so its top edge sits at the Room Name input (past the Room
            Type label + the 8-pill picker, which wraps to ~2 rows at this column width, + the
            column gap + the Room Name label). Tuned to the current field layout, not measured.
          - flex '0 0 340px' -> '1 1 300px' + smaller minWidth so the column can SHRINK instead
            of overflowing the (overflow-clipped) content area -- that overflow was cutting the
            card's right-side corners, which read as "only some corners rounded".
          - inner grid box --bg-surface -> --bg-base: a touch lighter behind the blueprint,
            still a hair darker than the outer card so the nesting still reads.
          Round 29: inner grid box reverted --bg-base -> --bg-surface (that wasn't the element
          Chris meant). Instead the OUTER card is nudged lighter -- a 6% white film layered over
          --bg-elevated (no-op in light theme, where it's already #fff), so the space AROUND the
          inner box reads lighter while the inner well goes back to its darker shade. */}
      <div style={{ flex: '1 1 300px', minWidth: 260, maxWidth: 380, alignSelf: 'flex-start', marginTop: 108 }}>
        <div style={{
          borderRadius: 14, border: '1px solid var(--border-default)',
          background: 'linear-gradient(rgba(255,255,255,0.06), rgba(255,255,255,0.06)), var(--bg-elevated)',
          padding: 14,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 10 }}>
            Room Preview
          </div>
          <div style={{
            height: 210, borderRadius: 8, overflow: 'hidden',
            border: '1px solid var(--border-default)', background: 'var(--bg-surface)',
          }}>
            <RoomShapePreview room={room} extraRooms={attachedRooms} sqFt={overviewSqFt} />
          </div>
          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 8 }}>
            Read-only — matches the Facility Map floor plan
          </div>
        </div>
      </div>

      </div>
    );

    if (activeTab === 'equipment') return suppliesLoading ? <RIVSpinner /> : supplies.length === 0
      ? <RIVEmpty icon="📦" title="No equipment linked" hint={`Set location/room area to "${room.name}" when adding supply items.`} />
      : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{supplies.length} item{supplies.length !== 1 ? 's' : ''}</div>
          {supplies.map((s: any) => (
            <div key={s.id} style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>{s.name}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {s.brand && <span>{s.brand}</span>}{s.model && <span>{s.model}</span>}
                <span>Qty: {s.quantity}</span>
                {s.condition && <span style={{ textTransform: 'capitalize' }}>{s.condition}</span>}
              </div>
            </div>
          ))}
        </div>
      );

    if (activeTab === 'extinguishers') return extLoading ? <RIVSpinner /> : extinguishers.length === 0
      ? <RIVEmpty icon="🧯" title="No extinguishers in this room" hint="Place an extinguisher marker on the Facility Map inside this room's boundary -- it shows up here automatically, no linking needed." />
      : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{extinguishers.length} extinguisher{extinguishers.length !== 1 ? 's' : ''}</div>
          {extinguishers.map((ex: any) => {
            const overdue = ex.next_due_date ? new Date(ex.next_due_date) < new Date() : false;
            // A marker not linked to a real facility_extinguishers record only carries its map
            // label (if any) -- still shown, per item 2's "position alone is enough" model.
            const displayName = ex.type_model ?? ex.label ?? 'Extinguisher';
            return (
              <div key={ex.id} style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: `1px solid ${overdue ? 'rgba(239,68,68,0.3)' : 'rgba(255,255,255,0.07)'}` }}>
                <div style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>{displayName}</div>
                {!ex.type_model && <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 1 }}>Not linked to an inventory record</div>}
                <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 3 }}>
                  {ex.location && <div>Location: {ex.location}</div>}
                  {ex.serial_number && <div>S/N: {ex.serial_number}</div>}
                  {ex.next_due_date && (
                    <div style={{ color: overdue ? '#fca5a5' : 'var(--text-muted)' }}>
                      {overdue ? '⚠ ' : ''}Next: {ex.next_due_date.slice(0, 10)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      );

    if (activeTab === 'paint') return (
      <div>
        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600 }}>Wall Paint Records</span>
          {canManage && (
            <button
              onClick={() => { setShowAddForm(f => !f); if (showAddForm) resetAddForm(); }}
              style={{ ...rivPaintBtnPrimary, fontSize: 11, padding: '5px 12px' }}
            >
              {showAddForm ? '✕ Cancel' : '+ Add Entry'}
            </button>
          )}
        </div>

        {/* Add / edit form */}
        {showAddForm && (
          <div style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 10, padding: 16, marginBottom: 16 }}>

            {/* Step 1 — Surface selector */}
            <div style={{ marginBottom: 14 }}>
              <div style={rivPaintLabelStyle}>
                {editingEntryId ? 'Step 1 — Change Surface' : 'Step 1 — Select Surface(s)'}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                {RIV_SURFACES.map(w => {
                  const active = addWalls.includes(w);
                  return (
                    <button
                      key={w}
                      onClick={() => setAddWalls(prev => active ? prev.filter(x => x !== w) : [...prev, w])}
                      style={{
                        padding: '5px 12px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                        border: active ? '1px solid #3b82f6' : '1px solid var(--border-default)',
                        background: active ? 'rgba(59,130,246,0.25)' : 'rgba(255,255,255,0.05)',
                        color: active ? '#93c5fd' : 'var(--text-muted)',
                        fontWeight: active ? 600 : 400,
                      }}
                    >
                      {w}
                    </button>
                  );
                })}
              </div>
              <div style={{ marginTop: 10 }}>
                <input
                  style={{ ...rivPaintInputStyle, maxWidth: 320 }}
                  placeholder="Label (optional) — e.g. Base coat, Accent, Trim"
                  value={addWallLabel}
                  onChange={e => setAddWallLabel(e.target.value)}
                />
              </div>
            </div>

            {/* Step 2 — Paint picker */}
            <div style={{ marginBottom: 14 }}>
              <div style={rivPaintLabelStyle}>Step 2 — Pick Paint from Facility Supplies</div>
              <input
                style={{ ...rivPaintInputStyle, marginTop: 6, marginBottom: 10 }}
                placeholder="Search paint colors..."
                value={searchInput}
                onChange={e => {
                  setSearchInput(e.target.value);
                  clearTimeout(rivSearchTimerRef.current);
                  rivSearchTimerRef.current = setTimeout(() => setSearchDisplay(e.target.value), 300);
                }}
              />
              {paintSuppliesLoading ? (
                <div style={{ color: 'var(--text-secondary)', fontSize: 12, textAlign: 'center', padding: '10px 0' }}>
                  Loading paint supplies…
                </div>
              ) : filteredPaintSupplies.length === 0 ? (
                <div style={{ color: 'var(--text-muted)', fontSize: 12, textAlign: 'center', padding: '10px 0', lineHeight: 1.5 }}>
                  No paint items found.<br />
                  Add paint items in Inventory → Facility Supplies → Paint.
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {filteredPaintSupplies.map((s: any) => {
                    const isSelected = addSelectedSupply?.id === s.id;
                    return (
                      <div
                        key={s.id}
                        onClick={() => setAddSelectedSupply(isSelected ? null : s)}
                        style={{
                          border: isSelected ? '2px solid #3b82f6' : '1px solid rgba(255,255,255,0.08)',
                          borderRadius: 8,
                          background: isSelected ? 'rgba(59,130,246,0.1)' : 'rgba(255,255,255,0.03)',
                          cursor: 'pointer', overflow: 'hidden',
                          transition: 'border-color 0.12s, background 0.12s',
                        }}
                      >
                        <div style={{ height: 80, overflow: 'hidden', position: 'relative' }}>
                          {s.image_url ? (
                            <img src={s.image_url} alt={s.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', background: s.color || 'rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <span style={{ fontSize: 22, opacity: 0.6 }}>🎨</span>
                            </div>
                          )}
                        </div>
                        <div style={{ padding: '6px 8px' }}>
                          <div style={{ color: 'var(--text-primary)', fontSize: 12, fontWeight: 500, lineHeight: 1.3 }}>{s.name}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: 10, marginTop: 2, lineHeight: 1.3 }}>
                            {[s.brand, s.color, s.finish].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Step 3 — Date + Notes */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
              <div>
                <div style={rivPaintLabelStyle}>Last Painted Date <span style={{ opacity: 0.45 }}>(optional)</span></div>
                <DatePicker value={addLastPainted} onChange={v => setAddLastPainted(v)} placeholder="Select a date" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-default)', borderRadius: 6, fontSize: 12, padding: '6px 8px' }} />
              </div>
              <div>
                <div style={rivPaintLabelStyle}>Notes <span style={{ opacity: 0.45 }}>(optional)</span></div>
                <input style={rivPaintInputStyle} placeholder="e.g. 2 coats, primer used" value={addNotes} onChange={e => setAddNotes(e.target.value)} />
              </div>
            </div>

            {/* Save / Cancel */}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowAddForm(false); resetAddForm(); }} style={rivPaintBtnGhost}>Cancel</button>
              <button
                onClick={saveEntry}
                disabled={addSaving || !addWalls.length || !addSelectedSupply}
                style={{ ...rivPaintBtnPrimary, opacity: (addSaving || !addWalls.length || !addSelectedSupply) ? 0.45 : 1, cursor: (!addWalls.length || !addSelectedSupply) ? 'not-allowed' : 'pointer' }}
              >
                {addSaving ? (editingEntryId ? 'Updating…' : 'Saving…') : (editingEntryId ? 'Update Entry' : 'Save Entry')}
              </button>
            </div>
          </div>
        )}

        {/* Entries list */}
        {paintLoading ? <RIVSpinner /> : paintEntries.length === 0 ? (
          <div style={{ textAlign: 'center', paddingTop: 28 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>🪣</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 13, fontWeight: 500, marginBottom: 6 }}>No paint records yet.</div>
            <div style={{ color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.5, marginBottom: 16 }}>Add your first entry to track colors used in this room.</div>
            {canManage && !showAddForm && (
              <button onClick={() => setShowAddForm(true)} style={{ ...rivPaintBtnPrimary, fontSize: 12 }}>+ Add Entry</button>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {RIV_SURFACES.map(wall => {
              const entries = rivEntriesByWall[wall] ?? [];
              if (entries.length === 0) return null;
              const isCollapsed = collapsedWalls.has(wall);
              return (
                <div key={wall}>
                  <div
                    onClick={() => setCollapsedWalls(prev => { const next = new Set(prev); if (next.has(wall)) next.delete(wall); else next.add(wall); return next; })}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginBottom: isCollapsed ? 0 : 8, paddingBottom: 6, borderBottom: '1px solid rgba(255,255,255,0.06)' }}
                  >
                    {isCollapsed
                      ? <ChevronRight size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                      : <ChevronDown  size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                    }
                    <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 600 }}>{wall}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 10, background: 'rgba(59,130,246,0.18)', color: '#93c5fd' }}>{entries.length}</span>
                  </div>
                  {!isCollapsed && entries.map((entry: any) => (
                    <div key={entry.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', borderRadius: 8, marginBottom: 6, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      <div style={{ width: 40, height: 40, borderRadius: 6, flexShrink: 0, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
                        {entry.supply_image_url ? (
                          <img src={entry.supply_image_url} alt={entry.supply_name ?? ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : (
                          <div style={{ width: '100%', height: '100%', background: entry.supply_color || 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ fontSize: 16 }}>🎨</span>
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500, lineHeight: 1.3 }}>{entry.supply_name ?? 'Unknown supply'}</div>
                        <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 2, lineHeight: 1.3 }}>
                          {[entry.supply_brand, entry.supply_color, entry.supply_finish].filter(Boolean).join(' · ')}
                        </div>
                        {entry.wall_label && (
                          <span style={{ display: 'inline-block', marginTop: 4, fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10, background: 'rgba(245,158,11,0.18)', color: '#fcd34d' }}>
                            {entry.wall_label}
                          </span>
                        )}
                        {entry.last_painted_date && (
                          <div style={{ color: 'var(--text-muted)', fontSize: 11, marginTop: 3 }}>
                            Painted: {entry.last_painted_date.slice(0, 10)}
                          </div>
                        )}
                      </div>
                      {canManage && (
                        <>
                          <button
                            onClick={() => {
                              setEditingEntryId(entry.id);
                              setAddWalls([entry.wall_name]);
                              setAddWallLabel(entry.wall_label ?? '');
                              setAddLastPainted((entry.last_painted_date ?? '').slice(0, 10));
                              setAddNotes(entry.notes ?? '');
                              if (entry.facility_supply_id) {
                                setAddSelectedSupply({
                                  id: entry.facility_supply_id,
                                  name: entry.supply_name ?? '',
                                  brand: entry.supply_brand ?? null,
                                  color: entry.supply_color ?? null,
                                  finish: entry.supply_finish ?? null,
                                  image_url: entry.supply_image_url ?? null,
                                });
                              }
                              setShowAddForm(true);
                            }}
                            title="Edit this paint entry"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex', borderRadius: 4, flexShrink: 0, transition: 'color 0.12s' }}
                            onMouseEnter={e => (e.currentTarget.style.color = '#93c5fd')}
                            onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => deleteEntry(entry.id)}
                            title="Remove this paint entry"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 4, display: 'flex', borderRadius: 4, flexShrink: 0, transition: 'color 0.12s' }}
                            onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
                            onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
                          >
                            <Trash2 size={14} />
                          </button>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );

    if (activeTab === 'fixtures') return fixturesLoading ? <RIVSpinner /> : fixtures.length === 0
      ? <RIVEmpty icon="💡" title="No fixtures found" hint={`No lighting/electrical items matched "${room.name}". Add supply items with "light", "fixture", "lamp", "electrical", or "bulb" in the name.`} />
      : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 4 }}>{fixtures.length} fixture{fixtures.length !== 1 ? 's' : ''}</div>
          {fixtures.map((s: any) => (
            <div key={s.id} style={{ padding: '10px 12px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500 }}>{s.name}</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 12, marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {s.brand && <span>{s.brand}</span>}{s.model && <span>{s.model}</span>}
                <span>Qty: {s.quantity}</span>
                {s.condition && <span style={{ textTransform: 'capitalize' }}>{s.condition}</span>}
              </div>
            </div>
          ))}
        </div>
      );

    if (activeTab === 'breakers') return breakersLoading ? <RIVSpinner /> : breakers.length === 0
      ? <RIVEmpty icon="⚡" title="No breakers linked" hint="Link breakers to this room from the Breakers tab." />
      : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {breakers.map((b: any) => {
            const sc = b.status === 'on' ? '#10b981' : b.status === 'tripped' ? '#ef4444' : '#64748b';
            return (
              <div key={b.id} style={{ padding: '10px 12px', borderRadius: 8, border: `1px solid ${sc}33`, background: b.status === 'on' ? 'rgba(16,185,129,0.1)' : b.status === 'tripped' ? 'rgba(239,68,68,0.1)' : 'rgba(100,116,139,0.08)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{b.label ?? `Slot #${b.slot_number}`}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 20, textTransform: 'uppercase', background: `${sc}22`, border: `1px solid ${sc}55`, color: sc }}>{b.status}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <span>Panel: {b.panel_name}</span>
                  <span>Slot {b.slot_number} {b.side === 'left' ? 'L' : 'R'}</span>
                  {b.amperage && <span>{b.amperage}A</span>}
                  {b.location_note && <span>· {b.location_note}</span>}
                </div>
              </div>
            );
          })}
        </div>
      );

    return <RIVEmpty icon="🚧" title="Coming soon" hint="Work order history for this room will appear here." />;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Tab strip — pill style matching map/rooms/breakers sub-tabs */}
      <div style={{ marginBottom: 12, flexShrink: 0 }}>
        <div className="riv-tab-strip" style={{ border: '1px solid var(--border-default)', borderRadius: 10, display: 'inline-flex', padding: 3, gap: 2 }}>
          {RIV_TABS.map(tab => (
            <button
              key={tab.id}
              className={activeTab === tab.id ? 'riv-tab-pill-active' : undefined}
              onClick={() => { setActiveTab(tab.id); localStorage.setItem('facilitymap_room_tab', tab.id); }}
              style={{
                borderRadius: 7, padding: '5px 14px', fontSize: 12, fontWeight: 500,
                cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                border: 'none', transition: 'all 0.15s',
                // Item 1 fix (regression from last round's light-theme pass): dropping the whole
                // `background` property here -- not just the active-branch rgba value -- also
                // dropped the INACTIVE branch's `background: 'transparent'`. Inline styles always
                // beat a CSS class, so the active tab's .riv-tab-pill-active background still won
                // (that half looked fine), but an inactive button with no inline background at all
                // falls back to the browser's native <button> chrome (a light gray/system box) --
                // that's what read as "visually broken". `undefined` here (active) omits the
                // property so the CSS class's background can actually apply.
                background: activeTab === tab.id ? undefined : 'transparent',
                color: activeTab === tab.id ? 'var(--text-primary)' : 'var(--text-secondary)',
              }}
            >
              {tab.icon}{tab.label}
            </button>
          ))}
        </div>
      </div>
      {/* Content. Round 29: the rounded card and the SCROLL are now two elements -- the vertical
          scrollbar used to paint over the card's right-side rounded corners (single scrolling box
          == square right edge). Outer keeps the border-radius + border + glass and clips
          (overflow:hidden); the inner child does the scrolling + padding, so its scrollbar is
          clipped inside the radius and all four corners read rounded. */}
      <div style={{
        flex: 1, minHeight: 0, overflow: 'hidden',
        background: 'rgba(255,255,255,0.045)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(255,255,255,0.10)',
        borderRadius: 12,
      }}>
        <div style={{ height: '100%', overflowY: 'auto', padding: 20 }}>
          {renderContent()}
        </div>
      </div>
    </div>
  );
}

// View state (pan/zoom/floor/rotation) persistence, keyed per facility so switching which facility
// an admin is viewing never leaks one facility's saved view into another's. Round 12: moved from
// sessionStorage to localStorage so "leave it at +90 and it reopens at +90" survives a full
// browser close, not just tab navigation within one session. The original anti-stale concern (a
// zoomed-in view from months ago being a surprise on a facility nobody's opened in a while) is
// kept via a savedAt timestamp -- entries older than MAP_VIEW_MAX_AGE_MS are discarded on load.
const MAP_VIEW_STORAGE_PREFIX = 'facilitymap_view_';
const MAP_VIEW_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
function loadPersistedMapView(facilityId: number | undefined | null): { scale: number; ox: number; oy: number; floor: number; rot: number } | null {
  if (facilityId == null) return null;
  try {
    const raw = localStorage.getItem(`${MAP_VIEW_STORAGE_PREFIX}${facilityId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.scale !== 'number' || typeof parsed.ox !== 'number' || typeof parsed.oy !== 'number' || typeof parsed.floor !== 'number') return null;
    // Discard anything older than the max age (or with no timestamp -- pre-Round-12 blobs).
    if (typeof parsed.savedAt !== 'number' || Date.now() - parsed.savedAt > MAP_VIEW_MAX_AGE_MS) {
      try { localStorage.removeItem(`${MAP_VIEW_STORAGE_PREFIX}${facilityId}`); } catch { /* ignore */ }
      return null;
    }
    // rot added later (Round 10 view-rotation) -- tolerate older blobs that predate it rather than
    // rejecting the whole persisted view, so a mid-upgrade tab still restores pan/zoom/floor.
    return { scale: parsed.scale, ox: parsed.ox, oy: parsed.oy, floor: parsed.floor, rot: typeof parsed.rot === 'number' && isFinite(parsed.rot) ? parsed.rot : 0 };
  } catch {
    return null;
  }
}
function saveMapView(facilityId: number | undefined | null, view: { scale: number; ox: number; oy: number; floor: number; rot: number }) {
  if (facilityId == null) return;
  try { localStorage.setItem(`${MAP_VIEW_STORAGE_PREFIX}${facilityId}`, JSON.stringify({ ...view, savedAt: Date.now() })); } catch { /* quota/private-mode -- view just won't persist, not fatal */ }
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function FacilityMap() {
  const { user } = useAuth();
  const { rooms, setRooms, isLoading, error, refetch, createRoom, deleteRoom, bulkUpdate } = useFacilityRooms();

  const roleAllowed = user?.role === 'manager' || user?.role === 'org_admin' || user?.role === 'admin';
  const canEdit = user?.role === 'admin' ||
    ((user?.orgPlanTier === 'pro' || user?.orgPlanTier === 'enterprise') && roleAllowed);
  const isAdmin = user?.role === 'admin';
  const isReadOnly = !canEdit;
  const canEditBreakers = user?.role === 'admin' ||
    user?.role === 'org_admin' ||
    user?.role === 'manager';

  // ── React state — drives DOM only ──
  // Item 3: seed from a persisted view if this facility has one (lazy initializer -- runs once, on
  // mount, not every render).
  const [currentFloor, setCurrentFloor] = useState<number>(() => loadPersistedMapView(user?.facilityId)?.floor ?? 0);
  const [mapView, setMapView] = useState<'facility' | 'rooms' | 'breakers'>(() => {
    const s = localStorage.getItem('facilitymap_subtab') as 'facility' | 'rooms' | 'breakers' | null;
    return (s === 'facility' || s === 'rooms' || s === 'breakers') ? s : 'facility';
  });

  // ── Breaker Panels ──
  type BreakerPanel = {
    id: number; name: string; location_id: number | null;
    total_slots: number; notes: string | null;
    location_label?: string;
    location_area_id?: number | null;
    fed_from_panel_id: number | null;
    fed_from_panel_name: string | null;
  };
  type Breaker = {
    id: number; panel_id: number; slot_number: number; side: 'left' | 'right';
    label: string | null; amperage: number | null; is_double_pole: boolean;
    status: 'on' | 'off' | 'tripped'; notes: string | null;
    pole_count: number;
    amperage_custom: string | null;
    // 'A' = the whole slot (every pre-tandem breaker), 'B' = the second half of a tandem ("1/2")
    // pair sharing the same panel_id+slot_number+side -- see migration_v138.sql.
    tandem_position: 'A' | 'B';
    locations: Array<{
      id: number; location_id: number | null; room_id: number | null;
      location_note: string | null;
    }>;
  };
  const [bpPanels, setBpPanels] = useState<BreakerPanel[]>([]);
  const [bpLoading, setBpLoading] = useState(false);
  const [selectedPanelId, setSelectedPanelId] = useState<number | null>(null);
  const [panelBreakers, setPanelBreakers] = useState<Breaker[]>([]);
  const [breakersLoading, setBreakersLoading] = useState(false);
  const [showCreatePanel, setShowCreatePanel] = useState(false);
  const [newPanelName, setNewPanelName] = useState('');
  const [newPanelSlots, setNewPanelSlots] = useState<number>(24);
  const [newPanelCustomSlots, setNewPanelCustomSlots] = useState('');
  const [newPanelNotes, setNewPanelNotes] = useState('');
  const [newPanelLocationId, setNewPanelLocationId] = useState<number | null>(null);
  const [newPanelAreaId, setNewPanelAreaId] = useState<number | null>(null);
  const [newPanelAreaOptions, setNewPanelAreaOptions] = useState<{id:number;label:string}[]>([]);
  const [newPanelLocOptions, setNewPanelLocOptions] = useState<{id:number;label:string}[]>([]);
  const [savingPanel, setSavingPanel] = useState(false);
  const [editingBreaker, setEditingBreaker] = useState<{
    slot: number; side: 'left' | 'right'; position: 'A' | 'B'; existing: Breaker | null;
  } | null>(null);
  const [bkLabel, setBkLabel] = useState('');
  const [bkAmperage, setBkAmperage] = useState('');
  const [bkDoublePole, setBkDoublePole] = useState(false);
  const [bkStatus, setBkStatus] = useState<'on'|'off'|'tripped'>('on');
  const [bkNotes, setBkNotes] = useState('');
  const [bkLocations, setBkLocations] = useState<Array<{
    id?: number; areaId: number | null; locationId: number | null; roomId: number | null;
    areaLabel: string; locationLabel: string; note: string;
    areaOptions: {id:number;label:string}[];
    locOptions: {id:number;label:string}[];
  }>>([]);
  const [savingBreaker, setSavingBreaker] = useState(false);
  const [deletingBreaker, setDeletingBreaker] = useState(false);
  const [expandedLocationGroup, setExpandedLocationGroup] = useState<number | null>(null);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editPanelData, setEditPanelData] = useState<{
    id: number; name: string; total_slots: number; notes: string;
    location_id: number | null; area_id: number | null;
    fed_from_panel_id: number | null;
  } | null>(null);
  const [savingEditPanel, setSavingEditPanel] = useState(false);
  const [editPanelAreaOptions, setEditPanelAreaOptions] = useState<{id:number;label:string}[]>([]);
  const [editPanelLocOptions, setEditPanelLocOptions] = useState<{id:number;label:string}[]>([]);
  const [editPanelCustomSlots, setEditPanelCustomSlots] = useState('');
  const [bkPoleCount, setBkPoleCount] = useState<0|1|2|3>(1);
  const [bkAmperageCustom, setBkAmperageCustom] = useState('');
  const [newPanelFedFrom, setNewPanelFedFrom] = useState<number | null>(null);

  const [displayZoom, setDisplayZoom] = useState(100);
  const [selectedRoom, setSelectedRoom] = useState<FacilityRoom | null>(null);
  const [hasDirty, setHasDirty] = useState(false);
  // Defaults to collapsed (missing key -> not 'true' -> false) and remembers the user's choice,
  // same localStorage-boolean idiom as WeatherPanel's isExpanded (Dashboard.tsx:323-325).
  const [showLegend, setShowLegend] = useState<boolean>(
    () => localStorage.getItem('facilitymap_legend_expanded') === 'true'
  );
  function toggleLegend() {
    const next = !showLegend;
    setShowLegend(next);
    localStorage.setItem('facilitymap_legend_expanded', String(next));
  }
  // Item 3: full-viewport edit mode. Portals the entire component tree (unchanged) to document.body
  // with fixed positioning instead of its normal embedded flow -- see the bottom of the return
  // statement. The existing ResizeObserver on containerRef already re-fits the SVG canvas to
  // whatever size its container ends up at, so no canvas-specific resize logic is needed here.
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  useEffect(() => {
    if (!isMapFullscreen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setIsMapFullscreen(false); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isMapFullscreen]);
  // Toggling fullscreen switches the component's root between a plain return and
  // ReactDOM.createPortal(...) -- React unmounts/remounts the whole subtree for that (see the
  // wheel-listener effect's comment), so zoomSnapshotCanvasRef ends up pointing at a brand new,
  // blank <canvas> with nothing ever drawn into it. zoomSnapshotStateRef is a plain ref though, not
  // tied to the DOM, so it survives the remount unchanged and would keep claiming a valid cache
  // exists -- the next wheel-zoom would hide the live SVG and show that empty canvas. Invalidate it
  // here so it can't outlive the canvas it was describing.
  useEffect(() => {
    zoomSnapshotStateRef.current = null;
    if (idleCaptureHandleRef.current != null) { cancelIdle(idleCaptureHandleRef.current); idleCaptureHandleRef.current = null; }
  }, [isMapFullscreen]);
  const [showPrint, setShowPrint] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [confirmDeleteRoom, setConfirmDeleteRoom] = useState<{ id: number; name: string } | null>(null);
  const deleteModalMouseDownTarget = useRef<EventTarget | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [tool, setTool] = useState<Tool>('pan');       // toolbar highlight only — never drives canvas redraw
  const [snapEnabled, setSnapEnabled] = useState(true);
  const [showGrid, setShowGrid] = useState(true);
  const [historyLength, setHistoryLength] = useState(0);
  const [redoLength, setRedoLength] = useState(0); // item 6: real redo stack, drives the Redo button's disabled state
  const [proportionalScaleActive, setProportionalScaleActive] = useState(false);
  const [traceScale, setTraceScale] = useState<number>(100);
  const [traceOffsetX, setTraceOffsetX] = useState<number>(0);
  const [traceOffsetY, setTraceOffsetY] = useState<number>(0);
  // Round 10 item 3: rotation (degrees, clockwise) of the background reference image itself --
  // independent of the map-VIEW rotation below. For straightening a crooked scan before tracing.
  // Persisted server-side per floor alongside scale/offset via facility_floor_images.rotation.
  const [traceRotation, setTraceRotation] = useState<number>(0);

  // ── Marker / door-delete / context-menu state ──
  const [selectedMarker, setSelectedMarker] = useState<FacilityMarker | null>(null);
  const [markerPanelPos, setMarkerPanelPos] = useState<{ x: number; y: number } | null>(null);
  const [showMarkerPopover, setShowMarkerPopover] = useState(false);
  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; roomId: number; doorIdx: number } | null>(null);
  const [extOptions, setExtOptions] = useState<{ id: number; tag: string; location_label?: string }[]>([]);
  const [showExtLinkList, setShowExtLinkList] = useState(false);

  // ── Trace image state ──
  const [traceImageId, setTraceImageId] = useState<number | null>(null);
  const [traceImageUrl, setTraceImageUrl] = useState<string | null>(null);
  // Whether this floor's row has a calibrated svg skeleton at all (independent of traceImageUrl,
  // which tracks the raster photo) -- item 7's "store both independently" fix. Drives whether the
  // svg<->raster switch control shows up in the Trace panel.
  const [hasSvgBackground, setHasSvgBackground] = useState(false);
  const [traceOpacity, setTraceOpacity] = useState<number>(30);
  // Visibility of the ACTIVE background layer -- skeleton (svg mode) or trace photo (raster mode),
  // whichever backgroundModeRef currently points at. Named per-trace historically; the eye-slash
  // toggle now covers both since a floor can have only a skeleton and no photo. Rooms are drawn
  // above this layer and are never gated on it -- they stay visible/editable regardless.
  const [bgVisible, setBgVisible] = useState(true);
  const [showTracePanel, setShowTracePanel] = useState(false);
  const [traceUploading, setTraceUploading] = useState(false);
  // Bumped by the skeleton import handler to re-run the floor-image load effect below, so a
  // freshly imported skeleton is applied to the same refs (svgMarkupRef, backgroundModeRef, …)
  // exactly as a floor switch would -- no duplicated apply logic, no page reload.
  const [bgReloadKey, setBgReloadKey] = useState(0);

  // ── Multi-select / cut / shortcuts state ──
  const [multiSelectCount, setMultiSelectCount] = useState(0);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  // ── Snap-to-rooms state ──
  const [snapToRooms, setSnapToRooms] = useState(true);

  // ── Room selector state ──
  const [selectorAreaOptions, setSelectorAreaOptions] = useState<{ id: number; label: string }[]>([]);
  const [selectorLocationOptions, setSelectorLocationOptions] = useState<{ id: number; label: string }[]>([]);
  const [selectorAreaId, setSelectorAreaId] = useState<number | null>(null);
  const [selectorLocationId, setSelectorLocationId] = useState<number | null>(null);
  const [selectorLocLoading, setSelectorLocLoading] = useState(false);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const [selectorAreaSearch, setSelectorAreaSearch] = useState('');
  const [selectorSelectedLabel, setSelectorSelectedLabel] = useState<string | null>(null);

  // ── Room detail panel state ──
  const [showRoomDetail, setShowRoomDetail] = useState(false);
  const [roomDetailRoom, setRoomDetailRoom] = useState<FacilityRoom | null>(null);
  // Round 22 item 1 (root cause): the PropertyPanel is a 360px grid column. Mounting it the instant
  // a room is selected shrinks the map <svg> ~372px on its right edge -- and if that happens while
  // the selecting mousedown is still held, getWorldPos (its un-rotate pivot is the live canvas
  // centre) and the rotation transform-origin both shift, so a still-armed dragRef then translates
  // the room by the phantom delta. Gate the column on this flag instead: it goes true only once the
  // selecting gesture has ended (handleMouseUp) or when selection came from a non-canvas path
  // (search / restore) -- never mid-mousedown. Idle single-column behaviour is unchanged.
  const [panelColumnReady, setPanelColumnReady] = useState(false);

  // ── Refs — map render state ──
  // Renamed conceptually from "canvas" to the map's root SVG element, but kept as `canvasRef` to
  // minimize the diff across the ~15 call sites below that read it (getBoundingClientRect, wheel
  // listener) -- none of that logic needed to change, only what element it points at.
  const canvasRef = useRef<SVGSVGElement>(null);
  // Perf: the <g> carrying the pan/zoom transform. Panning updates its CSS transform imperatively
  // (bypassing React entirely -- see handleMouseMove's pan branch) instead of going through a full
  // draw()-triggered re-render on every mousemove. Profiled: a real render costs ~90-300ms on
  // First Floor's 89 rooms even with rAF-coalescing (item 2), since reconciling ~90 room
  // components + a multi-thousand-node skeleton subtree is expensive regardless of how often it's
  // triggered. Panning never changes room/skeleton data, only this one transform -- so during a
  // pan, React's render is skipped altogether; draw() still fires once on pan-end so React
  // reconciles the final ox/oy for anything else that depends on a normal render happening.
  const panGroupRef = useRef<SVGGElement>(null);
  // Round 15: the outer view-rotation <g>. Held so the eased-transition polish (fit / 90-degree
  // snap / toolbar zoom) can toggle a short `transition` on it imperatively without React ever
  // managing that property. `will-change: transform` on both groups (set in the JSX) promotes
  // them to their own compositor layer so a live pan/pinch is a GPU transform, not a full
  // repaint of the ~hundreds-of-nodes vector scene every frame.
  const rotGroupRef = useRef<SVGGElement>(null);
  // Perf: same bypass extended to wheel-zoom (see handleWheel) -- during an active zoom flick,
  // panGroupRef's transform (translate+scale) and this badge's text are written imperatively, no
  // React re-render, so the already-rendered SVG content is just translated/scaled in place like a
  // map tile instead of recomputing ~90 rooms' fill/stroke/text per wheel tick. A short idle timeout
  // (wheelIdleTimerRef) fires the one real draw() once wheel events stop, settling the final frame
  // at full detail -- wheel has no natural "mouseup" the way pan does, hence the timeout instead.
  const zoomBadgeRef = useRef<HTMLSpanElement>(null);
  const wheelIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Perf item 5: proactive raster cache for zoom, replacing an earlier REACTIVE version (captured
  // at gesture-start) that was reverted -- that version raced its own idle-settle timer: under the
  // load a slow capture created, wheel ticks arrived late, the settle timer fired mid-capture,
  // tore the in-flight result down, and the next tick restarted capture from scratch (measured: up
  // to 15 redundant captures for one 15-tick gesture, 47s instead of 8s). This version captures
  // only when GENUINELY idle (via scheduleIdleCapture, called from pan-end and zoom-settle, never
  // from inside an active gesture), so there's no gesture for a slow capture to race against, and
  // completing a capture never tears down or restarts anything -- it only ever populates
  // zoomSnapshotStateRef for whichever gesture asks for it next.
  const zoomSnapshotCanvasRef = useRef<HTMLCanvasElement>(null);
  const zoomSnapshotStateRef = useRef<{ ox: number; oy: number; scale: number } | null>(null);
  const zoomSnapshotCapturingRef = useRef(false);
  // Whether the CURRENT gesture (if any) is actively displaying the cached bitmap -- distinct from
  // zoomSnapshotStateRef, which just tracks whether a valid cache exists at all.
  const zoomGestureUsingSnapshotRef = useRef(false);
  // Perf (item 2): captureIdleSnapshot used to XMLSerializer-serialize the ENTIRE live <svg>
  // (~90 room elements + the multi-thousand-node skeleton subtree) on EVERY capture -- and a
  // capture fires after every pan-end and every zoom-settle, the vast majority of which never
  // touch room geometry at all. Profiled as the single biggest main-thread cost of the whole
  // map (65 long tasks, ~68-70% of blocking time in a pan+zoom+select session). Now the static
  // inner markup of panGroupRef is serialized ONCE and cached here, keyed by a cheap state hash
  // (snapshotMarkupKeyRef). Subsequent captures that changed only the outer transform (pan,
  // zoom, fit, toolbar zoom -- everything view-only) reuse the cached string and only rebuild
  // the tiny <svg><g transform>...</g></svg> wrapper. The key is DERIVED from the actual room
  // geometry / label / skeleton / trace state every capture, not a hand-maintained dirty flag
  // across ~70 draw() call sites -- so it cannot silently drift the way a flag would, and a
  // genuine geometry edit always busts it on the very next capture.
  const snapshotMarkupRef = useRef<string | null>(null);
  const snapshotMarkupKeyRef = useRef<string>('');
  const idleCaptureHandleRef = useRef<number | ReturnType<typeof setTimeout> | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasSizeRef = useRef({ w: 800, h: 600 });
  const canvasReadyRef = useRef(false);
  // Forces a React re-render so the SVG JSX (computed from the refs below at render time) picks
  // up the latest values -- this is what `draw()` now does, replacing the old imperative canvas
  // repaint. Every existing draw() call site is unchanged; only what draw() does internally changed.
  const [renderTick, setRenderTick] = useState(0);
  // rAF-coalescing for draw() (perf item 2): handleMouseMove calls draw() directly and
  // synchronously on every native mousemove during a pan/drag, with no batching -- profiled at
  // ~1:1, every single mousemove producing its own full re-render. This flag makes any number of
  // draw() calls within the same animation frame collapse into a single scheduled re-render,
  // capping the render rate at the display's actual refresh rate instead of the mouse's event
  // rate. Standard rAF-coalescing pattern.
  const rafScheduledRef = useRef(false);

  // Item 3: seed pan/zoom from a persisted view too, same as currentFloor above. useRef's argument
  // (unlike useState's) has no lazy-initializer form -- it's fine to just call
  // loadPersistedMapView again here since it's cheap (one sessionStorage read + JSON.parse) and
  // React only actually uses a useRef initial value on the very first render regardless.
  const scaleRef = useRef(loadPersistedMapView(user?.facilityId)?.scale ?? 1);
  const oxRef = useRef(loadPersistedMapView(user?.facilityId)?.ox ?? 40);
  const oyRef = useRef(loadPersistedMapView(user?.facilityId)?.oy ?? 40);
  // Round 10 item 2: map-VIEW rotation (degrees, clockwise), applied as a CSS `rotate()` on a
  // wrapping <g> around panGroupRef -- panGroupRef itself keeps only translate+scale, so the
  // imperative pan/zoom transform writes never need a rotation term. Read at render time in
  // renderMapSvg; persisted alongside scale/ox/oy/floor in the same sessionStorage blob.
  const rotRef = useRef<number>(loadPersistedMapView(user?.facilityId)?.rot ?? 0);
  const [displayRot, setDisplayRot] = useState<number>(() => Math.round(loadPersistedMapView(user?.facilityId)?.rot ?? 0));

  const toolRef = useRef<Tool>('pan');
  const snapRef = useRef(true);
  const showGridRef = useRef(true);
  const selectedIdRef = useRef<number | null>(null);
  const dirtyIdsRef = useRef<Set<number>>(new Set());
  const roomsRef = useRef<FacilityRoom[]>([]);
  const currentFloorRef = useRef<number>(loadPersistedMapView(user?.facilityId)?.floor ?? 0); // item 3: matches currentFloor's own seed above

  const historyRef = useRef<FacilityRoom[][]>([]);
  const redoRef = useRef<FacilityRoom[][]>([]); // item 6: real redo stack, capped at 50 like undo
  const lastSavedRef = useRef<FacilityRoom[]>([]);

  const drawingRef = useRef<DrawingState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  const resizeRef = useRef<ResizeState | null>(null);
  const panRef = useRef<PanState | null>(null);
  // Mobile: two-finger pan + pinch-zoom gesture state. Only ever set while >=2 fingers are on the
  // canvas -- pure view manipulation (ox/oy/scale), completely separate from the tool/selection
  // state machine the mouse handlers drive. singleTouchActiveRef tracks whether a one-finger touch
  // is currently being forwarded into those mouse handlers (single finger == "the mouse").
  const touchGestureRef = useRef<{ startDist: number; startScale: number; startMidX: number; startMidY: number; startOx: number; startOy: number } | null>(null);
  const singleTouchActiveRef = useRef(false);
  // Item 5: an individually-selected door/window (Shift+Click), independent of room selection --
  // doors/windows aren't their own FacilityRoom records, just entries in a room's own `doors`
  // array, so "selecting one" means tracking which entry, not a separate id. selectedDoorRef
  // drives the canvas highlight and drag-eligibility; doorDragRef is only set while actively
  // dragging that door to a new wall/position.
  const selectedDoorRef = useRef<{ roomId: number; doorIndex: number } | null>(null);
  const doorDragRef = useRef<{ roomId: number; doorIndex: number } | null>(null);

  const initialFitDoneRef = useRef(false);

  // ── Trace image refs ──
  const traceImgRef = useRef<HTMLImageElement | null>(null);
  const traceOpacityRef = useRef<number>(0.30);
  const bgVisibleRef = useRef<boolean>(true); // mirrors bgVisible for read inside renderMapSvg / draw
  const traceLoadedRef = useRef<boolean>(false);
  const traceScaleRef = useRef<number>(100);
  const traceOffsetXRef = useRef<number>(0);
  const traceOffsetYRef = useRef<number>(0);
  const traceRotationRef = useRef<number>(0); // Round 10 item 3: background-image rotation, degrees
  const traceSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Live SVG background (facility_floor_images.background_mode = 'svg') ──
  // Now rendered as a plain nested <svg> sibling inside the SAME <g transform> as everything
  // else (rooms, doors, markers) -- one shared coordinate space, not a separately CSS-transformed
  // DOM layer kept in sync by hand. That's the whole reason this rebuild replaced canvas+overlay-div.
  // 'pending' until the floor-image fetch resolves -- renders transparent (dark container shows
  // through), same as 'svg'. Only settles to 'raster' (light paper-color fallback rect) once we've
  // actually confirmed this floor has no svg skeleton. Prevents the light-background flash that
  // happened when this defaulted straight to 'raster' on every mount/floor-change, painting
  // #f0ede8 for the entire fetch round-trip before flipping to the real mode.
  const backgroundModeRef = useRef<'raster' | 'svg' | 'pending'>('pending');
  const svgMarkupRef = useRef<string | null>(null);
  // Inches-per-raw-SVG-unit for the current floor's skeleton -- read from facility_floor_images'
  // existing (otherwise-idle-in-svg-mode) `scale` column, NOT hardcoded. Defaults to 1 (no-op)
  // when unset so a floor without a calibrated background doesn't scale garbage.
  const svgScaleInchesPerUnitRef = useRef<number>(1);

  // ── Marker refs ──
  const markersRef = useRef<FacilityMarker[]>([]);
  const selectedMarkerIdRef = useRef<number | null>(null);
  const markerPlacementTypeRef = useRef<string>('extinguisher');
  const markerDragRef = useRef<{ marker: FacilityMarker; offsetX: number; offsetY: number } | null>(null);
  const previewRef = useRef<{ roomId: number; wall: string; pos: number; edge?: { fx: number; fy: number; nx: number; ny: number }; canvasX: number; canvasY: number } | null>(null);
  // Round 22 item 1 (2nd layer): view signature snapshotted at every mousedown (see handleMouseDown).
  // If it no longer matches on a subsequent mousemove while a drag/resize/vertex/marker/draw gesture
  // is armed, the view or canvas size moved under the gesture -- cancel it rather than compute a
  // room/marker translation from the now-stale arm-time offsets.
  const gestureViewSigRef = useRef('');
  // Item 2: Add Bathroom has no cursor-tracked placement gesture (it's a single-click auto-add,
  // not a Tool the user drags/aims), so its "preview before commit" is a ghost shown while hovering
  // the button itself rather than following the mouse -- same idea as the other placement previews,
  // adapted to the one tool that isn't cursor-driven.
  const bathroomPreviewActiveRef = useRef(false);
  const doorSwingRef = useRef<'inward' | 'outward'>('inward');
  const windowWidthRef = useRef<number>(30);
  const markerPreviewPosRef = useRef<{ x: number; y: number } | null>(null);
  const arrowPutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectedIdsRef = useRef<Set<number>>(new Set());
  const polyVertexDragRef = useRef<{ roomId: number; vertexIdx: number } | null>(null);
  // Room-fill hover (item 2) + edge-midpoint "add a corner" hover (item 3) -- neither was tracked
  // before; both are plain refs read inside draw(), consistent with this file's existing pattern
  // of never triggering a React re-render from mousemove.
  const hoveredRoomIdRef = useRef<number | null>(null);
  const hoveredMidpointRef = useRef<{ roomId: number; edgeIdx: number } | null>(null);
  // Item 4: true whenever the select-tool cursor is over a draggable object -- room fill/edge,
  // door/window, marker. Drives the move/grab cursor. A ref, not state -- the select-tool hover
  // branch already calls draw() on any hover change, which forces the re-render that re-reads this
  // in the `cursor` computation.
  const hoverSelectableRef = useRef<boolean>(false);
  // Round 14 item 3: true when the cursor is over a precise edit GRIP of the selected room -- a
  // polygon vertex, an edge midpoint, or a rect resize handle. These sit on the room's boundary,
  // so hoverSelectableRef also goes true via the room hover -- this ref wins in the cursor
  // computation and forces the plain arrow, because a grip reads as "click to edit a point", not
  // "grab to move an object".
  const hoverHandleRef = useRef<boolean>(false);
  const glowRef = useRef<{ roomId: number; alpha: number; animId: number } | null>(null);
  // Round 12 item 3: while true, a view-moving helper (scrollToRoom / any recenter) must no-op --
  // held briefly across a delete/add so those operations can't shift pan/zoom/rotation on their own.
  const viewLockRef = useRef(false);
  const selectorBtnRef = useRef<HTMLButtonElement>(null);
  // Item 6: hidden file input the Import Skeleton toolbar button clicks programmatically, now that
  // the button is a real <button> (combo-jello-spin/spinIconOut, matches Export's size/animation)
  // instead of a <label> wrapping the input directly.
  const skeletonFileInputRef = useRef<HTMLInputElement>(null);

  // ── Snap-to-rooms refs ──
  const snapToRoomsRef = useRef(true);
  const snapHighlightRef = useRef<{ x1: number; y1: number; x2: number; y2: number } | null>(null);

  // floor rooms (current floor) read from refs
  const floorRoomsLive = useCallback(
    () => roomsRef.current.filter(r => r.floor_index === currentFloorRef.current),
    [],
  );

  // ── THE draw function — reads refs + live canvas, never from React render ──
  // draw() no longer paints a canvas -- it forces a React re-render, and the JSX below (computed
  // fresh from the same refs at render time by renderMapSvg()) reflects the latest state. Every
  // existing draw() call site elsewhere in this file is unchanged.
  const draw = useCallback(() => {
    if (rafScheduledRef.current) return;
    rafScheduledRef.current = true;
    requestAnimationFrame(() => {
      rafScheduledRef.current = false;
      // Flush zoom-badge display state here too — setting it directly in handleWheel would force
      // a synchronous re-render on every wheel tick, bypassing this same rAF-coalescing entirely.
      setDisplayZoom(Math.round(scaleRef.current * 100));
      setRenderTick(t => (t + 1) % 1_000_000);
    });
  }, []);

  // Round 15 polish: a short ease for the DISCRETE, non-interactive view jumps only -- fit-to-view,
  // the 90-degree rotate snap, the toolbar/mobile zoom buttons. Never for live drag / pinch /
  // wheel, which write the transform imperatively every frame and must track the pointer 1:1.
  // React never manages `style.transition` (it isn't in the JSX style object), so toggling it
  // imperatively here is safe across the re-render that draw() triggers.
  const animateViewJump = useCallback((which: 'pan' | 'rot' | 'both') => {
    const dur = 180;
    const apply = (el: SVGGElement | null) => {
      if (!el) return;
      el.style.transition = `transform ${dur}ms ease`;
      window.setTimeout(() => { el.style.transition = ''; }, dur + 60);
    };
    if (which !== 'rot') apply(panGroupRef.current);
    if (which !== 'pan') apply(rotGroupRef.current);
  }, []);

  // Kill any in-flight jump ease the instant a live gesture starts, so the first imperative
  // transform writes of a pan/pinch aren't animated (which reads as lag).
  const clearViewJumpEase = useCallback(() => {
    if (panGroupRef.current) panGroupRef.current.style.transition = '';
    if (rotGroupRef.current) rotGroupRef.current.style.transition = '';
  }, []);

  // requestIdleCallback wrapper (Safari has no native support -- falls back to a plain timer).
  const scheduleIdle = (cb: () => void, timeout: number): number | ReturnType<typeof setTimeout> => {
    const ric = (window as any).requestIdleCallback;
    return typeof ric === 'function' ? ric(cb, { timeout }) : setTimeout(cb, 300);
  };
  const cancelIdle = (handle: number | ReturnType<typeof setTimeout>) => {
    const cic = (window as any).cancelIdleCallback;
    if (typeof cic === 'function') cic(handle); else clearTimeout(handle as ReturnType<typeof setTimeout>);
  };

  // Same transform math as pan's bypass, generalized to also rescale: the overlay canvas holds a
  // bitmap of the scene as it looked at capture time (base.ox/oy/scale); this repositions/rescales
  // it to match the CURRENT live ox/oy/scale via a pure CSS translate+scale -- a cheap GPU bitmap
  // resample, never a re-rasterization of the underlying vector content.
  const applySnapshotTransform = useCallback(() => {
    const base = zoomSnapshotStateRef.current;
    const cv = zoomSnapshotCanvasRef.current;
    if (!base || !cv) return;
    const relScale = scaleRef.current / base.scale;
    const tx = oxRef.current - base.ox * relScale;
    const ty = oyRef.current - base.oy * relScale;
    cv.style.transform = `translate(${tx}px, ${ty}px) scale(${relScale})`;
  }, []);

  // Hides the cache overlay and restores the live vector layer -- called once a zoom gesture
  // settles. ALSO invalidates zoomSnapshotStateRef here -- an earlier version deliberately left it
  // alive (reasoning: "the cache's own validity is independent of whether THIS gesture used it"),
  // but that was the actual bug: the settle redraw moves the LIVE scale to wherever the gesture
  // ended, while the cache still records wherever it was BEFORE that gesture started. A fresh
  // capture (scheduleIdleCapture, called right after this everywhere) takes ~600ms-schedule +
  // ~700-900ms-decode to actually land -- any new zoom gesture started before that (e.g. "zoom in,
  // zoom out" at a normal pace, faster than ~1.5s between them) would find the OLD, now-stale cache
  // still marked valid and blit it at the wrong relative scale. Confirmed live: this reproduced the
  // exact "big then small" symptom with two ordinary wheel-zoom gestures a beat apart, no toolbar
  // buttons or fullscreen involved at all. Invalidating on every settle means a gesture that starts
  // before a fresh capture is ready correctly falls back to the plain live-vector bypass (safe,
  // correct, just not cache-accelerated) instead of ever blitting a bitmap that doesn't match reality.
  const endZoomGestureDisplay = useCallback(() => {
    if (panGroupRef.current) panGroupRef.current.style.visibility = '';
    if (zoomSnapshotCanvasRef.current) zoomSnapshotCanvasRef.current.style.display = 'none';
    zoomGestureUsingSnapshotRef.current = false;
    zoomSnapshotStateRef.current = null;
  }, []);

  // Round 17: enter "blit the raster snapshot instead of transforming the live SVG" mode for a
  // gesture -- extracted from handleWheel so mouse-drag pan and touch pinch reuse the EXACT same
  // mechanism (a <canvas> element is reliably composited; an SVG <g> transform apparently is not,
  // which is why last round's will-change on the group made no perceptible difference). Returns
  // true if it engaged. Only engages when captureIdleSnapshot has already primed a raster of the
  // CURRENT settled view -- which it only does at rot === 0 -- so a rotated-view pan/pinch, or one
  // started before the idle capture lands, just falls back to transforming the live SVG as today.
  const beginSnapshotDisplay = useCallback((): boolean => {
    if (!zoomSnapshotStateRef.current || zoomGestureUsingSnapshotRef.current) return false;
    zoomGestureUsingSnapshotRef.current = true;
    if (panGroupRef.current) panGroupRef.current.style.visibility = 'hidden';
    if (zoomSnapshotCanvasRef.current) zoomSnapshotCanvasRef.current.style.display = 'block';
    applySnapshotTransform(); // paint at the current (== base) position immediately, no flash
    return true;
  }, [applySnapshotTransform]);

  // Round 17: settle a snapshot-displayed gesture -- put the real SVG back at the FINAL position
  // synchronously BEFORE unhiding it, so there's no one-frame flash of the pre-gesture position
  // while draw()'s rAF is still pending. Safe whether or not the snapshot was actually engaged.
  const endSnapshotDisplay = useCallback(() => {
    if (zoomGestureUsingSnapshotRef.current && panGroupRef.current) {
      panGroupRef.current.style.transform = `translate(${oxRef.current}px, ${oyRef.current}px) scale(${scaleRef.current})`;
    }
    endZoomGestureDisplay();
  }, [endZoomGestureDisplay]);

  // Cheap FNV-1a hash of everything that affects the snapshot's STATIC inner content: per-room
  // geometry (x/y/w/h, polygon vertices), labels (name/room_number), type, door/fire-wall
  // placement, the skeleton background markup + its scale, grid presence, and the trace image's
  // placement. Deliberately EXCLUDES ox/oy/scale (that's the outer transform, re-applied fresh
  // every capture) and selection/hover/glow (transient highlight decoration a <600ms mid-gesture
  // placeholder does not need pixel-accurate). Runs in microseconds over a floor's ~90 rooms --
  // the whole point is to spend that instead of a full XMLSerializer pass on every capture.
  const computeSnapshotMarkupKey = useCallback(() => {
    const fi = currentFloorRef.current;
    let h = 0x811c9dc5 >>> 0;
    const mix = (n: number) => { h = Math.imul(h ^ (n >>> 0), 0x01000193) >>> 0; };
    const mixStr = (s: string | null | undefined) => {
      if (!s) { mix(1); return; }
      for (let i = 0; i < s.length; i++) mix(s.charCodeAt(i));
      mix(s.length);
    };
    for (const r of roomsRef.current) {
      if (r.floor_index !== fi) continue;
      mix(r.id);
      mix(Math.round(Number(r.x))); mix(Math.round(Number(r.y)));
      mix(Math.round(Number(r.w))); mix(Math.round(Number(r.h)));
      mixStr(r.name); mixStr(r.room_number); mixStr(r.type); mixStr(r.color ?? null);
      if (r.points && r.points.length) {
        mix(r.points.length);
        for (const p of r.points) { mix(Math.round(p.x)); mix(Math.round(p.y)); }
      } else { mix(0); }
      mix(r.doors?.length ?? 0);
      if (r.doors) for (const d of r.doors) { mixStr(d.wall); mix(Math.round((d.pos ?? 0) * 1000)); }
      mix(r.fire_wall_edges?.length ?? 0);
      if (r.fire_wall_edges) for (const e of r.fire_wall_edges) mixStr(e);
    }
    return [
      fi,
      h,
      backgroundModeRef.current,
      svgMarkupRef.current ? svgMarkupRef.current.length : 0,
      Math.round(svgScaleInchesPerUnitRef.current * 1000),
      showGridRef.current ? 1 : 0,
      bgVisibleRef.current ? 1 : 0,
      traceLoadedRef.current ? 1 : 0,
      Math.round(traceScaleRef.current),
      Math.round(traceOffsetXRef.current),
      Math.round(traceOffsetYRef.current),
      Math.round(traceOpacityRef.current * 100),
      Math.round(traceRotationRef.current),
    ].join('|');
  }, []);

  // Rasterizes the current live SVG onto the (hidden) overlay canvas -- no toDataURL/PNG round-trip,
  // a canvas can be CSS-transformed as-is. Rendered at a REDUCED internal resolution (RES_SCALE)
  // since this is only ever shown mid-gesture as a soft placeholder, never as the settled result
  // (the settle redraw is always the real, full-detail live SVG). Guarded to only ever run when
  // nothing is actively gesturing -- called exclusively from scheduleIdleCapture, never reactively
  // from inside a gesture (that race caused the earlier reverted version's runaway-capture bug).
  //
  // Perf (item 2): instead of XMLSerializer-serializing the ENTIRE live <svg> (~90 rooms + the
  // multi-thousand-node skeleton) on every capture, panGroupRef's static inner markup is
  // serialized ONCE and cached (snapshotMarkupRef), keyed by computeSnapshotMarkupKey(). A capture
  // whose key still matches -- i.e. only pan/zoom/fit changed, which is the overwhelming majority
  // of captures -- reuses that string and only rebuilds the tiny <svg><g transform> wrapper.
  //
  // Fix (item 1): the wrapper is built with an EXPLICIT pixel width/height + viewBox. The old code
  // serialized the live <svg width="100%" height="100%"> verbatim; with no natural size, a
  // data:-URI Image rasterized via drawImage(img,0,0,cw,ch) resolves "100%" to the 0.6-scale
  // backing-store width, so content laid out in absolute px was then CSS-upscaled by 1/RES_SCALE
  // (~1.67x) when the canvas is shown at its full W x H CSS size -- every cached frame ~1.67x too
  // large and offset. Prior rounds only made the cache rare enough (never on back-to-back gestures
  // with no idle gap) to mask it; a ~1s pause lets a fresh capture land and the next gesture blits
  // the over-scaled bitmap = the "big then snaps small" flash. An explicit W makes rasterization a
  // clean W-space -> 0.6-backing downscale that the canvas's W x H CSS size then restores 1:1.
  const captureIdleSnapshot = useCallback(() => {
    const svgEl = canvasRef.current;
    const cv = zoomSnapshotCanvasRef.current;
    const pg = panGroupRef.current;
    if (!svgEl || !cv || !pg || zoomSnapshotCapturingRef.current) return;
    if (panRef.current || wheelIdleTimerRef.current) return; // not actually idle -- bail
    // Round 19: canvasSizeRef is still the 800x600 default until the ResizeObserver has measured
    // the container -- capturing before that bakes the wrong W/H into the bitmap. The RO's
    // ready path re-calls scheduleIdleCapture, so bailing here just defers, it doesn't drop.
    if (!canvasReadyRef.current) return;
    // Round 10 item 2: the raster fast-path serializes panGroupRef's inner markup only -- it does
    // NOT capture the outer rotation <g>. While the view is rotated, a cached bitmap would be
    // un-rotated and blit wrong mid-gesture. Skip the cache entirely when rotated; handleWheel
    // already falls back to the live-vector bypass (correct, just not cache-accelerated) when
    // there's no snapshot. A fresh capture re-primes automatically once rotation returns to 0.
    if (rotRef.current !== 0) { zoomSnapshotStateRef.current = null; return; }
    zoomSnapshotCapturingRef.current = true;
    const captureOx = oxRef.current, captureOy = oyRef.current, captureScale = scaleRef.current;
    const { w: W, h: H } = canvasSizeRef.current;
    const RES_SCALE = 0.6;
    try {
      const key = computeSnapshotMarkupKey();
      if (key !== snapshotMarkupKeyRef.current || snapshotMarkupRef.current == null) {
        // Geometry / labels / skeleton / trace actually changed (or first capture) -- pay the
        // one serialization. innerHTML of the <g> is its children only, in world coords; the
        // pan/zoom transform lives on the <g> itself and is re-applied by the wrapper below.
        snapshotMarkupRef.current = pg.innerHTML;
        snapshotMarkupKeyRef.current = key;
      }
      // The viewport-fixed background rect is a SIBLING of panGroup in the live <svg>, not part of
      // its inner markup -- re-add it here (outside the transform <g>) so the snapshot's ground
      // matches the live layer's exactly. Always transparent now (item 2 fix, see the live rect's
      // own comment below) -- both modes let the same container navy show through, so there's no
      // opaque-vs-transparent mismatch left to flash between during a gesture.
      const bgFill = 'transparent';
      const svgStr =
        `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">` +
        `<rect x="0" y="0" width="${W}" height="${H}" fill="${bgFill}"/>` +
        `<g transform="translate(${captureOx} ${captureOy}) scale(${captureScale})">` +
        `${snapshotMarkupRef.current}</g></svg>`;
      const srcImg = new Image();
      srcImg.onload = () => {
        try {
          // If the view moved (pan / wheel / toolbar zoom / fit) during the decode, the baseline
          // recorded at kickoff no longer describes reality -- committing it would blit this bitmap
          // at the wrong relative scale on the next gesture (item 1's second gap). Discard; a fresh
          // scheduleIdleCapture will re-prime once the view is genuinely idle again.
          if (
            oxRef.current !== captureOx || oyRef.current !== captureOy || scaleRef.current !== captureScale ||
            panRef.current || wheelIdleTimerRef.current
          ) {
            zoomSnapshotStateRef.current = null;
            zoomSnapshotCapturingRef.current = false;
            return;
          }
          const cw = Math.round(W * RES_SCALE), ch = Math.round(H * RES_SCALE);
          cv.width = cw;
          cv.height = ch;
          const ctx = cv.getContext('2d')!;
          ctx.clearRect(0, 0, cw, ch);
          ctx.drawImage(srcImg, 0, 0, cw, ch);
          cv.style.width = `${W}px`;
          cv.style.height = `${H}px`;
          zoomSnapshotStateRef.current = { ox: captureOx, oy: captureOy, scale: captureScale };
        } catch {
          zoomSnapshotStateRef.current = null; // tainted canvas (cross-origin trace image) — bail
        }
        zoomSnapshotCapturingRef.current = false;
      };
      srcImg.onerror = () => { zoomSnapshotCapturingRef.current = false; };
      srcImg.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgStr)}`;
    } catch {
      zoomSnapshotCapturingRef.current = false;
    }
  }, [computeSnapshotMarkupKey]);

  // Primes the cache for whenever the NEXT zoom gesture starts. Called after pan-end and after
  // zoom-settle, never from inside an active gesture. Cancels/reschedules on repeated calls so a
  // burst of quick settle events (e.g. rapid clicks) only ever queues one pending capture.
  const scheduleIdleCapture = useCallback(() => {
    if (idleCaptureHandleRef.current != null) { cancelIdle(idleCaptureHandleRef.current); idleCaptureHandleRef.current = null; }
    idleCaptureHandleRef.current = scheduleIdle(() => {
      idleCaptureHandleRef.current = null;
      if (panRef.current || wheelIdleTimerRef.current) return; // a new gesture started meanwhile
      captureIdleSnapshot();
    }, 600);
  }, [captureIdleSnapshot]);

  // ── renderMapSvg — returns the map's SVG content. Reads refs at render time, exactly where
  // draw() used to read them at paint time. Rooms, doors, fire walls, markers, and the skeleton
  // background all live inside ONE <g transform> -- one shared coordinate space, not a canvas
  // plus a separately CSS-transformed DOM layer kept in sync by hand. ──
  function renderMapSvg() {
    const { w: W, h: H } = canvasSizeRef.current;
    const scale = scaleRef.current;
    const ox = oxRef.current;
    const oy = oyRef.current;

    const rs = roomsRef.current.filter(r => r.floor_index === currentFloorRef.current);
    const selId = selectedIdRef.current;
    const dirty = dirtyIdsRef.current;
    const multiSel = selectedIdsRef.current;
    const floorHasPolygons = rs.some(rm => rm.points && rm.points.length > 2);
    const hoveredId = hoveredRoomIdRef.current;
    const hoveredMid = hoveredMidpointRef.current;
    const sel = rs.find(r => r.id === selId);

    // Round 10 item 2: map-view rotation (deg). Applied to the outer wrapper <g> below.
    const rot = rotRef.current;
    // Counter-rotation passed to room labels + marker glyphs so they stay upright on a rotated
    // canvas (geometry -- walls/doors/windows/grid -- rotates with the view; text does not).
    const labelCounterRot = rot;
    // Round 10 item 3: background reference-image rotation (deg), pivoted on the room-bbox centre.
    const traceRotG = traceRotationRef.current;
    let _bx0 = Infinity, _by0 = Infinity, _bx1 = -Infinity, _by1 = -Infinity;
    for (const r of rs) {
      if (r.points && r.points.length > 2) {
        for (const p of r.points) { if (p.x < _bx0) _bx0 = p.x; if (p.x > _bx1) _bx1 = p.x; if (p.y < _by0) _by0 = p.y; if (p.y > _by1) _by1 = p.y; }
      } else {
        const rx = Number(r.x), ry = Number(r.y), rw = Number(r.w), rh = Number(r.h);
        if (rx < _bx0) _bx0 = rx; if (rx + rw > _bx1) _bx1 = rx + rw; if (ry < _by0) _by0 = ry; if (ry + rh > _by1) _by1 = ry + rh;
      }
    }
    const bgPivotOk = isFinite(_bx0) && _bx1 > _bx0 && _by1 > _by0;
    const bgCx = bgPivotOk ? (_bx0 + _bx1) / 2 : 0;
    const bgCy = bgPivotOk ? (_by0 + _by1) / 2 : 0;

    // Trace (raster) image -- fixed world-space anchor (item 2 fix). Previously this recomputed
    // the image's world-space x/y/width/height from the CURRENT pan/zoom (ox/oy/scale) every
    // render -- worldViewW = W/scale deliberately shrinks the image's world footprint as scale
    // increases, exactly canceling the outer <g>'s zoom, so the image always filled the viewport
    // and never visibly resized. Room polygons, using fixed world coordinates, scaled normally --
    // so on any zoom the two drifted apart. Anchoring to the floor's own room bounding box instead
    // (same bbox fitToView() uses) gives the image a FIXED world-space footprint, same as rooms
    // and the svg skeleton, so it now scales/pans through the shared <g transform> like everything
    // else. traceOffsetX/Y keep their existing meaning (world-unit nudge from center) -- only what
    // they're measured FROM changed, from a floating viewport-relative center to a fixed one.
    let traceImg: React.ReactNode = null;
    if (backgroundModeRef.current === 'raster' && bgVisibleRef.current && traceImageUrl) {
      let bMinX = Infinity, bMinY = Infinity, bMaxX = -Infinity, bMaxY = -Infinity;
      for (const r of rs) {
        if (r.points && r.points.length > 2) {
          for (const p of r.points) {
            if (p.x < bMinX) bMinX = p.x; if (p.x > bMaxX) bMaxX = p.x;
            if (p.y < bMinY) bMinY = p.y; if (p.y > bMaxY) bMaxY = p.y;
          }
        } else {
          const rx = Number(r.x), ry = Number(r.y), rw = Number(r.w), rh = Number(r.h);
          if (rx < bMinX) bMinX = rx; if (rx + rw > bMaxX) bMaxX = rx + rw;
          if (ry < bMinY) bMinY = ry; if (ry + rh > bMaxY) bMaxY = ry + rh;
        }
      }
      if (isFinite(bMinX) && isFinite(bMinY) && bMaxX > bMinX && bMaxY > bMinY) {
        const bboxW = bMaxX - bMinX, bboxH = bMaxY - bMinY;
        const bboxCx = (bMinX + bMaxX) / 2, bboxCy = (bMinY + bMaxY) / 2;
        const naturalW = traceImgRef.current?.naturalWidth || 1;
        const naturalH = traceImgRef.current?.naturalHeight || 1;
        const imgAspect = naturalW / naturalH;
        const bboxAspect = bboxW / bboxH;
        let baseW: number, baseH: number;
        if (imgAspect > bboxAspect) { baseW = bboxW; baseH = baseW / imgAspect; }
        else { baseH = bboxH; baseW = baseH * imgAspect; }
        const userScale = traceScaleRef.current / 100;
        const finalW = baseW * userScale, finalH = baseH * userScale;
        const drawX = bboxCx - finalW / 2 + traceOffsetXRef.current;
        const drawY = bboxCy - finalH / 2 + traceOffsetYRef.current;
        traceImg = (
          <g transform={traceRotG ? `rotate(${traceRotG} ${bgCx} ${bgCy})` : undefined}>
            <image href={capCloudinaryWidth(traceImageUrl, 2000)} x={drawX} y={drawY} width={finalW} height={finalH} opacity={traceOpacityRef.current} preserveAspectRatio="none" />
          </g>
        );
      }
    }

    return (
      <>
        {/* item 2 fix: always transparent, matching skeleton mode exactly, regardless of
            backgroundModeRef -- both modes let the container div's own navy (rgba(26,38,56,0.97))
            show through as the ground outside the image/skeleton bounds. This used to sample the
            trace photo's own edge-pixel color instead (a previous round's fix intended to make the
            ground blend with the photo's paper tone), but a real photographed document's outermost
            edge pixels are typically whatever surrounds the paper in the shot -- a desk, a shadow,
            a camera vignette -- not the paper itself, so that sampling produced a near-black ground
            for exactly the kind of photo this feature exists for, not a lighter blended tone. */}
        <rect x={0} y={0} width={W} height={H} fill="transparent" />
        {/* Round 10 item 2: outer wrapper carries ONLY the view rotation, about the canvas centre.
            panGroupRef keeps translate+scale so every imperative pan/zoom transform write is
            unchanged. getWorldPos / handleWheel un-rotate the pointer (unrotateCanvasPoint) about
            the same centre so hit-testing stays in the untransformed world space rooms/markers are
            stored in -- the extinguisher point-in-polygon check is unaffected. */}
        {/* Round 23 item 2: transformBox 'view-box' -- WITHOUT this, older engines default the
            SVG transform-origin reference to the element's own bounding box (fill-box), so
            `${W/2}px` is measured from the room-geometry bbox corner, not the SVG viewport --
            the rotation then pivots hundreds of px away from the true canvas centre while
            getWorldPos un-rotates about the viewport centre, and rotated placement/hit-test land
            far off. view-box makes `${W/2}px ${H/2}px` genuinely the canvas centre. */}
        <g ref={rotGroupRef} style={{ transform: rot ? `rotate(${rot}deg)` : undefined, transformOrigin: `${W / 2}px ${H / 2}px`, transformBox: 'view-box', willChange: 'transform' }}>
        <g ref={panGroupRef} style={{ transform: `translate(${ox}px, ${oy}px) scale(${scale})`, transformOrigin: '0 0', transformBox: 'view-box', willChange: 'transform' }}>
          {showGridRef.current && <DotGridSvg scale={scale} />}
          {backgroundModeRef.current === 'svg' && svgMarkupRef.current && bgVisibleRef.current && (
            <>
              {/* Recolors the skeleton's raw light-theme export colors for our dark background --
                  ported verbatim from mockups/room-location-editor-mockup.html's .floorplan-svg
                  rules (its source has the full, authoritative values; not approximated). Site/
                  grounds clutter (driveway, parking, canopy) and unrelated annotation colors
                  (fire/egress/hazard callouts we render ourselves as separate layers) are dropped
                  entirely, matching the mockup. One deliberate deviation: the mockup also hides
                  `text:not([fill])` (the room-number labels baked into the blueprint) because it
                  renders its own separate JS room-number labels instead -- omitted here since an
                  earlier round explicitly fixed those labels being invisible, and hiding them again
                  would undo that fix. Flagging this rather than silently diverging. */}
              <style>{`
                .fm-skeleton { background: transparent; }
                .fm-skeleton [fill="#ccffcc"] { fill: #101a2c; }
                .fm-skeleton [stroke="#ccffcc"] { stroke: #101a2c; }
                .fm-skeleton [fill="#bfbfbf"], .fm-skeleton [stroke="#bfbfbf"],
                .fm-skeleton [fill="#a5a5a5"], .fm-skeleton [stroke="#a5a5a5"],
                .fm-skeleton [fill="#d8d8d8"], .fm-skeleton [stroke="#d8d8d8"],
                .fm-skeleton [fill="#f2f2f2"],
                .fm-skeleton [fill="#fbd7bb"],
                .fm-skeleton [fill="#ffcc99"],
                .fm-skeleton [fill="#ffcc00"], .fm-skeleton [stroke="#ffcc00"]
                  { display: none; }
                .fm-skeleton [fill="#ffff99"] { fill: rgba(255,255,255,0.045); }
                .fm-skeleton [stroke="#ffff99"] { stroke: rgba(215,228,245,0.35); }
                .fm-skeleton [fill="#7f7f7f"] { fill: rgba(255,255,255,0.02); }
                .fm-skeleton [fill="#cc99ff"] { fill: rgba(139,92,246,0.35); }
                .fm-skeleton [fill="#0070c0"] { fill: #3b82f6; }
                .fm-skeleton [stroke="#000000"] { stroke: rgba(215,228,245,0.55); }
                .fm-skeleton text { fill: rgba(215,228,245,0.85); }
                .fm-skeleton [fill="#0000ff"] { fill: rgba(147,197,253,0.7); }
                .fm-skeleton [stroke="#0000ff"] { stroke: rgba(147,197,253,0.4); }
                .fm-skeleton [fill="#ff0000"], .fm-skeleton [stroke="#ff0000"],
                .fm-skeleton [fill="#c00000"], .fm-skeleton [stroke="#c00000"],
                .fm-skeleton [fill="#00ff00"],
                .fm-skeleton [fill="#ff99cc"],
                .fm-skeleton [fill="#ffc000"], .fm-skeleton [stroke="#ffc000"],
                .fm-skeleton [fill="#ccffff"],
                .fm-skeleton [stroke="#00b0f0"], .fm-skeleton [fill="#00b0f0"],
                .fm-skeleton [stroke="#333399"]
                  { display: none; }
              `}</style>
              <g className="fm-skeleton" transform={`${traceRotG ? `rotate(${traceRotG} ${bgCx} ${bgCy}) ` : ''}scale(${svgScaleInchesPerUnitRef.current})`} dangerouslySetInnerHTML={{ __html: svgMarkupRef.current }} />
            </>
          )}
          {traceImg}

          {rs.map(r => (
            <RoomShapeSvg
              key={r.id}
              room={r}
              selected={r.id === selId}
              dirty={dirty.has(r.id)}
              multiSelected={multiSel.has(r.id)}
              hovered={r.id === hoveredId}
              floorHasPolygons={floorHasPolygons}
              hoveredMidpointIdx={(hoveredMid && hoveredMid.roomId === r.id) ? hoveredMid.edgeIdx : null}
              scale={scale}
              labelCounterRot={labelCounterRot}
            />
          ))}
          {rs.map(r => r.fire_wall_edges?.length ? <FireWallsSvg key={`fw-${r.id}`} room={r} /> : null)}
          {rs.map(r => r.doors?.length ? (
            <DoorsSvg
              key={`d-${r.id}`} room={r} scale={scale}
              highlightIndex={selectedDoorRef.current?.roomId === r.id ? selectedDoorRef.current.doorIndex : null}
            />
          ) : null)}

          {/* Rect handles only for non-polygon selected room (polygon draws its own vertex handles) */}
          {sel && !(sel.points && sel.points.length > 2) && <HandlesSvg room={sel} />}

          {/* Glow pulse for room selector navigation */}
          {glowRef.current && (() => {
            const gd = glowRef.current!;
            const gr = rs.find(r => r.id === gd.roomId);
            if (!gr || (gr.points && gr.points.length > 2)) return null;
            return (
              <rect
                x={Number(gr.x)} y={Number(gr.y)} width={Number(gr.w)} height={Number(gr.h)}
                fill="none" stroke={`rgba(59,130,246,${gd.alpha})`} strokeWidth={3 / scale}
                style={{ filter: `drop-shadow(0 0 ${24 / scale}px rgba(59,130,246,${gd.alpha}))` }}
              />
            );
          })()}

          {/* Snap-to-rooms highlight */}
          {snapHighlightRef.current && (
            <line
              x1={snapHighlightRef.current.x1} y1={snapHighlightRef.current.y1}
              x2={snapHighlightRef.current.x2} y2={snapHighlightRef.current.y2}
              stroke="#60a5fa" strokeWidth={1.5 / scale} strokeDasharray={`${5 / scale} ${3 / scale}`}
            />
          )}

          {/* Proportional scale indicator */}
          {resizeRef.current?.proportional && (() => {
            const propResize = resizeRef.current!;
            const pr = rs.find(r => r.id === propResize.roomId);
            if (!pr) return null;
            const pct = Math.round((Number(pr.w) / propResize.origW) * 100);
            const pcx = Number(pr.x) + Number(pr.w) / 2, pcy = Number(pr.y);
            const fs = 11 / scale;
            return (
              <g>
                <rect x={pcx - 40 / scale} y={pcy - 28 / scale} width={80 / scale} height={20 / scale} fill="rgba(15,23,42,0.9)" />
                <text x={pcx} y={pcy - 18 / scale} fill="#93c5fd" fontSize={fs} fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
                  Scale: {pct}%
                </text>
              </g>
            );
          })()}

          {/* Draw-new-room preview */}
          {drawingRef.current && (
            <rect
              x={drawingRef.current.x} y={drawingRef.current.y} width={drawingRef.current.w} height={drawingRef.current.h}
              fill="rgba(59,130,246,0.12)" stroke="#3b82f6" strokeWidth={1.5 / scale}
            />
          )}

          {/* Door/window placement preview */}
          <DoorWindowPreviewSvg
            rooms={rs} scale={scale}
            preview={previewRef.current} activeTool={toolRef.current}
            windowWidth={windowWidthRef.current} doorSwing={doorSwingRef.current}
          />

          {/* Add Bathroom placement preview (item 2) -- not cursor-tracked like door/window since
              Add Bathroom is a single-click auto-add, not an aimed placement tool; ghost shows
              while hovering the button itself, using the exact same geometry handleAddBathroom
              actually creates from (east of the selected room, 40x40, door on its west wall). */}
          {bathroomPreviewActiveRef.current && isAdmin && sel && (() => {
            // effectiveRect, not sel.x/sel.w directly -- same fix as handleAddBathroom itself
            // (item 1), so the ghost lands exactly where the real bathroom will.
            const selRect = effectiveRect(sel);
            const bx = selRect.x + selRect.w;
            const by = selRect.y;
            return (
              <g opacity={0.6} stroke="#60a5fa" strokeWidth={1.5 / scale} strokeDasharray={`${4 / scale} ${2 / scale}`}>
                <rect x={bx} y={by} width={40} height={40} fill="rgba(96,165,250,0.14)" />
                <line x1={bx} y1={by + 16} x2={bx} y2={by + 24} stroke="#93c5fd" strokeDasharray="none" strokeWidth={3 / scale} />
              </g>
            );
          })()}

          {/* Marker placement preview */}
          {toolRef.current === 'marker' && markerPreviewPosRef.current && (
            <MarkerShapeSvg cx={markerPreviewPosRef.current.x} cy={markerPreviewPosRef.current.y} markerType={markerPlacementTypeRef.current} alpha={0.5} selected={false} hasLink={false} label={null} scale={scale} counterRot={labelCounterRot} />
          )}

          {/* Markers */}
          {markersRef.current.filter(m => m.floor_index === currentFloorRef.current).map(m => (
            <MarkerShapeSvg
              key={m.id} cx={Number(m.x)} cy={Number(m.y)} markerType={m.marker_type} alpha={1}
              selected={m.id === selectedMarkerIdRef.current} hasLink={!!m.linked_extinguisher_id} label={m.label} scale={scale}
              counterRot={labelCounterRot}
            />
          ))}
        </g>
        </g>
      </>
    );
  }

  // Item 3: persists the current pan/zoom/floor so it survives a full unmount (navigating away from
  // this page and back) or the facility-map block's own remount (leaving and returning to the
  // Facility Map sub-tab -- {mapView === 'facility' && (...)} unmounts/remounts that whole subtree,
  // same as the wheel-listener effect's own fix this round covers). Declared before fitToView since
  // it's referenced in fitToView's own deps array, which is evaluated at render/define time, not
  // call time -- referencing something declared later here throws a real TDZ error, not just a
  // stale-closure risk (the exact class of bug the showToast reorder fixed a few rounds back).
  const saveViewState = useCallback(() => {
    saveMapView(user?.facilityId, { scale: scaleRef.current, ox: oxRef.current, oy: oyRef.current, floor: currentFloorRef.current, rot: rotRef.current });
  }, [user?.facilityId]);

  // Round 12 item 3: capture the current pan/zoom/rotation and re-assert it across the next two
  // frames, holding viewLockRef so scrollToRoom (the one select-triggered recenter path) no-ops
  // in the meantime. Called at the top of delete/add so a stale effect keyed on a removed/added
  // id can't leave the view somewhere the user didn't put it.
  const lockView = useCallback(() => {
    const v = { ox: oxRef.current, oy: oyRef.current, scale: scaleRef.current, rot: rotRef.current };
    viewLockRef.current = true;
    const restore = () => { oxRef.current = v.ox; oyRef.current = v.oy; scaleRef.current = v.scale; rotRef.current = v.rot; };
    restore();
    requestAnimationFrame(() => {
      restore();
      requestAnimationFrame(() => { restore(); viewLockRef.current = false; draw(); });
    });
  }, [draw]);

  // ── fitToView — writes refs, sets displayZoom, calls draw ──
  // Round 13 item 1: resetRotation defaults to FALSE. View rotation is a per-facility persisted
  // value; framing a floor's content (floor switch, initial mount, resize) must not reorient it.
  // Only an explicit "Fit to screen" action (toolbar button / 'h' key) passes true to also snap
  // the view upright.
  const fitToView = useCallback((resetRotation = false) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const W = rect.width;
    const H = rect.height;
    if (W < 50 || H < 50) return;
    const floor = roomsRef.current.filter(r => r.floor_index === currentFloorRef.current);
    if (floor.length === 0) return;
    // Bounding box must include polygon `points`, not just rect x/y/w/h -- a polygon room's w/h
    // are always 0 (see the "points + w:0,h:0" convention used throughout this file), so ignoring
    // points here would make every polygon room contribute only a degenerate zero-size point at
    // its old rect anchor, leaving its real (much larger) footprint entirely outside the computed
    // fit. Confirmed live: with 89 of a floor's rooms now polygons, the old x/y/w/h-only version
    // of this function fit the view almost entirely to the handful of still-rectangle rooms,
    // making every converted room render far outside the visible viewport.
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const r of floor) {
      if (r.points && r.points.length > 2) {
        for (const p of r.points) {
          if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
          if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
        }
      } else {
        const rx = Number(r.x), ry = Number(r.y), rw = Number(r.w), rh = Number(r.h);
        if (rx < minX) minX = rx; if (rx + rw > maxX) maxX = rx + rw;
        if (ry < minY) minY = ry; if (ry + rh > maxY) maxY = ry + rh;
      }
    }
    const rangeX = maxX - minX;
    const rangeY = maxY - minY;
    if (rangeX <= 0 || rangeY <= 0) return;
    const rawScale = Math.min((W - 80) / rangeX, (H - 80) / rangeY);
    const clampedScale = Math.min(Math.max(rawScale, 0.3), 3.0);
    if (!isFinite(clampedScale) || clampedScale <= 0) return;
    scaleRef.current = clampedScale;
    oxRef.current = (W - rangeX * clampedScale) / 2 - minX * clampedScale;
    oyRef.current = (H - rangeY * clampedScale) / 2 - minY * clampedScale;
    // Round 10 item 2 / Round 13 item 1: only an explicit fit-to-screen action snaps upright;
    // a floor switch or automatic re-fit leaves the persisted per-facility rotation alone.
    if (resetRotation) {
      rotRef.current = 0;
      setDisplayRot(0);
    }
    setDisplayZoom(Math.round(clampedScale * 100));
    // Same reason as zoomByCenter: this changes scale/position outside handleWheel, so an existing
    // idle-capture cache (from an earlier pan/wheel-settle) would go stale and get blit at the wrong
    // relative scale on the next wheel-zoom. Invalidate + reprime.
    zoomSnapshotStateRef.current = null;
    scheduleIdleCapture();
    animateViewJump(resetRotation ? 'both' : 'pan'); // Round 15 polish: ease the jump, not a gesture
    draw();
    saveViewState();
  }, [draw, scheduleIdleCapture, saveViewState, animateViewJump]);

  // ── Helpers shared by handlers ──
  // Item 6: called once at the START of every discrete action (drag-start, resize-start, vertex-
  // drag-start, door/window placement, bathroom add, delete, cut/join, etc. -- ~15 call sites, all
  // in mousedown-time or one-shot handlers, confirmed NONE inside handleMouseMove) to snapshot the
  // pre-action state for undo. This was already a real, already-50-deep stack, not a single slot --
  // the audited concern was whether it records every intermediate drag TICK rather than one entry
  // per completed action, and it doesn't: dragging a room only ever calls this once, at mousedown,
  // before the drag begins, never again during the mousemove ticks that follow. What was actually
  // missing was redo -- a brand new action correctly invalidates any pending redo stack (you can't
  // redo "forward" past a new divergent change), so clear it here too.
  const pushHistory = useCallback(() => {
    historyRef.current = [...historyRef.current.slice(-49), roomsRef.current];
    setHistoryLength(historyRef.current.length);
    redoRef.current = [];
    setRedoLength(0);
  }, []);

  const markDirty = useCallback((id: number) => {
    dirtyIdsRef.current.add(id);
    setHasDirty(true);
  }, []);

  const commitRoom = useCallback((id: number) => {
    setRooms(roomsRef.current);
    markDirty(id);
    // Item 2(a) fix: keep the imperative selectedIdRef in lockstep with the setSelectedRoom state
    // update below. Every other commitRoom call site already operates on whatever room
    // selectedIdRef.current already points at (drag/resize/vertex-edit all require the room to be
    // selected first), so this line is a no-op there -- but door/window placement calls
    // commitRoom(hit.id) for whichever room the click landed on, which is NOT necessarily the room
    // selectedIdRef.current was already pointing at (you don't have to select a room first to place
    // a door/window on it with the tool active). setSelectedRoom(updated) below made the PANEL show
    // the right room immediately, but selectedIdRef.current stayed stale -- so
    // handleUpdateDoorWidth/handleDeleteDoor (which read selectedIdRef.current, not the React state)
    // were reading the wrong room, or no room at all (null -> silent early return), until a real
    // select-tool click on the canvas finally set the ref too. That's why the property panel's own
    // width-preset buttons looked inert right after creating a door/window: right controls, wrong
    // (or no) id underneath them.
    selectedIdRef.current = id;
    const updated = roomsRef.current.find(r => r.id === id);
    if (updated) setSelectedRoom(updated);
  }, [setRooms, markDirty]);

  // ── Mount: fetch ──
  useEffect(() => { refetch(); }, [refetch]);

  // ── Floors derived from rooms ──
  const floors = useMemo(() => {
    const map = new Map<number, string>();
    rooms.forEach(r => map.set(r.floor_index, r.floor_name));
    if (map.size === 0) map.set(0, 'First Floor');
    return Array.from(map.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([idx, name]) => ({ floor_index: idx, floor_name: name }));
  }, [rooms]);

  // Auto-select a valid floor if current one disappears (no draw here)
  useEffect(() => {
    if (!floors.find(f => f.floor_index === currentFloor)) {
      const ni = floors[0]?.floor_index ?? 0;
      setCurrentFloor(ni);
      currentFloorRef.current = ni;
    }
  }, [floors, currentFloor]);

  // ── THE single draw effect — depends ONLY on [rooms, currentFloor] ──
  useEffect(() => {
    currentFloorRef.current = currentFloor;
    roomsRef.current = rooms;
    if (rooms.length > 0 && !initialFitDoneRef.current) {
      initialFitDoneRef.current = true;
      lastSavedRef.current = rooms;
      // Item 3: only fit-to-content on a genuinely first-ever visit for this facility (no
      // persisted view found) -- scaleRef/oxRef/oyRef/currentFloor were already seeded from
      // sessionStorage at their own declarations if a persisted view existed, so fitting here
      // would overwrite that restored view with a fresh fit-to-bounds, defeating the point.
      if (!loadPersistedMapView(user?.facilityId)) {
        window.setTimeout(() => fitToView(), 400);
      }
    }
    draw();
    // Round 19: PROACTIVELY (re)prime the raster snapshot. This effect fires on first mount (once
    // rooms load), on every content edit (add / delete / reshape / label -- setRooms swaps the
    // array), and on floor switch -- exactly the moments the cached bitmap could be stale or
    // missing. Without this, the snapshot was only ever built as a side effect of a *previous*
    // view gesture's settle, so the FIRST gesture after a fresh load (or after any edit) fell back
    // to the slow live-SVG <g> transform for its opening frames -- the "smooth, then jumpy again"
    // symptom. scheduleIdleCapture debounces (cancel + reschedule), runs on idle, no-ops
    // mid-gesture, and captureIdleSnapshot only re-serialises when computeSnapshotMarkupKey shows
    // the geometry actually changed, so this stays cheap.
    scheduleIdleCapture();
  }, [rooms, currentFloor]); // eslint-disable-line react-hooks/exhaustive-deps

  // Item 3: this used to force a fresh fitToView() every time the Facility Map sub-tab became
  // active again, which is exactly what was discarding the user's pan/zoom on Rooms-tab-and-back
  // navigation -- {mapView === 'facility' && (...)} unmounts/remounts the whole facility-map
  // block including the <svg>, but scaleRef/oxRef/oyRef live on THIS component (which stays
  // mounted the whole time a tab switch happens) and are read declaratively into the new <g>'s
  // transform on first paint (see renderMapSvg's `<g ref={panGroupRef} style={{ transform:
  // ... }}>`), so the restored view is already correct with no fit needed -- draw() alone is
  // enough to make the freshly-remounted <svg> actually paint using those still-current refs.
  useEffect(() => {
    if (mapView === 'facility') {
      window.setTimeout(() => { draw(); }, 50);
    }
  }, [mapView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load breaker panels when Breakers tab becomes active
  useEffect(() => {
    if (mapView === 'breakers') loadBpPanels();
  }, [mapView]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load breakers when a panel is selected
  useEffect(() => {
    if (selectedPanelId != null) loadPanelBreakers(selectedPanelId);
    else setPanelBreakers([]);
  }, [selectedPanelId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load area options for Create Panel modal AND breaker editor
  useEffect(() => {
    if (!showCreatePanel && editingBreaker === null) return;
    const facilityId = user?.facilityId;
    if (!facilityId) return;
    api.get(`/dropdowns?dropdown_type=area&facility_id=${facilityId}`)
      .then(res => setNewPanelAreaOptions(((res as any).options ?? []) as {id:number;label:string}[]))
      .catch(() => {});
  }, [showCreatePanel, editingBreaker]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (newPanelAreaId == null) { setNewPanelLocOptions([]); return; }
    const facilityId = user?.facilityId;
    if (!facilityId) return;
    api.get(`/dropdowns?dropdown_type=location&facility_id=${facilityId}&parent_id=${newPanelAreaId}`)
      .then(res => setNewPanelLocOptions(((res as any).options ?? []) as {id:number;label:string}[]))
      .catch(() => {});
  }, [newPanelAreaId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showEditPanel) return;
    const facilityId = user?.facilityId;
    if (!facilityId) return;
    api.get(`/dropdowns?dropdown_type=area&facility_id=${facilityId}`)
      .then(res => setEditPanelAreaOptions(((res as any).options ?? []) as {id:number;label:string}[]))
      .catch(() => {});
  }, [showEditPanel]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const areaId = editPanelData?.area_id;
    if (!areaId) { setEditPanelLocOptions([]); return; }
    const facilityId = user?.facilityId;
    if (!facilityId) return;
    api.get(`/dropdowns?dropdown_type=location&facility_id=${facilityId}&parent_id=${areaId}`)
      .then(res => setEditPanelLocOptions(((res as any).options ?? []) as {id:number;label:string}[]))
      .catch(() => {});
  }, [editPanelData?.area_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep panel content fresh if the selected room object changes in rooms (no draw)
  useEffect(() => {
    if (selectedRoom) {
      const u = rooms.find(r => r.id === selectedRoom.id);
      if (u && u !== selectedRoom) setSelectedRoom(u);
    }
  }, [rooms]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── ResizeObserver — track container size, then draw. Never fits. ──
  // No DPR/pixel-backing-store handling needed for SVG (unlike a <canvas>, it scales natively) --
  // canvasSizeRef just needs the CSS size for the fitToView/zoom math.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const { width, height } = entry.contentRect;
      if (width < 50 || height < 50) return;
      const sizeChanged = Math.abs(canvasSizeRef.current.w - width) > 1 || Math.abs(canvasSizeRef.current.h - height) > 1;
      canvasSizeRef.current = { w: width, h: height };
      // 2nd-layer guard (Round 22 item 1): the canvas resized. If a drag/resize/vertex/marker/draw
      // gesture is mid-flight, its arm-time offsets are now against a stale viewport -- cancel it
      // so a resize (panel column, window resize, fullscreen toggle) can't turn into a stray move.
      if (sizeChanged && (dragRef.current || resizeRef.current || polyVertexDragRef.current || markerDragRef.current || drawingRef.current)) {
        dragRef.current = null;
        if (resizeRef.current?.proportional) setProportionalScaleActive(false);
        resizeRef.current = null;
        polyVertexDragRef.current = null;
        markerDragRef.current = null;
        drawingRef.current = null;
        snapHighlightRef.current = null;
        gestureViewSigRef.current = '';
      }
      const wasReady = canvasReadyRef.current;
      canvasReadyRef.current = true;
      if (roomsRef.current.length > 0) {
        draw();
        if (!wasReady) {
          setTimeout(() => fitToView(), 200);
          // Round 19: the container is measured now -- prime the raster snapshot. The single-draw
          // effect's own scheduleIdleCapture may have already fired and bailed (canvasReadyRef was
          // still false); this is the retrigger.
          scheduleIdleCapture();
        }
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [draw, fitToView, scheduleIdleCapture]);

  // ── Undo / Redo (item 6) ──
  const handleUndo = useCallback(() => {
    if (!historyRef.current.length) return;
    const snapshot = historyRef.current[historyRef.current.length - 1];
    historyRef.current = historyRef.current.slice(0, -1);
    setHistoryLength(historyRef.current.length);
    // Push the state being LEFT (not the one being restored) onto redo, so Redo moves forward to
    // exactly where Undo just left from -- capped at 50 like the undo stack itself.
    redoRef.current = [...redoRef.current.slice(-49), roomsRef.current];
    setRedoLength(redoRef.current.length);
    roomsRef.current = snapshot;
    setRooms(snapshot);
    dirtyIdsRef.current = new Set(snapshot.map(r => r.id));
    setHasDirty(dirtyIdsRef.current.size > 0);
    draw();
  }, [setRooms, draw]);

  const handleRedo = useCallback(() => {
    if (!redoRef.current.length) return;
    const snapshot = redoRef.current[redoRef.current.length - 1];
    redoRef.current = redoRef.current.slice(0, -1);
    setRedoLength(redoRef.current.length);
    // Symmetric with handleUndo: push the state being left back onto the undo stack, so Undo can
    // step back through a Redo exactly the same way it steps back through any other action.
    historyRef.current = [...historyRef.current.slice(-49), roomsRef.current];
    setHistoryLength(historyRef.current.length);
    roomsRef.current = snapshot;
    setRooms(snapshot);
    dirtyIdsRef.current = new Set(snapshot.map(r => r.id));
    setHasDirty(dirtyIdsRef.current.size > 0);
    draw();
  }, [setRooms, draw]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
        return;
      }
      // Item 6: Redo -- both the standard Ctrl+Shift+Z and the Windows-convention Ctrl+Y.
      if ((e.ctrlKey || e.metaKey) && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
        e.preventDefault();
        handleRedo();
        return;
      }
      if ((e.key === 'r' || e.key === 'R') && !e.ctrlKey && !e.metaKey) {
        if (toolRef.current === 'door') {
          doorSwingRef.current = doorSwingRef.current === 'inward' ? 'outward' : 'inward';
          draw();
        }
        return;
      }
      if (e.key === 'Escape') {
        if (toolRef.current === 'marker') {
          toolRef.current = 'pan';
          setTool('pan');
          markerPreviewPosRef.current = null;
          draw();
        }
        if (selectedMarkerIdRef.current != null) {
          selectedMarkerIdRef.current = null;
          setSelectedMarker(null);
          setMarkerPanelPos(null);
          draw();
        }
        setCtxMenu(null);
        setSelectorOpen(false);
        setSelectorAreaSearch('');
        // Clear multi-selection
        if (selectedIdsRef.current.size > 0) {
          selectedIdsRef.current.clear();
          setMultiSelectCount(0);
          draw();
        }
        // Item 5: clear an individually-selected door/window too
        if (selectedDoorRef.current != null) {
          selectedDoorRef.current = null;
          doorDragRef.current = null;
          draw();
        }
      }

      // ── Tool shortcuts (when no input focused) ────────────────────────────
      const tag = (document.activeElement as HTMLElement | null)?.tagName?.toUpperCase();
      const inputFocused = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
      if (!inputFocused) {
        if ((e.ctrlKey || e.metaKey) && e.key === 's') {
          e.preventDefault();
          // Save dirty rooms
          const ids = Array.from(dirtyIdsRef.current);
          if (ids.length > 0) {
            const dirty = ids.map(id => roomsRef.current.find(r => r.id === id)).filter((r): r is FacilityRoom => !!r);
            setIsSaving(true);
            bulkUpdate(dirty).then(ok => {
              setIsSaving(false);
              if (ok) { dirtyIdsRef.current = new Set(); setHasDirty(false); lastSavedRef.current = roomsRef.current.slice(); }
            });
          }
          return;
        }
        if (e.key === 'h' || e.key === 'H') { fitToView(true); return; }
        if (e.key === 's' && !e.ctrlKey && !e.metaKey && canEdit) { toolRef.current = 'select'; setTool('select'); return; }
        if (e.key === 'd' && !e.ctrlKey && !e.metaKey && canEdit) { toolRef.current = 'draw'; setTool('draw'); return; }
        if (e.key === 'p' && !e.ctrlKey && !e.metaKey) { toolRef.current = 'pan'; setTool('pan'); return; }
        if (e.key === 'w' && !e.ctrlKey && !e.metaKey && canEdit) { toolRef.current = 'window'; setTool('window'); return; }
      }

      // ── Arrow keys — fine / coarse nudge ─────────────────────────────────
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        const arrowTag = (document.activeElement as HTMLElement | null)?.tagName?.toUpperCase();
        if (arrowTag === 'INPUT' || arrowTag === 'TEXTAREA' || arrowTag === 'SELECT') return;
        const step = e.shiftKey ? 10 : 1;

        // Screen-space unit vector for the pressed key. Round 14 item 4: un-rotate it into world
        // space (same transform as getWorldPos and the drag-pan delta) so the nudge always follows
        // what's on screen, not the unrotated storage frame. At the only rotations the view allows
        // (0 / +-90 / 180) this stays a clean cardinal vector.
        let sx = 0, sy = 0;
        if (e.key === 'ArrowLeft') sx = -1;
        else if (e.key === 'ArrowRight') sx = 1;
        else if (e.key === 'ArrowUp') sy = -1;
        else if (e.key === 'ArrowDown') sy = 1;
        const wv0 = unrotateCanvasPoint(sx, sy, 0, 0, rotRef.current);
        // Round to kill trig float-noise -- the view only rotates in 90-degree steps, so this is
        // always exactly one of -1 / 0 / 1 on each axis.
        const wv = { x: Math.round(wv0.x), y: Math.round(wv0.y) };

        // Round 13/14 item 4: if a door/window is the current selection (list-card click or
        // Shift+Click), the arrows nudge IT along its own wall, not the room. `pos` is a 0..1
        // fraction, so step units convert via the wall length. `along` is the component of the
        // (un-rotated) screen direction that runs along the wall's own axis -- world X for an
        // n/s wall, world Y for a w/e wall; an arrow perpendicular to the wall on screen is a no-op.
        const dr = selectedDoorRef.current;
        if (dr) {
          const dRoom = roomsRef.current.find(r => r.id === dr.roomId);
          const d = dRoom?.doors?.[dr.doorIndex];
          if (!dRoom || !d) return;
          // Use the SAME resolver DoorsSvg renders from, instead of re-deriving direction from
          // `d.wall` + the bbox. `d.wall` is only a compass hint once an `edge` signature is
          // present (see the door type in useFacilityRooms.ts) -- its matched polygon segment can
          // run either way, so assuming +pos always means +world-x/+world-y (as the old bbox-only
          // math did) silently reversed the arrows for exactly the doors that have a signature.
          // Projecting the screen-nudge vector onto the resolver's own signed al keeps nudge
          // direction and rendered direction from ever disagreeing.
          const A = resolveDoorAnchor(dRoom, d as any);
          const wallLen = A.len;
          if (wallLen <= 0) return;
          const along = (wv.x * A.alx + wv.y * A.aly) * step;
          if (Math.abs(along) < 1e-6) { e.preventDefault(); return; }
          e.preventDefault();
          // Edge-signature doors (jogged / staggered / traced polygon walls) are bounded by their
          // matched edge's real length and their own opening width (edgeDoorPosBounds). Everything
          // else -- rectangular rooms, pre-signature data -- keeps the original 0.1..0.9 clamp.
          const onPolyEdge = !!(d as any).edge && !!(dRoom.points && dRoom.points.length > 2);
          const bounds = onPolyEdge ? edgeDoorPosBounds(d as any, wallLen) : { lo: 0.1, hi: 0.9 };
          const newPos = Math.max(bounds.lo, Math.min(bounds.hi, ((d as any).pos ?? 0.5) + along / wallLen));
          const newDoors = (dRoom.doors ?? []).map((x, i) => i === dr.doorIndex ? { ...x, pos: newPos } : x);
          roomsRef.current = roomsRef.current.map(r => r.id === dr.roomId ? { ...r, doors: newDoors } : r);
          dirtyIdsRef.current.add(dr.roomId);
          setHasDirty(true);
          const updated = roomsRef.current.find(r => r.id === dr.roomId);
          if (updated && selectedIdRef.current === dr.roomId) setSelectedRoom(updated);
          draw();
          if (arrowPutTimerRef.current) clearTimeout(arrowPutTimerRef.current);
          arrowPutTimerRef.current = setTimeout(async () => {
            const r = roomsRef.current.find(rm => rm.id === dr.roomId);
            if (r) { try { await api.put(`/facility-rooms/${dr.roomId}`, { doors: r.doors }); } catch { /* silent */ } }
          }, 500);
          return;
        }

        const id = selectedIdRef.current;
        if (id == null) return;
        e.preventDefault();
        const room = roomsRef.current.find(r => r.id === id);
        if (!room) return;
        // Round 14 item 4: same un-rotated screen direction as the door path above, so a room
        // nudge also follows the screen, not the storage frame, when the view is rotated.
        const dx = wv.x * step;
        const dy = wv.y * step;
        // Round 12 item 7: translate `points` by the same delta as x/y. RoomShapeSvg renders a
        // polygon room exclusively from `points`, so the old x/y-only nudge moved the data but not
        // the shape on screen (same class of bug the whole-room-drag fix addressed). Any bathroom
        // attached to this room travels by the same delta too, matching the drag path.
        const translateRoom = (r: FacilityRoom): FacilityRoom => ({
          ...r,
          x: Number(r.x) + dx,
          y: Number(r.y) + dy,
          ...(r.points ? { points: r.points.map(p => ({ x: p.x + dx, y: p.y + dy })) } : {}),
        });
        const attachedIds = roomsRef.current
          .filter(r => {
            if (r.id === id || !r.notes) return false;
            try { return Number(JSON.parse(r.notes as string)?.parent_room_id) === id; } catch { return false; }
          })
          .map(r => r.id);
        const movedIds = new Set<number>([id, ...attachedIds]);
        roomsRef.current = roomsRef.current.map(r => movedIds.has(r.id) ? translateRoom(r) : r);
        movedIds.forEach(mid => dirtyIdsRef.current.add(mid));
        setHasDirty(true);
        draw();
        // Real-time X,Y update in property panel
        const movedRoom = roomsRef.current.find(r => r.id === id);
        if (movedRoom) setSelectedRoom(movedRoom);
        // Debounce PUT — fire 500ms after last keypress. Send `points` too (jsonb column) so the
        // shape's new position actually persists, and persist each moved attached bathroom.
        if (arrowPutTimerRef.current) clearTimeout(arrowPutTimerRef.current);
        arrowPutTimerRef.current = setTimeout(async () => {
          for (const mid of movedIds) {
            const r = roomsRef.current.find(rm => rm.id === mid);
            if (!r) continue;
            const payload: Record<string, unknown> = { x: r.x, y: r.y };
            if (r.points) payload.points = r.points;
            try { await api.put(`/facility-rooms/${mid}`, payload); } catch { /* silent */ }
          }
        }, 500);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleUndo, handleRedo, draw, fitToView, bulkUpdate, canEdit]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Trace image — load on floor change ──
  useEffect(() => {
    const facilityId = user?.facilityId;
    if (!facilityId) return;

    traceLoadedRef.current = false;
    traceImgRef.current = null;
    setTraceImageId(null);
    setTraceImageUrl(null);
    setHasSvgBackground(false);
    backgroundModeRef.current = 'pending';
    svgMarkupRef.current = null;
    svgScaleInchesPerUnitRef.current = 1;
    setTraceRotation(0);
    traceRotationRef.current = 0;

    api.get<{ id: number; image_url: string | null; opacity: number; scale?: number; svg_scale_inches_per_unit?: number | null; background_mode?: 'raster' | 'svg'; svg_markup?: string | null; rotation?: number | null } | null>(
      `/facility-floor-images?floor_index=${currentFloor}`
    ).then(res => {
      const row = (res as any).data as { id: number; image_url: string | null; opacity: number; scale?: number; svg_scale_inches_per_unit?: number | null; background_mode?: 'raster' | 'svg'; svg_markup?: string | null; rotation?: number | null } | null;
      if (!row) { backgroundModeRef.current = 'raster'; draw(); return; }

      // Row id is needed as the PATCH target for the mode-switch control regardless of which mode
      // is currently active, so it's set unconditionally here (previously only set in the raster
      // branch below).
      setTraceImageId(row.id);

      // Round 10 item 3: background-image rotation applies to whichever mode is active (one row,
      // one value), so seed it unconditionally alongside the row id.
      {
        const rotDeg = Number((row as any).rotation ?? 0);
        const safeRot = Number.isFinite(rotDeg) ? rotDeg : 0;
        setTraceRotation(safeRot);
        traceRotationRef.current = safeRot;
      }

      // Cache the svg skeleton if this row has one, independent of whether it's the CURRENTLY
      // active background -- item 7's fix. Without this, switching back to svg after a raster
      // upload would need a full page reload just to re-populate svgMarkupRef.
      if (row.svg_markup) {
        setHasSvgBackground(true);
        // svg_scale_inches_per_unit (migration_v131) -- was previously the shared `scale` column,
        // which also serves as the raster trace-photo's own "% of floor plan" value. Once a floor
        // could hold both backgrounds at once (item 7), that collided: an svg calibration value
        // like 5.6191 got read as a raster scale of "561%", rendering the photo hugely oversized.
        svgScaleInchesPerUnitRef.current = Number(row.svg_scale_inches_per_unit) > 0 ? Number(row.svg_scale_inches_per_unit) : 1;
        // The stored skeleton's own viewBox is cropped to the building footprint (e.g.
        // "139.0 18.6 370.0 734.9"), not the original "0 0 612 792" canvas the room polygons were
        // converted through -- override it (string-level, since this is injected via
        // dangerouslySetInnerHTML rather than a DOM node we can query/mutate after mount) so 1
        // raw SVG unit lines up with 1 unit in that same space, with explicit width/height so 1
        // raw unit equals 1 local SVG-user-space unit here.
        svgMarkupRef.current = row.svg_markup
          .replace(/viewBox="[^"]*"/, 'viewBox="0 0 612 792"')
          .replace(/<svg /, '<svg width="612" height="792" ');
      }

      // Cache the raster trace photo if this row has one, likewise independent of active mode.
      if (row.image_url) {
        setTraceImageUrl(row.image_url);
        const opacityPct = Math.round(row.opacity * 100);
        setTraceOpacity(opacityPct);
        traceOpacityRef.current = row.opacity;
        const scalePct = Math.round(((row as any).scale ?? 1.0) * 100);
        setTraceScale(scalePct);
        traceScaleRef.current = scalePct;
        setTraceOffsetX(Number((row as any).offset_x ?? 0));
        traceOffsetXRef.current = Number((row as any).offset_x ?? 0);
        setTraceOffsetY(Number((row as any).offset_y ?? 0));
        traceOffsetYRef.current = Number((row as any).offset_y ?? 0);

        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          traceImgRef.current = img;
          traceLoadedRef.current = true;
          draw();
        };
        img.src = row.image_url;
      }

      backgroundModeRef.current = (row.background_mode === 'svg' && row.svg_markup) ? 'svg' : 'raster';
      draw();
      // Round 19: the background layer (skeleton markup / trace photo / mode) just changed -- it's
      // in computeSnapshotMarkupKey but not a `rooms` edit, so re-prime the snapshot here too.
      // Covers floor change, skeleton import, trace upload, and the bg-mode toggle (all bump one
      // of this effect's deps / bgReloadKey).
      scheduleIdleCapture();
    }).catch(() => { backgroundModeRef.current = 'raster'; draw(); });
  }, [currentFloor, user?.facilityId, bgReloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Markers: fetch on floor change ──
  useEffect(() => {
    const facilityId = user?.facilityId;
    if (!facilityId) return;
    selectedMarkerIdRef.current = null;
    setSelectedMarker(null);
    setMarkerPanelPos(null);
    api.get<FacilityMarker[]>(`/facility-markers?floor_index=${currentFloor}`)
      .then(res => {
        markersRef.current = (res.data ?? []).map(m => ({ ...m, x: Number(m.x), y: Number(m.y) }));
        draw();
      })
      .catch(() => {});
  }, [currentFloor, user?.facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Room selector: load areas once ──
  useEffect(() => {
    const facilityId = user?.facilityId;
    if (!facilityId) return;
    api.get(`/dropdowns?dropdown_type=area&facility_id=${facilityId}`)
      .then(res => {
        const opts = ((res as any).options ?? []) as { id: number; label: string }[];
        setSelectorAreaOptions(opts);
      })
      .catch(() => {});
  }, [user?.facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Room selector: load locations when area changes ──
  useEffect(() => {
    if (selectorAreaId == null) { setSelectorLocationOptions([]); return; }
    const facilityId = user?.facilityId;
    if (!facilityId) return;
    setSelectorLocLoading(true);
    api.get(`/dropdowns?dropdown_type=location&facility_id=${facilityId}&parent_id=${selectorAreaId}`)
      .then(res => {
        const opts = ((res as any).options ?? []) as { id: number; label: string }[];
        setSelectorLocationOptions(opts);
      })
      .catch(() => setSelectorLocationOptions([]))
      .finally(() => setSelectorLocLoading(false));
  }, [selectorAreaId, user?.facilityId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── URL param ?room=LOCATION_ID — auto-select on first load ──
  const urlRoomLoadedRef = useRef(false);
  useEffect(() => {
    if (urlRoomLoadedRef.current || rooms.length === 0) return;
    const params = new URLSearchParams(window.location.search);
    const roomParam = params.get('room');
    if (!roomParam) return;
    urlRoomLoadedRef.current = true;
    const locId = Number(roomParam);
    if (!locId) return;
    const room = rooms.find(r => r.linked_location_id === locId);
    if (!room) return;
    if (room.floor_index !== currentFloorRef.current) {
      setCurrentFloor(room.floor_index);
      currentFloorRef.current = room.floor_index;
    }
    setTimeout(() => {
      scrollToRoom(room);
      setRoomDetailRoom(room);
      setShowRoomDetail(true);
    }, 500);
  }, [rooms]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Restore selected room from localStorage on mount (after rooms load) ──
  const didRestoreRoomRef = useRef(false);
  useEffect(() => {
    if (didRestoreRoomRef.current || rooms.length === 0 || roomDetailRoom) return;
    const storedId = localStorage.getItem('facilitymap_selected_room_id');
    if (!storedId) return;
    const room = rooms.find(r => r.id === Number(storedId));
    if (!room) {
      localStorage.removeItem('facilitymap_selected_room_id');
      localStorage.removeItem('facilitymap_selected_room_label');
      localStorage.removeItem('facilitymap_room_tab');
      return;
    }
    didRestoreRoomRef.current = true;
    const storedLabel = localStorage.getItem('facilitymap_selected_room_label');
    setRoomDetailRoom(room);
    setSelectorSelectedLabel(storedLabel ?? room.name);
  }, [rooms]); // eslint-disable-line react-hooks/exhaustive-deps

  // Round 22 item 1: drive the PropertyPanel column's mount timing. On deselect -> hide it. On a
  // selection that did NOT come from a held canvas gesture (room search, keyboard, restore) -> show
  // it immediately. A canvas mouse-select arms a drag ref in the same mousedown, so this effect
  // sees that ref set and defers -- handleMouseUp flips it true once the button is released, after
  // which the 360px column can mount without resizing the <svg> mid-click.
  useEffect(() => {
    if (!selectedRoom) { setPanelColumnReady(false); return; }
    if (!dragRef.current && !resizeRef.current && !polyVertexDragRef.current && !markerDragRef.current) {
      setPanelColumnReady(true);
    }
  }, [selectedRoom]);

  // ── Trace: upload handler ──
  const handleTraceUpload = useCallback(async (file: File) => {
    const facilityId = user?.facilityId;
    if (!facilityId) return;
    setTraceUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      fd.append('floor_index', String(currentFloor));
      fd.append('opacity', String(traceOpacityRef.current));

      // Same VITE_API_URL resolution as client/src/api/client.ts's axios instance -- this route
      // bypasses that shared instance (per CLAUDE_RULES.md's "long request" rule, uploads go direct
      // to VITE_API_URL to skip the Vercel proxy's 10s timeout), but was missing that instance's
      // dev fallback: VITE_API_URL is deliberately left UNSET for local dev (.env.local.example),
      // so interpolating it directly produced a literal "undefined" in the URL and a 404 on every
      // local upload. Production (where VITE_API_URL IS set) was never affected by this.
      const rawApiUrl = import.meta.env.VITE_API_URL as string | undefined;
      const apiBase = rawApiUrl
        ? (rawApiUrl.startsWith('http') ? rawApiUrl : `https://${rawApiUrl}`)
        : '/api';
      const resp = await fetch(
        `${apiBase}/facility-floor-images`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
          body: fd,
        }
      );
      const json = await resp.json();
      if (!json.success) throw new Error(json.error ?? 'Upload failed');
      const row = json.data as { id: number; image_url: string; opacity: number };
      // Server always resets this floor's background_mode to 'raster' on upload (and clears
      // svg_markup) -- mirror that here, otherwise the render gate below stays on whatever mode
      // the floor was in before (e.g. 'svg' for a floor with a skeleton background already loaded)
      // and the freshly uploaded trace photo never shows despite the upload having succeeded.
      backgroundModeRef.current = 'raster';
      setTraceImageId(row.id);
      setTraceImageUrl(row.image_url);
      const opacityPct = Math.round(row.opacity * 100);
      setTraceOpacity(opacityPct);
      traceOpacityRef.current = row.opacity;
      // New image always starts at default scale/offset
      setTraceScale(100);
      traceScaleRef.current = 100;
      setTraceOffsetX(0);
      traceOffsetXRef.current = 0;
      setTraceOffsetY(0);
      traceOffsetYRef.current = 0;

      const img = new window.Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        traceImgRef.current = img;
        traceLoadedRef.current = true;
        draw();
      };
      img.src = row.image_url;
    } catch { /* silent */ }
    setTraceUploading(false);
  }, [currentFloor, draw, user?.facilityId]);

  // ── Breaker Panels data loading ──
  const loadBpPanels = useCallback(async () => {
    setBpLoading(true);
    try {
      const res = await apiClient.get('/breaker-panels');
      setBpPanels((res.data as any).data ?? []);
    } catch (e) {
      console.error('[breaker-panels]', e);
    } finally {
      setBpLoading(false);
    }
  }, []);

  const loadPanelBreakers = useCallback(async (panelId: number) => {
    setBreakersLoading(true);
    try {
      const res = await apiClient.get(`/breaker-panels/${panelId}/breakers`);
      setPanelBreakers((res.data as any).data ?? []);
    } catch (e) {
      console.error('[breaker-panels/breakers]', e);
    } finally {
      setBreakersLoading(false);
    }
  }, []);

  // ── Breaker Panels actions ──
  async function createPanel() {
    if (!newPanelName.trim()) return;
    const slots = newPanelSlots === 0
      ? parseInt(newPanelCustomSlots) || 24
      : newPanelSlots;
    setSavingPanel(true);
    try {
      await apiClient.post('/breaker-panels', {
        name: newPanelName.trim(),
        location_id: newPanelLocationId || null,
        total_slots: slots,
        notes: newPanelNotes.trim() || null,
        fed_from_panel_id: newPanelFedFrom || null,
      });
      setShowCreatePanel(false);
      setNewPanelName(''); setNewPanelSlots(24); setNewPanelNotes('');
      setNewPanelAreaId(null); setNewPanelLocationId(null); setNewPanelFedFrom(null);
      await loadBpPanels();
    } catch (e) {
      console.error('[create-panel]', e);
    } finally {
      setSavingPanel(false);
    }
  }

  async function deletePanel(id: number) {
    if (!confirm('Delete this panel and all its breakers?')) return;
    try {
      await apiClient.delete(`/breaker-panels/${id}`);
      if (selectedPanelId === id) { setSelectedPanelId(null); setPanelBreakers([]); }
      await loadBpPanels();
    } catch (e) {
      console.error('[delete-panel]', e);
    }
  }

  function openBreakerSlot(slot: number, side: 'left' | 'right', position: 'A' | 'B' = 'A') {
    const existing = panelBreakers.find(
      b => b.slot_number === slot && b.side === side && b.tandem_position === position
    ) ?? null;
    setEditingBreaker({ slot, side, position, existing });
    setBkLabel(existing?.label ?? '');
    setBkAmperage(existing?.amperage ? String(existing.amperage) : '');
    // A fresh (not-yet-saved) 'B' half is definitionally a tandem breaker sharing its slot with
    // 'A' -- default its Pole Configuration to "1/2 Slot" instead of the normal "1 Pole" default.
    setBkPoleCount((existing?.pole_count ?? (position === 'B' ? 0 : 1)) as 0|1|2|3);
    setBkAmperageCustom(existing?.amperage_custom ?? '');
    setBkNotes(existing?.notes ?? '');
    setBkLocations([]);
    setTimeout(() => setBkStatus(existing?.status ?? 'on'), 0);
    if (existing?.locations) {
      setBkLocations(existing.locations.map(l => ({
        id: l.id,
        areaId: null,
        locationId: l.location_id,
        roomId: l.room_id ?? null,
        areaLabel: '',
        locationLabel: '',
        note: l.location_note ?? '',
        areaOptions: newPanelAreaOptions,
        locOptions: [],
      })));

      // The breaker's saved links only carry location_id / room_id (see GET /:id/breakers), not
      // the area, and the two selects render from areaId + locOptions -- so without this the
      // linked row opened as "Select area…" / "Select room…" placeholders (areaId null, no
      // options to match locationId against) even though the link is real. One un-parented
      // location fetch carries every location's parent_id (= its area), which is enough to
      // resolve each link's area and its sibling rooms. Only rows loaded from the DB (with an id)
      // are touched, so a row the user adds while this resolves is left alone.
      const facilityId = user?.facilityId;
      if (facilityId && existing.locations.length > 0) {
        api.get(`/dropdowns?dropdown_type=location&facility_id=${facilityId}`)
          .then(res => {
            const all = ((res as any).options ?? []) as { id: number; label: string; parent_id: number | null }[];
            setBkLocations(locs => locs.map(l => {
              if (l.id == null || !l.locationId) return l;
              const linked = all.find(o => o.id === l.locationId);
              if (!linked || linked.parent_id == null) return l;
              return {
                ...l,
                areaId: linked.parent_id,
                locOptions: all.filter(o => o.parent_id === linked.parent_id).map(o => ({ id: o.id, label: o.label })),
              };
            }));
          })
          .catch(() => {});
      }
    }
  }

  async function saveBreaker() {
    if (!selectedPanelId || !editingBreaker) return;
    setSavingBreaker(true);
    try {
      let breakerId: number;
      if (editingBreaker.existing) {
        await apiClient.put(
          `/breaker-panels/breakers/${editingBreaker.existing.id}`,
          {
            label: bkLabel.trim() || null,
            amperage: bkAmperage && bkAmperage !== 'custom' ? parseInt(bkAmperage) : null,
            pole_count: bkPoleCount,
            amperage_custom: bkAmperageCustom.trim() || null,
            status: bkStatus,
            notes: bkNotes.trim() || null,
          }
        );
        breakerId = editingBreaker.existing.id;
      } else {
        const res = await apiClient.post(
          `/breaker-panels/${selectedPanelId}/breakers`,
          {
            slot_number: editingBreaker.slot,
            side: editingBreaker.side,
            tandem_position: editingBreaker.position,
            label: bkLabel.trim() || null,
            amperage: bkAmperage && bkAmperage !== 'custom' ? parseInt(bkAmperage) : null,
            pole_count: bkPoleCount,
            amperage_custom: bkAmperageCustom.trim() || null,
            status: bkStatus,
            notes: bkNotes.trim() || null,
          }
        );
        breakerId = (res.data as any).data.id;
      }
      for (const loc of bkLocations) {
        if (!loc.id && loc.locationId) {
          await apiClient.post(
            `/breaker-panels/breakers/${breakerId}/locations`,
            { location_id: loc.locationId, room_id: loc.roomId ?? null, location_note: loc.note || null }
          );
        }
      }
      setEditingBreaker(null);
      await loadPanelBreakers(selectedPanelId);
      setSelectedPanelId(prev => prev);
    } catch (e) {
      console.error('[save-breaker]', e);
    } finally {
      setSavingBreaker(false);
    }
  }

  async function deleteBreaker() {
    if (!editingBreaker?.existing || !selectedPanelId) return;
    if (!confirm('Remove this breaker assignment?')) return;
    setDeletingBreaker(true);
    try {
      await apiClient.delete(`/breaker-panels/breakers/${editingBreaker.existing.id}`);
      setEditingBreaker(null);
      await loadPanelBreakers(selectedPanelId);
    } catch (e) {
      console.error('[delete-breaker]', e);
    } finally {
      setDeletingBreaker(false);
    }
  }

  // Escape closes/cancels, Enter saves -- same convention as the SavedInput pattern and the other
  // Enter/Escape input handlers throughout this app (e.g. Settings.tsx). Skips Enter while the
  // multi-line Notes textarea is focused so a normal newline keystroke isn't hijacked into a save.
  // Mirrors the small self-contained isMapFullscreen Escape effect above rather than folding into
  // the big canvas keydown handler, which has unrelated tool/room-editing concerns.
  useEffect(() => {
    if (!editingBreaker) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); setEditingBreaker(null); return; }
      if (e.key === 'Enter') {
        const tag = (document.activeElement as HTMLElement | null)?.tagName?.toUpperCase();
        if (tag === 'TEXTAREA') return;
        e.preventDefault();
        if (!savingBreaker) saveBreaker();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingBreaker, savingBreaker]); // eslint-disable-line react-hooks/exhaustive-deps

  async function removeBreakerLocation(locId: number, bkId: number) {
    try {
      await apiClient.delete(`/breaker-panels/breaker-locations/${locId}`);
      await loadPanelBreakers(bkId);
    } catch (e) {
      console.error('[remove-breaker-location]', e);
    }
  }

  // ── Trace: save settings (opacity + scale + offset) ──
  const saveTraceSettings = useCallback(async () => {
    // Round 19: trace opacity/scale/offset/rotation feed computeSnapshotMarkupKey but aren't a
    // `rooms` state change, so the single-draw effect won't re-prime for them -- do it here (this
    // is the debounced "user stopped adjusting" point).
    scheduleIdleCapture();
    const id = traceImageId;
    if (id == null) return;
    try {
      await api.patch(`/facility-floor-images/${id}`, {
        opacity: traceOpacityRef.current,
        scale: traceScaleRef.current / 100,
        offset_x: traceOffsetXRef.current,
        offset_y: traceOffsetYRef.current,
        rotation: traceRotationRef.current,
      });
    } catch { /* silent */ }
  }, [traceImageId, scheduleIdleCapture]);

  // ── Trace: remove handler ──
  const handleTraceRemove = useCallback(async () => {
    const id = traceImageId;
    if (id == null) return;
    try {
      if (hasSvgBackground) {
        // This floor also has a calibrated skeleton -- clear just the raster photo (image_url)
        // and switch back to svg mode, instead of deleting the whole row. Same "store both
        // independently" fix as the raster/svg upload routes (item 7): a "Remove trace photo"
        // action should never take the skeleton down with it.
        await api.patch(`/facility-floor-images/${id}`, { image_url: null, background_mode: 'svg' });
        backgroundModeRef.current = 'svg';
      } else {
        await api.delete(`/facility-floor-images/${id}`);
      }
    } catch { /* silent */ }
    setTraceImageId(hasSvgBackground ? id : null);
    setTraceImageUrl(null);
    setTraceOpacity(30);
    traceOpacityRef.current = 0.30;
    setTraceScale(100);
    traceScaleRef.current = 100;
    setTraceOffsetX(0);
    traceOffsetXRef.current = 0;
    setTraceOffsetY(0);
    traceOffsetYRef.current = 0;
    // Only zero rotation on a full row delete. When the skeleton is kept (hasSvgBackground), its
    // own rotation lives in the same column and the PATCH above preserves it -- don't clobber it.
    if (!hasSvgBackground) {
      setTraceRotation(0);
      traceRotationRef.current = 0;
    }
    traceImgRef.current = null;
    traceLoadedRef.current = false;
    draw();
  }, [traceImageId, hasSvgBackground, draw]);

  // ── Switch which background is currently displayed (item 7's "switch back" control) --
  // svg_markup/image_url are both cached client-side already (see the load-on-floor-change
  // effect), so this is a pure display-mode flip: no re-upload or re-fetch needed either way. ──
  const switchBackgroundMode = useCallback(async (mode: 'raster' | 'svg') => {
    const id = traceImageId;
    if (id == null) return;
    const prevMode = backgroundModeRef.current;
    backgroundModeRef.current = mode; // optimistic -- instant switch, no flicker waiting on the PATCH
    draw();
    scheduleIdleCapture(); // Round 19: background mode changed -> re-prime the snapshot
    try {
      const res = await api.patch(`/facility-floor-images/${id}`, { background_mode: mode });
      if (!res.success) { backgroundModeRef.current = prevMode; draw(); scheduleIdleCapture(); }
    } catch {
      backgroundModeRef.current = prevMode;
      draw();
      scheduleIdleCapture();
    }
  }, [traceImageId, draw, scheduleIdleCapture]);

  // ── Mouse helpers ──
  // A compact fingerprint of everything that maps a screen point to a world point: pan, zoom, view
  // rotation, and the live canvas size (the un-rotate pivot + rotation transform-origin both key
  // off the canvas centre). Compared across a held gesture to detect the view shifting underneath.
  const viewSig = useCallback(() => (
    `${Math.round(oxRef.current)}|${Math.round(oyRef.current)}|${scaleRef.current.toFixed(3)}|${rotRef.current}|${Math.round(canvasSizeRef.current.w)}|${Math.round(canvasSizeRef.current.h)}`
  ), []);

  const getWorldPos = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    // Round 23 item 2: keep canvasSizeRef exactly in step with the live element size. renderMapSvg's
    // rotation <g> transform-origin reads it (`${W/2}px ${H/2}px`); un-rotating here about a
    // DIFFERENT centre (a stale canvasSizeRef vs the fresh rect) means forward and inverse don't
    // cancel and a rotated placement/hit-test drifts -- this was the marker-lands-hundreds-of-px-off
    // bug at 90 degrees. Both sides now resolve to the same number.
    if (rect.width > 1 && rect.height > 1) canvasSizeRef.current = { w: rect.width, h: rect.height };
    // Un-rotate the pointer about the canvas centre before converting to world space -- the view
    // rotation lives on a wrapper <g> only, so hit-testing/placement stays in the untransformed
    // world space rooms and markers are stored in (extinguisher point-in-polygon unaffected).
    const p = unrotateCanvasPoint(e.clientX - rect.left, e.clientY - rect.top, rect.width / 2, rect.height / 2, rotRef.current);
    return canvasToWorld(p.x, p.y, oxRef.current, oyRef.current, scaleRef.current);
  }, []);

  const handleMouseDown = useCallback(async (e: React.MouseEvent<SVGSVGElement>) => {
    // Cancel any pending trace-settings save — prevents spurious API call when canvas is clicked
    // while a trace offset input has focus (onBlur would otherwise fire saveTraceSettings)
    if (traceSaveTimerRef.current) { clearTimeout(traceSaveTimerRef.current); traceSaveTimerRef.current = null; }
    setCtxMenu(null);
    setShowRoomDetail(false);
    const { wx, wy } = getWorldPos(e); // also refreshes canvasSizeRef from the live rect
    // 2nd-layer guard: remember the view state as of this mousedown. handleMouseMove compares
    // against it while a gesture is armed and aborts if it changed (see gestureViewSigRef).
    gestureViewSigRef.current = viewSig();
    const t = toolRef.current;

    if (t === 'pan' || e.button === 1) {
      // Round 13 item 2: suppress the browser's native middle-click autoscroll (the little
      // scroll-anchor puck + page scrolling) so a middle-drag only pans the map. React's
      // onMouseDown is non-passive, so preventDefault here is honoured.
      if (e.button === 1) e.preventDefault();
      clearViewJumpEase(); // Round 15: a live pan must track 1:1, never through an in-flight ease
      // Starting a new gesture -- cancel any pending idle-capture (not idle anymore), and if a zoom
      // gesture's cache overlay is still showing (its own settle timer hasn't fired yet), end it
      // now so panGroupRef's imperative writes below aren't fighting a hidden live layer.
      if (idleCaptureHandleRef.current != null) { cancelIdle(idleCaptureHandleRef.current); idleCaptureHandleRef.current = null; }
      if (zoomGestureUsingSnapshotRef.current) {
        if (wheelIdleTimerRef.current) { clearTimeout(wheelIdleTimerRef.current); wheelIdleTimerRef.current = null; }
        endZoomGestureDisplay();
        draw();
      }
      const rect = canvasRef.current!.getBoundingClientRect();
      panRef.current = { mx: e.clientX - rect.left, my: e.clientY - rect.top, ox: oxRef.current, oy: oyRef.current };
      // Round 17: blit the raster snapshot for the pan if one is primed (rot === 0 only); else
      // handleMouseMove's pan branch transforms the live SVG as before.
      beginSnapshotDisplay();
      return;
    }

    // ── Marker tool: place marker on click ──
    if (t === 'marker' && canEdit) {
      try {
        const res = await api.post<FacilityMarker>('/facility-markers', {
          floor_index: currentFloorRef.current,
          x: wx, y: wy,
          marker_type: markerPlacementTypeRef.current,
        });
        if (res.success && res.data) {
          const nm = { ...res.data, x: Number(res.data.x), y: Number(res.data.y) };
          markersRef.current = [...markersRef.current, nm];
          draw();
        }
      } catch { /* silent */ }
      return;
    }

    if (t === 'select') {
      // ── Item 5: Shift+Click selects an individual door/window directly, bypassing the normal
      // room-hit/vertex/handle logic below entirely -- the escape hatch for one left behind by
      // item 4's bug (or just for repositioning/flipping one in general), since a door has no
      // hit-test of its own in the normal select flow (it's an entry in a room's `doors` array,
      // not a selectable shape). Also selects the owning room (so its property panel opens) and
      // arms doorDragRef so the same mousedown can flow straight into a drag. ──
      if (e.shiftKey && canEdit) {
        // Item 4: 20 screen px (was 14) -- the door's rendered "block" (gap + swing arc) is wider
        // than the old radius, so clicks that visually landed on it were missing.
        const doorHit = hitDoorAt(floorRoomsLive(), wx, wy, 20 / scaleRef.current);
        if (doorHit) {
          const owner = floorRoomsLive().find(r => r.id === doorHit.roomId);
          if (owner) {
            pushHistory();
            selectedDoorRef.current = doorHit;
            doorDragRef.current = doorHit;
            selectedIdsRef.current.clear();
            setMultiSelectCount(0);
            selectedIdRef.current = owner.id;
            setSelectedRoom(owner);
            draw();
            return;
          }
        }
      }
      // Round 12 item 4: a door selected from its list row (or a previous Shift+Click) becomes
      // draggable with a plain mousedown near it -- no Shift needed the second time. Only re-arms
      // for the ALREADY-selected door, so a normal click elsewhere still selects rooms as before.
      if (!e.shiftKey && canEdit && selectedDoorRef.current) {
        const near = hitDoorAt(floorRoomsLive(), wx, wy, 20 / scaleRef.current);
        if (near && near.roomId === selectedDoorRef.current.roomId && near.doorIndex === selectedDoorRef.current.doorIndex) {
          pushHistory();
          doorDragRef.current = selectedDoorRef.current;
          draw();
          return;
        }
      }
      // ── Check marker hit (item 4: world-space distance * scale, matching the right-click
      // handler -- the old screen-space form compared a rotated pointer against an un-rotated
      // worldToCanvas() projection, so it missed once the view was rotated). ──
      const floorMarkers = markersRef.current.filter(m => m.floor_index === currentFloorRef.current);
      for (const m of floorMarkers) {
        if (Math.hypot(wx - Number(m.x), wy - Number(m.y)) * scaleRef.current <= 14) {
          selectedMarkerIdRef.current = m.id;
          setSelectedMarker(m);
          setShowExtLinkList(false);
          setExtOptions([]);
          markerDragRef.current = { marker: m, offsetX: wx - Number(m.x), offsetY: wy - Number(m.y) };
          setMarkerPanelPos({
            x: Math.min(e.clientX + 20, window.innerWidth - 260),
            y: Math.min(e.clientY - 30, window.innerHeight - 300),
          });
          selectedIdRef.current = null;
          setSelectedRoom(null);
          draw();
          return;
        }
      }
      // Clicking canvas clears marker selection
      if (selectedMarkerIdRef.current != null) {
        selectedMarkerIdRef.current = null;
        setSelectedMarker(null);
        setMarkerPanelPos(null);
      }

      const floor = floorRoomsLive();
      if (selectedIdRef.current != null) {
        const sel = floor.find(r => r.id === selectedIdRef.current);
        if (sel) {
          // Alt+Click on a selected room's edge inserts a new vertex at the clicked point (not just
          // at a precomputed midpoint), then hands off to the same drag mechanism as any other
          // vertex. Works on a plain rectangle room too (no points array yet) by deriving its 4
          // corners from x/y/w/h and adding a points array from there -- x/y/w/h are deliberately
          // left untouched (not zeroed), since DoorsSvg/FireWallsSvg always position from x/y/w/h
          // regardless of points, so any existing doors/fire walls keep rendering correctly after
          // this conversion. Checked before Ctrl+Click (delete) and the rect-handle branch below so
          // it applies uniformly to both room shapes; gated on altKey so it can't collide with
          // Ctrl+Click vertex-delete or the existing plain-click vertex-drag-start.
          if (e.altKey && canEdit) {
            const boundary = (sel.points && sel.points.length > 2) ? sel.points : getRectBoundaryPoints(sel);
            const tol = (MIDPOINT_RADIUS + 4) / scaleRef.current;
            let bestDist = Infinity, bestIdx = -1, bestPt: { x: number; y: number } | null = null;
            for (let i = 0; i < boundary.length; i++) {
              const a = boundary[i], b = boundary[(i + 1) % boundary.length];
              const proj = projectPointOnSegment(wx, wy, a, b);
              const dist = Math.hypot(wx - proj.x, wy - proj.y);
              if (dist < bestDist) { bestDist = dist; bestIdx = i; bestPt = proj; }
            }
            if (bestPt && bestDist <= tol) {
              pushHistory();
              const newVertexIdx = bestIdx + 1;
              const newPts = [...boundary];
              newPts.splice(newVertexIdx, 0, bestPt);
              roomsRef.current = roomsRef.current.map(r => r.id === sel.id ? { ...r, points: newPts } : r);
              setSelectedRoom({ ...sel, points: newPts });
              polyVertexDragRef.current = { roomId: sel.id, vertexIdx: newVertexIdx };
              draw();
              return;
            }
          }
          // Polygon vertex drag — check before rect handles
          if (sel.points && sel.points.length > 2) {
            const hs = (HANDLE_RADIUS + 3) / scaleRef.current;
            let hitVertex = false;
            for (let vi = 0; vi < sel.points.length; vi++) {
              const vp = sel.points[vi];
              if (Math.abs(wx - vp.x) <= hs && Math.abs(wy - vp.y) <= hs) {
                // Ctrl+Click (Cmd+Click on Mac) on an existing vertex deletes it instead of
                // starting a drag -- same minimum-3-vertices guard as the rest of the polygon
                // editing logic (a 2-point "polygon" isn't a shape).
                if (e.ctrlKey || e.metaKey) {
                  if (sel.points.length > 3) {
                    pushHistory();
                    const newPts = sel.points.filter((_, i) => i !== vi);
                    // Recompute x/y/w/h bbox too -- removing a corner can shrink the shape.
                    const xs = newPts.map(p => p.x), ys = newPts.map(p => p.y);
                    const bbox = { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
                    roomsRef.current = roomsRef.current.map(r => r.id === sel.id ? { ...r, points: newPts, ...bbox } : r);
                    commitRoom(sel.id);
                    draw();
                  }
                  hitVertex = true;
                  break;
                }
                pushHistory();
                polyVertexDragRef.current = { roomId: sel.id, vertexIdx: vi };
                hitVertex = true;
                break;
              }
            }
            if (hitVertex) return;
            // ── Add-a-corner: hit an edge-midpoint dot -- insert a real vertex at that edge's
            // midpoint, then hand off to the exact same polyVertexDragRef drag mechanism used for
            // existing corners above, so the rest of the drag/commit path is fully shared, not
            // reinvented. ──
            const hms = (MIDPOINT_RADIUS + 3) / scaleRef.current;
            const mids = edgeMidpoints(sel.points);
            for (let ei = 0; ei < mids.length; ei++) {
              const mp = mids[ei];
              if (Math.abs(wx - mp.x) <= hms && Math.abs(wy - mp.y) <= hms) {
                pushHistory();
                const newVertexIdx = ei + 1;
                const newPts = [...sel.points];
                newPts.splice(newVertexIdx, 0, { x: mp.x, y: mp.y });
                roomsRef.current = roomsRef.current.map(r => r.id === sel.id ? { ...r, points: newPts } : r);
                hoveredMidpointRef.current = null;
                setSelectedRoom({ ...sel, points: newPts });
                polyVertexDragRef.current = { roomId: sel.id, vertexIdx: newVertexIdx };
                draw();
                return;
              }
            }
          } else {
            const handle = hitHandle(sel, wx, wy, scaleRef.current);
            if (handle) {
              pushHistory();
              if (e.shiftKey) {
                const cx = Number(sel.x) + Number(sel.w) / 2;
                const cy = Number(sel.y) + Number(sel.h) / 2;
                const origDist = Math.max(1, Math.sqrt((wx - cx) ** 2 + (wy - cy) ** 2));
                resizeRef.current = { roomId: sel.id, dir: handle.dir, origX: sel.x, origY: sel.y, origW: sel.w, origH: sel.h, startWX: wx, startWY: wy, proportional: true, centerX: cx, centerY: cy, origDist };
                setProportionalScaleActive(true);
              } else {
                resizeRef.current = { roomId: sel.id, dir: handle.dir, origX: sel.x, origY: sel.y, origW: sel.w, origH: sel.h, startWX: wx, startWY: wy };
              }
              return;
            }
          }
        }
      }
      // Item 4: interior hit first, then nearest edge within ~10 screen px -- so a click that
      // lands just outside a room's boundary (very common when zoomed out, where the fill is a
      // few px wide) still selects it. Same helper the door/window tool already uses.
      const hit = hitRoomForWallTool(floor, wx, wy, 10 / scaleRef.current);
      if (hit) {
        if (e.ctrlKey || e.metaKey) {
          // Multi-select toggle (Ctrl+Click) — no drag
          if (selectedIdsRef.current.has(hit.id)) {
            selectedIdsRef.current.delete(hit.id);
          } else {
            selectedIdsRef.current.add(hit.id);
          }
          selectedIdRef.current = hit.id;
          setSelectedRoom(hit);
          setMultiSelectCount(selectedIdsRef.current.size);
          draw();
        } else {
          pushHistory();
          selectedIdsRef.current.clear();
          setMultiSelectCount(0);
          selectedIdRef.current = hit.id;
          setSelectedRoom(hit);
          selectedDoorRef.current = null; // item 5: a plain room click drops any door highlight
          // Item 4: snapshot any bathroom attached to the room being dragged (notes.parent_room_id
          // === hit.id) so mousemove can translate it by the same delta as the parent.
          const attached = roomsRef.current
            .filter(r => r.id !== hit.id && r.floor_index === hit.floor_index && r.notes)
            .filter(r => {
              try { return Number(JSON.parse(r.notes as string)?.parent_room_id) === hit.id; } catch { return false; }
            })
            .map(r => ({ id: r.id, origX: Number(r.x), origY: Number(r.y), origPoints: r.points ? r.points.map(p => ({ x: p.x, y: p.y })) : null }));
          dragRef.current = {
            roomId: hit.id, offsetX: wx - hit.x, offsetY: wy - hit.y,
            origX: Number(hit.x), origY: Number(hit.y),
            origPoints: hit.points ? hit.points.map(p => ({ x: p.x, y: p.y })) : null,
            attached,
          };
          draw();
        }
      } else {
        selectedIdsRef.current.clear();
        setMultiSelectCount(0);
        selectedIdRef.current = null;
        setSelectedRoom(null);
        selectedDoorRef.current = null; // item 5
        draw();
      }
      return;
    }

    if (t === 'draw' && canEdit) {
      const sx = snap(wx, snapRef.current), sy = snap(wy, snapRef.current);
      drawingRef.current = { startX: sx, startY: sy, x: sx, y: sy, w: 0, h: 0 };
      return;
    }

    if ((t === 'door' || t === 'window') && canEdit) {
      // 14 screen px of tolerance either side of a wall -- see hitRoomForWallTool's comment.
      const hit = hitRoomForWallTool(floorRoomsLive(), wx, wy, 14 / scaleRef.current);
      if (!hit) return;
      // One anchor model for every room shape (computeDoorPlacement): a rectangular room -> the
      // bounding-box {wall,pos} exactly as before; a non-rect polygon (L / notch / Cut / traced)
      // -> the nearest ACTUAL edge, `pos` along that edge, plus a reshape-robust `edge` signature.
      // hitRoomForWallTool already resolved the room by nearest segment; the exact edge is picked
      // here so there is a single place that does it, not a fourth ad-hoc copy.
      const place = computeDoorPlacement(hit, wx, wy);
      pushHistory();
      const entry: any = t === 'window'
        ? { ...place, type: 'window', width: windowWidthRef.current }
        : { ...place, swing: doorSwingRef.current, hinge: 'left' };
      const newDoors = [...(hit.doors ?? []), entry];
      roomsRef.current = roomsRef.current.map(r => r.id === hit.id ? { ...r, doors: newDoors } : r);
      commitRoom(hit.id);
      draw();
    }
  }, [getWorldPos, floorRoomsLive, canEdit, pushHistory, commitRoom, draw, endZoomGestureDisplay, clearViewJumpEase, beginSnapshotDisplay]);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const rect = canvasRef.current!.getBoundingClientRect();
    const cx = e.clientX - rect.left, cy = e.clientY - rect.top;

    if (panRef.current) {
      const p = panRef.current;
      // Item 6: the drag delta is a SCREEN-space vector, but ox/oy live in panGroupRef's own
      // (un-rotated) space -- the view rotation sits on the wrapper <g> outside it. Rotate the
      // delta by -rot (about the origin, hence cx=cy=0) before applying, or a rotated view pans
      // along swapped axes. Mirrors what the mobile two-finger midpoint math already does.
      const d = unrotateCanvasPoint(cx - p.mx, cy - p.my, 0, 0, rotRef.current);
      oxRef.current = p.ox + d.x;
      oyRef.current = p.oy + d.y;
      // Round 17: blit the composited raster snapshot if this pan engaged it (rot === 0 + a primed
      // cache); otherwise transform the live SVG <g> as before. Either way no draw()/React
      // re-render during the pan -- handleMouseUp's pan-end branch settles once.
      if (zoomGestureUsingSnapshotRef.current) {
        applySnapshotTransform();
      } else if (panGroupRef.current) {
        panGroupRef.current.style.transform = `translate(${oxRef.current}px, ${oyRef.current}px) scale(${scaleRef.current})`;
      }
      return;
    }

    const { wx, wy } = getWorldPos(e); // also refreshes canvasSizeRef from the live rect

    // 2nd-layer guard (Round 22 item 1): if the view / canvas size shifted between this gesture's
    // mousedown and now -- e.g. the PropertyPanel column mounting and resizing the <svg> while the
    // button is still held -- the arm-time offsets are stale. Cancel the gesture rather than
    // translate the room / marker / vertex by the phantom delta. Pan has its own branch above and
    // legitimately moves the view, so it is never reached here.
    if ((dragRef.current || resizeRef.current || polyVertexDragRef.current || markerDragRef.current || doorDragRef.current || drawingRef.current)
        && gestureViewSigRef.current && gestureViewSigRef.current !== viewSig()) {
      dragRef.current = null;
      if (resizeRef.current?.proportional) setProportionalScaleActive(false);
      resizeRef.current = null;
      polyVertexDragRef.current = null;
      markerDragRef.current = null;
      doorDragRef.current = null;
      drawingRef.current = null;
      snapHighlightRef.current = null;
      gestureViewSigRef.current = '';
      draw();
      return;
    }

    // ── Item 5: dragging an individually-selected door/window -- constrained to its OWN room's
    // walls (any of the 4), never migrating it to a different room. ──
    if (doorDragRef.current) {
      const { roomId, doorIndex } = doorDragRef.current;
      const room = roomsRef.current.find(r => r.id === roomId);
      if (room && room.doors && room.doors[doorIndex]) {
        // Same anchor model as placement: rect -> {wall,pos}; non-rect polygon -> matched edge +
        // signature. Clear any stale `edge` when the door is dragged onto a now-rectangular room.
        const place = computeDoorPlacement(room, wx, wy);
        const newDoors = room.doors.map((d, i) => {
          if (i !== doorIndex) return d;
          const nd: any = { ...d, wall: place.wall, pos: place.pos };
          if (place.edge) nd.edge = place.edge; else delete nd.edge;
          return nd;
        });
        roomsRef.current = roomsRef.current.map(r => r.id === roomId ? { ...r, doors: newDoors } : r);
        draw();
      }
      return;
    }

    // ── Polygon vertex drag ──
    if (polyVertexDragRef.current) {
      const { roomId, vertexIdx } = polyVertexDragRef.current;
      const pRoom = roomsRef.current.find(r => r.id === roomId);
      if (pRoom && pRoom.points) {
        const snx = snap(wx, snapRef.current);
        const sny = snap(wy, snapRef.current);
        const newPts = pRoom.points.map((pt, i) => i === vertexIdx ? { x: snx, y: sny } : pt);
        // Keep x/y/w/h as the CURRENT bbox of points, not frozen at creation-time -- reshaping a
        // room used to leave x/y/w/h stale, which is exactly the desync effectiveRect's callers
        // (door/window math, DoorsSvg) now defend against, but keeping the raw fields honest here
        // too means anything that still reads them directly elsewhere in the file stays correct.
        const xs = newPts.map(p => p.x), ys = newPts.map(p => p.y);
        const bbox = { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
        roomsRef.current = roomsRef.current.map(r => r.id === roomId ? { ...r, points: newPts, ...bbox } : r);
        draw();
      }
      return;
    }

    // ── Marker drag ──
    if (markerDragRef.current) {
      const { marker, offsetX, offsetY } = markerDragRef.current;
      const nx = wx - offsetX, ny = wy - offsetY;
      markersRef.current = markersRef.current.map(m => m.id === marker.id ? { ...m, x: nx, y: ny } : m);
      draw();
      return;
    }

    if (drawingRef.current) {
      const d = drawingRef.current;
      const ex = snap(wx, snapRef.current), ey = snap(wy, snapRef.current);
      d.x = Math.min(d.startX, ex);
      d.y = Math.min(d.startY, ey);
      d.w = Math.abs(ex - d.startX);
      d.h = Math.abs(ey - d.startY);
      draw();
      return;
    }

    if (dragRef.current && canEdit) {
      const dg = dragRef.current;
      let nx = snap(wx - dg.offsetX, snapRef.current);
      let ny = snap(wy - dg.offsetY, snapRef.current);

      // Snap to adjacent rooms
      snapHighlightRef.current = null;
      if (snapToRoomsRef.current) {
        const dragged = roomsRef.current.find(r => r.id === dg.roomId);
        if (dragged) {
          // effectiveRect: dragged's own size and every other room's edges, so alignment snaps to
          // where rooms actually visually are, not a possibly-stale x/y/w/h.
          const { w: dw, h: dh } = effectiveRect(dragged);
          const THRESH = 15;
          const others = roomsRef.current.filter(r => r.id !== dg.roomId && r.floor_index === currentFloorRef.current);
          let bestDist = THRESH + 1;
          let snapNx = nx, snapNy = ny, snapLine: { x1: number; y1: number; x2: number; y2: number } | null = null;

          for (const other of others) {
            const { x: ox2, y: oy2, w: ow, h: oh } = effectiveRect(other);

            // 8 edge-alignment snap points: left/right/top/bottom of dragged aligned to left/right/top/bottom of other
            const checks: { dx: number; dy: number; axis: 'x' | 'y'; lineVal: number; lineMin: number; lineMax: number }[] = [
              // dragged left = other right
              { dx: (ox2 + ow) - nx,         dy: 0, axis: 'x', lineVal: ox2 + ow, lineMin: Math.min(ny, oy2), lineMax: Math.max(ny + dh, oy2 + oh) },
              // dragged right = other left
              { dx: ox2 - (nx + dw),          dy: 0, axis: 'x', lineVal: ox2,      lineMin: Math.min(ny, oy2), lineMax: Math.max(ny + dh, oy2 + oh) },
              // dragged left = other left
              { dx: ox2 - nx,                 dy: 0, axis: 'x', lineVal: ox2,      lineMin: Math.min(ny, oy2), lineMax: Math.max(ny + dh, oy2 + oh) },
              // dragged right = other right
              { dx: (ox2 + ow) - (nx + dw),   dy: 0, axis: 'x', lineVal: ox2 + ow, lineMin: Math.min(ny, oy2), lineMax: Math.max(ny + dh, oy2 + oh) },
              // dragged top = other bottom
              { dx: 0, dy: (oy2 + oh) - ny,   axis: 'y', lineVal: oy2 + oh, lineMin: Math.min(nx, ox2), lineMax: Math.max(nx + dw, ox2 + ow) },
              // dragged bottom = other top
              { dx: 0, dy: oy2 - (ny + dh),   axis: 'y', lineVal: oy2,      lineMin: Math.min(nx, ox2), lineMax: Math.max(nx + dw, ox2 + ow) },
              // dragged top = other top
              { dx: 0, dy: oy2 - ny,           axis: 'y', lineVal: oy2,      lineMin: Math.min(nx, ox2), lineMax: Math.max(nx + dw, ox2 + ow) },
              // dragged bottom = other bottom
              { dx: 0, dy: (oy2 + oh) - (ny + dh), axis: 'y', lineVal: oy2 + oh, lineMin: Math.min(nx, ox2), lineMax: Math.max(nx + dw, ox2 + ow) },
            ];

            for (const c of checks) {
              const dist = Math.abs(c.axis === 'x' ? c.dx : c.dy);
              if (dist < THRESH && dist < bestDist) {
                bestDist = dist;
                snapNx = c.axis === 'x' ? nx + c.dx : nx;
                snapNy = c.axis === 'y' ? ny + c.dy : ny;
                if (c.axis === 'x') {
                  snapLine = { x1: c.lineVal, y1: c.lineMin, x2: c.lineVal, y2: c.lineMax };
                } else {
                  snapLine = { x1: c.lineMin, y1: c.lineVal, x2: c.lineMax, y2: c.lineVal };
                }
              }
            }
          }

          if (bestDist <= THRESH) {
            nx = snapNx;
            ny = snapNy;
            snapHighlightRef.current = snapLine;
          }

          // Item 5: snap back to the room's OWN drag-start position. The alignment loop above only
          // builds targets from OTHER rooms, so a room dragged away and back never re-locked to
          // exactly where it started (grid-snap alone can't, unless the origin happened to be
          // grid-aligned). Per-axis, same THRESH, and checked last so "home" wins over an
          // incidental alignment with a neighbour when both are in range.
          let homeX = false, homeY = false;
          if (Math.abs(nx - dg.origX) <= THRESH) { nx = dg.origX; homeX = true; }
          if (Math.abs(ny - dg.origY) <= THRESH) { ny = dg.origY; homeY = true; }
          if (homeX || homeY) {
            snapHighlightRef.current = homeX
              ? { x1: dg.origX, y1: dg.origY, x2: dg.origX, y2: dg.origY + dh }
              : { x1: dg.origX, y1: dg.origY, x2: dg.origX + dw, y2: dg.origY };
          }
        }
      }

      // Translate `points` by the same delta as x/y (item 3 fix) -- RoomShapeSvg renders
      // exclusively from `points` for any polygon room, so updating x/y alone (the old behavior)
      // never moved anything on screen; the room's data silently drifted out from under its own
      // frozen visual shape instead. dg.origX/origY/origPoints were captured once at drag-start
      // (mousedown) specifically so this is a pure translation from the ORIGINAL points, not a
      // cumulative delta from the previous mousemove tick (which would drift under snapping).
      const deltaX = nx - dg.origX, deltaY = ny - dg.origY;
      const newPoints = dg.origPoints ? dg.origPoints.map(p => ({ x: p.x + deltaX, y: p.y + deltaY })) : null;
      roomsRef.current = roomsRef.current.map(r => {
        if (r.id === dg.roomId) return { ...r, x: nx, y: ny, ...(newPoints ? { points: newPoints } : {}) };
        // Item 4: any bathroom attached to the room being dragged travels by the exact same delta,
        // from its OWN drag-start snapshot (same reasoning as dg.origX/origY/origPoints above --
        // a pure translation from where it started, not a cumulative per-tick delta).
        const att = dg.attached.find(a => a.id === r.id);
        if (!att) return r;
        const attPoints = att.origPoints ? att.origPoints.map(p => ({ x: p.x + deltaX, y: p.y + deltaY })) : null;
        return { ...r, x: att.origX + deltaX, y: att.origY + deltaY, ...(attPoints ? { points: attPoints } : {}) };
      });
      draw();
      return;
    }

    if (resizeRef.current && canEdit) {
      const rs = resizeRef.current;
      if (rs.proportional && rs.centerX !== undefined && rs.centerY !== undefined && rs.origDist !== undefined) {
        // Proportional scale from center
        const curDist = Math.max(1, Math.sqrt((wx - rs.centerX) ** 2 + (wy - rs.centerY) ** 2));
        const factor = curDist / rs.origDist;
        const nw = Math.max(MIN_ROOM_SIZE, rs.origW * factor);
        const nh = Math.max(MIN_ROOM_SIZE, rs.origH * factor);
        const nx = rs.centerX - nw / 2;
        const ny = rs.centerY - nh / 2;
        roomsRef.current = roomsRef.current.map(r => r.id === rs.roomId ? { ...r, x: nx, y: ny, w: nw, h: nh } : r);
      } else {
        const patch = applyResize(rs, wx, wy, snapRef.current);
        roomsRef.current = roomsRef.current.map(r => r.id === rs.roomId ? { ...r, ...patch } : r);
      }
      draw();
      return;
    }

    const t = toolRef.current;

    // ── Door/window placement preview ──
    if (t === 'door' || t === 'window') {
      const hit = hitRoomForWallTool(floorRoomsLive(), wx, wy, 14 / scaleRef.current);
      if (hit) {
        // Same resolver the actual placement (pointer-down branch) uses -- so the ghost tracks the
        // real target edge on a jogged / L / traced wall, not the outer bounding-box line. Carries
        // the `edge` signature through so DoorWindowPreviewSvg renders against that segment.
        const place = computeDoorPlacement(hit, wx, wy);
        previewRef.current = { roomId: hit.id, wall: place.wall, pos: place.pos, edge: place.edge, canvasX: cx, canvasY: cy };
      } else {
        previewRef.current = null;
      }
      draw();
      return;
    }

    // ── Marker tool: placement preview follows cursor ──
    if (t === 'marker') {
      markerPreviewPosRef.current = { x: wx, y: wy };
      draw();
      return;
    }

    // ── Idle hover (item 2 room-fill hover, item 3 add-a-corner midpoint hover) — only reached
    // when nothing above is being actively dragged/panned/drawn. ──
    if (t === 'select') {
      const floor = floorRoomsLive();
      let nextMidpoint: { roomId: number; edgeIdx: number } | null = null;
      // Round 14 item 3: track edit-grip hover (vertex / midpoint / rect handle) of the selected
      // room. Computed here so the cursor can force the plain arrow even though the room-hover
      // hit-test below ALSO matches at a grip (a grip is on the room boundary).
      let overHandle = false;
      const selId = selectedIdRef.current;
      if (selId != null) {
        const sel = floor.find(r => r.id === selId);
        if (sel && sel.points && sel.points.length > 2) {
          const hs = (HANDLE_RADIUS + 3) / scaleRef.current;
          const mids = edgeMidpoints(sel.points);
          for (let i = 0; i < mids.length; i++) {
            if (Math.abs(wx - mids[i].x) <= hs && Math.abs(wy - mids[i].y) <= hs) {
              nextMidpoint = { roomId: sel.id, edgeIdx: i };
              break;
            }
          }
          if (nextMidpoint || sel.points.some(vp => Math.abs(wx - vp.x) <= hs && Math.abs(wy - vp.y) <= hs)) overHandle = true;
        } else if (sel && hitHandle(sel, wx, wy, scaleRef.current)) {
          overHandle = true;
        }
      }
      const hoveredRoom = nextMidpoint ? null : hitRoomForWallTool(floor, wx, wy, 10 / scaleRef.current);
      const nextRoomId = hoveredRoom ? hoveredRoom.id : null;
      // The move/grab cursor is for room / door / marker hover only (see hoverHandleRef for grips).
      const overSelectable = !!hoveredRoom
        || !!hitDoorAt(floor, wx, wy, 20 / scaleRef.current)
        || markersRef.current.some(m => m.floor_index === currentFloorRef.current
            && Math.hypot(wx - Number(m.x), wy - Number(m.y)) * scaleRef.current <= 14);
      if (hoveredMidpointRef.current?.roomId !== nextMidpoint?.roomId || hoveredMidpointRef.current?.edgeIdx !== nextMidpoint?.edgeIdx || hoveredRoomIdRef.current !== nextRoomId || hoverSelectableRef.current !== overSelectable || hoverHandleRef.current !== overHandle) {
        hoveredMidpointRef.current = nextMidpoint;
        hoveredRoomIdRef.current = nextRoomId;
        hoverSelectableRef.current = overSelectable;
        hoverHandleRef.current = overHandle;
        draw();
      }
    }
  }, [getWorldPos, floorRoomsLive, canEdit, draw, applySnapshotTransform]);

  const handleMouseUp = useCallback(async () => {
    previewRef.current = null;
    markerPreviewPosRef.current = null;
    gestureViewSigRef.current = '';
    // Round 22 item 1 (root cause): the selecting gesture is over -- safe to mount the PropertyPanel
    // column now (it resizes the <svg>, which must never happen while a mousedown is held). Grid
    // class also gates on !showRoomDetail, so this is a no-op when the Room Detail panel is up.
    if (selectedIdRef.current != null) setPanelColumnReady(true);
    if (panRef.current) {
      panRef.current = null;
      // Round 17: if this pan was blitting the raster snapshot, hand back to the live SVG at the
      // final position (endSnapshotDisplay writes the SVG transform sync, then unhides it), then
      // the normal settle: full redraw + re-prime the idle cache + persist.
      endSnapshotDisplay();
      draw();
      scheduleIdleCapture();
      saveViewState();
      return;
    }
    // ── Item 5: individually-dragged door/window end -- persist its new wall/pos, same immediate-
    // PUT pattern as handleUpdateDoorWidth (not the dirty/batch-save system). ──
    if (doorDragRef.current) {
      const { roomId } = doorDragRef.current;
      doorDragRef.current = null;
      const room = roomsRef.current.find(r => r.id === roomId);
      if (room) {
        setRooms(roomsRef.current);
        setSelectedRoom(room);
        try { await api.put(`/facility-rooms/${roomId}`, { doors: room.doors }); } catch { /* silent */ }
      }
      draw();
      return;
    }
    // ── Polygon vertex drag end ──
    if (polyVertexDragRef.current) {
      const { roomId } = polyVertexDragRef.current;
      polyVertexDragRef.current = null;
      commitRoom(roomId);
      return;
    }
    // ── Marker drag end: PUT final position ──
    if (markerDragRef.current) {
      const { marker } = markerDragRef.current;
      const updated = markersRef.current.find(m => m.id === marker.id);
      markerDragRef.current = null;
      if (updated) {
        // Item 2 (this round): no room_id write needed here anymore -- the Rooms tab now derives
        // "which extinguishers are in this room" purely from marker position at read-time
        // (point-in-polygon against the room's own boundary), not a stored link that had to be
        // kept in sync on every move. See RoomInlineView's extinguishers-tab fetch.
        try { await api.put(`/facility-markers/${marker.id}`, { x: Number(updated.x), y: Number(updated.y) }); } catch { /* silent */ }
      }
      draw();
      return;
    }
    if (dragRef.current) {
      const { roomId: id, attached } = dragRef.current;
      dragRef.current = null;
      snapHighlightRef.current = null;
      // Item 4: mark any attached bathroom dirty too, alongside the dragged room itself, so "Save
      // Changes" actually persists its new position (it moved in local state via the mousemove
      // translation above, but was never otherwise flagged as changed).
      attached.forEach(a => markDirty(a.id));
      commitRoom(id);
      return;
    }
    if (resizeRef.current) { const id = resizeRef.current.roomId; const wasProp = resizeRef.current.proportional; resizeRef.current = null; if (wasProp) setProportionalScaleActive(false); commitRoom(id); return; }

    const d = drawingRef.current;
    if (d) {
      drawingRef.current = null;
      if (canEdit && d.w >= MIN_ROOM_SIZE && d.h >= MIN_ROOM_SIZE) {
        lockView(); // Round 12 item 3: drawing a new room must not shift the view
        pushHistory();
        const newRoom = await createRoom({
          floor_index: currentFloorRef.current,
          floor_name: floors.find(f => f.floor_index === currentFloorRef.current)?.floor_name ?? 'Floor',
          name: 'New Room',
          room_number: null,
          type: 'other',
          x: d.x, y: d.y, w: d.w, h: d.h,
          // Born as a 4-corner polygon (same corners a first Alt+Click conversion would produce)
          // so vertex-drag / edge-midpoint "add a corner" / Alt+Click vertex-insert all work from
          // the very first interaction -- no separate unlock step. x/y/w/h kept as-is above.
          points: rectCornerPoints(d.x, d.y, d.w, d.h),
          doors: [], fire_wall_edges: [],
          linked_area_id: null, linked_location_id: null, needs_linking: false,
          dimensions_ft_w: null, dimensions_ft_h: null,
          notes: null, sort_order: 0,
        } as NewRoomData);
        if (newRoom) {
          selectedIdRef.current = newRoom.id;
          setSelectedRoom(newRoom);
          toolRef.current = 'select';
          setTool('select');
        }
      } else {
        draw();
      }
    }
  }, [canEdit, createRoom, floors, pushHistory, commitRoom, draw, scheduleIdleCapture, markDirty, setRooms, saveViewState, lockView, endSnapshotDisplay]);

  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    e.stopPropagation();
    clearViewJumpEase(); // Round 15: wheel-zoom tracks per tick, not through an in-flight ease
    // When window tool is active: scroll adjusts window width, not zoom
    if (toolRef.current === 'window') {
      const delta = e.deltaY < 0 ? 3 : -3;
      windowWidthRef.current = Math.max(15, Math.min(60, windowWidthRef.current + delta));
      draw();
      return;
    }
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    if (rect.width > 1 && rect.height > 1) canvasSizeRef.current = { w: rect.width, h: rect.height }; // Round 23 item 2, see getWorldPos
    // Un-rotate the cursor about the canvas centre so "zoom about the pointer" stays anchored to
    // the world point actually under the cursor when the view is rotated (same as getWorldPos).
    const rp = unrotateCanvasPoint(e.clientX - rect.left, e.clientY - rect.top, rect.width / 2, rect.height / 2, rotRef.current);
    const cx = rp.x, cy = rp.y;
    const factor = e.deltaY < 0 ? 1.1 : 0.9;
    const prev = scaleRef.current;
    const ns = Math.max(0.1, Math.min(5, prev * factor));
    if (ns === prev) return; // already at min/max zoom clamp — nothing changed, skip the re-render
    oxRef.current = cx - (cx - oxRef.current) * (ns / prev);
    oyRef.current = cy - (cy - oyRef.current) * (ns / prev);
    scaleRef.current = ns;
    if (zoomBadgeRef.current) {
      zoomBadgeRef.current.textContent = `${Math.round(ns * 100)}%`;
    }
    // We're actively gesturing again -- not idle. Cancel any pending idle-capture rather than let
    // it fire mid-gesture (that race is what broke the earlier reverted version).
    if (idleCaptureHandleRef.current != null) { cancelIdle(idleCaptureHandleRef.current); idleCaptureHandleRef.current = null; }

    // First tick of a fresh gesture that finds an already-ready cache: start displaying it -- zero
    // capture latency in the critical path, since it was rendered proactively during idle time.
    beginSnapshotDisplay();
    if (zoomGestureUsingSnapshotRef.current) {
      applySnapshotTransform(); // cheap: pure CSS bitmap resample, no vector re-rasterization
    } else if (panGroupRef.current) {
      // No cache ready yet (e.g. first zoom right after page load, before anything ever went idle)
      // -- graceful degradation to the plain live-vector bypass, same cost as before this feature.
      panGroupRef.current.style.transform = `translate(${oxRef.current}px, ${oyRef.current}px) scale(${ns})`;
    }

    if (wheelIdleTimerRef.current) clearTimeout(wheelIdleTimerRef.current);
    wheelIdleTimerRef.current = setTimeout(() => {
      wheelIdleTimerRef.current = null;
      endSnapshotDisplay();
      draw(); // one real full-detail redraw once the zoom gesture settles
      scheduleIdleCapture(); // prime the cache for whenever the next gesture starts
      saveViewState(); // item 3: persist once the zoom gesture actually settles, not per wheel tick
    }, 500);
  }, [draw, applySnapshotTransform, beginSnapshotDisplay, endSnapshotDisplay, scheduleIdleCapture, saveViewState, clearViewJumpEase]);

  // Non-passive wheel listener — preventDefault stops page scroll while canvas is focused.
  // isMapFullscreen and mapView are in the deps not because the listener itself needs their
  // values, but because EITHER one changing can swap out the DOM node canvasRef points at:
  // toggling fullscreen switches the component's root between a plain return and
  // ReactDOM.createPortal(...) (a different element type at the root, so React unmounts/remounts
  // the whole subtree); switching the Facility Map / Rooms / Breakers sub-tabs conditionally
  // renders the entire facility-map block (`{mapView === 'facility' && (...)}`), including the
  // <svg>, so leaving and returning to the Facility Map tab destroys the old <svg> and mounts a
  // brand new one exactly the same way fullscreen does. Either way canvasRef.current becomes a
  // BRAND NEW <svg> node. handleWheel is a stable useCallback (deps never change), so without
  // BOTH of these in the deps, the effect never re-runs after either remount and the listener
  // stays attached to the old, now-detached node forever -- wheel silently stops responding on the
  // new (visible) node until the next full page reload. Confirmed via a live repro for fullscreen
  // (entering/exiting mid-gesture reliably reproduced permanently-dead wheel input with every
  // gesture-tracking ref/flag otherwise showing a perfectly clean, idle state, ruling out a stuck
  // flag and pointing at the listener attachment itself); mapView was the same gap, just never
  // covered by that first fix since the two remount paths look identical here.
  // Round 12 item 2: attach to the whole map viewport (containerRef), not just the <svg>. Wheeling
  // over an on-canvas overlay (legend, mobile-zoom control, trace panel) is still "over the map"
  // and must not scroll the page -- those aren't <svg> descendants, so a listener on the <svg>
  // alone missed them. Non-passive + preventDefault (in handleWheel) is what actually stops the
  // page scroll; overscrollBehavior: 'contain' on the container is the backstop against chaining.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel, isMapFullscreen, mapView]);

  // ── Mobile touch: single finger drives the existing mouse handlers (== the pointer, so the
  // current tool's place/select/drag behaviour works exactly as on desktop), two fingers do
  // pan + pinch-zoom (never colliding with single-finger marker placement/dragging). ──
  // Builds the minimal MouseEvent shape the mouse handlers actually read from a Touch -- clientX/Y
  // (getWorldPos), button (0 = not a middle-click pan), the modifier keys (TouchEvent carries
  // shiftKey/ctrlKey/altKey/metaKey), and no-op preventDefault/stopPropagation -- so a one-finger
  // touch reuses every branch of handleMouseDown/Move/Up without duplicating any of it.
  const synthMouseFromTouch = useCallback((t: Touch, src: TouchEvent): React.MouseEvent<SVGSVGElement> => ({
    clientX: t.clientX, clientY: t.clientY, button: 0, buttons: 1,
    shiftKey: src.shiftKey, ctrlKey: src.ctrlKey, altKey: src.altKey, metaKey: src.metaKey,
    currentTarget: canvasRef.current as unknown as (EventTarget & SVGSVGElement),
    target: src.target,
    preventDefault: () => {}, stopPropagation: () => {},
  } as unknown as React.MouseEvent<SVGSVGElement>), []);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (e.touches.length >= 2) {
      // A 2nd finger landed -- abort any single-finger interaction cleanly before starting the
      // two-finger gesture, so a half-started room drag doesn't get left dangling.
      if (singleTouchActiveRef.current) { handleMouseUp(); singleTouchActiveRef.current = false; }
      clearViewJumpEase(); // Round 15: pinch tracks 1:1
      const a = e.touches[0], b = e.touches[1];
      const rect = canvas.getBoundingClientRect();
      // Un-rotate the pinch midpoint about the canvas centre (same as getWorldPos/handleWheel) so
      // the pan/zoom math operates in panGroupRef's own space when the view is rotated.
      const sm = unrotateCanvasPoint((a.clientX + b.clientX) / 2 - rect.left, (a.clientY + b.clientY) / 2 - rect.top, rect.width / 2, rect.height / 2, rotRef.current);
      touchGestureRef.current = {
        startDist: Math.max(1, Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)),
        startScale: scaleRef.current,
        startMidX: sm.x,
        startMidY: sm.y,
        startOx: oxRef.current,
        startOy: oyRef.current,
      };
      beginSnapshotDisplay(); // Round 17: blit the raster snapshot for the pinch (rot === 0 only)
      e.preventDefault();
      return;
    }
    if (e.touches.length === 1) {
      singleTouchActiveRef.current = true;
      e.preventDefault(); // suppress the browser's own synthesized mouse events -- we forward ours
      handleMouseDown(synthMouseFromTouch(e.touches[0], e));
    }
  }, [handleMouseDown, handleMouseUp, synthMouseFromTouch, clearViewJumpEase, beginSnapshotDisplay]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (touchGestureRef.current && e.touches.length >= 2) {
      const g = touchGestureRef.current;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const a = e.touches[0], b = e.touches[1];
      const rect = canvas.getBoundingClientRect();
      const dist = Math.max(1, Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY));
      const mid = unrotateCanvasPoint((a.clientX + b.clientX) / 2 - rect.left, (a.clientY + b.clientY) / 2 - rect.top, rect.width / 2, rect.height / 2, rotRef.current);
      const midX = mid.x;
      const midY = mid.y;
      // Pinch: scale by the finger-spread ratio, clamped to the same [0.1, 5] range as wheel-zoom.
      const ns = Math.max(0.1, Math.min(5, g.startScale * (dist / g.startDist)));
      const k = ns / g.startScale;
      // Keep the world point under the gesture's START midpoint pinned there while scaling, then
      // add the pan delta from the midpoint itself moving (two-finger drag). Derivation:
      // ox_new = midX - (startMidX - startOx) * k  -- same "zoom about a point" math as
      // zoomByCenter/handleWheel, with the extra (midX - startMidX) folding into midX directly.
      oxRef.current = midX - (g.startMidX - g.startOx) * k;
      oyRef.current = midY - (g.startMidY - g.startOy) * k;
      scaleRef.current = ns;
      // Round 17: blit the composited raster snapshot if the pinch engaged it; otherwise transform
      // the live SVG <g> as before. Same choice the mouse-pan path now makes.
      if (zoomGestureUsingSnapshotRef.current) {
        applySnapshotTransform();
      } else if (panGroupRef.current) {
        panGroupRef.current.style.transform = `translate(${oxRef.current}px, ${oyRef.current}px) scale(${ns})`;
      }
      if (zoomBadgeRef.current) zoomBadgeRef.current.textContent = `${Math.round(ns * 100)}%`;
      e.preventDefault();
      return;
    }
    if (singleTouchActiveRef.current && e.touches.length === 1) {
      handleMouseMove(synthMouseFromTouch(e.touches[0], e));
      e.preventDefault();
    }
  }, [handleMouseMove, synthMouseFromTouch, applySnapshotTransform]);

  const handleTouchEnd = useCallback((e: TouchEvent) => {
    if (touchGestureRef.current) {
      // Two-finger gesture ended (or dropped below 2 fingers). Settle: one real full-detail redraw
      // + persist, same as a wheel-zoom settle. Do NOT start a single-finger interaction from any
      // leftover finger -- require a clean lift-and-retouch so a pinch doesn't slide into a drag.
      touchGestureRef.current = null;
      singleTouchActiveRef.current = false;
      setDisplayZoom(Math.round(scaleRef.current * 100));
      // Round 17: hand back from the raster snapshot to the live SVG at the final position (was a
      // bare `zoomSnapshotStateRef.current = null`, which would have left the canvas shown and the
      // SVG hidden if the pinch had engaged the snapshot).
      endSnapshotDisplay();
      scheduleIdleCapture();
      draw();
      saveViewState();
      return;
    }
    if (singleTouchActiveRef.current && e.touches.length === 0) {
      singleTouchActiveRef.current = false;
      handleMouseUp();
    }
  }, [handleMouseUp, draw, scheduleIdleCapture, saveViewState, endSnapshotDisplay]);

  // Non-passive native touch listeners (React's onTouch* are passive, so preventDefault there is a
  // no-op -- exact same reason the wheel listener is attached this way). isMapFullscreen/mapView in
  // the deps for the same reason too: either one swaps out the <svg> node canvasRef points at.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });
    canvas.addEventListener('touchcancel', handleTouchEnd, { passive: false });
    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
      canvas.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, isMapFullscreen, mapView]);

  const handleDblClick = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    const { wx, wy } = getWorldPos(e);
    const floor = floorRoomsLive();

    // Double-click a vertex on the currently-selected polygon room to delete it (mockup's
    // documented interaction; confirmed this never existed anywhere in this file before this
    // rebuild). Checked before the generic room-select-on-double-click fallback below, and only
    // when the room double-clicked is the room already selected -- otherwise a stray double-click
    // elsewhere shouldn't silently start deleting corners of whatever was selected previously.
    if (canEdit && selectedIdRef.current != null) {
      const sel = floor.find(r => r.id === selectedIdRef.current);
      if (sel && sel.points && sel.points.length > 3) { // minimum 3 corners -- never delete below that
        const hs = (HANDLE_RADIUS + 3) / scaleRef.current;
        const vi = sel.points.findIndex(p => Math.abs(wx - p.x) <= hs && Math.abs(wy - p.y) <= hs);
        if (vi !== -1) {
          pushHistory();
          const newPts = sel.points.filter((_, i) => i !== vi);
          roomsRef.current = roomsRef.current.map(r => r.id === sel.id ? { ...r, points: newPts } : r);
          commitRoom(sel.id);
          draw();
          return;
        }
      }
    }

    const hit = hitRoom(floor, wx, wy);
    if (hit) {
      selectedIdRef.current = hit.id;
      setSelectedRoom(hit);
      draw();
    }
  }, [getWorldPos, floorRoomsLive, canEdit, pushHistory, commitRoom, draw]);

  // ── Toolbar actions ──
  const selectTool = useCallback((t: Tool) => {
    if (!canEdit && t !== 'pan') return;
    toolRef.current = t;
    setTool(t);
  }, [canEdit]);

  const zoomByCenter = useCallback((factor: number) => {
    const { w, h } = canvasSizeRef.current;
    const cx = w / 2, cy = h / 2;
    const prev = scaleRef.current;
    const ns = Math.max(0.1, Math.min(5, prev * factor));
    oxRef.current = cx - (cx - oxRef.current) * (ns / prev);
    oyRef.current = cy - (cy - oyRef.current) * (ns / prev);
    scaleRef.current = ns;
    setDisplayZoom(Math.round(ns * 100));
    // This changes scale/position without going through handleWheel, so it never touches the
    // idle-capture cache -- if a cache already existed from an earlier pan/wheel-settle, it would
    // silently go stale (still recording the OLD scale/position), and the NEXT wheel-zoom gesture
    // would blit it at a wildly wrong relative scale (measured: 1.9x instead of the correct ~1.1x
    // for one tick) since applySnapshotTransform assumes the cache's recorded scale still matches
    // reality. Invalidate it here and prime a fresh one for whenever the view next goes idle.
    zoomSnapshotStateRef.current = null;
    scheduleIdleCapture();
    animateViewJump('pan'); // Round 15 polish
    draw();
    saveViewState(); // item 3
  }, [draw, scheduleIdleCapture, saveViewState, animateViewJump]);

  // Round 10 item 2: rotate the whole map view. delta is degrees clockwise; abs=true sets an
  // absolute angle instead (used by the reset-to-0 control). Normalized to (-180, 180]. The
  // rotation lives on a wrapper <g> only -- no stored geometry changes -- so this just re-draws
  // and persists alongside pan/zoom. Snapshot cache is invalidated (captureIdleSnapshot skips
  // while rotated; re-primes once back to 0).
  // Item 7: map-view rotation is quarter-turns only. `delta` is applied then snapped to the
  // nearest 90 (so a partial stored value from before this change also lands on a clean step);
  // abs=true sets an absolute angle (the reset-to-0 chip). Normalized to (-180, 180].
  const rotateView = useCallback((delta: number, abs = false) => {
    const raw = abs ? delta : rotRef.current + delta;
    const stepped = Math.round(raw / 90) * 90;
    const next = ((((stepped + 180) % 360) + 360) % 360) - 180;
    rotRef.current = next;
    setDisplayRot(Math.round(next));
    zoomSnapshotStateRef.current = null;
    scheduleIdleCapture();
    animateViewJump('rot'); // Round 15 polish: ease the 90-degree snap
    draw();
    saveViewState();
  }, [draw, scheduleIdleCapture, saveViewState, animateViewJump]);

  const toggleGrid = useCallback(() => {
    showGridRef.current = !showGridRef.current;
    setShowGrid(showGridRef.current);
    draw();
  }, [draw]);

  const toggleSnap = useCallback(() => {
    snapRef.current = !snapRef.current;
    setSnapEnabled(snapRef.current);
  }, []);

  const handleSave = useCallback(async () => {
    const ids = Array.from(dirtyIdsRef.current);
    const dirty = ids
      .map(id => roomsRef.current.find(r => r.id === id))
      .filter((r): r is FacilityRoom => !!r);
    if (!dirty.length) return;
    setIsSaving(true);
    const ok = await bulkUpdate(dirty);
    setIsSaving(false);
    if (ok) {
      dirtyIdsRef.current = new Set();
      setHasDirty(false);
      lastSavedRef.current = roomsRef.current.slice();
    }
  }, [bulkUpdate]);

  const showToast = useCallback((msg: string) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(null), 3500);
  }, []);

  const handleCancel = useCallback(() => {
    const restored = lastSavedRef.current;
    roomsRef.current = restored;
    setRooms(restored);
    dirtyIdsRef.current = new Set();
    setHasDirty(false);
    historyRef.current = [];
    setHistoryLength(0);
    // Item 4 fix: whatever was selected may not exist in `restored` at all -- e.g. a bathroom
    // created (and immediately persisted) after the last save/discard baseline vanishes from the
    // reverted array, but this never used to clear the selection pointing at it. That dangling
    // selectedRoom/selectedIdRef is exactly the stale reference that made a later delete attempt on
    // it look like it silently failed: the confirm modal would still open (selectedIdRef still set)
    // and executeDeleteRoom would look for it in roomsRef.current and find nothing to work with.
    selectedIdRef.current = null;
    setSelectedRoom(null);
    selectedIdsRef.current.clear();
    setMultiSelectCount(0);
    setShowCancelConfirm(false);
    draw();
  }, [setRooms, draw]);

  // Opens the confirm dialog (item 5) instead of deleting immediately -- actual deletion moved to
  // executeDeleteRoom below, invoked only once the user confirms.
  const handleDeleteSelected = useCallback(() => {
    const id = selectedIdRef.current;
    if (id == null) return;
    const room = roomsRef.current.find(r => r.id === id);
    setConfirmDeleteRoom({ id, name: room?.name ?? 'this room' });
  }, []);

  // Round 23 item 1: select / delete a sub-room (bathroom) straight from the parent's property
  // panel list, without having to find it on the canvas first.
  const handleSelectRoomById = useCallback((rid: number) => {
    const r = roomsRef.current.find(x => x.id === rid);
    if (!r) return;
    selectedIdsRef.current.clear();
    setMultiSelectCount(0);
    selectedDoorRef.current = null;
    selectedIdRef.current = rid;
    setSelectedRoom(r);
    draw();
  }, [draw]);

  const handleDeleteRoomById = useCallback((rid: number) => {
    const r = roomsRef.current.find(x => x.id === rid);
    setConfirmDeleteRoom({ id: rid, name: r?.name ?? 'this room' });
  }, []);

  const executeDeleteRoom = useCallback(async () => {
    const id = confirmDeleteRoom?.id ?? selectedIdRef.current;
    if (id == null) return;
    setConfirmDeleteRoom(null);
    lockView(); // Round 12 item 3: a delete must never move the pan/zoom/rotation on its own
    pushHistory();
    // Delete FIRST, check the result, and only mutate local state once the room is actually gone
    // server-side -- a failed delete must leave everything exactly as it was.
    const ok = await deleteRoom(id);
    if (!ok) {
      showToast('Delete failed — server error');
      return; // room stays selected/visible; nothing else changes, since nothing else happened
    }
    // Build the definitive post-delete snapshot ourselves rather than reading roomsRef.current
    // afterward -- deleteRoom's own setRooms call hasn't been reflected back into roomsRef.current
    // yet (that only happens on the next render's sync effect), so reading it here would still see
    // the just-deleted room.
    const after = roomsRef.current.filter(r => r.id !== id);
    // Round 12 item 3: the old code removed the PARENT room's LAST door whenever a room with a
    // parent_room_id (a bathroom) was deleted -- a leftover from when the bathroom's connecting
    // door lived on the parent's east wall. Round 11 moved that door onto the bathroom itself, so
    // it's already gone with the bathroom; slice(0,-1) here was stripping an unrelated real door
    // off the parent. Removed entirely -- deleting a bathroom now touches nothing but the bathroom.
    roomsRef.current = after;
    setRooms(after);
    // The delete is durably persisted now -- keep Discard's baseline in sync so a later "Discard
    // Changes" doesn't resurrect this room by reverting to a snapshot from before it was deleted.
    lastSavedRef.current = after.slice();
    selectedIdRef.current = null;
    setSelectedRoom(null);
    dirtyIdsRef.current.delete(id);
    setHasDirty(dirtyIdsRef.current.size > 0);
    draw();
  }, [confirmDeleteRoom, deleteRoom, pushHistory, setRooms, draw, showToast, lockView]);

  const patchSelected = useCallback((patch: Partial<FacilityRoom>) => {
    const cur = selectedIdRef.current;
    if (cur == null) return;
    pushHistory();
    roomsRef.current = roomsRef.current.map(r => r.id === cur ? { ...r, ...patch } : r);
    setRooms(roomsRef.current);
    markDirty(cur);
    const updated = roomsRef.current.find(r => r.id === cur);
    if (updated) setSelectedRoom(updated);
    draw();
  }, [setRooms, pushHistory, markDirty, draw]);

  const startGlow = useCallback((roomId: number) => {
    if (glowRef.current) cancelAnimationFrame(glowRef.current.animId);
    const state = { roomId, alpha: 0.9, animId: 0 };
    glowRef.current = state;
    const tick = () => {
      if (!glowRef.current || glowRef.current.roomId !== roomId) return;
      glowRef.current.alpha -= 0.016;
      // Round 15: while a view gesture is live, don't force a full re-render every frame just to
      // fade a highlight nobody is looking at -- the gesture's own settle draw() will pick up the
      // (dimmer or gone) glow. The alpha keeps decaying so the loop still ends on time.
      const gesturing = !!(panRef.current || touchGestureRef.current || wheelIdleTimerRef.current);
      if (glowRef.current.alpha <= 0) {
        glowRef.current = null;
        if (!gesturing) draw();
        return;
      }
      if (!gesturing) draw();
      glowRef.current.animId = requestAnimationFrame(tick);
    };
    state.animId = requestAnimationFrame(tick);
  }, [draw]);

  const scrollToRoom = useCallback((room: FacilityRoom) => {
    if (viewLockRef.current) return; // Round 12 item 3: a delete/add in progress must not recenter
    const { w: W, h: H } = canvasSizeRef.current;
    // Same class of bug fitToView had: a polygon room's w/h are always 0, so room.x/room.y alone
    // (its old, pre-conversion rect anchor) is not its real centroid -- compute from points when
    // present, exactly like fitToView's bounding-box fix above.
    let cx: number, cy: number;
    if (room.points && room.points.length > 2) {
      cx = room.points.reduce((s, p) => s + p.x, 0) / room.points.length;
      cy = room.points.reduce((s, p) => s + p.y, 0) / room.points.length;
    } else {
      cx = Number(room.x) + Number(room.w) / 2;
      cy = Number(room.y) + Number(room.h) / 2;
    }
    const scale = scaleRef.current;
    oxRef.current = W / 2 - cx * scale;
    oyRef.current = H / 2 - cy * scale;
    // Round 19: this recenters the view without going through a gesture settle, so the primed
    // snapshot's base ox/oy is now stale -- invalidate + re-prime for the next gesture.
    zoomSnapshotStateRef.current = null;
    scheduleIdleCapture();
    draw();
    startGlow(room.id);
  }, [draw, startGlow, scheduleIdleCapture]);

  const handleSelectorLocationChange = useCallback((locId: number, locOptions: { id: number; label: string }[], areaOptions: { id: number; label: string }[], areaId: number | null) => {
    setSelectorLocationId(locId);
    const room = roomsRef.current.find(r => r.linked_location_id === locId);
    if (!room) {
      showToast('No room linked to this location yet');
      return;
    }
    const switchFloor = room.floor_index !== currentFloorRef.current;
    if (switchFloor) {
      setCurrentFloor(room.floor_index);
      currentFloorRef.current = room.floor_index;
    }
    localStorage.setItem('facilitymap_selected_room_id', String(room.id));
    setTimeout(() => {
      scrollToRoom(room);
      setSelectedRoom(null);
      selectedIdRef.current = null;
      setRoomDetailRoom(room);
      // Rooms tab uses RoomInlineView — slide-in suppressed from this path
    }, switchFloor ? 150 : 0);
  }, [roomsRef, showToast, scrollToRoom]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCut = useCallback(async () => {
    const ids = Array.from(selectedIdsRef.current);
    if (ids.length !== 2) return;
    const floor = floorRoomsLive();
    const roomA = floor.find(r => r.id === ids[0]);
    const roomB = floor.find(r => r.id === ids[1]);
    if (!roomA || !roomB) return;

    // Smaller area = cutter, larger = target
    const areaA = Number(roomA.w) * Number(roomA.h);
    const areaB = Number(roomB.w) * Number(roomB.h);
    const [target, cutter] = areaA >= areaB ? [roomA, roomB] : [roomB, roomA];

    const tx1 = Number(target.x), ty1 = Number(target.y);
    const tx2 = tx1 + Number(target.w), ty2 = ty1 + Number(target.h);
    const cx1 = Number(cutter.x), cy1 = Number(cutter.y);
    const cx2 = cx1 + Number(cutter.w), cy2 = cy1 + Number(cutter.h);

    // Intersection rect
    const ix1 = Math.max(tx1, cx1), iy1 = Math.max(ty1, cy1);
    const ix2 = Math.min(tx2, cx2), iy2 = Math.min(ty2, cy2);

    if (ix2 <= ix1 || iy2 <= iy1) {
      showToast("Rooms don't overlap — nothing to cut");
      return;
    }

    // Classify which edges of target the intersection touches
    const atLeft   = ix1 <= tx1;
    const atRight  = ix2 >= tx2;
    const atTop    = iy1 <= ty1;
    const atBottom = iy2 >= ty2;

    let pts: { x: number; y: number }[];

    if (atLeft && atTop) {
      // Top-left corner → L-shape
      pts = [
        { x: ix2, y: ty1 }, { x: tx2, y: ty1 }, { x: tx2, y: ty2 },
        { x: tx1, y: ty2 }, { x: tx1, y: iy2 }, { x: ix2, y: iy2 },
      ];
    } else if (atRight && atTop) {
      // Top-right corner
      pts = [
        { x: tx1, y: ty1 }, { x: ix1, y: ty1 }, { x: ix1, y: iy2 },
        { x: tx2, y: iy2 }, { x: tx2, y: ty2 }, { x: tx1, y: ty2 },
      ];
    } else if (atLeft && atBottom) {
      // Bottom-left corner
      pts = [
        { x: tx1, y: ty1 }, { x: tx2, y: ty1 }, { x: tx2, y: ty2 },
        { x: ix2, y: ty2 }, { x: ix2, y: iy1 }, { x: tx1, y: iy1 },
      ];
    } else if (atRight && atBottom) {
      // Bottom-right corner
      pts = [
        { x: tx1, y: ty1 }, { x: tx2, y: ty1 }, { x: tx2, y: iy1 },
        { x: ix1, y: iy1 }, { x: ix1, y: ty2 }, { x: tx1, y: ty2 },
      ];
    } else if (atLeft && !atTop && !atBottom) {
      // Left edge notch
      pts = [
        { x: tx1, y: ty1 }, { x: tx2, y: ty1 }, { x: tx2, y: ty2 },
        { x: tx1, y: ty2 }, { x: tx1, y: iy2 }, { x: ix2, y: iy2 },
        { x: ix2, y: iy1 }, { x: tx1, y: iy1 },
      ];
    } else if (atRight && !atTop && !atBottom) {
      // Right edge notch
      pts = [
        { x: tx1, y: ty1 }, { x: tx2, y: ty1 }, { x: tx2, y: iy1 },
        { x: ix1, y: iy1 }, { x: ix1, y: iy2 }, { x: tx2, y: iy2 },
        { x: tx2, y: ty2 }, { x: tx1, y: ty2 },
      ];
    } else if (atTop && !atLeft && !atRight) {
      // Top edge notch
      pts = [
        { x: tx1, y: ty1 }, { x: ix1, y: ty1 }, { x: ix1, y: iy2 },
        { x: ix2, y: iy2 }, { x: ix2, y: ty1 }, { x: tx2, y: ty1 },
        { x: tx2, y: ty2 }, { x: tx1, y: ty2 },
      ];
    } else if (atBottom && !atLeft && !atRight) {
      // Bottom edge notch
      pts = [
        { x: tx1, y: ty1 }, { x: tx2, y: ty1 }, { x: tx2, y: ty2 },
        { x: ix2, y: ty2 }, { x: ix2, y: iy1 }, { x: ix1, y: iy1 },
        { x: ix1, y: ty2 }, { x: tx1, y: ty2 },
      ];
    } else {
      // Center cut — not supported
      showToast('Center cuts not supported — cutter must overlap a corner or edge');
      return;
    }

    pushHistory();

    // PUT target with polygon, clear w/h
    try {
      await api.put(`/facility-rooms/${target.id}`, { points: pts, w: 0, h: 0 });
      roomsRef.current = roomsRef.current.map(r =>
        r.id === target.id ? { ...r, points: pts, w: 0, h: 0 } : r
      );
    } catch {
      showToast('Cut failed — server error');
      return;
    }

    // DELETE cutter
    try {
      await api.delete(`/facility-rooms/${cutter.id}`);
      roomsRef.current = roomsRef.current.filter(r => r.id !== cutter.id);
    } catch { /* silent */ }

    setRooms(roomsRef.current);
    selectedIdsRef.current.clear();
    setMultiSelectCount(0);
    selectedIdRef.current = target.id;
    const updated = roomsRef.current.find(r => r.id === target.id);
    setSelectedRoom(updated ?? null);
    draw();
  }, [floorRoomsLive, pushHistory, setRooms, showToast, draw]);

  // ── Join two rooms into bounding-box union polygon ─────────────────────────
  const handleJoin = useCallback(async () => {
    const ids = Array.from(selectedIdsRef.current);
    if (ids.length !== 2) return;
    const floor = floorRoomsLive();
    const roomA = floor.find(r => r.id === ids[0]);
    const roomB = floor.find(r => r.id === ids[1]);
    if (!roomA || !roomB) return;

    const ax1 = Number(roomA.x), ay1 = Number(roomA.y), ax2 = ax1 + Number(roomA.w), ay2 = ay1 + Number(roomA.h);
    const bx1 = Number(roomB.x), by1 = Number(roomB.y), bx2 = bx1 + Number(roomB.w), by2 = by1 + Number(roomB.h);

    // Check adjacency/overlap: bounding boxes must touch or overlap
    const overlapX = ax1 < bx2 + 1 && ax2 > bx1 - 1;
    const overlapY = ay1 < by2 + 1 && ay2 > by1 - 1;
    if (!overlapX || !overlapY) {
      showToast("Rooms not adjacent — can't join");
      return;
    }

    // Larger area absorbs smaller
    const areaA = Number(roomA.w) * Number(roomA.h);
    const areaB = Number(roomB.w) * Number(roomB.h);
    const [target, smaller] = areaA >= areaB ? [roomA, roomB] : [roomB, roomA];

    // Bounding-box union rectangle
    const ux1 = Math.min(ax1, bx1), uy1 = Math.min(ay1, by1);
    const ux2 = Math.max(ax2, bx2), uy2 = Math.max(ay2, by2);
    const uw = ux2 - ux1, uh = uy2 - uy1;

    pushHistory();

    // PUT target with union rect, clear polygon points
    try {
      await api.put(`/facility-rooms/${target.id}`, { x: ux1, y: uy1, w: uw, h: uh, points: [] });
      roomsRef.current = roomsRef.current.map(r =>
        r.id === target.id ? { ...r, x: ux1, y: uy1, w: uw, h: uh, points: [] } : r
      );
    } catch {
      showToast('Join failed — server error');
      return;
    }

    // DELETE smaller room
    try {
      await api.delete(`/facility-rooms/${smaller.id}`);
      roomsRef.current = roomsRef.current.filter(r => r.id !== smaller.id);
    } catch { /* silent */ }

    setRooms(roomsRef.current);
    selectedIdsRef.current.clear();
    setMultiSelectCount(0);
    selectedIdRef.current = target.id;
    const updated = roomsRef.current.find(r => r.id === target.id);
    setSelectedRoom(updated ?? null);
    draw();
  }, [floorRoomsLive, pushHistory, setRooms, showToast, draw]);

  const handleAddFloor = useCallback(async () => {
    const name = prompt('New floor name:');
    if (!name) return;
    const idx = Math.max(...floors.map(f => f.floor_index), -1) + 1;
    await createRoom({ floor_index: idx, floor_name: name, name: 'New Room', room_number: null, type: 'other', x: 100, y: 100, w: 120, h: 80, points: rectCornerPoints(100, 100, 120, 80), doors: [], fire_wall_edges: [], linked_area_id: null, linked_location_id: null, needs_linking: false, dimensions_ft_w: null, dimensions_ft_h: null, notes: null, sort_order: 0 } as NewRoomData);
    setCurrentFloor(idx);
    currentFloorRef.current = idx;
  }, [floors, createRoom]);

  const handleLinkArea = useCallback(async (areaId: number | null, locationId: number | null): Promise<void> => {
    const cur = selectedIdRef.current;
    if (cur == null) return;
    try {
      await api.put<FacilityRoom>(`/facility-rooms/${cur}`, { linked_area_id: areaId, linked_location_id: locationId });
      roomsRef.current = roomsRef.current.map(r => r.id === cur ? { ...r, linked_area_id: areaId, linked_location_id: locationId } : r);
      setRooms(roomsRef.current);
      const updated = roomsRef.current.find(r => r.id === cur);
      if (updated) setSelectedRoom(updated);
    } catch { /* silent — linkage is non-critical */ }
  }, [setRooms]);

  const handlePrint = useCallback(() => {
    setShowPrint(false);
    window.print();
  }, []);

  // Item 4: snapshot the current floor's room data as JSON, client-side only (no server round
  // trip -- everything needed is already in roomsRef). One floor at a time, matching whatever's
  // currently displayed, so it lines up with floorRoomsLive() (the same source renderMapSvg reads).
  const handleExportRooms = useCallback(() => {
    const floorRooms = floorRoomsLive();
    const floorName = floors.find(f => f.floor_index === currentFloorRef.current)?.floor_name
      ?? floorLabel(currentFloorRef.current);
    const payload = {
      floor_index: currentFloorRef.current,
      floor_name: floorName,
      exported_at: new Date().toISOString(),
      rooms: floorRooms.map(r => ({
        id: r.id, name: r.name, room_number: r.room_number, type: r.type,
        x: r.x, y: r.y, w: r.w, h: r.h, points: r.points ?? null,
        doors: r.doors, fire_wall_edges: r.fire_wall_edges, color: r.color ?? null,
      })),
    };
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `facility-rooms-${floorName.replace(/\s+/g, '_')}-${new Date().toISOString().slice(0, 10)}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [floorRoomsLive, floors]);

  // ── Skeleton (svg background) import / export — portable file, replaces per-facility dev seed
  // scripts. Export re-fetches the row so it emits exactly what's persisted (the RAW svg_markup,
  // not the client's viewBox-rewritten svgMarkupRef). Import posts to the same /facility-floor-
  // images/svg upsert the seed script used, then bumps bgReloadKey so the load effect re-applies
  // it. Recoloring CSS / render path are untouched -- this is only the load/save plumbing. ──
  const handleExportSkeleton = useCallback(async () => {
    const fi = currentFloorRef.current;
    const floorName = floors.find(f => f.floor_index === fi)?.floor_name ?? floorLabel(fi);
    try {
      const res = await api.get<any>(`/facility-floor-images?floor_index=${fi}`);
      const row = (res as any).data;
      if (!row || !row.svg_markup) { showToast('This floor has no skeleton to export'); return; }
      const payload = {
        kind: 'proqure-facility-skeleton',
        version: 1,
        exported_at: new Date().toISOString(),
        floor_index: fi,
        floor_name: floorName,
        background_mode: row.background_mode ?? 'svg',
        svg_markup: row.svg_markup,
        svg_scale_inches_per_unit: row.svg_scale_inches_per_unit ?? null,
        opacity: row.opacity ?? 1,
        offset_x: row.offset_x ?? 0,
        offset_y: row.offset_y ?? 0,
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facility-skeleton-${floorName.replace(/\s+/g, '_')}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { showToast('Skeleton export failed'); }
  }, [floors, showToast]);

  const handleImportSkeleton = useCallback(async (file: File) => {
    const fi = currentFloorRef.current;
    try {
      const text = await file.text();
      const looksJson = file.name.toLowerCase().endsWith('.json') || text.trimStart().startsWith('{');
      let svgMarkup = '';
      let svgScale: number | null = null;
      let opacity: number | null = null;
      if (looksJson) {
        const parsed = JSON.parse(text);
        svgMarkup = typeof parsed.svg_markup === 'string' ? parsed.svg_markup : '';
        svgScale = parsed.svg_scale_inches_per_unit != null ? Number(parsed.svg_scale_inches_per_unit) : null;
        opacity = parsed.opacity != null ? Number(parsed.opacity) : null;
      } else {
        svgMarkup = text; // raw .svg — calibration falls back to whatever the floor already has
      }
      if (!svgMarkup.includes('<svg')) { showToast('No <svg> markup found in that file'); return; }
      const body: Record<string, unknown> = { floor_index: fi, svg_markup: svgMarkup };
      if (svgScale != null && svgScale > 0) body.scale = svgScale;
      if (opacity != null && opacity >= 0) body.opacity = opacity;
      const res = await api.post<any>('/facility-floor-images/svg', body);
      if (!(res as any).success || !(res as any).data) { showToast('Skeleton import failed'); return; }
      bgVisibleRef.current = true;
      setBgVisible(true);
      setBgReloadKey(k => k + 1); // re-run the floor-image load effect to apply it
      showToast('Skeleton imported');
    } catch { showToast('Skeleton import failed — invalid file'); }
  }, [showToast]);

  const handleAddBathroom = useCallback(async () => {
    const cur = selectedRoom;
    if (!cur || !isAdmin) return;
    // effectiveRect, not cur.x/cur.w directly (item 1 fix) -- for any parent room whose points had
    // already drifted from its raw x/y/w/h (the whole-room-drag bug fixed alongside this), reading
    // the raw fields placed the bathroom relative to where the parent USED to be, not where it
    // visually is -- looking like "a fixed/default corner" unrelated to the room it was added from.
    const curRect = effectiveRect(cur);
    const bx = curRect.x + curRect.w;
    const by = curRect.y;
    lockView(); // Round 12 item 3: adding a bathroom must not shift the view
    pushHistory();
    // Round 11 item 1: the connecting door now lives on the BATHROOM's own west wall, not the
    // parent's east wall. Reason: the previous approach put it on the parent, so it stayed put
    // when the bathroom was dragged, and never appeared in the bathroom's own Doors & Windows
    // list (so its swing/wall controls were unreachable). As a normal door object on the
    // bathroom it's anchored to the bathroom (travels with it -- the attached-drag path already
    // moves the whole bathroom room, door included) and gets the standard list UI. The
    // bathroom's west wall and the parent's east wall are the same physical segment at creation,
    // so a single door entry still draws the opening gap for both rooms.
    const bathroom = await createRoom({
      floor_index: currentFloorRef.current,
      floor_name: floors.find(f => f.floor_index === currentFloorRef.current)?.floor_name ?? 'Floor',
      name: 'Bathroom',
      room_number: null,
      type: 'service',
      x: bx, y: by, w: 40, h: 40,
      points: rectCornerPoints(bx, by, 40, 40),
      // Round 12 item 5: explicit width 18. DoorsSvg draws the gap+arc a length of
      // `radius = max(8, width ?? 32)` FROM the anchor -- the default 32 on a 40-unit bathroom
      // wall overshot the far corner by ~14 units, so the door looked detached AND its visual
      // centre sat far enough from the hit anchor that Shift+Click missed. 18 fits the wall (and
      // is the PropertyPanel width input's own minimum).
      doors: [{ wall: 'w', pos: 0.5, swing: 'inward', hinge: 'left', width: 18 }] as any,
      fire_wall_edges: [],
      linked_area_id: null, linked_location_id: null, needs_linking: false,
      dimensions_ft_w: null, dimensions_ft_h: null,
      notes: JSON.stringify({ parent_room_id: cur.id }), sort_order: 0,
    } as NewRoomData);
    if (bathroom) {
      // createRoom's own setRooms call (inside useFacilityRooms) hasn't been reflected back into
      // roomsRef.current yet -- append the new bathroom ourselves rather than waiting a render.
      const after = [...roomsRef.current, bathroom];
      roomsRef.current = after;
      setRooms(after);
      // Keep Discard's baseline in sync so a later "Discard Changes" doesn't erase this bathroom
      // from local state while it still exists server-side.
      lastSavedRef.current = after.slice();
      // Select the new bathroom so its door's wall / swing controls are immediately reachable.
      selectedIdRef.current = bathroom.id;
      selectedIdsRef.current.clear();
      setMultiSelectCount(0);
      setSelectedRoom(bathroom);
    } else {
      showToast('Bathroom creation failed — server error');
      draw();
      return;
    }
    draw();
  }, [selectedRoom, isAdmin, pushHistory, createRoom, floors, setRooms, draw, showToast, lockView]);

  const handleBathroomHoverChange = useCallback((hovering: boolean) => {
    bathroomPreviewActiveRef.current = hovering;
    draw();
  }, [draw]);

  // ── Delete door/window from property panel ────────────────────────────────
  const handleDeleteDoor = useCallback(async (doorIndex: number) => {
    const id = selectedIdRef.current;
    if (id == null) return;
    const room = roomsRef.current.find(r => r.id === id);
    if (!room) return;
    const newDoors = (room.doors ?? []).filter((_, i) => i !== doorIndex);
    roomsRef.current = roomsRef.current.map(r => r.id === id ? { ...r, doors: newDoors } : r);
    setRooms(roomsRef.current);
    const updated = roomsRef.current.find(r => r.id === id);
    if (updated) setSelectedRoom(updated);
    // Item 5: deleting a door invalidates any selected-door index on this room -- later entries
    // shift down by one, so a stale index would now point at the wrong door (or nothing).
    if (selectedDoorRef.current?.roomId === id) selectedDoorRef.current = null;
    draw();
    try { await api.put(`/facility-rooms/${id}`, { doors: newDoors }); } catch { /* silent */ }
  }, [setRooms, draw]);

  // ── Update door/window width from property panel ─────────────────────────
  const handleUpdateDoorWidth = useCallback(async (doorIndex: number, widthIn: number) => {
    const id = selectedIdRef.current;
    if (id == null) return;
    const room = roomsRef.current.find(r => r.id === id);
    if (!room) return;
    const newDoors = (room.doors ?? []).map((d, i) => i === doorIndex ? { ...d, width: widthIn } : d);
    roomsRef.current = roomsRef.current.map(r => r.id === id ? { ...r, doors: newDoors } : r);
    setRooms(roomsRef.current);
    const updated = roomsRef.current.find(r => r.id === id);
    if (updated) setSelectedRoom(updated);
    draw();
    try { await api.put(`/facility-rooms/${id}`, { doors: newDoors }); } catch { /* silent */ }
  }, [setRooms, draw]);

  // ── Round 20: one button cycles a door through all 4 combinations of swing (inward/outward --
  // which side of the wall it opens toward) x hinge (left/right -- which end of the opening the
  // leaf pivots from). Round 22: fixed loop order so one full cycle walks the leaf around the
  // opening without jumping across it -- consecutive clicks read as:
  //   0: inward,left  ->  1: inward,right  ->  2: outward,right  ->  3: outward,left  -> wrap.
  // Missing fields default to swing='inward', hinge='left' (what pre-Round-20 doors render as).
  // Door-only, gated by `isDoor` at the PropertyPanel call site -- windows have no swing/hinge
  // concept. ──
  const handleCycleDoorPivot = useCallback(async (doorIndex: number) => {
    const id = selectedIdRef.current;
    if (id == null) return;
    const room = roomsRef.current.find(r => r.id === id);
    if (!room) return;
    const cycle: { swing: 'inward' | 'outward'; hinge: 'left' | 'right' }[] = [
      { swing: 'inward', hinge: 'left' },
      { swing: 'inward', hinge: 'right' },
      { swing: 'outward', hinge: 'right' },
      { swing: 'outward', hinge: 'left' },
    ];
    const newDoors = (room.doors ?? []).map((d, i) => {
      if (i !== doorIndex) return d;
      const curSwing = (d as any).swing === 'outward' ? 'outward' : 'inward';
      const curHinge = (d as any).hinge === 'right' ? 'right' : 'left';
      const cur = cycle.findIndex(s => s.swing === curSwing && s.hinge === curHinge);
      const nextState = cycle[(cur + 1) % cycle.length];
      return { ...d, swing: nextState.swing, hinge: nextState.hinge };
    });
    roomsRef.current = roomsRef.current.map(r => r.id === id ? { ...r, doors: newDoors } : r);
    setRooms(roomsRef.current);
    const updated = roomsRef.current.find(r => r.id === id);
    if (updated) setSelectedRoom(updated);
    draw();
    try { await api.put(`/facility-rooms/${id}`, { doors: newDoors }); } catch { /* silent */ }
  }, [setRooms, draw]);

  // ── Round 11 item 3: move a door/window to a different wall of its own room, from the
  // Doors & Windows list. `pos` is preserved, so it stays the same fraction along the new wall.
  // Same immediate-PUT pattern as width/swing above. ──
  const handleUpdateDoorWall = useCallback(async (doorIndex: number, wall: 'n' | 'e' | 's' | 'w') => {
    const id = selectedIdRef.current;
    if (id == null) return;
    const room = roomsRef.current.find(r => r.id === id);
    if (!room) return;
    // Panel "move to wall" is a bounding-box compass choice -> drop any edge signature so the door
    // re-anchors to that bbox side. (Drag-on-canvas is the way to pin it to a specific jog segment.)
    const newDoors = (room.doors ?? []).map((d, i) => {
      if (i !== doorIndex) return d;
      const nd: any = { ...d, wall };
      delete nd.edge;
      return nd;
    });
    roomsRef.current = roomsRef.current.map(r => r.id === id ? { ...r, doors: newDoors } : r);
    setRooms(roomsRef.current);
    const updated = roomsRef.current.find(r => r.id === id);
    if (updated) setSelectedRoom(updated);
    draw();
    try { await api.put(`/facility-rooms/${id}`, { doors: newDoors }); } catch { /* silent */ }
  }, [setRooms, draw]);

  // ── Round 12 item 4: select a door/window from its Doors & Windows list row -- mirrors what a
  // canvas Shift+Click does (highlight ring via selectedDoorRef, owner already selected since the
  // panel is showing it). A subsequent plain mousedown near it on the canvas then arms doorDragRef
  // (see handleMouseDown), so a list-selected door is immediately draggable without needing Shift. ──
  const handleSelectDoorRow = useCallback((doorIndex: number) => {
    const id = selectedIdRef.current;
    if (id == null) return;
    selectedDoorRef.current = { roomId: id, doorIndex };
    doorDragRef.current = null;
    draw();
  }, [draw]);

  // ── Delete door/window from context menu ──────────────────────────────────
  const handleDeleteCtxDoor = useCallback(async () => {
    if (!ctxMenu) return;
    const { roomId, doorIdx } = ctxMenu;
    const room = roomsRef.current.find(r => r.id === roomId);
    if (!room) return;
    const newDoors = (room.doors ?? []).filter((_, i) => i !== doorIdx);
    roomsRef.current = roomsRef.current.map(r => r.id === roomId ? { ...r, doors: newDoors } : r);
    setRooms(roomsRef.current);
    if (selectedIdRef.current === roomId) {
      const updated = roomsRef.current.find(r => r.id === roomId);
      if (updated) setSelectedRoom(updated);
    }
    draw();
    try { await api.put(`/facility-rooms/${roomId}`, { doors: newDoors }); } catch { /* silent */ }
  }, [ctxMenu, setRooms, draw]);

  // ── Right-click — context menu near door/window ───────────────────────────
  const handleContextMenu = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    e.preventDefault();
    if (!canEdit) return;
    const { wx, wy } = getWorldPos(e);
    const scale = scaleRef.current;
    const floor = floorRoomsLive();
    for (const r of floor) {
      const doors = r.doors ?? [];
      for (let i = 0; i < doors.length; i++) {
        // Shared resolver -- honours effectiveRect and an `edge` signature, same as render/hit-test.
        const { hx: dx, hy: dy } = resolveDoorAnchor(r, doors[i]);
        const dist = Math.sqrt((wx - dx) ** 2 + (wy - dy) ** 2);
        if (dist * scale <= 15) {
          setCtxMenu({ x: e.clientX, y: e.clientY, roomId: r.id, doorIdx: i });
          return;
        }
      }
    }
    setCtxMenu(null);
  }, [canEdit, getWorldPos, floorRoomsLive]);

  // Item 4: select-tool cursor reflects hover/drag state. renderTick (bumped by draw()) is in the
  // dep list only so this recomputes when the hover branch or a drag tick calls draw() -- the
  // values themselves are read live from refs.
  const anyDragActive = !!(dragRef.current || resizeRef.current || markerDragRef.current || doorDragRef.current || polyVertexDragRef.current);
  const cursor = proportionalScaleActive ? 'nwse-resize'
    : tool === 'pan' ? (panRef.current ? 'grabbing' : 'grab')
    : tool === 'marker' ? 'copy'
    : (tool === 'draw' || tool === 'window') ? 'crosshair'
    : tool === 'door' ? 'cell'
    : tool === 'select' ? (anyDragActive ? 'grabbing' : hoverHandleRef.current ? 'default' : hoverSelectableRef.current ? 'move' : 'default')
    : 'default';
  void renderTick;
  const floorRoomsView = useMemo(() => rooms.filter(r => r.floor_index === currentFloor), [rooms, currentFloor]);
  const sortedSelectorAreas = useMemo(
    () => [...selectorAreaOptions].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [selectorAreaOptions]
  );
  const sortedSelectorLocations = useMemo(() => sortLocations(selectorLocationOptions), [selectorLocationOptions]);

  // ─── Render ────────────────────────────────────────────────────────────────

  const mapUI = (
    <div
      style={
        isMapFullscreen
          ? { display: 'flex', flexDirection: 'column', position: 'fixed', inset: 0, zIndex: 2000, height: '100vh', overflow: 'hidden', background: 'rgba(20,30,50,0.97)', padding: 16 }
          : { display: 'flex', flexDirection: 'column', height: 'calc(100vh - 113px)', overflow: 'hidden' }
      }
      onClick={() => ctxMenu && setCtxMenu(null)}
    >

      {/* Floor tabs + view-mode tabs -- item 1 fix: these are two INDEPENDENT pill-tab groups
          (floor selector vs. Facility Map/Rooms/Breakers), previously both nested inside a single
          shared .pill-tab-container div. That outer container's own border/background wrapped
          around both groups as one visual unit, so they read as one merged/joined bar even though
          the view-tabs group had its own inner .pill-tab-container too. This row is now a plain
          flex wrapper with its own gap; the floor tabs get their own .pill-tab-container (matching
          the view-tabs group's existing pattern), so there are genuinely two separate pill groups
          with real space between them, not one shared border spanning the whole row. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 10, flexWrap: 'wrap' }}>
        {/* Item 7 fix: gate the whole container on mapView, not just its children. The children
            were already conditional (`mapView === 'facility' && floors.map(...)`), but the
            container div itself rendered unconditionally -- on the Rooms/Breakers tabs that left
            an empty .pill-tab-container in the DOM, and with zero children an inline-flex box with
            only its own 3px padding and a 20px border-radius (much larger than the resulting ~6px
            box) collapses into a small round blob -- exactly the unstyled circle sitting just
            above "Room Directory" on the Rooms tab. */}
        {mapView === 'facility' && (
        <div className="pill-tab-container" style={{ flexWrap: 'wrap' }}>
          {floors.map(f => {
            const roomCount = rooms.filter(r => r.floor_index === f.floor_index).length;
            return (
              <button
                key={f.floor_index}
                className={`pill-tab${currentFloor === f.floor_index ? ' active' : ''}`}
                onClick={() => {
                  setCurrentFloor(f.floor_index);
                  currentFloorRef.current = f.floor_index;
                  selectedIdRef.current = null;
                  setSelectedRoom(null);
                  draw();
                  window.setTimeout(() => fitToView(), 100);
                }}
              >
                {floorLabel(f.floor_index, f.floor_name)}
                <span className="flr-count">{roomCount}</span>
              </button>
            );
          })}
          {isAdmin && (
            <button
              onClick={handleAddFloor}
              style={{ padding: '3px 10px', borderRadius: 20, fontSize: 13, cursor: 'pointer', border: '0.5px solid var(--border-default)', background: 'transparent', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4 }}
            >
              <Plus size={13} /> Floor
            </button>
          )}
        </div>
        )}

        {/* View tabs — right side of floor bar */}
        <div className="pill-tab-container" style={{ marginLeft: 'auto' }}>
          {([
            { id: 'facility' as const, label: 'Facility Map', icon: <MapIcon size={13} /> },
            { id: 'rooms'    as const, label: 'Rooms',        icon: <LayoutGrid size={13} /> },
            { id: 'breakers' as const, label: 'Breakers',     icon: <Zap size={13} /> },
          ]).map(tab => (
            <button
              key={tab.id}
              className={`pill-tab${mapView === tab.id ? ' active' : ''}`}
              onClick={() => { setMapView(tab.id); localStorage.setItem('facilitymap_subtab', tab.id); }}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
      </div>

      {mapView === 'facility' && (
      <>

      {isReadOnly && (
        <div style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 8, padding: '8px 14px', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#fcd34d' }}>
          <AlertTriangle size={15} />
          View-only mode — upgrade to Pro to edit floor plans
        </div>
      )}

      {/* Toolbars -- 6 functional groups, left to right (item 4 regroup). Each bordered cluster
          reuses the same hand-rolled "pill" chrome already established by every group in this row
          (rgba(255,255,255,0.05) bg, 8px radius, 3px padding, 1px border, 2px gap) -- not a new
          grouping style. */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>

        {/* Group 1: Select, Pan, Undo/Reset */}
        <div style={{ display: 'flex', gap: 2, background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: 3, border: '1px solid var(--border-default)' }}>
          {([
            { id: 'select' as Tool, icon: <MousePointer2 size={15} />, tip: 'Select' },
            { id: 'pan'    as Tool, icon: <Hand size={15} />,          tip: 'Pan' },
          ] as const).map(t => {
            const disabled = t.id !== 'pan' && !canEdit;
            return (
              <button
                key={t.id}
                title={t.tip}
                onClick={() => selectTool(t.id)}
                onMouseLeave={spinIconOut}
                disabled={disabled}
                className="combo-jello-spin"
                data-tint="neutral"
                style={{
                  padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-default)', cursor: disabled ? 'default' : 'pointer', display: 'flex', alignItems: 'center',
                  background: tool === t.id ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.04)',
                  color: tool === t.id ? 'var(--text-primary)' : 'var(--text-muted)',
                  opacity: disabled ? 0.4 : 1,
                }}
              >
                <span className="btn-icon">{t.icon}</span>
              </button>
            );
          })}
          <div style={{ width: 1, background: 'rgba(255,255,255,0.1)', margin: '3px 2px' }} />
          <button
            title="Undo (Ctrl+Z)"
            onClick={handleUndo}
            onMouseLeave={spinIconOut}
            disabled={historyLength === 0}
            className="combo-jello-spin"
            data-tint="neutral"
            style={{
              padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-default)', display: 'flex', alignItems: 'center',
              cursor: historyLength === 0 ? 'default' : 'pointer',
              background: 'rgba(255,255,255,0.04)',
              color: historyLength === 0 ? 'var(--text-muted)' : 'var(--text-muted)',
            }}
          >
            <RotateCcw size={15} className="btn-icon" />
          </button>
          <button
            title="Redo (Ctrl+Shift+Z)"
            onClick={handleRedo}
            onMouseLeave={spinIconOut}
            disabled={redoLength === 0}
            className="combo-jello-spin"
            data-tint="neutral"
            style={{
              padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-default)', display: 'flex', alignItems: 'center',
              cursor: redoLength === 0 ? 'default' : 'pointer',
              background: 'rgba(255,255,255,0.04)',
              color: 'var(--text-muted)',
            }}
          >
            <RotateCw size={15} className="btn-icon" />
          </button>
        </div>

        {/* Group 2: Draw Room, Add Door, Add Window, Markers (icon-only, moved in from its old
            standalone labeled slot -- no separate "+ Add Room" button anymore, it was redundant
            with the Draw Room tool icon here). */}
        <div style={{ display: 'flex', gap: 2, background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: 3, border: '1px solid var(--border-default)' }}>
          {([
            { id: 'draw'   as Tool, icon: <Square size={15} />,                 tip: 'Draw Room' },
            { id: 'door'   as Tool, icon: <Minus size={15} />,                  tip: 'Add Door' },
            { id: 'window' as Tool, icon: <SplitSquareHorizontal size={15} />,  tip: 'Add Window' },
          ] as const).map(t => {
            const disabled = !canEdit;
            return (
              <button
                key={t.id}
                title={t.tip}
                onClick={() => selectTool(t.id)}
                onMouseLeave={spinIconOut}
                disabled={disabled}
                className="combo-jello-spin"
                data-tint="neutral"
                style={{
                  padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-default)', cursor: disabled ? 'default' : 'pointer', display: 'flex', alignItems: 'center',
                  background: tool === t.id ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.04)',
                  color: tool === t.id ? 'var(--text-primary)' : 'var(--text-muted)',
                  opacity: disabled ? 0.4 : 1,
                }}
              >
                <span className="btn-icon">{t.icon}</span>
              </button>
            );
          })}
          {isAdmin && (
            <button
              title="Place facility markers"
              onClick={() => { if (canEdit) setShowMarkerPopover(v => !v); }}
              onMouseLeave={spinIconOut}
              disabled={!canEdit}
              className="combo-jello-spin" data-tint="neutral"
              style={{
                padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-default)', cursor: canEdit ? 'pointer' : 'default', display: 'flex', alignItems: 'center',
                background: tool === 'marker' ? 'rgba(34,197,94,0.18)' : showMarkerPopover ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.04)',
                color: tool === 'marker' || showMarkerPopover ? '#86efac' : 'var(--text-muted)',
                opacity: canEdit ? 1 : 0.4,
              }}
            >
              <MapPin size={15} className="btn-icon" />
            </button>
          )}
        </div>

        {/* Group 3: snap-to-grid, snap-to-rooms, show-grid, shortcuts -- unchanged, no reordering */}
        <div style={{ display: 'flex', gap: 2, background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: 3, border: '1px solid var(--border-default)' }}>
          <button
            title="Snap to grid" onClick={toggleSnap} onMouseLeave={spinIconOut}
            className="combo-jello-spin" data-tint="neutral"
            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-default)', cursor: 'pointer', display: 'flex', alignItems: 'center', background: snapEnabled ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.04)', color: snapEnabled ? 'var(--text-primary)' : 'var(--text-muted)' }}
          >
            <Magnet size={15} className="btn-icon" />
          </button>
          <button
            title="Snap to rooms"
            onClick={() => {
              const next = !snapToRooms;
              setSnapToRooms(next);
              snapToRoomsRef.current = next;
            }}
            onMouseLeave={spinIconOut}
            className="combo-jello-spin" data-tint="neutral"
            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-default)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, background: snapToRooms ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.04)', color: snapToRooms ? 'var(--text-primary)' : 'var(--text-muted)', fontSize: 11 }}
          >
            <SplitSquareHorizontal size={13} className="btn-icon" />
          </button>
          <button
            title="Show grid" onClick={toggleGrid} onMouseLeave={spinIconOut}
            className="combo-jello-spin" data-tint="neutral"
            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-default)', cursor: 'pointer', display: 'flex', alignItems: 'center', background: showGrid ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.04)', color: showGrid ? 'var(--text-primary)' : 'var(--text-muted)' }}
          >
            <Grid3X3 size={15} className="btn-icon" />
          </button>
          <button
            title="Keyboard shortcuts (?)"
            onClick={() => setShowShortcutsModal(true)}
            onMouseLeave={spinIconOut}
            className="combo-jello-spin" data-tint="neutral"
            style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-default)', cursor: 'pointer', display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.04)', color: 'var(--text-muted)' }}
          >
            <HelpCircle size={15} className="btn-icon" />
          </button>
        </div>

        {/* Group 4: zoom in, zoom out, fit to screen, fullscreen -- unchanged, no reordering */}
        <div style={{ display: 'flex', gap: 2, background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: 3, border: '1px solid var(--border-default)' }}>
          <button title="Zoom in" onClick={() => zoomByCenter(1.2)} onMouseLeave={spinIconOut} className="combo-jello-spin" data-tint="neutral" style={toolBtnStyle}><ZoomIn size={15} className="btn-icon" /></button>
          <button title="Zoom out" onClick={() => zoomByCenter(1 / 1.2)} onMouseLeave={spinIconOut} className="combo-jello-spin" data-tint="neutral" style={toolBtnStyle}><ZoomOut size={15} className="btn-icon" /></button>
          <button title="Fit to screen" onClick={() => fitToView(true)} onMouseLeave={spinIconOut} className="combo-jello-spin" data-tint="neutral" style={toolBtnStyle}><Maximize2 size={15} className="btn-icon" /></button>
          <button
            title={isMapFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen edit mode'}
            onClick={() => setIsMapFullscreen(v => !v)}
            onMouseLeave={spinIconOut}
            className="combo-jello-spin" data-tint="neutral"
            style={toolBtnStyle}
          >
            {isMapFullscreen ? <Shrink size={15} className="btn-icon" /> : <Expand size={15} className="btn-icon" />}
          </button>
        </div>

        {/* Group 4b (Round 10 item 2; Round 11 item 7): rotate the whole map view in fixed 90°
            quarter-turns. Labels + marker glyphs counter-rotate to stay upright (RoomLabelSvg /
            MarkerShapeSvg counterRot); walls/doors/grid rotate with the view. The angle chip
            doubles as reset-to-0 and only shows when rotated. */}
        <div style={{ display: 'flex', gap: 2, alignItems: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: 3, border: '1px solid var(--border-default)' }}>
          <button title="Rotate view 90° counter-clockwise" onClick={() => rotateView(-90)} onMouseLeave={spinIconOut} className="combo-jello-spin" data-tint="neutral" style={toolBtnStyle}><RotateCcw size={15} className="btn-icon" /></button>
          <button title="Rotate view 90° clockwise" onClick={() => rotateView(90)} onMouseLeave={spinIconOut} className="combo-jello-spin" data-tint="neutral" style={toolBtnStyle}><RotateCw size={15} className="btn-icon" /></button>
          {displayRot !== 0 && (
            <button
              title="Reset view rotation to 0°"
              onClick={() => rotateView(0, true)}
              style={{ ...toolBtnStyle, width: 'auto', padding: '0 6px', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}
            >
              {displayRot > 0 ? `+${displayRot}` : displayRot}°
            </button>
          )}
        </div>

        <span ref={zoomBadgeRef} style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 4 }}>{displayZoom}%</span>

        {/* Group 5: Skeleton export/import (icon-only, same size -- item 6 fix: import used to be
            a <label> wrapping a hidden file input, so it never got combo-jello-spin/spinIconOut
            (labels aren't buttons) and its default rendering box didn't match the export <button>'s
            size. Now both are real <button>s sharing the exact same toolBtnStyle object, and import
            triggers the hidden input via a ref instead of relying on label-wraps-input. Also folds
            in the background show/hide toggle and the Trace button (unchanged internals, moved). */}
        {(isAdmin || traceImageUrl || hasSvgBackground) && (
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: '3px 8px 3px 8px', border: '1px solid var(--border-default)' }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600 }}>Skeleton</span>
            {isAdmin && (
              <>
                {hasSvgBackground && (
                  <button title="Export this floor's skeleton (JSON)" onClick={handleExportSkeleton} onMouseLeave={spinIconOut} className="combo-jello-spin" data-tint="neutral" style={toolBtnStyle}>
                    <Download size={15} className="btn-icon" />
                  </button>
                )}
                <button title="Import a skeleton for this floor (.json from Export, or a raw .svg)" onClick={() => skeletonFileInputRef.current?.click()} onMouseLeave={spinIconOut} className="combo-jello-spin" data-tint="neutral" style={toolBtnStyle}>
                  <Upload size={15} className="btn-icon" />
                </button>
                <input
                  ref={skeletonFileInputRef}
                  type="file"
                  accept=".json,application/json,.svg,image/svg+xml"
                  style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) { handleImportSkeleton(f); e.target.value = ''; } }}
                />
              </>
            )}
            {(traceImageUrl || hasSvgBackground) && (
              <button
                title={`${bgVisible ? 'Hide' : 'Show'} ${backgroundModeRef.current === 'svg' ? 'skeleton' : 'trace'}`}
                onClick={() => {
                  const next = !bgVisible;
                  setBgVisible(next);
                  bgVisibleRef.current = next;
                  draw();
                }}
                onMouseLeave={spinIconOut}
                className="combo-jello-spin" data-tint="neutral"
                style={{ ...toolBtnOutlineStyle, gap: 5 }}
              >
                {bgVisible ? <EyeOff size={14} className="btn-icon" /> : <Eye size={14} className="btn-icon" />}
              </button>
            )}
            {isAdmin && (
              <button
                title="Trace image"
                onClick={() => setShowTracePanel(v => !v)}
                onMouseLeave={spinIconOut}
                className="combo-jello-spin" data-tint="neutral"
                style={{
                  ...toolBtnOutlineStyle,
                  background: showTracePanel ? 'rgba(139,92,246,0.18)' : 'rgba(255,255,255,0.04)',
                  borderColor: showTracePanel ? 'rgba(139,92,246,0.4)' : 'var(--border-default)',
                  color: showTracePanel ? '#c4b5fd' : 'var(--text-secondary)',
                  gap: 5,
                }}
              >
                <FileImage size={14} className="btn-icon" /> Trace
              </button>
            )}
          </div>
        )}

        {/* Group 6: Legend, Print, Export Rooms -- rightmost */}
        <div style={{ display: 'flex', gap: 2, background: 'rgba(255,255,255,0.05)', borderRadius: 8, padding: 3, border: '1px solid var(--border-default)' }}>
          <button
            title="Legend"
            onClick={toggleLegend}
            onMouseLeave={spinIconOut}
            className="combo-jello-spin" data-tint="neutral"
            style={{
              ...toolBtnStyle,
              // Same illuminated-active treatment as the Trace button (purple fill/border/text when
              // on), just applied when the legend panel is open instead.
              background: showLegend ? 'rgba(139,92,246,0.18)' : 'rgba(255,255,255,0.04)',
              borderColor: showLegend ? 'rgba(139,92,246,0.4)' : 'var(--border-default)',
              color: showLegend ? '#c4b5fd' : 'var(--text-secondary)',
            }}
          >
            <BookOpen size={15} className="btn-icon" />
          </button>
          <button title="Print" onClick={() => setShowPrint(true)} onMouseLeave={spinIconOut} className="combo-jello-spin" data-tint="neutral" style={toolBtnStyle}><Printer size={15} className="btn-icon" /></button>
          <button title="Export Rooms (JSON)" onClick={handleExportRooms} onMouseLeave={spinIconOut} className="combo-jello-spin" data-tint="neutral" style={toolBtnStyle}><Download size={15} className="btn-icon" /></button>
        </div>

        {canEdit && hasDirty && (
          <>
            {/* Item 8: icon-only, BUTTON_SYSTEM combos. title + aria-label keep them accessible. */}
            <button
              onClick={() => setShowCancelConfirm(true)}
              onMouseLeave={spinIconOut}
              title="Discard changes" aria-label="Discard changes"
              className="combo-cancel-jello"
              style={{ marginLeft: 'auto', padding: '5px 9px', borderRadius: 6, border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <X size={15} className="btn-icon" />
            </button>
            <button
              onClick={handleSave} disabled={isSaving}
              onMouseLeave={spinIconOut}
              title={isSaving ? 'Saving…' : 'Save changes'} aria-label="Save changes"
              className="combo2"
              style={{ padding: '5px 10px', borderRadius: 6, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            >
              <Save size={15} className="btn-icon" />
            </button>
          </>
        )}

        {canEdit && selectedRoom && (
          <button
            onClick={handleDeleteSelected}
            onMouseLeave={spinIconOut}
            title="Delete selected room" aria-label="Delete selected room"
            className="combo3-danger"
            style={{ padding: '5px 9px', borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <Trash2 size={15} className="btn-icon" />
          </button>
        )}

        {isAdmin && multiSelectCount === 2 && (
          <>
            <button
              title="Subtract smaller room from larger (corner/edge overlap)"
              onClick={handleCut}
              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(250,204,21,0.35)', background: 'rgba(250,204,21,0.1)', color: '#fde68a', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <Scissors size={13} /> Cut
            </button>
            <button
              title="Join two adjacent rooms into one"
              onClick={handleJoin}
              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(52,211,153,0.35)', background: 'rgba(52,211,153,0.1)', color: '#6ee7b7', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <Link2 size={13} /> Join
            </button>
          </>
        )}
      </div>

      {/* Trace panel */}
      {isAdmin && showTracePanel && (
        <div style={{
          marginBottom: 8,
          padding: '10px 14px',
          background: 'rgba(139,92,246,0.08)',
          border: '1px solid rgba(139,92,246,0.2)',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>
            Trace image
          </span>

          {/* Background mode switch -- only shows once there's something to switch between, and
              only needs a floor-image row id (traceImageId is now set unconditionally on load, not
              just for raster) to target the PATCH. */}
          {hasSvgBackground && traceImageUrl && traceImageId != null && (
            <div style={{ display: 'flex', gap: 2, background: 'rgba(255,255,255,0.05)', borderRadius: 6, padding: 2, border: '1px solid var(--border-default)' }}>
              <button
                onClick={() => switchBackgroundMode('svg')}
                style={{
                  padding: '4px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer', border: 'none',
                  background: backgroundModeRef.current === 'svg' ? 'rgba(139,92,246,0.25)' : 'transparent',
                  color: backgroundModeRef.current === 'svg' ? '#c4b5fd' : 'var(--text-muted)',
                }}
              >
                Skeleton
              </button>
              <button
                onClick={() => switchBackgroundMode('raster')}
                style={{
                  padding: '4px 10px', borderRadius: 5, fontSize: 12, cursor: 'pointer', border: 'none',
                  background: backgroundModeRef.current === 'raster' ? 'rgba(139,92,246,0.25)' : 'transparent',
                  color: backgroundModeRef.current === 'raster' ? '#c4b5fd' : 'var(--text-muted)',
                }}
              >
                Trace Photo
              </button>
            </div>
          )}

          {/* Round 10 item 3: rotate the background reference image itself (raster photo OR
              skeleton -- one value per floor, whichever is active), independent of the map-view
              rotation in the toolbar. Freeform degrees for straightening a crooked scan, plus
              ±90° for quick quarter-turns. Persisted server-side via saveTraceSettings ->
              facility_floor_images.rotation. Shown for skeleton-only floors too. */}
          {(traceImageUrl || hasSvgBackground) && traceImageId != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Image rotate:</span>
              <input
                type="number"
                value={traceRotation}
                min={-180}
                max={180}
                step={1}
                style={{ width: 56, padding: '3px 6px', borderRadius: 4, border: '1px solid var(--border-default)', background: 'rgba(255,255,255,0.08)', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }}
                onChange={e => {
                  const v = Number(e.target.value);
                  setTraceRotation(v);
                  traceRotationRef.current = v;
                  draw();
                }}
                onBlur={() => {
                  if (traceSaveTimerRef.current) clearTimeout(traceSaveTimerRef.current);
                  traceSaveTimerRef.current = setTimeout(() => saveTraceSettings(), 400);
                }}
              />
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>°</span>
              {([-90, 90] as const).map(d => (
                <button
                  key={d}
                  onClick={() => {
                    const v = ((((traceRotationRef.current + d) + 180) % 360) + 360) % 360 - 180;
                    setTraceRotation(v);
                    traceRotationRef.current = v;
                    draw();
                    if (traceSaveTimerRef.current) clearTimeout(traceSaveTimerRef.current);
                    traceSaveTimerRef.current = setTimeout(() => saveTraceSettings(), 400);
                  }}
                  style={{ padding: '4px 8px', borderRadius: 6, fontSize: 12, cursor: 'pointer', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-muted)' }}
                >
                  {d > 0 ? `+${d}` : d}°
                </button>
              ))}
            </div>
          )}

          {!traceImageUrl ? (
            <label style={{
              display: 'flex', alignItems: 'center', gap: 6, cursor: traceUploading ? 'default' : 'pointer',
              padding: '4px 12px', borderRadius: 6, fontSize: 13,
              background: 'rgba(139,92,246,0.18)', border: '1px solid rgba(139,92,246,0.3)',
              color: traceUploading ? 'var(--text-muted)' : '#c4b5fd',
            }}>
              <FileImage size={13} />
              {traceUploading ? 'Uploading…' : 'Upload PNG / JPG'}
              <input
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                disabled={traceUploading}
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) { handleTraceUpload(f); e.target.value = ''; }
                }}
              />
            </label>
          ) : (
            <>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                Opacity: {traceOpacity}%
              </span>
              <input
                type="range"
                min={10}
                max={70}
                value={traceOpacity}
                style={{ width: 120, accentColor: '#8b5cf6' }}
                onChange={e => {
                  const pct = Number(e.target.value);
                  setTraceOpacity(pct);
                  traceOpacityRef.current = pct / 100;
                  draw();
                }}
                onMouseUp={() => {
                  if (traceSaveTimerRef.current) clearTimeout(traceSaveTimerRef.current);
                  traceSaveTimerRef.current = setTimeout(() => saveTraceSettings(), 400);
                }}
              />
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Scale: {traceScale}% of floor plan</span>
              {/* item 1 fix: no step attribute (defaults to 1, same as Opacity), no rounding, and
                  onChange already fires continuously on every drag tick, same mechanism as Opacity
                  -- there's no actual quantization in the update path to remove. The real, concrete
                  difference is resolution per pixel of drag: Opacity maps 60 units (10-70) onto
                  120px = 0.5 units/px, while Scale mapped 250 units (50-300) onto only 100px =
                  2.5 units/px -- 5x coarser, so the same small mouse movement changes the displayed
                  percentage 5x faster, which reads as "jumping in large steps" even though the
                  value itself is moving by 1 at a time. Widening to 220px brings that down to
                  ~1.14 units/px, closing most of the gap without touching the slider's actual
                  range/behavior at all. */}
              <input
                type="range"
                min={50}
                max={300}
                value={traceScale}
                style={{ width: 220, accentColor: '#8b5cf6' }}
                onChange={e => {
                  const pct = Number(e.target.value);
                  setTraceScale(pct);
                  traceScaleRef.current = pct;
                  draw();
                }}
                onMouseUp={() => {
                  if (traceSaveTimerRef.current) clearTimeout(traceSaveTimerRef.current);
                  traceSaveTimerRef.current = setTimeout(() => saveTraceSettings(), 400);
                }}
              />
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>X offset:</span>
              <input
                type="number"
                value={traceOffsetX}
                min={-500}
                max={500}
                step={10}
                style={{ width: 60, padding: '3px 6px', borderRadius: 4, border: '1px solid var(--border-default)', background: 'rgba(255,255,255,0.08)', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }}
                onChange={e => {
                  const v = Number(e.target.value);
                  setTraceOffsetX(v);
                  traceOffsetXRef.current = v;
                  draw();
                }}
                onBlur={() => {
                  if (traceSaveTimerRef.current) clearTimeout(traceSaveTimerRef.current);
                  traceSaveTimerRef.current = setTimeout(() => saveTraceSettings(), 400);
                }}
              />
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Y offset:</span>
              <input
                type="number"
                value={traceOffsetY}
                min={-500}
                max={500}
                step={10}
                style={{ width: 60, padding: '3px 6px', borderRadius: 4, border: '1px solid var(--border-default)', background: 'rgba(255,255,255,0.08)', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }}
                onChange={e => {
                  const v = Number(e.target.value);
                  setTraceOffsetY(v);
                  traceOffsetYRef.current = v;
                  draw();
                }}
                onBlur={() => {
                  if (traceSaveTimerRef.current) clearTimeout(traceSaveTimerRef.current);
                  traceSaveTimerRef.current = setTimeout(() => saveTraceSettings(), 400);
                }}
              />
              <button
                onClick={() => {
                  setTraceScale(100); traceScaleRef.current = 100;
                  setTraceOffsetX(0); traceOffsetXRef.current = 0;
                  setTraceOffsetY(0); traceOffsetYRef.current = 0;
                  setTraceRotation(0); traceRotationRef.current = 0;
                  draw();
                  if (traceSaveTimerRef.current) clearTimeout(traceSaveTimerRef.current);
                  traceSaveTimerRef.current = setTimeout(() => saveTraceSettings(), 400);
                }}
                style={{ padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'var(--text-muted)' }}
              >
                Reset
              </button>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 6, cursor: traceUploading ? 'default' : 'pointer',
                padding: '4px 10px', borderRadius: 6, fontSize: 12,
                background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
                color: traceUploading ? 'var(--text-muted)' : 'var(--text-muted)',
              }}>
                <FileImage size={12} />
                {traceUploading ? 'Uploading…' : 'Replace'}
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  disabled={traceUploading}
                  onChange={e => {
                    const f = e.target.files?.[0];
                    if (f) { handleTraceUpload(f); e.target.value = ''; }
                  }}
                />
              </label>
              <button
                onClick={handleTraceRemove}
                style={{
                  padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
                  color: '#fca5a5', display: 'flex', alignItems: 'center', gap: 5,
                }}
              >
                <X size={12} /> Remove
              </button>
            </>
          )}
        </div>
      )}

      {/* Marker popover */}
      {isAdmin && showMarkerPopover && (
        <div style={{
          marginBottom: 8, padding: '10px 14px',
          background: 'rgba(34,197,94,0.06)', border: '1px solid rgba(34,197,94,0.2)',
          borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)', fontWeight: 500 }}>Place Marker</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, auto)', gap: 6 }}>
            {(MARKER_DEFS as readonly { id: string; color: string; symbol: string; shape: string; label: string }[]).map(m => (
              <button
                key={m.id}
                title={m.label}
                onClick={() => {
                  markerPlacementTypeRef.current = m.id;
                  selectTool('marker');
                  setShowMarkerPopover(false);
                }}
                style={{
                  padding: '4px 10px', borderRadius: 6, fontSize: 12, cursor: 'pointer',
                  border: `1px solid ${m.color}60`,
                  background: `${m.color}20`,
                  color: m.color,
                  display: 'flex', alignItems: 'center', gap: 5,
                }}
              >
                {m.symbol} {m.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setShowMarkerPopover(false)}
            style={{ padding: '4px 8px', borderRadius: 5, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          >
            <X size={12} />
          </button>
        </div>
      )}

      {/* Canvas + Property Panel -- permanent two-column grid (item 4): map flexible, panel a
          fixed 360px column, both visible at once, collapsing to a single column under 1000px
          (.facilitymap-grid in buttonAnimations.css). PropertyPanel no longer portals itself to
          document.body/overlays the map -- it's rendered as a normal grid child right here. This
          is scoped entirely to FacilityMap's own panel; the shared slide-in system (createPortal,
          LOCKED overlay/z-index rules) used by Vendors/Orders/etc. is untouched. */}
      <div className={`facilitymap-grid${(!showRoomDetail && selectedRoom && panelColumnReady) ? '' : ' no-panel'}`}>
      <div ref={containerRef} style={{ flex: 1, borderRadius: 10, overflow: 'hidden', overscrollBehavior: 'contain', border: '1px solid rgba(255,255,255,0.08)', position: 'relative', background: 'rgba(26,38,56,0.97)' }}>
        {isLoading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', zIndex: 10, color: 'var(--text-secondary)', fontSize: 14 }}>
            Loading…
          </div>
        )}
        {error && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.4)', zIndex: 10, color: '#fca5a5', fontSize: 14 }}>
            {error}
          </div>
        )}
        {!isLoading && !error && floorRoomsView.length === 0 && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none', flexDirection: 'column', gap: 8 }}>
            <Square size={32} style={{ color: 'var(--text-muted)' }} />
            <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>
              {canEdit ? 'Select "Draw Room" and drag to create rooms' : 'No rooms on this floor'}
            </span>
          </div>
        )}
        {/* Single <svg> root -- skeleton background, room polygons, doors, fire walls, markers,
            and vertex/midpoint handles all live inside ONE <g transform> (see renderMapSvg).
            No canvas, no separately CSS-transformed DOM layer to keep in sync by hand. */}
        <svg
          ref={canvasRef}
          width="100%" height="100%"
          // touchAction: 'none' -- the canvas handles all its own touch gestures (single-finger
          // tool actions, two-finger pan/pinch, see the native touch listeners). Without this the
          // browser's own pan/pinch/double-tap-zoom would fight them. Only the <svg> gets it, so
          // the rest of the page still scrolls normally on touch.
          style={{ display: 'block', cursor, touchAction: 'none' }}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={() => { previewRef.current = null; markerPreviewPosRef.current = null; draw(); handleMouseUp(); }}
          onDoubleClick={handleDblClick}
          onContextMenu={handleContextMenu}
        >
          {renderMapSvg()}
        </svg>
        {/* Proactive raster cache (see captureIdleSnapshot/scheduleIdleCapture) -- hidden by
            default, shown during an active wheel-zoom / drag-pan / pinch gesture that finds a
            ready cache (Round 17). pointerEvents 'none' so wheel/mousemove keep reaching the live
            <svg> underneath. willChange:transform IS effective here -- unlike an SVG <g>, a
            <canvas> element gets a real compositor layer, so the per-frame gesture transform is
            a GPU move, not a repaint of the vector scene. */}
        <canvas
          ref={zoomSnapshotCanvasRef}
          style={{ position: 'absolute', top: 0, left: 0, display: 'none', transformOrigin: '0 0', pointerEvents: 'none', willChange: 'transform' }}
        />
        <LegendCorner collapsed={!showLegend} onToggle={toggleLegend} />
        {/* Mobile-only on-canvas zoom control (see .fm-mobile-zoom in buttonAnimations.css --
            display:none unless the device's primary pointer is coarse/touch). Reuses zoomByCenter,
            the exact same function the toolbar's own zoom buttons call; desktop is untouched. */}
        <div className="fm-mobile-zoom" style={{ position: 'absolute', bottom: 16, right: 16, zIndex: 20, flexDirection: 'column', gap: 6 }}>
          <button
            onClick={() => zoomByCenter(1.2)}
            aria-label="Zoom in"
            style={{ width: 44, height: 44, borderRadius: 10, border: '1px solid var(--border-default)', background: 'rgba(20,30,50,0.92)', color: 'var(--text-primary)', fontSize: 22, fontWeight: 600, lineHeight: 1, cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.35)' }}
          >+</button>
          <button
            onClick={() => zoomByCenter(1 / 1.2)}
            aria-label="Zoom out"
            style={{ width: 44, height: 44, borderRadius: 10, border: '1px solid var(--border-default)', background: 'rgba(20,30,50,0.92)', color: 'var(--text-primary)', fontSize: 24, fontWeight: 600, lineHeight: 1, cursor: 'pointer', boxShadow: '0 2px 10px rgba(0,0,0,0.35)' }}
          >−</button>
        </div>
      </div>
      {!showRoomDetail && selectedRoom && panelColumnReady && (
        <PropertyPanel
          room={selectedRoom}
          canEdit={canEdit}
          canLinkEdit={user?.role === 'admin' || user?.role === 'org_admin'}
          facilityId={user?.facilityId ?? 0}
          onClose={() => { selectedIdRef.current = null; setSelectedRoom(null); selectedIdsRef.current.clear(); setMultiSelectCount(0); draw(); }}
          onChange={patchSelected}
          onDelete={handleDeleteSelected}
          onSave={handleSave}
          onLinkArea={handleLinkArea}
          isSaving={isSaving}
          isAdmin={isAdmin}
          onAddBathroom={handleAddBathroom}
          onBathroomHoverChange={handleBathroomHoverChange}
          onDeleteDoor={handleDeleteDoor}
          onUpdateDoorWidth={handleUpdateDoorWidth}
          onCycleDoorPivot={handleCycleDoorPivot}
          onUpdateDoorWall={handleUpdateDoorWall}
          onSelectDoorRow={handleSelectDoorRow}
          selectedDoorIndex={selectedDoorRef.current?.roomId === selectedRoom?.id ? selectedDoorRef.current.doorIndex : null}
          multiSelectCount={multiSelectCount}
          onCut={isAdmin ? handleCut : undefined}
          onJoin={isAdmin ? handleJoin : undefined}
          attachedRooms={rooms.filter(r => {
            if (r.id === selectedRoom.id || !r.notes) return false;
            try { return Number(JSON.parse(r.notes as string)?.parent_room_id) === selectedRoom.id; } catch { return false; }
          })}
          onSelectRoomById={handleSelectRoomById}
          onDeleteRoomById={handleDeleteRoomById}
        />
      )}
      </div>
    </>
    )}

    {mapView === 'rooms' && (
      <div style={{ flex: 1, display: 'flex', gap: 16, overflow: 'hidden' }}>

        {/* LEFT COLUMN — picker + room summary */}
        <div style={{ width: 280, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 10, border: '1px solid var(--border-default)', background: 'rgba(255,255,255,0.02)' }}>
          {/* Header */}
          <div style={{ padding: '14px 18px', borderBottom: '1px solid rgba(255,255,255,0.08)', flexShrink: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <LayoutGrid size={15} style={{ color: '#60a5fa' }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Room Directory</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Select a room to view its details
            </div>
          </div>

          {/* Picker */}
          <div style={{ padding: '16px 18px', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Browse by area and location:</div>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <button
                ref={selectorBtnRef}
                onClick={() => setSelectorOpen(v => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${selectorSelectedLabel ? 'rgba(59,130,246,0.4)' : 'var(--border-default)'}`,
                  background: selectorSelectedLabel ? 'rgba(59,130,246,0.12)' : 'rgba(255,255,255,0.06)',
                  color: selectorSelectedLabel ? '#93c5fd' : 'var(--text-secondary)',
                  fontSize: 13,
                }}
              >
                <MapPin size={13} style={{ flexShrink: 0 }} />
                <span>{selectorSelectedLabel ?? 'Select a room…'}</span>
                {selectorSelectedLabel && (
                  <span
                    onClick={e => {
                      e.stopPropagation();
                      localStorage.removeItem('facilitymap_selected_room_id');
                      localStorage.removeItem('facilitymap_selected_room_label');
                      localStorage.removeItem('facilitymap_room_tab');
                      setSelectorSelectedLabel(null);
                      setSelectorAreaId(null);
                      setSelectorLocationId(null);
                      setSelectorLocationOptions([]);
                      setSelectorOpen(false);
                      setShowRoomDetail(false);
                      setRoomDetailRoom(null);
                    }}
                    style={{ marginLeft: 4, display: 'flex', alignItems: 'center', color: 'var(--text-muted)', cursor: 'pointer' }}
                  >
                    <X size={11} />
                  </span>
                )}
                <ChevronDown size={11} style={{ flexShrink: 0, opacity: 0.5, marginLeft: 4, transform: selectorOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>

              {selectorOpen && ReactDOM.createPortal(
                <>
                  <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }} onMouseDown={() => { setSelectorOpen(false); setSelectorAreaSearch(''); }} />
                  {(() => {
                    const rect = selectorBtnRef.current?.getBoundingClientRect();
                    const popLeft = rect ? Math.min(rect.left, window.innerWidth - 490) : 100;
                    return (
                      <div
                        style={{ position: 'fixed', top: rect ? rect.bottom + 6 : 200, left: popLeft, zIndex: 1001, width: 480, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', display: 'flex', overflow: 'hidden', maxHeight: 360 }}
                        onMouseDown={e => e.stopPropagation()}
                      >
                        <div style={{ width: 200, borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column' }}>
                          <div style={{ padding: '8px 10px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                            <input autoFocus placeholder="Search areas…" value={selectorAreaSearch} onChange={e => setSelectorAreaSearch(e.target.value)} style={{ width: '100%', background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: 12 }} />
                          </div>
                          <div style={{ flex: 1, overflowY: 'auto' }}>
                            {sortedSelectorAreas.filter(a => !selectorAreaSearch || a.label.toLowerCase().includes(selectorAreaSearch.toLowerCase())).map(a => (
                              <button
                                key={a.id}
                                onMouseDown={e => { e.stopPropagation(); setSelectorAreaId(a.id); }}
                                style={{ width: '100%', display: 'block', padding: '8px 12px', background: selectorAreaId === a.id ? 'rgba(59,130,246,0.15)' : 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.04)', color: selectorAreaId === a.id ? '#93c5fd' : 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', textAlign: 'left' }}
                              >
                                {a.label}
                              </button>
                            ))}
                          </div>
                        </div>
                        <div style={{ flex: 1, overflowY: 'auto' }}>
                          {selectorAreaId == null ? (
                            <div style={{ padding: 20, color: 'var(--text-muted)', fontSize: 12, textAlign: 'center' }}>Select an area to see rooms</div>
                          ) : (
                            sortedSelectorLocations.map(l => (
                              <button
                                key={l.id}
                                onMouseDown={e => {
                                  e.stopPropagation();
                                  const aLabel = selectorAreaOptions.find(a => a.id === selectorAreaId)?.label ?? '';
                                  const selectedLabel = `${aLabel} — ${l.label}`;
                                  setSelectorSelectedLabel(selectedLabel);
                                  localStorage.setItem('facilitymap_selected_room_label', selectedLabel);
                                  handleSelectorLocationChange(l.id, selectorLocationOptions, selectorAreaOptions, selectorAreaId);
                                  setSelectorOpen(false);
                                  setSelectorAreaSearch('');
                                }}
                                style={{ width: '100%', display: 'block', padding: '8px 12px', background: 'transparent', border: 'none', borderBottom: '1px solid rgba(255,255,255,0.04)', color: 'var(--text-secondary)', fontSize: 12, cursor: 'pointer', textAlign: 'left' }}
                                onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.07)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
                              >
                                {l.label}
                              </button>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </>,
                document.body
              )}
            </div>
          </div>

          {/* Room summary card */}
          {roomDetailRoom && (() => {
            const rc = ROOM_COLORS[roomDetailRoom.type] ?? ROOM_COLORS.other;
            return (
              <div style={{ margin: '0 12px 12px', padding: '12px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.045)', backdropFilter: 'blur(16px)', border: '1px solid rgba(255,255,255,0.10)', flexShrink: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {roomDetailRoom.name}
                </div>
                {roomDetailRoom.room_number && (
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Room {roomDetailRoom.room_number}</div>
                )}
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: rc.fill, color: rc.text, border: `1px solid ${rc.stroke}` }}>
                    {ROOM_TYPE_LABELS_MAP[roomDetailRoom.type] ?? roomDetailRoom.type}
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{floorLabel(roomDetailRoom.floor_index, roomDetailRoom.floor_name)}</span>
                </div>
              </div>
            );
          })()}
        </div>

        {/* RIGHT COLUMN — inline room detail */}
        <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {roomDetailRoom ? (
            <RoomInlineView
              room={roomDetailRoom}
              facilityId={user?.facilityId ?? 0}
              userRole={user?.role ?? ''}
              initialTab={(localStorage.getItem('facilitymap_room_tab') as RIVTab | null) ?? 'overview'}
              onRoomUpdate={updated => setRooms(prev => prev.map(r => r.id === roomDetailRoom.id ? { ...r, ...updated } : r))}
              attachedRooms={rooms.filter(r => {
                if (r.id === roomDetailRoom.id || !r.notes) return false;
                try { return Number(JSON.parse(r.notes as string)?.parent_room_id) === roomDetailRoom.id; } catch { return false; }
              })}
            />
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, textAlign: 'center' }}>
              <LayoutGrid size={32} style={{ color: 'var(--text-muted)' }} />
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-muted)' }}>Select a room from the directory</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>to view its details</div>
            </div>
          )}
        </div>

      </div>
    )}

      {mapView === 'breakers' && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', gap: 0 }}>
          {/* Header */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '10px 0', marginBottom: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Zap size={16} style={{ color: '#fbbf24' }} />
              <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>Breaker Panels</span>
              {bpPanels.length > 0 && (
                <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {bpPanels.length} panel{bpPanels.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
            {canEditBreakers && (
              <button
                onClick={() => setShowCreatePanel(true)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  padding: '5px 12px', borderRadius: 7, fontSize: 12, fontWeight: 500,
                  border: '1px solid rgba(251,191,36,0.3)', background: 'rgba(251,191,36,0.1)',
                  color: '#fbbf24', cursor: 'pointer',
                }}
              >
                <Plus size={12} /> Add Panel
              </button>
            )}
          </div>

          {/* Body: left panel list / right board */}
          <div style={{ flex: 1, display: 'flex', gap: 12, overflow: 'hidden' }}>
            {/* LEFT — Panel list */}
            <div style={{ width: 220, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 6, overflowY: 'auto' }}>
              {bpLoading && (
                <div style={{ color: 'var(--text-muted)', fontSize: 12, padding: 12 }}>Loading…</div>
              )}
              {!bpLoading && bpPanels.length === 0 && (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, padding: 20, textAlign: 'center' }}>
                  <Zap size={28} style={{ color: 'rgba(251,191,36,0.3)' }} />
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    No panels yet.<br />Click Add Panel to start.
                  </div>
                </div>
              )}
              {(() => {
                const panelsByLocation = bpPanels.reduce((acc, panel) => {
                  const key = panel.location_id ?? -1;
                  if (!acc[key]) acc[key] = { label: panel.location_label ?? 'No Location', panels: [] };
                  acc[key].panels.push(panel);
                  return acc;
                }, {} as Record<number, { label: string; panels: BreakerPanel[] }>);
                const locationGroups = Object.entries(panelsByLocation).map(([k, v]) => ({
                  locationId: Number(k), label: v.label, panels: v.panels,
                }));
                return locationGroups.map(group => (
                  <div key={group.locationId} style={{ marginBottom: 4 }}>
                    <button
                      onClick={() => setExpandedLocationGroup(
                        expandedLocationGroup === group.locationId ? null : group.locationId
                      )}
                      style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 8px', borderRadius: 7, border: '1px solid var(--border-default)', background: expandedLocationGroup === group.locationId ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}
                    >
                      <span>{group.label}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 10, opacity: 0.6 }}>{group.panels.length}</span>
                        <ChevronDown size={11} style={{ transform: expandedLocationGroup === group.locationId ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
                      </div>
                    </button>
                    {expandedLocationGroup === group.locationId && (
                      <div style={{ marginTop: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {group.panels.map(panel => (
                          <div
                            key={panel.id}
                            onClick={() => {
                              const newId = selectedPanelId === panel.id ? null : panel.id;
                              setSelectedPanelId(newId);
                              if (newId) setExpandedLocationGroup(panel.location_id ?? -1);
                            }}
                            style={{ padding: '10px 12px', borderRadius: 9, cursor: 'pointer', border: `1px solid ${selectedPanelId === panel.id ? 'rgba(251,191,36,0.5)' : 'var(--border-default)'}`, background: selectedPanelId === panel.id ? 'rgba(251,191,36,0.16)' : 'rgba(255,255,255,0.03)', transition: 'all 0.15s' }}
                          >
                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 6 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {panel.name}
                                </div>
                                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                                  {panel.total_slots} slots
                                  {panel.fed_from_panel_name && (
                                    <span style={{ marginLeft: 6, color: 'rgba(251,191,36,0.6)' }}>↑ {panel.fed_from_panel_name}</span>
                                  )}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                                {canEditBreakers && (
                                  <button
                                    onClick={e => { e.stopPropagation(); setEditPanelData({ id: panel.id, name: panel.name, total_slots: panel.total_slots, notes: panel.notes ?? '', location_id: panel.location_id, area_id: panel.location_area_id ?? null, fed_from_panel_id: panel.fed_from_panel_id }); setEditPanelCustomSlots([12, 24, 40, 42].includes(panel.total_slots) ? '' : String(panel.total_slots)); setShowEditPanel(true); }}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px 4px', borderRadius: 4 }}
                                    onMouseEnter={e => (e.currentTarget.style.color = '#60a5fa')}
                                    onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
                                    title="Edit panel"
                                  >
                                    <Pencil size={11} />
                                  </button>
                                )}
                                <button
                                  onClick={e => { e.stopPropagation(); deletePanel(panel.id); }}
                                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: '2px 4px', borderRadius: 4 }}
                                  onMouseEnter={e => (e.currentTarget.style.color = '#f87171')}
                                  onMouseLeave={e => (e.currentTarget.style.color = 'var(--text-muted)')}
                                  title="Delete panel"
                                >
                                  <Trash2 size={12} />
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>

            {/* RIGHT — Breaker board */}
            <div style={{ flex: 1, borderRadius: 10, overflow: 'hidden', border: '1px solid var(--border-default)', background: 'rgba(255,255,255,0.02)', display: 'flex', flexDirection: 'column' }}>
              {!selectedPanelId ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 13 }}>
                  <Zap size={24} style={{ opacity: 0.3 }} />
                  Select a panel to view its breakers
                </div>
              ) : breakersLoading ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
                  Loading breakers…
                </div>
              ) : (() => {
                const panel = bpPanels.find(p => p.id === selectedPanelId);
                if (!panel) return null;
                const totalSlots = panel.total_slots;
                const rowCount = Math.ceil(totalSlots / 2);
                const SLOT_H = 40;
                const getBreakerAt = (slot: number, side: 'left' | 'right', position: 'A' | 'B') =>
                  panelBreakers.find(b => b.slot_number === slot && b.side === side && b.tandem_position === position) ?? null;
                // Resolves what a slot+side cell should show. A slot can hold its own breaker(s)
                // ('A' alone, or 'A'+'B' as a tandem pair) -- if it does, those win outright. Only
                // when it has NEITHER does it fall back to the old "swallowed by a taller
                // multi-pole breaker below" behavior (unchanged; multi-pole breakers are always
                // tandem_position 'A' since a half-slot tandem breaker can't also span slots).
                const getSlotOwner = (slot: number, side: 'left' | 'right') => {
                  const a = getBreakerAt(slot, side, 'A');
                  const b = getBreakerAt(slot, side, 'B');
                  if (a || b) return { a, b, isPrimary: true };
                  for (const br of panelBreakers) {
                    if (br.side !== side || br.pole_count <= 1 || br.tandem_position !== 'A') continue;
                    for (let p = 1; p < br.pole_count; p++) {
                      if (br.slot_number + (p * 2) === slot) {
                        return { a: br, b: null, isPrimary: false };
                      }
                    }
                  }
                  return { a: null, b: null, isPrimary: true };
                };
                const statusColor = (s: string) =>
                  s === 'on' ? '#10b981' : s === 'tripped' ? '#ef4444' : '#64748b';
                const statusBg = (s: string) =>
                  s === 'on' ? 'rgba(16,185,129,0.12)' : s === 'tripped' ? 'rgba(239,68,68,0.12)' : 'rgba(100,116,139,0.1)';

                // One half of a split tandem card -- its own mini clickable cell, independent of
                // the other half. Empty half shows a "+ A"/"+ B" invite instead of the slot number
                // (the slot number is already shown once, between the two legs).
                const renderTandemHalf = (
                  slot: number, side: 'left' | 'right', position: 'A' | 'B',
                  breaker: Breaker | null, align: 'left' | 'right'
                ) => (
                  <div
                    onClick={() => canEditBreakers && openBreakerSlot(slot, side, position)}
                    style={{
                      flex: 1, borderRadius: 4, position: 'relative',
                      border: `1px solid ${breaker ? statusColor(breaker.status) + '55' : 'var(--border-default)'}`,
                      background: breaker ? statusBg(breaker.status) : 'rgba(255,255,255,0.025)',
                      cursor: canEditBreakers ? 'pointer' : 'default', padding: '1px 6px',
                      display: 'flex', alignItems: 'center', overflow: 'hidden',
                      justifyContent: align === 'left' ? 'flex-start' : 'flex-end',
                      transition: 'all 0.12s',
                    }}
                    onMouseEnter={e => canEditBreakers && (e.currentTarget.style.background = breaker ? statusBg(breaker.status) : 'rgba(255,255,255,0.07)')}
                    onMouseLeave={e => canEditBreakers && (e.currentTarget.style.background = breaker ? statusBg(breaker.status) : 'rgba(255,255,255,0.025)')}
                  >
                    {breaker ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: align === 'left' ? 'flex-start' : 'flex-end', minWidth: 0, maxWidth: '100%' }}>
                        <div style={{ fontSize: 9, fontWeight: 600, color: statusColor(breaker.status), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                          {breaker.label ?? '—'}
                        </div>
                        <div style={{ fontSize: 8, color: 'var(--text-muted)', lineHeight: 1 }}>
                          {breaker.amperage_custom || (breaker.amperage ? `${breaker.amperage}A` : '')}
                        </div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 9, color: 'var(--text-muted)' }}>+ {position}</div>
                    )}
                  </div>
                );

                const renderSlot = (
                  slot: number,
                  side: 'left' | 'right',
                  a: Breaker | null,
                  b: Breaker | null,
                  align: 'left' | 'right',
                  isPrimary: boolean = true
                ) => {
                  // Split the card in half only when there's a real tandem pair to show -- 'B'
                  // already exists, or 'A' explicitly marked itself half a slot ("1/2 Slot"
                  // pole_count 0) inviting a second breaker. Every ordinary (non-tandem) breaker
                  // renders exactly as before, unsplit.
                  const isSplit = isPrimary && (b !== null || a?.pole_count === 0);
                  if (isSplit) {
                    return (
                      <div style={{ flex: 1, height: SLOT_H, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {renderTandemHalf(slot, side, 'A', a, align)}
                        {renderTandemHalf(slot, side, 'B', b, align)}
                      </div>
                    );
                  }
                  const breaker = a;
                  return (
                    <div
                      onClick={() => {
                        if (!canEditBreakers) return;
                        if (!isPrimary && breaker) {
                          openBreakerSlot(breaker.slot_number, breaker.side);
                        } else {
                          openBreakerSlot(slot, side);
                        }
                      }}
                      style={{
                        flex: 1, height: SLOT_H, borderRadius: 6, position: 'relative',
                        border: `1px solid ${!isPrimary && breaker ? statusColor(breaker.status) + '22' : breaker ? statusColor(breaker.status) + '55' : 'var(--border-default)'}`,
                        background: !isPrimary && breaker ? statusBg(breaker.status) : breaker ? statusBg(breaker.status) : 'rgba(255,255,255,0.03)',
                        borderBottom: isPrimary && breaker?.pole_count === 2
                          ? `3px solid ${statusColor(breaker.status)}`
                          : isPrimary && breaker?.pole_count === 3 ? '3px solid #a78bfa' : '1px solid var(--border-default)',
                        cursor: canEditBreakers ? 'pointer' : 'default', padding: '4px 8px',
                        display: 'flex', flexDirection: 'column', justifyContent: 'center',
                        alignItems: align === 'left' ? 'flex-start' : 'flex-end',
                        transition: 'all 0.12s', textAlign: align,
                        opacity: !isPrimary && breaker ? 0.5 : 1,
                      }}
                      onMouseEnter={e => canEditBreakers && (e.currentTarget.style.background = breaker ? statusBg(breaker.status) : 'rgba(255,255,255,0.07)')}
                      onMouseLeave={e => canEditBreakers && (e.currentTarget.style.background = !isPrimary && breaker ? statusBg(breaker.status) : breaker ? statusBg(breaker.status) : 'rgba(255,255,255,0.03)')}
                    >
                      {!isPrimary && breaker ? (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: align === 'left' ? 'flex-start' : 'flex-end' }}>
                          <div style={{ width: 3, height: '60%', borderRadius: 2, background: statusColor(breaker.status), opacity: 0.5, marginLeft: align === 'left' ? 6 : 'auto', marginRight: align === 'right' ? 6 : 'auto' }} />
                        </div>
                      ) : breaker && isPrimary ? (
                        <>
                          {breaker.pole_count !== 1 && (
                            <div style={{ position: 'absolute', top: 3, right: 4, fontSize: 9, fontWeight: 700, color: statusColor(breaker.status), opacity: 0.7 }}>
                              {breaker.pole_count === 0 ? '½' : breaker.pole_count === 2 ? '2P' : '3P'}
                            </div>
                          )}
                          <div style={{ fontSize: 11, fontWeight: 600, color: statusColor(breaker.status), whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%' }}>
                            {breaker.label ?? '—'}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
                            {breaker.amperage_custom || (breaker.amperage ? `${breaker.amperage}A` : '')}
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>#{slot}</div>
                      )}
                    </div>
                  );
                };

                return (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{panel.name}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{totalSlots}-slot panel</span>
                      <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>Click any slot to assign</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 48px 1fr', padding: '6px 16px', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Left Leg (Odd)</div>
                      <div />
                      <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'right' }}>Right Leg (Even)</div>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', padding: '8px 16px' }}>
                      {Array.from({ length: rowCount }, (_, i) => {
                        const leftSlot = i * 2 + 1;
                        const rightSlot = i * 2 + 2;
                        const leftOwner = getSlotOwner(leftSlot, 'left');
                        const rightOwner = rightSlot <= totalSlots ? getSlotOwner(rightSlot, 'right') : { a: null, b: null, isPrimary: true };
                        return (
                          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 48px 1fr', gap: 4, marginBottom: 4 }}>
                            {renderSlot(leftSlot, 'left', leftOwner.a, leftOwner.b, 'left', leftOwner.isPrimary)}
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: 'var(--text-muted)', gap: 2 }}>
                              <span>{leftSlot}</span>
                              <div style={{ width: 1, flex: 1, background: 'rgba(255,255,255,0.06)' }} />
                              {rightSlot <= totalSlots && <span>{rightSlot}</span>}
                            </div>
                            {rightSlot <= totalSlots
                              ? renderSlot(rightSlot, 'right', rightOwner.a, rightOwner.b, 'right', rightOwner.isPrimary)
                              : <div style={{ flex: 1 }} />}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Create Panel modal */}
      {showCreatePanel && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onMouseDown={() => setShowCreatePanel(false)}
        >
          <div
            style={{ background: 'var(--bg-elevated)', borderRadius: 14, width: '100%', maxWidth: 440, border: '1px solid var(--border-default)', padding: 24 }}
            onMouseDown={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20 }}>
              <Zap size={16} style={{ color: '#fbbf24' }} />
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>Add Breaker Panel</h3>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Panel Name *</label>
              <input autoFocus className="form-input" placeholder='"Main Panel", "Sub-Panel B"' value={newPanelName} onChange={e => setNewPanelName(e.target.value)} onKeyDown={e => e.key === 'Enter' && createPanel()} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Location</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                <select className="form-input" value={newPanelAreaId ?? ''} onChange={e => { const id = e.target.value ? Number(e.target.value) : null; setNewPanelAreaId(id); setNewPanelLocationId(null); }} style={{ fontSize: 13 }}>
                  <option value="">Select area…</option>
                  {newPanelAreaOptions.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
                <select className="form-input" value={newPanelLocationId ?? ''} onChange={e => setNewPanelLocationId(e.target.value ? Number(e.target.value) : null)} disabled={!newPanelAreaId || newPanelLocOptions.length === 0} style={{ fontSize: 13 }}>
                  <option value="">Select location…</option>
                  {newPanelLocOptions.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Fed From (Sub-Panel)</label>
              <select className="form-input" value={newPanelFedFrom ?? ''} onChange={e => setNewPanelFedFrom(e.target.value ? Number(e.target.value) : null)} style={{ fontSize: 13 }}>
                <option value="">None (Main Panel)</option>
                {bpPanels.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Panel Size</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[12, 24, 40, 42].map(s => (
                  <button key={s} type="button" onClick={() => { setNewPanelSlots(s); setNewPanelCustomSlots(''); }} style={{ padding: '4px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, border: `1px solid ${newPanelSlots === s && newPanelCustomSlots === '' ? 'rgba(251,191,36,0.5)' : 'rgba(255,255,255,0.12)'}`, background: newPanelSlots === s && newPanelCustomSlots === '' ? 'rgba(251,191,36,0.12)' : 'transparent', color: newPanelSlots === s && newPanelCustomSlots === '' ? '#fbbf24' : 'var(--text-muted)', cursor: 'pointer' }}>{s}</button>
                ))}
                <input className="form-input" placeholder="Custom" value={newPanelCustomSlots} onChange={e => { setNewPanelCustomSlots(e.target.value); setNewPanelSlots(0); }} style={{ width: 70, fontSize: 12, padding: '4px 8px', textAlign: 'center' }} />
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Notes</label>
              <textarea className="form-input" placeholder="Optional notes about this panel…" value={newPanelNotes} onChange={e => setNewPanelNotes(e.target.value)} style={{ resize: 'vertical', minHeight: 60, fontSize: 13 }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setShowCreatePanel(false)}>Cancel</button>
              <button type="button" onClick={createPanel} disabled={!newPanelName.trim() || savingPanel} style={{ padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, border: '1px solid rgba(251,191,36,0.4)', background: 'rgba(251,191,36,0.15)', color: '#fbbf24', cursor: 'pointer', opacity: !newPanelName.trim() || savingPanel ? 0.5 : 1 }}>
                {savingPanel ? 'Creating…' : 'Create Panel'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Panel modal */}
      {showEditPanel && editPanelData && ReactDOM.createPortal(
        <div style={{ position:'fixed',inset:0,zIndex:1000,background:'rgba(0,0,0,0.7)',backdropFilter:'blur(8px)',display:'flex',alignItems:'center',justifyContent:'center',padding:16 }} onMouseDown={() => setShowEditPanel(false)}>
          <div style={{ background:'var(--bg-elevated)',borderRadius:14,width:'100%',maxWidth:440,border:'1px solid var(--border-default)',padding:24 }} onMouseDown={e => e.stopPropagation()}>
            <div style={{ display:'flex',alignItems:'center',gap:8,marginBottom:20 }}>
              <Zap size={16} style={{ color:'#fbbf24' }} />
              <h3 style={{ fontSize:15,fontWeight:600,color:'var(--text-primary)',margin:0 }}>Edit Panel</h3>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11,fontWeight:500,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em',display:'block',marginBottom:6 }}>Panel Name *</label>
              <input className="form-input" value={editPanelData.name} onChange={e => setEditPanelData(d => d ? {...d, name: e.target.value} : d)} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11,fontWeight:500,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em',display:'block',marginBottom:6 }}>Location</label>
              <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:8 }}>
                <select className="form-input" value={editPanelData?.area_id ?? ''} onChange={e => { const id = e.target.value ? Number(e.target.value) : null; setEditPanelData(d => d ? {...d, area_id: id, location_id: null} : d); }} style={{ fontSize:13 }}>
                  <option value="">Select area…</option>
                  {editPanelAreaOptions.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
                <select className="form-input" value={editPanelData?.location_id ?? ''} onChange={e => setEditPanelData(d => d ? {...d, location_id: e.target.value ? Number(e.target.value) : null} : d)} disabled={!editPanelData?.area_id || editPanelLocOptions.length === 0} style={{ fontSize:13 }}>
                  <option value="">Select location…</option>
                  {editPanelLocOptions.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11,fontWeight:500,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em',display:'block',marginBottom:6 }}>Fed From</label>
              <div style={{ display:'flex',gap:8,alignItems:'center' }}>
                <select className="form-input" value={editPanelData.fed_from_panel_id ?? ''} onChange={e => setEditPanelData(d => d ? {...d, fed_from_panel_id: e.target.value ? Number(e.target.value) : null} : d)} style={{ flex:1,fontSize:13 }}>
                  <option value="">None (Main Panel)</option>
                  {bpPanels.filter(p => p.id !== editPanelData.id).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                {editPanelData.fed_from_panel_id && (
                  <button type="button" onClick={() => { setShowEditPanel(false); setSelectedPanelId(editPanelData.fed_from_panel_id); }} style={{ padding:'5px 10px',borderRadius:7,fontSize:12,border:'1px solid rgba(59,130,246,0.3)',background:'rgba(59,130,246,0.1)',color:'#60a5fa',cursor:'pointer' }}>
                    Go to Panel →
                  </button>
                )}
              </div>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:11,fontWeight:500,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em',display:'block',marginBottom:6 }}>Total Slots</label>
              <div style={{ display:'flex',gap:6,flexWrap:'wrap' }}>
                {[12,24,40,42].map(s => (
                  <button key={s} type="button" onClick={() => { setEditPanelData(d => d ? {...d, total_slots: s} : d); setEditPanelCustomSlots(''); }} style={{ padding:'4px 12px',borderRadius:6,fontSize:12,fontWeight:500,border:`1px solid ${editPanelData.total_slots===s && editPanelCustomSlots===''?'rgba(251,191,36,0.5)':'var(--border-default)'}`,background:editPanelData.total_slots===s && editPanelCustomSlots===''?'rgba(251,191,36,0.12)':'transparent',color:editPanelData.total_slots===s && editPanelCustomSlots===''?'#fbbf24':'var(--text-muted)',cursor:'pointer' }}>{s}</button>
                ))}
                <input className="form-input" placeholder="Custom" value={editPanelCustomSlots} onChange={e => { const v = e.target.value; setEditPanelCustomSlots(v); const n = parseInt(v); setEditPanelData(d => d ? {...d, total_slots: n || d.total_slots} : d); }} style={{ width: 70, fontSize: 12, padding: '4px 8px', textAlign: 'center' }} />
              </div>
            </div>
            <div style={{ marginBottom:20 }}>
              <label style={{ fontSize:11,fontWeight:500,color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.06em',display:'block',marginBottom:6 }}>Notes</label>
              <textarea className="form-input" value={editPanelData.notes} onChange={e => setEditPanelData(d => d ? {...d, notes: e.target.value} : d)} style={{ resize:'vertical',minHeight:60,fontSize:13 }} />
            </div>
            <div style={{ display:'flex',gap:8,justifyContent:'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setShowEditPanel(false)}>Cancel</button>
              <button type="button" disabled={!editPanelData.name.trim() || savingEditPanel} onClick={async () => { if (!editPanelData) return; setSavingEditPanel(true); try { await apiClient.put(`/breaker-panels/${editPanelData.id}`, { name: editPanelData.name.trim(), location_id: editPanelData.location_id, total_slots: editPanelData.total_slots, notes: editPanelData.notes.trim() || null, fed_from_panel_id: editPanelData.fed_from_panel_id || null }); setShowEditPanel(false); await loadBpPanels(); } catch(e) { console.error('[edit-panel]', e); } finally { setSavingEditPanel(false); } }} style={{ padding:'6px 16px',borderRadius:8,fontSize:13,fontWeight:500,border:'1px solid rgba(251,191,36,0.4)',background:'rgba(251,191,36,0.15)',color:'#fbbf24',cursor:'pointer',opacity:!editPanelData.name.trim()||savingEditPanel?0.5:1 }}>
                {savingEditPanel ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Breaker slot edit modal */}
      {editingBreaker && ReactDOM.createPortal(
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onMouseDown={() => setEditingBreaker(null)}
        >
          <div
            style={{ background: 'var(--bg-elevated)', borderRadius: 14, width: '100%', maxWidth: 500, border: '1px solid var(--border-default)', padding: 24, maxHeight: '90vh', overflowY: 'auto' }}
            onMouseDown={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
              <h3 style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
                Slot #{editingBreaker.slot} — {editingBreaker.side === 'left' ? 'Left' : 'Right'} Leg
                {editingBreaker.position === 'B' && <span style={{ color: '#fbbf24', fontWeight: 500 }}> · Half B</span>}
                {editingBreaker.position === 'A' && bkPoleCount === 0 && <span style={{ color: '#fbbf24', fontWeight: 500 }}> · Half A</span>}
              </h3>
              {editingBreaker.existing && (
                <button type="button" onClick={deleteBreaker} disabled={deletingBreaker} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, padding: '3px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', color: '#f87171', cursor: 'pointer' }}>
                  <Trash2 size={11} /> {deletingBreaker ? 'Removing…' : 'Remove'}
                </button>
              )}
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Label</label>
              <input autoFocus className="form-input" placeholder='"Room 101 outlets", "Kitchen lights"' value={bkLabel} onChange={e => setBkLabel(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Amperage</label>
                <select className="form-input" value={bkAmperage} onChange={e => { if (e.target.value === 'custom') { setBkAmperage('custom'); } else { setBkAmperage(e.target.value); setBkAmperageCustom(''); } }} style={{ fontSize: 13 }}>
                  <option value="">—</option>
                  {[15, 20, 30, 40, 50, 60, 80, 100, 150, 200].map(a => <option key={a} value={a}>{a}A</option>)}
                  <option value="custom">Custom…</option>
                </select>
                {bkAmperage === 'custom' && (
                  <input className="form-input" placeholder="e.g. 90A, 125A" value={bkAmperageCustom} onChange={e => setBkAmperageCustom(e.target.value)} style={{ marginTop: 6, fontSize: 12 }} />
                )}
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Status</label>
                <select className="form-input" value={bkStatus} onChange={e => setBkStatus(e.target.value as 'on'|'off'|'tripped')} style={{ fontSize: 13 }}>
                  <option value="on">On</option>
                  <option value="off">Off</option>
                  <option value="tripped">Tripped</option>
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Pole Configuration</label>
              <div style={{ display: 'flex', gap: 6 }}>
                {([{ v: 0, label: '½ Slot' }, { v: 1, label: '1 Pole' }, { v: 2, label: '2 Pole' }, { v: 3, label: '3 Pole' }] as {v:0|1|2|3; label:string}[]).map(opt => (
                  <button key={opt.v} type="button" onClick={() => setBkPoleCount(opt.v)} style={{ flex: 1, padding: '5px 0', borderRadius: 7, fontSize: 12, fontWeight: 500, border: `1px solid ${bkPoleCount===opt.v?'rgba(251,191,36,0.5)':'var(--border-default)'}`, background: bkPoleCount===opt.v?'rgba(251,191,36,0.12)':'transparent', color: bkPoleCount===opt.v?'#fbbf24':'var(--text-muted)', cursor: 'pointer' }}>
                    {opt.label}
                  </button>
                ))}
              </div>
              {/* Tandem ("1/2") breaker: a slot marked ½ Slot can hold a second, independent
                  breaker sharing the same physical position (tandem_position 'B'). This switches
                  between editing the two halves -- each is its own breaker row, saved separately. */}
              {editingBreaker.position === 'A' && bkPoleCount === 0 && (() => {
                const bExisting = panelBreakers.find(bk => bk.slot_number === editingBreaker.slot && bk.side === editingBreaker.side && bk.tandem_position === 'B');
                if (!editingBreaker.existing) {
                  return <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>Save this half first, then its tandem partner can be added.</div>;
                }
                return (
                  <button type="button" onClick={() => openBreakerSlot(editingBreaker.slot, editingBreaker.side, 'B')} style={{ marginTop: 8, width: '100%', padding: '6px 0', borderRadius: 7, fontSize: 12, fontWeight: 500, border: '1px solid rgba(251,191,36,0.35)', background: 'rgba(251,191,36,0.08)', color: '#fbbf24', cursor: 'pointer' }}>
                    {bExisting ? 'Edit Other Half (B) →' : '+ Add Other Half (Tandem)'}
                  </button>
                );
              })()}
              {editingBreaker.position === 'B' && (
                <button type="button" onClick={() => openBreakerSlot(editingBreaker.slot, editingBreaker.side, 'A')} style={{ marginTop: 8, width: '100%', padding: '6px 0', borderRadius: 7, fontSize: 12, fontWeight: 500, border: '1px solid rgba(251,191,36,0.35)', background: 'rgba(251,191,36,0.08)', color: '#fbbf24', cursor: 'pointer' }}>
                  ← Back to First Half (A)
                </button>
              )}
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Linked Rooms / Locations</label>
                <button type="button" onClick={() => setBkLocations(locs => [...locs, { areaId: null, locationId: null, roomId: null, areaLabel: '', locationLabel: '', note: '', areaOptions: newPanelAreaOptions, locOptions: [] }])} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 5, border: '1px solid rgba(59,130,246,0.3)', background: 'rgba(59,130,246,0.08)', color: '#60a5fa', cursor: 'pointer' }}>
                  + Add Location
                </button>
              </div>
              {bkLocations.map((loc, idx) => (
                <div key={idx} style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 6, border: '1px solid rgba(255,255,255,0.07)', background: 'rgba(255,255,255,0.03)' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                    <select className="form-input" value={loc.areaId ?? ''} onChange={async e => {
                      const areaId = e.target.value ? Number(e.target.value) : null;
                      let locOpts: {id:number;label:string}[] = [];
                      if (areaId) {
                        const res = await api.get(`/dropdowns?dropdown_type=location&facility_id=${user?.facilityId}&parent_id=${areaId}`);
                        locOpts = ((res as any).options ?? []) as {id:number;label:string}[];
                      }
                      setBkLocations(locs => locs.map((l, i) => i === idx ? { ...l, areaId, locationId: null, roomId: null, locOptions: locOpts } : l));
                    }} style={{ fontSize: 12 }}>
                      <option value="">Select area…</option>
                      {newPanelAreaOptions.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                    </select>
                    <select className="form-input" value={loc.locationId ?? ''} onChange={e => { const locationId = e.target.value ? Number(e.target.value) : null; const roomId = locationId ? (rooms.find(r => r.linked_location_id === locationId)?.id ?? null) : null; setBkLocations(locs => locs.map((l, i) => i === idx ? { ...l, locationId, roomId } : l)); }} disabled={!loc.areaId || loc.locOptions.length === 0} style={{ fontSize: 12 }}>
                      <option value="">Select room…</option>
                      {loc.locOptions.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                    </select>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input className="form-input" placeholder='Location note (e.g. "north wall outlets")' value={loc.note} onChange={e => setBkLocations(locs => locs.map((l, i) => i === idx ? { ...l, note: e.target.value } : l))} style={{ flex: 1, fontSize: 12 }} />
                    <button type="button" onClick={async () => { if (loc.id && editingBreaker?.existing) { await removeBreakerLocation(loc.id, editingBreaker.existing.id); } setBkLocations(locs => locs.filter((_, i) => i !== idx)); }} style={{ background: 'none', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 6, cursor: 'pointer', padding: '0 8px', color: 'rgba(239,68,68,0.5)' }} onMouseEnter={e => (e.currentTarget.style.color = '#f87171')} onMouseLeave={e => (e.currentTarget.style.color = 'rgba(239,68,68,0.5)')}>
                      <X size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Notes</label>
              <textarea className="form-input" placeholder="Optional notes about this breaker…" value={bkNotes} onChange={e => setBkNotes(e.target.value)} style={{ resize: 'vertical', minHeight: 52, fontSize: 13 }} />
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={() => setEditingBreaker(null)}>Cancel</button>
              <button type="button" onClick={saveBreaker} disabled={savingBreaker} style={{ padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 500, border: '1px solid rgba(59,130,246,0.4)', background: 'rgba(59,130,246,0.15)', color: '#60a5fa', cursor: 'pointer', opacity: savingBreaker ? 0.5 : 1 }}>
                {savingBreaker ? 'Saving…' : 'Save Breaker'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Context menu — right-click near door/window */}
      {ctxMenu && (
        <div
          style={{
            position: 'fixed', left: ctxMenu.x, top: ctxMenu.y, zIndex: 600,
            background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
            borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.45)', minWidth: 170, overflow: 'hidden',
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          <button
            onClick={() => { handleDeleteCtxDoor(); setCtxMenu(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 14px', background: 'none', border: 'none', color: '#fca5a5', fontSize: 13, cursor: 'pointer', textAlign: 'left' }}
          >
            <Trash2 size={13} /> Delete door/window
          </button>
        </div>
      )}

      {/* Marker panel — selected marker */}
      {selectedMarker && markerPanelPos && (
        <div
          style={{
            position: 'fixed', left: markerPanelPos.x, top: markerPanelPos.y, zIndex: 500,
            background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
            borderRadius: 10, padding: '12px 14px', minWidth: 210,
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
          }}
          onMouseDown={e => e.stopPropagation()}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
              {(MARKER_DEFS as readonly { id: string; label: string }[]).find(m => m.id === selectedMarker.marker_type)?.label ?? selectedMarker.marker_type}
            </span>
            <button
              onClick={() => { setSelectedMarker(null); setMarkerPanelPos(null); selectedMarkerIdRef.current = null; draw(); }}
              style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', padding: 2 }}
            >
              <X size={14} />
            </button>
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Label</div>
            <input
              value={selectedMarker.label ?? ''}
              onChange={e => setSelectedMarker(prev => prev ? { ...prev, label: e.target.value } : prev)}
              onBlur={async e => {
                const label = e.target.value || null;
                markersRef.current = markersRef.current.map(m => m.id === selectedMarker.id ? { ...m, label } : m);
                draw();
                try { await api.put(`/facility-markers/${selectedMarker.id}`, { label }); } catch { /* silent */ }
              }}
              placeholder="Optional label…"
              style={{ width: '100%', boxSizing: 'border-box', padding: '5px 8px', borderRadius: 5, border: '1px solid var(--border-default)', background: 'rgba(255,255,255,0.06)', color: 'var(--text-primary)', fontSize: 12, outline: 'none' }}
            />
          </div>
          {selectedMarker.marker_type === 'extinguisher' && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Link Extinguisher</div>
              {!showExtLinkList ? (
                <button
                  onClick={async () => {
                    if (extOptions.length === 0) {
                      const res = await api.get<any>('/extinguishers');
                      setExtOptions((res.data ?? []).map((ex: any) => ({ id: ex.id, tag: ex.tag_number ?? String(ex.id), location_label: ex.location_label })));
                    }
                    setShowExtLinkList(true);
                  }}
                  style={{ fontSize: 12, color: '#93c5fd', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                >
                  {selectedMarker.linked_extinguisher_id ? `Linked #${selectedMarker.linked_extinguisher_id}` : 'Link extinguisher…'}
                </button>
              ) : (
                <select
                  size={4}
                  style={{ width: '100%', fontSize: 12, background: 'rgba(255,255,255,0.06)', border: '1px solid var(--border-default)', color: 'var(--text-primary)', borderRadius: 5, padding: 4 }}
                  onChange={async e => {
                    const lid = e.target.value ? Number(e.target.value) : null;
                    setShowExtLinkList(false);
                    setSelectedMarker(prev => prev ? { ...prev, linked_extinguisher_id: lid } : prev);
                    markersRef.current = markersRef.current.map(m => m.id === selectedMarker.id ? { ...m, linked_extinguisher_id: lid } : m);
                    draw();
                    // Item 2 (this round): no room_id write needed -- linking still records which
                    // real inventory record a marker refers to (used to enrich the Rooms tab's
                    // Extinguishers list with real details when present), but a marker's presence
                    // IN a room's list no longer depends on this link at all, only on position.
                    try { await api.put(`/facility-markers/${selectedMarker.id}`, { linked_extinguisher_id: lid }); } catch { /* silent */ }
                  }}
                >
                  <option value="">— None —</option>
                  {extOptions.map(ex => (
                    <option key={ex.id} value={ex.id}>{ex.tag}{ex.location_label ? ` (${ex.location_label})` : ''}</option>
                  ))}
                </select>
              )}
            </div>
          )}
          <button
            onClick={async () => {
              const mid = selectedMarker.id;
              markersRef.current = markersRef.current.filter(m => m.id !== mid);
              setSelectedMarker(null); setMarkerPanelPos(null);
              selectedMarkerIdRef.current = null;
              draw();
              try { await api.delete(`/facility-markers/${mid}`); } catch { /* silent */ }
            }}
            style={{ width: '100%', padding: '6px 0', borderRadius: 6, border: '1px solid rgba(239,68,68,0.35)', background: 'rgba(239,68,68,0.1)', color: '#fca5a5', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}
          >
            <Trash2 size={12} /> Delete Marker
          </button>
        </div>
      )}

      {showRoomDetail && roomDetailRoom && (
        <RoomDetailPanel
          room={roomDetailRoom}
          facilityId={user?.facilityId ?? 0}
          onClose={() => { setShowRoomDetail(false); setRoomDetailRoom(null); }}
        />
      )}

      {showPrint && (
        <PrintModal
          floorName={floorLabel(currentFloor, floors.find(f => f.floor_index === currentFloor)?.floor_name)}
          facilityName={user?.facilityName ?? 'Facility'}
          onClose={() => setShowPrint(false)}
          onPrint={handlePrint}
        />
      )}
      {showCancelConfirm && (
        <CancelConfirmModal
          onClose={() => setShowCancelConfirm(false)}
          onConfirm={handleCancel}
        />
      )}
      {showShortcutsModal && <ShortcutsModal onClose={() => setShowShortcutsModal(false)} />}

      {/* Delete room confirmation (item 5) -- ported verbatim from Vendors.tsx's own delete-vendor
          confirm dialog (modal-overlay/modal design-system classes, btn-danger on the confirm
          button, mousedown-target tracking so a click-drag-release starting inside the modal but
          ending on the overlay doesn't accidentally close it), not a new one-off pattern. */}
      {confirmDeleteRoom && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          // z-index override: .modal-overlay's default 1000 (global.css) sits BELOW the
          // fullscreen map wrapper's z-index:2000 (both position:fixed, both portaled to
          // document.body as siblings -- stacking is by z-index value, not DOM order). In
          // fullscreen mode the modal was rendering but fully hidden/unclickable behind the
          // opaque fullscreen backdrop. Bumping just this instance above 2000 fixes fullscreen
          // without touching the shared .modal-overlay class used elsewhere in the app.
          style={{ zIndex: 2100 }}
          onMouseDown={(e) => { deleteModalMouseDownTarget.current = e.target; }}
          onMouseUp={(e) => { if (deleteModalMouseDownTarget.current === e.currentTarget && e.target === e.currentTarget) setConfirmDeleteRoom(null); deleteModalMouseDownTarget.current = null; }}
        >
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3 className="modal-title">Delete Room</h3>
              <button className="modal-close" onClick={() => setConfirmDeleteRoom(null)}>
                <X size={18} className="btn-icon" />
              </button>
            </div>
            <p style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
              Are you sure you want to delete <strong style={{ color: 'var(--text-primary)' }}>{confirmDeleteRoom.name}</strong>?
              This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost combo-cancel-jello" onClick={() => setConfirmDeleteRoom(null)}>
                Cancel
              </button>
              <button className="btn btn-danger" onClick={executeDeleteRoom}>
                Delete Room
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Toast */}
      {toastMsg && (
        <div style={{
          position: 'fixed', bottom: 28, left: '50%', transform: 'translateX(-50%)',
          zIndex: 3000, background: 'rgba(20,30,50,0.97)',
          border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
          padding: '10px 22px', color: '#f1f5f9', fontSize: 13,
          boxShadow: '0 4px 20px rgba(0,0,0,0.5)', whiteSpace: 'nowrap',
        }}>
          {toastMsg}
        </div>
      )}
    </div>
  );
  return isMapFullscreen ? ReactDOM.createPortal(mapUI, document.body) : mapUI;
}

const toolBtnStyle: React.CSSProperties = {
  padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border-default)', cursor: 'pointer',
  background: 'rgba(255,255,255,0.04)', color: 'var(--text-muted)', display: 'flex', alignItems: 'center',
};
const toolBtnOutlineStyle: React.CSSProperties = {
  padding: '5px 10px', borderRadius: 8, border: '1px solid var(--border-default)',
  background: 'rgba(255,255,255,0.04)', color: 'var(--text-secondary)', cursor: 'pointer', display: 'flex', alignItems: 'center',
};
